import * as THREE from 'three/webgpu';
import rawRenderTuning from '../../content/tuning/render.json';
import {
  DEBUG_OVERLAY_USERDATA,
  VramAccountant,
  censusScene,
  markOverlay,
  type VramCensus,
} from '../render/assets/vramAccountant';
import {
  BAKE_ONLY,
  hemisphereFillSh,
  holeFillIntensity,
  isBakeOnlyLight,
  resolveIndirect,
  type IndirectTerm,
} from '../render/indirect';
import type { AssetService } from '../assets';
import type { LightingSystemId } from '../render/lighting';
// The world's punctual lights (E2). Eager, and it must be: the rig is read once during scene
// construction so it is complete before the first material compiles and before `liveLighting()`
// forms the provenance fingerprint (`lightModel.ts` argues both). It pulls only `three`, the
// BAKE_ONLY key and the sun's own angle converter — nothing lazy, nothing GI.
import {
  applySun,
  createWorldLights,
  describeLightRig,
  type WorldLightRigInfo,
} from '../render/lights/worldLights';
// B3: the ONE applier of `castShadow`/`receiveShadow` (a declaration per class, a walk + a
// `childadded` hook), the sun's cascade rig, and the pure tables both read.
import { applyShadowFlags, shadowCensus, type ShadowCensus } from '../render/lights/shadowFlags';
import { createCsmRig, type CsmRig, type CsmState } from '../render/lights/csmRig';
// Wraps ONE three function so identical WGSL stops being compiled under 1,952 different names.
// Must run before the first material build — see the call site in `createScene`.
import { installSharedBufferNames } from '../render/sharedBufferNames';
import {
  ENTITY_SHADOW_DEFAULT,
  SHADOW_FLAGS,
  localShadowBudget,
  parseSunShadowSplitMode,
  sunShadowPolicy,
  type AppliedShadowUpdate,
  type SunShadowSplitMode,
} from '../render/lights/lightModel';
import type { StressInfo } from '../render/stress';
import { makeSurface } from '../render/materials/surfaceFactory';
import {
  createRenderPipeline,
  type PipelineState,
  type RenderPipelineHandle,
} from '../render/pipeline';
import { createResolutionController, type ResolutionStatus } from '../render/resolution';
import {
  SKY_OFF,
  bakeFarForSky,
  createSky,
  sunPositionOf,
  type SkyHandle,
  type SkyInfo,
} from '../render/sky';
import {
  DEFAULT_POWER_PREFERENCE,
  describeGpu,
  GPU_REMEDY,
  parsePowerPreference,
  probeGpus,
  type GpuProbe,
} from '../render/gpuAdapter';
import { detectTier, type Tier } from '../render/tier';
/*
 * ⚠️ THE ONE `render/gi/**` VALUE IMPORT IN THIS EAGER FILE, and it is deliberate. `gi()` is
 * synchronous, so the bake-state derivation cannot be behind an `await` like every other GI
 * module here; `bakeState.ts` is dependency-free and five lines of `if` precisely so that being
 * eager costs nothing. `bakeQuality` is TYPE-ONLY (erased) — its table is dynamic-imported inside
 * the bake bracket with the rest.
 */
import { deriveBakeState, type BakeState } from '../render/gi/bakeState';
// Eager and dependency-free by design (`probeBrick.ts`'s header). Only the DEFAULT of the
// depth-visibility lever is taken from here — importing it from `sparseVolume`, where the uniform
// is born, would drag the light class and the whole load path into the ENTRY chunk.
/*
 * ⚠️ THE SECOND `render/gi/**` VALUE IMPORT IN THIS EAGER FILE, and it is deliberate for the
 * same reason as the first. The activation is resolved during scene CONSTRUCTION — before any
 * `await import` of the GI chunk could have happened — because it decides whether that chunk is
 * ever reached at all. `giActivation.ts` is dependency-free (no three, no zod, no probeFile)
 * precisely so being eager costs nothing.
 */
import { resolveGiActivation, type GiActivation } from '../render/gi/giActivation';
// TYPE ONLY — erased at transform, so the dev-only debug-view chunk stays out of the main bundle.
// The VALUE import is one `await import(...)` inside a compile-time `import.meta.env.DEV` branch
// (`tests/renderGiFence.test.ts` asserts exactly that, and `checkBundle` asserts the output).
import type { DebugViewHandle } from '../render/debugView/view';
// S5: type-only — the overlay's input shape, so mount can hand the physics world's colliders in.
import type { CollisionShape, Vec3 as CollisionVec3 } from '../world/collision';
import type {
  DebugViewInfo,
  ProbeViewPatch,
  ProbeViewSettings,
} from '../render/debugView/registry';
import type { BakeQualityName, ResolvedBakeQuality } from '../render/gi/bakeQuality';
// TYPE ONLY, and the module it names is dependency-free on purpose (`render/gi/bakePlan.ts`): the
// EDITOR value-imports it for the bake panel's text, and the editor chunk IS emitted in production.
// Nothing of the compiler travels with it — `giBakePlan()` below runs the compiler behind the same
// compile-time DEV gate as the rest of the bake.
import type { GiBakePlan, GiBakePlanVolume } from '../render/gi/bakePlan';
import {
  parseResolutionTuning,
  resolveOutputPixelRatio,
  resolveUpscaleMode,
} from '../render/tuning';
import { parseUpscaleMode } from '../render/upscale';
import { CharacterView } from './characterView';
import {
  ROLL_ANIM_EXIT,
  rollDip,
  rollLead,
  rollReachBlend,
  rollSpinAngle,
} from '../anim/playerGraph';
// platformPosAt stays a CODE import while platform DEFS arrive as world data (E0.5): the defs
// are content, but the trajectory math is runtime behavior shared with the physics service and
// the headless tests — that asymmetry is deliberate (worlds carry data, never code; ADR-0001).
import { platformPosAt } from '../level/gym';
import type { FeedbackEvent } from '../sim/feedback';
import type { MotionState } from '../sim/motion';
import { surfaceFor, type MovementTuning, type SurfaceMap } from '../sim/tuning';
import type { V3 } from '../sim/vec';
import { describeFogPlan, resolveFogPlan, type FogPlan, type FogState } from '../render/fog/fogModel';
import { createHeightFog, type HeightFogHandle } from '../render/fog/fogNode';
import { authoredGiVolumes, resolveFogConfig, resolveGiAdaptive } from '../world/schema';
import type { WorldData, WorldFogConfig, WorldLightingConfig } from '../world/schema';
import { DustVfx } from './vfx';
import { damped, smoothDamp } from './spring';

/**
 * Presentation layer: strictly downstream of sim state. WebGPURenderer with automatic WebGL2
 * fallback — the shipped baseline is defined on the fallback path (see architecture doc, D2.2).
 *
 * MATRIX POLICY (rendering plan §4.1, R1): this module flips the GLOBAL default
 * `Object3D.DEFAULT_MATRIX_AUTO_UPDATE = false` at import time — static objects pay zero
 * scene-graph cost per frame; everything that MOVES must opt back in explicitly, because a
 * missed opt-in is a frozen visual with NO error. The current opt-in set:
 *   - camera (orbit rig / editor fly / lookAt every frame)
 *   - sun.shadow.camera (LightShadow.updateMatrices repositions it via position+lookAt)
 *   - player group + its whole wrapper chain SUBTREE: squash, charHolder, rollSpin, body, nose,
 *     and the async-loaded character root incl. its BONES (skinned animation composes local
 *     matrices every frame) — via optInSubtree(player), which also hooks `childadded` so
 *     late-attached children (the character glTF) opt in automatically
 *   - entityRoot subtree — host-spawned object3ds (pots, shards, markers), hook-propagated so
 *     anything an entity adds later (shards mid-play) opts in too
 *   - editorRoot subtree — selection helper, gizmo proxy, TransformControls helper, drag preview
 *   - gym + platforms InstancedMesh objects (instance matrices are attribute data and unaffected,
 *     but the mesh object itself must not freeze if ever moved), the vfx points, the arc line
 *   - probe grid Light (dev toggle), cube camera inside the vendored baker (FORK note there)
 * Static-but-posed-once objects (ground plane, sun) call updateMatrix() ONCE after posing.
 * Separate mini-scenes (editor thumbnails) carry their own opt-ins — see src/editor/thumbnails.ts.
 * NOTE for the Friday visual pass: confirm nothing froze (especially editor gizmo + character).
 *
 * Camera (pulled forward from the M3 spec, C1/C5 subset): manual orbit + critically damped
 * springs on the tracked target, ground-projected Y (no bob on every jump — follow Y only when
 * grounded, on a big drop, or after 0.9 s airborne), velocity lookahead, and a speed FOV kick.
 * Still M3: collision pull-in, framing-by-pitch, trauma shake, auto-yaw assist.
 */
export interface CameraRig {
  yaw: number;
  pitch: number;
  distance: number;
  /** ms since the player last touched the camera — gates the auto-yaw assist. Set by the host. */
  msSinceCamInput: number;
  /** Auto-yaw assist toggle (panel-bound). Never engages while airborne. */
  autoYaw: boolean;
}

/** Camera feel — panel-bound so feathering is a slider, not a rebuild. */
export interface CameraTuning {
  horizSmooth: number;
  lookaheadTime: number;
  lookaheadMax: number;
  /** Lateral lookahead multiplier — sideways swings are the nausea source, damp them hardest. */
  lookaheadLateral: number;
  lookaheadSmooth: number;
  fovKickDeg: number;
  autoYawRateDeg: number;
}

/**
 * Default probe spacing for the dev grid, in metres — mirrors `gi/probeVolume.ts`'s
 * `DEFAULT_SPACING_M`, duplicated as a literal so the eager scene module never imports the lazy
 * GI chunk just to read one number.
 */
const GI_DEFAULT_SPACING_M = 4;

/**
 * …and the probe BUDGET, mirroring `gi/probeVolume.ts`'s `DEFAULT_MAX_PROBES` for the same reason.
 *
 * It is a HARD budget now, not a coarsening trigger: over it, `probeVolume()` THROWS
 * (`ProbeBudgetError`) and this bracket reports `gi.state: 'failed'`. The old 4,096 existed to
 * bound a tab freeze by silently making the grid coarser, and that is the behaviour that made
 * every "1 m bake" in G2-G4 something other than 1 m. The number is high because the thing it
 * guards is BAKE TIME, the bake is a build step, and a request that does not fit must be told so.
 */
/*
 * ⭐ RAISED 262,144 → 500,000, AND THE NEW NUMBER HAS A REASON THE OLD ONE DID NOT.
 *
 * 262,144 was a round power of two with no measured basis, and its stated justification — bake
 * time — moved by 24× when the traced producer landed: Sponza is 51,009 captures at ~1,034 s
 * rasterised and ~43 s traced. Scaled linearly, 262,144 is ~90 minutes rasterised and ~3.5 minutes
 * traced. A cap sized against the first number is the wrong size for the second.
 *
 * ⚠️ IT IS **NOT** RAISED TO THE HARDWARE CEILING, and the 4× headroom below that ceiling is not
 * margin — it is a DIFFERENT RAIL nobody wrote down next to the first. `probeGrid.makeFacingCache`
 * asserts 1,048,576 captures for `maxTextureDimension2D`, but `probeCompile`'s
 * `atlasBytes > maxAtlasBytes` binds first: at 64 B per probe and Sponza's measured 1.784 padding
 * ratio, the 64 MB atlas budget is reached around 560,000 captures. 500,000 is therefore the
 * largest ceiling at which the PROBE-COUNT refusal still fires BEFORE the atlas-byte one — which
 * is what keeps the error message truthful. Past it the two rails cross and an artist who authored
 * too many probes is told about atlas bytes instead.
 *
 * The real argument for raising it at all is Mars: 242 × 688 × 102 m playable is ~272,000 probes
 * at the 4 m default spacing, for the playable box alone, before a single volume is authored. The
 * level could not bake by either producer. `GiSchema.maxProbes` now lets a world say its own
 * number; this is only the fallback.
 */
const GI_DEFAULT_MAX_PROBES = 500_000;

/**
 * The bake's cube-face resolution and SH sample count. Literals until G5; named now because the
 * offline baker STAMPS THEM INTO THE HEADER, and a setting that is recorded as provenance has to
 * have one definition or the record is of a bake nobody ran.
 *
 * 32 is also load-bearing for `src/render/sky.ts` trap 4: at this size the 1.07° solar disc is a
 * fifth of a texel, which is why hiding it measured as no change at all. Raising it makes that a
 * systematic bias rather than a lottery — so the number is not free to move quietly, and the
 * header now records which one an atlas was made with.
 */
/**
 * The HARD budget on ALLOCATED brick-atlas bytes, and it is a SECOND, INDEPENDENT rail beside the
 * capture budget (`?giMaxProbes=`) rather than a derived one. `maxCaptures` bounds PROBES; the
 * atlas grid is sized from BRICKS, and the two diverge badly on a sparse authoring — 500,000 lone
 * bricks are only 62.5 M captures but an 80 x 108 x 58 grid, i.e. 3.5 GB, with every dimension
 * legal and nothing thrown (`probeCompile.ts` §2.5).
 *
 * 64 MB is roughly ten times the Mars estimate (~6.1 MB allocated for ~830 bricks) and twenty
 * times a true 1.2 m Sponza volume. It is a rail against a pathological authoring, not a budget an
 * artist is meant to work against — memory is explicitly NOT the constraint here, and nothing
 * coarsens when it is hit: the compile throws and `gi.state` reads `'failed'`.
 */
/**
 * ── ⭐ THE FLOOR A PROBE VOLUME IS CLAMPED TO, AND THE FLOOR ITS PROVENANCE IS JUDGED AGAINST ────
 *
 * `probeVolume({ floorY })` exists because *"probes buried under the floor are the single largest
 * waste in the bake"* — a synthesised volume is deliberately CUT OFF at y = 0 rather than following
 * the drawn geometry downward. That is a good rule and it is not the subject here. What matters is
 * that the same number has to be used by `checkProvenance`'s `geometryDrift` arm, or the two
 * disagree — and they DID, measured on `playground-01` on 2026-09-03:
 *
 *     drawn geometry escapes the baked grid by 1.03 m
 *       (grid 32.0x16.0x48.0 m at [0.0,8.0,-8.0], drawn 27.0x3.2x30.5 m at [0.0,0.6,-10.3])
 *
 * The atlas was written by THIS BUILD, minutes earlier, from THIS world, and covers every metre of
 * it laterally. The only escape is 1.03 m of geometry BELOW y = 0 — i.e. exactly the region the
 * volume was told not to cover. The bake and the check were each right on their own and refused
 * each other's output.
 *
 * ⚠️ AND IT IS A RACE, WHICH IS WHY IT SURVIVED THIS LONG. `drawnBounds` walks the LIVE graph, so
 * whether the sub-floor object is in the box at all depends on what has finished loading when
 * `onAllLoaded` fires. Measured in the same session: a cold boot refused this atlas and a warm
 * reload of the SAME page bound it. "Sometimes my bake loads" is the shape the owner has been
 * reporting, and this is one mechanism for it.
 *
 * So the drawn box is clamped to this floor before it is judged, at the ONE place that builds the
 * live provenance. Everything the check was written for survives: lateral escape, vertical escape
 * UPWARD, and the 1882 x 1279 x 861 m unwritten-matrix-chain case that the arm exists to catch.
 */
const GI_FLOOR_Y = 0;

const GI_MAX_ATLAS_BYTES = 64 * 1024 * 1024;

const GI_BAKE_CUBEMAP_SIZE = 32;
/**
 * ── THE SH PROJECTION'S SAMPLE COUNT — the DEFAULT, overridable with `?giSamples=` ─────────────
 *
 * Each probe's nine coefficients are estimated from this many point samples of a
 * `6 × 32² = 6,144`-texel cube. 256 of 6,144 is a 4 % sampling, and the error it leaves is not
 * white noise on screen: it is UNCORRELATED BETWEEN NEIGHBOURING PROBES, so a small bright feature
 * (a lit window, a bright floor patch) lands differently in adjacent cages and reads as a
 * low-frequency MOTTLE across a wall — the worst form for it to take.
 *
 * ⭐ AND IT IS THE CHEAPEST QUALITY AXIS WE HAVE, which is worth stating precisely because it
 * inverts the intuition. Per probe the bake does SIX CUBE-FACE SCENE RENDERS (full material
 * shading, shadow maps, 600–2,300 draws each on Sponza) and ONE 10-fragment projection pass whose
 * fragments loop `sampleCount` cube taps. At 256 that is 2,560 texture taps against six real
 * frames of Sponza. Doubling it is invisible next to the capture; doubling the PROBE COUNT is not.
 * So probe count and bounces are the two levers that cost bake time, and this one is nearly free.
 *
 * The default stays 256 until the A/B in this stage's report is repeated on content that is not
 * Sponza; `?giSamples=` and `--samples` exist so the number can be measured rather than argued,
 * and the header already records which one an atlas was made with.
 *
 * ⚠️ `GI_BAKE_CUBEMAP_SIZE` NOW HAS A KNOB (`?giCubemap=` / `--cubemap`, and `production` sets 64)
 * AND THE CAVEAT THAT ONCE ARGUED AGAINST ONE STANDS UNCHANGED — it is a caveat, not a veto, and
 * conflating the two would have left a preset axis the owner asked for silently missing. 32 is
 * load-bearing for `sky.ts` trap 4: at this size the 1.07° solar disc is a fifth of a texel, which
 * is why hiding it for the capture measured as no change at all. The bake ALWAYS hides the disc,
 * so a larger cube is not a correctness risk — what it changes is that measurement's headroom,
 * from a lottery into a systematic bias. So: **no sun-related number taken at 32 transfers to
 * another cube size**, the DEFAULT stays 32 (every such measurement was taken there), the page
 * says so out loud on any bake that is not at 32, and the header records which one an atlas was
 * made with. The knob exists so the number can be recorded, not so it can be moved casually.
 */
const GI_BAKE_SAMPLE_COUNT = 256;

/**
 * ── ⭐ THE ANSWER TO "I PRESSED BAKE — DID THIS TAB PICK IT UP?" (L2) ─────────────────────────
 *
 * Flat, JSON-serializable and reported rather than toasted, for the same reason `GiBakePlan`'s
 * refusal is DATA: the panel renders it in place, the e2e asserts it with no pixel baseline, and a
 * rebind that could not happen says WHY instead of leaving the artist to infer it from a picture
 * that did not change.
 *
 * `probesBefore === probesAfter` is not a failure — a re-bake of an unchanged world writes the
 * same capture count — which is why `ok` is its own field and the two counts are reported beside
 * it rather than compared into a verdict here.
 */
export interface GiRebindResult {
  /** Did a volume from the file on disk end up bound to this session? */
  ok: boolean;
  /**
   * One word, and the vocabulary is deliberately the loader's own plus two of this path's:
   * `fetchProbeVolume`'s four failures, `checkProvenance`'s reason for a refusal, `'not-file'`
   * (this session is not running baked GI at all) and `'tier'` (the WebGL2 fallback / tier 0).
   * Null on success.
   */
  reason: string | null;
  /** The sentence a human reads. Empty on success. */
  detail: string;
  /** Captures bound BEFORE the call, and after it. 0 means nothing was bound. */
  probesBefore: number;
  probesAfter: number;
  /**
   * How the new field reached the shader: `republish` re-points the two permanent nodes at new
   * bytes (no shader touched), `construct` builds a light (every lit material recompiles), `none`
   * means nothing was bound. It is REPORTED because the two have very different costs and a
   * session that quietly started taking the expensive one should be visible.
   */
  how: 'republish' | 'construct' | 'none';
}

/**
 * The probe grid's self-report. Flat + JSON-serializable — the dev hook publishes it, and the
 * whole point is that a degenerate bake is visible in the TABLE rather than only in the picture.
 */
export interface GiInfo {
  /**
   * `baking` is set BEFORE the bake starts and survives it, because the bake is one long
   * synchronous JS turn: without it, "the trigger never fired" and "the bake is still running"
   * are the same observation — which is exactly the ambiguity that cost a 15-minute test timeout.
   */
  state: 'baking' | 'baked' | 'failed';
  /**
   * ⭐ **IS THE LIGHTING YOU ARE LOOKING AT CURRENT FOR THIS WORLD?** (owner direction #4)
   *
   * `unbaked | baking | baked | stale | failed` — the model, its derivation and the two limits it
   * knowingly carries are all at `probeFile.deriveBakeState`, which is where it is computed.
   *
   * ⚠️ THIS IS NOT `state` WITH MORE VALUES. `state` answers *"did the bake function run"*;
   * this answers *"is this world's lighting current"*. They were one field until now, which is
   * why a REFUSED file and a world that was never baked were the same observation
   * (`source: 'none'`) — and those two demand completely different actions from an artist.
   *
   * DERIVED on the way out of `gi()`, never stored: there are eight sites that build a `GiInfo`
   * and a state remembered at eight sites is a state that will be wrong at one of them.
   */
  bakeState: BakeState;
  /**
   * ⭐ HOW MANY TIMES THIS SESSION RE-BOUND A FILE WITHOUT A RELOAD (L2).
   *
   * 0 for every boot that has only ever loaded once — which is every shipping session. It is the
   * witness that separates *"the atlas was already bound when the page opened"* from *"the atlas
   * arrived after a bake, in this tab, with no reload"*, and those are indistinguishable from
   * every other field: `source`, `bakeState`, `probes` and the header all read identically.
   *
   * DERIVED ON THE WAY OUT of `gi()` from one counter, exactly like `bakeState`, so none of the
   * `giInfo` assignment sites can forget it or disagree about it.
   */
  rebinds: number;
  on: boolean;
  /** Probe counts per axis. */
  resolution: [number, number, number];
  /** `nx * ny * nz` — six scene renders each. */
  probes: number;
  /** Realised probe spacing per axis, in metres. */
  spacingM: [number, number, number];
  /**
   * The spacing that was requested. Under `axon.probeVolume/2` NOTHING COARSENS IT — a request
   * that does not fit its budget is a `ProbeBudgetError`, not a quieter grid — so this and
   * `spacingM` can no longer disagree by anything but the per-axis `round(extent/s)+1`
   * quantisation. `coarsened` and `cappedAxes` are DELETED with the mechanisms that set them; the
   * argument is at `probeVolume.ProbeBudgetError`.
   */
  spacingRequestedM: number;
  /**
   * The COMPILED sparse structure, or null when this volume did not come from one (the in-browser
   * dense bake, or no volume at all).
   *
   * Every one of these is here because a measurement that cannot quote them is not reproducible
   * (checklist item 5). `bricksByLevel` in particular is the "is it actually nested" witness: a
   * structure with one nonzero entry compiled every brick at one density, which is the silent
   * coarsening this format exists to make impossible, wearing a new hat.
   */
  structure: {
    brickCount: number;
    captureCount: number;
    bricksByLevel: number[];
    transitionBricks: number;
    indirDims: [number, number, number];
    brickGrid: [number, number, number];
    indirectionBytes: number;
    atlasBytes: number;
    paddingRatio: number;
    normalBiasM: number;
  } | null;
  /** `?giIntensity=` — 1 unless a run is deliberately exaggerating the grid to prove it is alive. */
  intensity: number;
  /*
   * ⛔ `visibility` WAS HERE (F2 → O3) — the Chebyshev term's 0…1 strength, reported beside
   * `intensity` so a leak number could never be quoted without the arm it was measured on. The
   * term is cut (`render/gi/probeBrick.ts`), so the field would now report a lever that does not
   * exist — and a debug surface reporting a dead field is how a reader concludes a term is on.
   */
  /** The volume's extents, in metres. */
  sizeM: [number, number, number];
  /** The volume's world-space centre. */
  centerM: [number, number, number];
  /** BUILD-time cost of the whole bake, in ms. Never a frame cost. */
  bakeMs: number;
  /** `bakeMs / probes` — the number that scales to an offline baker's budget. */
  bakeMsPerProbe: number;
  /** `?giBounces=` — extra passes after the direct one. 1 by default (one bounce). */
  bounces: number;
  /**
   * ⭐ THE QUALITY PRESET IN FORCE (`?giQuality=` / `--quality`), or null for a volume that
   * predates presets or was never baked from one (the spike fixture, a file with no `bake.quality`).
   *
   * `name` alone is a REQUEST; the FACT is the sibling fields it resolved to — `bounces`,
   * `validity`, `cubemapSize`, `sampleCount`, `dilationPasses` and `spacingM` — and `overrides`
   * names where an explicit flag beat the preset. Reported because two atlases of one world at two
   * qualities are the same size, load identically and differ only in how good the light is: a
   * measurement that cannot say which one it measured is not a measurement.
   */
  quality: { name: string; overrides: string[] } | null;
  /** The bake's cube-face resolution. See `bakeQuality.cubemapSize` for why this one is not free. */
  cubemapSize: number;
  /** `?giSamples=` — the SH projection's sample count. */
  sampleCount: number;
  /**
   * `?giValidity=1` — per-probe validity was computed and the SH stored pre-multiplied by it
   * (`render/gi/probeGrid.ts`, FORK block). Reported because the bake COST roughly doubles with
   * it, and because "did the validity pass actually run?" must be answerable from state.
   */
  validity: boolean;
  /**
   * HOW THE BOUND FILE'S BRICKS WERE PLACED (B2-sparse) — `bake.placement` off the header, or the
   * compile's own report for an in-tab bake. `null` ⇒ the file predates placement records. Read
   * by the bake panel (`BakeStatusInput.placement`) to say PLACEMENT WILL CHANGE before a re-bake.
   */
  placement?: { adaptive: boolean; capturesUniform: number } | null;
  /**
   * THE SEAM GUARD (G1.4) — how many rendered frames violated **"parented AND visible ⇒ the atlas
   * exists"**. It must be 0 forever, and since F3 it is 0 STRUCTURALLY rather than by watching.
   *
   * That is the invariant R1.6-D broke. A dense `LightProbeGrid` in a light set with
   * `texture === null` made `LightProbeGridNode.setup()` take its "contribute nothing" branch, and
   * three's light cache key cannot see `light.texture` — so the do-nothing program was cached
   * FOREVER, and one such frame was permanent damage. The per-frame counter existed because the
   * damage is invisible after the fact.
   *
   * ⭐ THE LIGHT CLASS WITH THE BRANCH IS DELETED. `SparseProbeVolume` is constructed HOLDING a
   * valid 1-brick all-zero atlas and a 1-texel indirection, so `SparseProbeVolumeNode.setup()` has
   * nothing to branch on and exactly ONE structural variant exists for the life of the page; an
   * unpublished volume contributes nothing by ARITHMETIC (`EMPTY_BRICK`'s texels are zero, the
   * validity divide yields 0), never by an early return. That is stated at the site
   * (`render/gi/sparseVolume.ts`) and asserted headlessly by the cold/warm graph census in
   * `tests/renderSparseAddress.test.ts` — a fence at the shader graph, which is stronger than a
   * frame counter that can only report damage already done.
   *
   * The field stays 0 and stays reported, because every existing measurement prints it and a
   * silently-dropped invariant reads as an invariant nobody checks.
   */
  seamBreaks: number;
  /**
   * How many punctual lights were marked BAKE-ONLY, integrated into the atlas and then removed
   * from the scene the moment `bake()` returned. This is UE's static-light model: an artist's
   * light that costs nothing at runtime because the volume is carrying it. See
   * `src/render/indirect.ts` → "BAKE-ONLY LIGHTS".
   *
   * ⭐ TWO SOURCES SINCE E2, and they are counted together because the invariant is the same for
   * both: the world's own `mobility: 'static'` lights (`world.lighting.lights[]` — the authored
   * path, present on every bake of a world that declares one) and `?bakeLights=1`, the dev sweep
   * that marks EVERY punctual light in the scene. 0 when a world authors no static light and the
   * lever is off — i.e. in every shipped world today.
   */
  bakedLights: number;
  /**
   * THE SECOND SEAM GUARD — frames that rendered a bake-only light while the atlas existed, i.e.
   * frames whose punctual light was counted TWICE (once in the SH, once live). Same invariant as
   * `seamBreaks`, same required value: 0, forever. A comment is not a fence.
   */
  bakedLightLeaks: number;
  /**
   * ⭐ WHERE THIS ATLAS CAME FROM (G5) — `'file'` (loaded from `worlds/<id>/gi/`), `'bake'` (the
   * in-browser SPARSE bake, `axon.probeVolume/2`), or `'none'` (no volume; the constant fill is
   * carrying the indirect term).
   *
   * `'dense'` — the v1 `LightProbeGrid` lattice, `?gi=dense` — IS GONE (F3). It was a separate
   * value rather than a second `'bake'` because the two produced DIFFERENT ATLASES, a lattice
   * versus a brick set; with the lattice retired there is one producer and the distinction has no
   * referent. Old G0–G4 numbers do not transfer regardless: `axon.probeVolume/3` refuses every
   * atlas baked before it.
   *
   * `'spike'` is the third and it is DEV-ONLY: the hand-built S1 fixture (`?giSparseSpike=1`),
   * which is neither baked nor loaded and whose light is a two-brick atlas written out by hand. It
   * is a value of this field rather than a separate flag for exactly the reason the field exists —
   * a run that cannot say where its atlas came from is not reproducible, and a spike frame looks
   * like a lit frame.
   *
   * Reported because after G5 there are TWO ways to arrive at a lit room and **a screenshot cannot
   * tell them apart**. Every measurement taken from here on has to print this field or it is not
   * reproducible: "the arcade reads 67.8" means nothing without "…from a 1 m atlas loaded off disk"
   * versus "…from a 2 m atlas baked in the tab".
   */
  source: 'bake' | 'file' | 'none' | 'spike';
  /**
   * Why a baked FILE was refused, or null. `reason` is one of `probeFile.ts`'s `StaleReason`s plus
   * the fetch failures; `detail` is the human sentence.
   *
   * A stale atlas is REFUSED, never used with a warning — see `checkProvenance`'s ruling. This
   * field is how a test asserts that the refusal happened and which fence caught it, instead of
   * grepping console output.
   */
  stale: { reason: string; detail: string } | null;
  /**
   * ⭐ WHICH BAKER MADE THE FILE THIS SESSION BOUND — `header.producer`, verbatim.
   *
   * `null` ⇒ the header predates the field (every atlas written before today), which is a DIFFERENT
   * fact from "the rasteriser made it" and is reported as a different sentence. `undefined` ⇒ this
   * `GiInfo` was not built from a loaded file at all, and the question has no answer to read: for
   * `source: 'bake'` the producer is the page's rasteriser BY CONSTRUCTION, and for `'none'` /
   * `'spike'` there is no file.
   *
   * Reported for the same reason `source` and `quality` are: a rasterised atlas and a ray-traced
   * one of the same world are the same size, load identically, pass every provenance fence, and
   * differ only in how good the light is. A measurement — or an owner's eye — that cannot say which
   * one it is looking at is not a measurement. `probeFile.describeProbeProducer` is the ONE place a
   * name becomes words; nothing here interprets the string.
   */
  producer?: string | null;
  /** Payload bytes of the loaded file, or 0. `probes × 56` by construction — worth reporting so a
   *  truncated or over-large file is visible in the table rather than only in a failed decode. */
  fileBytes: number;
  /**
   * ⭐ DILATION (G6), READ OFF THE LOADED HEADER — never off a URL or a command line.
   *
   * Null for a source that is not a file. A dilated and an undilated atlas are the same size, load
   * identically, satisfy every provenance fence and differ only in how bright the interior is, so
   * "which arm is this?" is not answerable from any other observable — and a measurement that
   * cannot say which arm it measured is not a measurement. `filled` is the count that separates a
   * dilation that ran from one that was merely requested.
   */
  dilation: {
    passes: number;
    filled: number;
    /**
     * ⚠️ PRESENT ONLY FOR A BAKE IN THIS SESSION. A loaded FILE can report `passes` and `filled`
     * and nothing else — the header carries those two and the atlas cannot be re-interrogated,
     * because a dilated capture is indistinguishable from one that legitimately saw that light.
     * So these are optional as a statement of fact, not as a convenience: `invalid` is the number
     * that separates "the fill had nothing to do" from "the fill did nothing", and only the
     * process that ran it can know it.
     */
    invalid?: number;
    remaining?: number;
    filledByLevel?: number[];
  } | null;
}

/** B3 — `GameScene.shadows()`. Every number is read off the scene graph, none off a declaration. */
export interface ShadowState {
  receivers: {
    /** The player group: capsule + nose, and the glTF character once it has landed. */
    player: ShadowCensus;
    /** The loaded character subtree alone, or null while the capsule stands in. */
    character: ShadowCensus | null;
    /** The companion's root (`axon-companion`), or null on a world without one. */
    companion: ShadowCensus | null;
    /** Everything under the entity root. */
    entities: ShadowCensus;
  };
  csm: CsmState;
}

/** One placement's colliders, world space, as the `collision` overlay draws them (S5). */
export interface CollisionOverlayPlacement {
  index: number;
  shapes: readonly CollisionShape[];
  ucxMissingAt: CollisionVec3 | null;
}

/** What `createScene` needs from the asset library (P1). Both objects are mount's, not ours. */
export interface SceneLibraryDeps {
  /** The one `AssetService`. The character's body and clips are acquired from it in the BOOT lane. */
  assets: AssetService;
  /** The accountant the service charges — `getVramMB()`'s `ledgerMB` term reads this same object. */
  vram: VramAccountant;
}

export interface GameScene {
  renderer: THREE.WebGPURenderer;
  backend: string;
  /**
   * WHICH PHYSICAL GPU, and whether the browser honoured the request (`render/gpuAdapter.ts`).
   * Published rather than logged because "the game is slow" and "the game is on the wrong GPU" are
   * the same observation until someone reads a vendor string — which is how it went unnoticed here.
   */
  gpu: GpuProbe;
  /** Device tier (src/render/tier.ts): 0 WebGL fallback · 1 mobile WebGPU · 2 desktop WebGPU. */
  tier: Tier;
  rig: CameraRig;
  camTune: CameraTuning;
  /** The one scene camera — the editor raycasts through it and drives it while flying. */
  camera: THREE.PerspectiveCamera;
  /** Mesh presentation fixes: rest-yaw, render scale, stride/cadence trims (panel-tunable). */
  charFix: {
    yawDeg: number;
    scale: number;
    strideMul: number;
    cadenceCapHz: number;
  };
  /** Parent group for registry-entity meshes (E0) — entities attach/detach themselves here. */
  entityRoot: THREE.Group;
  /** Parent group for editor-only helpers (selection box, gizmo helper). */
  editorRoot: THREE.Group;
  /**
   * The 240 m reference grid. Hidden at construction and shown by the editor for its session —
   * it is a RULER, not level geometry, and it used to ship to players unguarded. The editor owns
   * the toggle rather than `createScene` reading a build flag, because "am I in editor mode" is
   * the editor's fact and a second copy of it here would be a second answer.
   */
  setGridVisible(on: boolean): void;
  /**
   * Editor camera override (A3): non-null bypasses syncPlayer's camera block and applies the
   * pose verbatim; null hands the camera back to the gameplay rig.
   */
  setExternalCamera(pose: { pos: V3; quat: [number, number, number, number] } | null): void;
  /** Rebuild gym + platform InstancedMeshes from (edited) world data. In-place when counts match. */
  rebuildWorld(world: WorldData): void;
  /**
   * ── ⭐ THE WORLD'S PUNCTUAL LIGHT RIG (E2) ─────────────────────────────────────────────────────
   *
   * What `world.lighting.lights[]` actually resolved to: how many are in the scene, how many are
   * `static` (built, marked `BAKE_ONLY`, NOT parented — free), how many cast shadows, the tier's
   * budget, and every authored light this pipeline REFUSED with the reason.
   *
   * Reported rather than inferred, for the same reason `stress` and `gi` are: a refused light and
   * a light that was never authored render identically, and only this field separates them. A
   * silent downgrade is the failure the refusal table exists to prevent, so the refusals have to
   * be observable from state, not only from a console line.
   */
  worldLights(): WorldLightRigInfo;
  /**
   * ⭐ B3 · SHADOW RECEIVERS AND THE SUN'S CASCADES, READ OFF THE GRAPH. `receivers` counts
   * `receiveShadow` per subtree — the flag `AnalyticLightNode.setup()` gates the whole shadow term
   * on, so "the character receives" is this number and not a screenshot. `csm` is the cascade
   * rig's state: which camera it bound, how many cascade objects are parented, whether the
   * per-cascade sizes were written. Published as `__fwg.state().shadows`.
   */
  shadows(): ShadowState;
  /**
   * The sun-cascade override in force and what the rig is running (EDITOR v2 · S4). `override`
   * is `?csm=` at boot or the last `setCsmOverride`; `cascades` is the rig's own count.
   */
  csm(): { override: number | null; cascades: number; enabled: boolean };
  /**
   * The Settings tab's runtime door onto `?csm=` (S4): `0` = the single map (the L1/L2 control
   * arm), `1..4` = a forced count, `null` = the tier/authored policy. Re-applies through the SAME
   * `csmRig.apply` the editor's light edits go through, against the ACTIVE lighting; a count
   * change is the rig's two-frame swap. Session-only — never in `worldHash`, never in a file.
   */
  setCsmOverride(n: number | null): { override: number | null; cascades: number; enabled: boolean };
  /**
   * ⭐ R5 — the runtime door onto `?shadowSplit=`: `'off' | 'near' | 'all'`, or `null` for the
   * shipped default. Same shape and same one `csmRig.apply` as `setCsmOverride` above.
   */
  setShadowSplit(mode: string | null): { override: number | null; cascades: number; enabled: boolean };
  /**
   * ⭐ The runtime door onto `?shadowDist=` — the cascade REACH in metres, session-only, `null` for
   * the authored/tier value. Same shape and same one `csmRig.apply` as the two doors either side.
   * The frozen static map's radius follows it by construction, never by a second read.
   */
  setShadowDistance(m: number | null): { override: number | null; cascades: number; enabled: boolean };
  /**
   * ⭐ R2 — the runtime door onto `?sunShadowUpdate=`: the sun's shadow SCHEDULE, session-only.
   * Anything but the three arm names clears the override back to the authored value.
   */
  setSunShadowUpdate(
    mode: string | null,
    n?: number,
  ): { override: number | null; cascades: number; enabled: boolean };
  /**
   * ⭐ R4 — run ONE warm frame with every render object (main pass AND every shadow pass) built
   * early and every pipeline created off the main thread. See `render/shadowWarm.ts` for why
   * `compileAsync` structurally cannot reach the shadow passes, and for the r185 re-assignment that
   * makes the prototype the only hook that survives. Never throws.
   */
  warmShadowPipelines(): Promise<{
    ok: boolean;
    reason?: string;
    handled: number;
    asyncPipelines: number;
    ms: number;
  }>;
  /**
   * ⭐ THE SESSION'S SHADOW-MAP MULTIPLIER (EDITOR v2 · S4's ninth row, `?shadowRes=`). `null` is
   * 1x. It multiplies the TIER'S CAP, never the authored `shadowMapSize` — which stays the
   * artist's request, stays level data and stays in `worldHash`. Applied through the same
   * `csmRig.apply` a light edit uses plus the world rig's own free `shadow.mapSize` write, so the
   * verdict is `runtime` and the census moves in the same frame.
   */
  shadowRes(): number | null;
  setShadowResScale(n: number | null): number | null;
  /**
   * Apply an edited light list (the editor's Lighting section). Reconciles in place — a light of
   * the same id, type and mobility-presence keeps its three object, so intensity/colour/pose edits
   * are uniform writes and cost nothing. `keyMoves` in the return says how many lights were
   * genuinely constructed or destroyed, i.e. how many times three regenerated every visible
   * material's WGSL (`LightsNode.customCacheKey`).
   */
  /**
   * ⚠️ IT TAKES THE WHOLE `lighting` BLOCK, NOT JUST `lights[]` (L1). The rig still CONSTRUCTS
   * only `lights[]` — the sun is `scene.ts`'s own object and the sky is a mesh — but the returned
   * `rows` are the resolved rig, sun and sky included, and the editor REPLACES the world object on
   * undo/redo/Open/New. Reading the block from a reference captured at boot would keep pricing the
   * previous world's sun, which is exactly the drift `replaceWorld` not copying `lighting` caused
   * once already.
   */
  applyWorldLights(lighting: WorldLightingConfig): WorldLightRigInfo;
  /**
   * ── ⭐ DEV: PREVIEW LIGHTING FOR UNBAKED `static` LIGHTS ───────────────────────────────────
   *
   * The owner's ask: *"static lights who haven't been baked yet (e.g. settings changed or newly
   * added) [should] render preview lighting into the scene, so that artists can dial the light in.
   * But obviously, that light doesn't emit dynamic light once baked, and never shipped if the bake
   * is stale."*
   *
   * `on` PARENTS this world's `static` lights for real — full direct light, full shadows if they
   * cast, full price, and the editor's frame-cost row prices them at that. `false` puts them back
   * where mobility says they belong: built, marked `BAKE_ONLY`, out of the graph, free.
   *
   * ⚠️ THE CALLER DECIDES, AND IT IS THE EDITOR, because the predicate is the editor's:
   * `indirect === 'baked' && (bakeState !== 'baked' || worldEditedSinceLoad())`. This method holds
   * no verdict of its own — `bakeState.ts` limit 2 (staleness is a LOAD-time verdict) stands, and a
   * third staleness authority is exactly what this design refused to add.
   *
   * FOUR FENCES stop it shipping or surviving a bake, and only the first is structural:
   *   1. It authors NOTHING. Presence is derived (`lightModel.previewPresence`), so `world.json`
   *      is byte-identical whether preview ran or not and `worldHash` cannot move. There is
   *      literally nothing to ship.
   *   2. This body is inside `import.meta.env.DEV` and ASSIGNS the marker literal
   *      `fwg-light-preview` inside the fold, so Rollup deletes both. `scripts/checkBundle.mjs`
   *      (c10) greps every served payload file for that literal — verified to fail first, the (c8)
   *      method. (The literal must be USED, not merely exported: an exported-but-unused constant
   *      is tree-shaken and the leg then passes vacuously. That is (c9)'s own recorded lesson.)
   *   3. `WorldLightsInfo.previewParented`, counted off `light.parent` — a positive number a Play
   *      session can assert is 0, rather than a flag reporting on itself.
   *   4. The bake bracket forces it FALSE before the capture loop and restores it in the `finally`,
   *      so an atlas is never photographed with preview deciding presence.
   *
   * Returns the freshly described rig so the caller's budget line moves in the same turn.
   */
  setLightPreview(on: boolean): WorldLightRigInfo;
  /** Current pick targets for the editor raycaster — refetch after every rebuildWorld. */
  getPickMeshes(): { gym: THREE.InstancedMesh; platforms: THREE.InstancedMesh };
  /** Positioned dust burst — EntityCtx hook (the feedback bus deliberately carries no position). */
  burstAt(pos: V3, count: number): void;
  /** Camera ray from an NDC click point onto the y=0 ground plane (editor placement), or null. */
  raycastGround(ndc: { x: number; y: number }): V3 | null;
  /** Feed one render frame: interpolated feet position, current sim state, this frame's events. */
  syncPlayer(feetPos: V3, state: MotionState, events: FeedbackEvent[]): void;
  /** Debug jump-arc polyline: tick-offsets from `origin`, or null to hide. */
  setArc(origin: V3 | null, points: V3[]): void;
  /** Pose the moving platforms at (interpolated) sim time `timeS` — call once per frame. */
  updatePlatforms(timeS: number): void;
  /**
   * Shader warm-up (plan R1): `renderer.compileAsync(scene, camera)` over the fully built scene
   * so the first frames don't stall on pipeline compilation. Guarded — a backend that cannot
   * precompile warns and continues.
   */
  warmup(): Promise<void>;
  /**
   * Resident GPU memory in MiB — the SCENE CENSUS (textures + geometry actually in the graph),
   * not the accrual ledger. See `getVramBreakdown` for what it deliberately cannot see.
   */
  getVramMB(): number;
  /**
   * ⭐ THE LAST COMPLETED FRAME'S DRAW AND TRIANGLE COUNTS — LATCHED, NOT POLLED.
   *
   * ⚠️ `renderer.info.render` MAY NOT BE READ FROM OUTSIDE THE FRAME, and this repo has now
   * learned that three separate times. three resets it once per ANIMATION FRAME
   * (`Animation.js:75`, `if (this.info.autoReset === true) this.info.reset()`), not once per
   * render, so its value is only meaningful between the draw and the next frame's reset:
   *
   *   · the GI baker read it across chunk boundaries and under-counted 153,917 draws as 3,914;
   *   · `giAutoBake`'s own comment records "read before the draw, `render.triangles` is 0 every
   *     frame — measured, not assumed", which is why that check sits AFTER `pipeline.render()`;
   *   · and the PERF BAR polls on its own timer from outside the loop entirely, which is why it
   *     has been reporting `0 draws · 0 tris` on a scene drawing 4,270 of them.
   *
   * A counter that is valid only inside a window nobody outside the loop can observe must be
   * latched by the loop. This is that latch: written once per frame immediately after the draw,
   * read by anything that wants the number.
   */
  frameStats(): { drawCalls: number; triangles: number };
  /** Apply a class's `EntityMeta.mobility` to a spawned subtree (the host's seam, via mount). */
  applyMobility(root: THREE.Object3D, mobility: 'static' | 'dynamic'): void;
  /**
   * Re-compose a STATIC subtree after an editor edit moved it. A static subtree has
   * `matrixAutoUpdate === false`, so writing `object3d.position` alone changes nothing on screen
   * and reports no error — this is the call that makes the edit visible.
   */
  refreshStaticTransform(root: THREE.Object3D): void;
  /**
   * The census behind `getVramMB()`, plus the accrual ledger's own total, plus the list of things
   * neither can see. R1.5: the budget in the rendering plan is enforced with this number, so it
   * has to be able to say how complete it is. `ledgerMB` is a SUBSET of the census today (the
   * character is charged AND in the scene) — the two are never added.
   */
  getVramBreakdown(): VramBreakdown;
  /**
   * Which lighting system `renderer.lighting` actually holds (`?lighting=`, dev-only, set BEFORE
   * `renderer.init()`). Always `'default'` in a production build. See `src/render/lighting.ts`.
   */
  lighting: LightingSystemId;
  /** The dev stress rig's self-report (`?lights=`/`?overdraw=`/`?props=`), or null. */
  stress(): StressInfo | null;
  /**
   * ── ⭐ DEV-ONLY: PICK A VIEW MODE BY NAME (E1) ─────────────────────────────────────────────────
   *
   * `'lit' | 'unlit' | 'indirect' | 'level'` — the table is `src/render/debugView/registry.ts`.
   * Returns false when the id is unknown or the mode's requirements are unmet (`indirect` and
   * `level` need a bound probe volume), and in BOTH cases the current mode is kept and a warning
   * names why. Never a silent fall-back to a different real mode, and never a GI visualiser drawn
   * with no volume: a uniform black frame is indistinguishable from "the volume delivers nothing
   * here", which is a wrong answer that looks like a measurement.
   *
   * Async because the whole subsystem is one dynamic import behind a compile-time DEV literal. In
   * a production build the branch is gone, `debugView` is null forever and this resolves `false`.
   */
  setViewMode(id: string): Promise<boolean>;
  /**
   * DEV-ONLY: raise or lower one show flag (`'probeVolumes' | 'brickBounds' | 'probes'`).
   * Independent booleans, not modes — any number compose, with each other and with any view mode.
   */
  setShowFlag(id: string, on: boolean): Promise<boolean>;
  /**
   * ── ⭐ DEV-ONLY: THE PROBE VIEWER'S THREE SESSION LEVERS (P1) ──────────────────────────────────
   *
   * `{ colour, radiusM, sphereRadiusM }`, any subset. Resolves to what was APPLIED — the pure
   * `clampProbeViewSettings` refuses an unknown colour id and clamps both ranges — and NEVER to the
   * request, because a control that echoes what it was asked for is how a slider comes to disagree
   * with the frame it is supposed to be driving.
   *
   * ⚠️ NOT world data. These are session-only: no `worldHash`, no bake, no persistence. Accepted
   * with the flag down and with no volume bound, so an artist who sets a radius, hides the probes
   * and shows them again gets their radius back.
   *
   * ⚠️ Resolves to **null** when there is no debug view this session — a production build, or a
   * `VITE_ENABLE_EDITOR=1` one where the chunk never arrives. Null rather than a defaults object,
   * for the same reason `debugView()` and `gi()` are nullable: "nothing was asked for this session"
   * is a different fact from "here are the settings", and a caller that cannot tell them apart
   * draws a control over a dead lever. It is also why the defaults are NOT imported here —
   * `scene.ts` is the EAGER main chunk and takes only `import type` from `render/debugView/**`; a
   * value import would drag the table into `dist/` and fail `checkBundle` block (c6).
   */
  setProbeView(patch: ProbeViewPatch): Promise<ProbeViewSettings | null>;
  /**
   * ── ⭐ DEV-ONLY: RE-READ WHAT THE SHOW FLAGS DRAW (E3) ─────────────────────────────────────────
   *
   * `probeVolumes` draws `world.lighting.gi.volumes` — data the EDITOR mutates in place. Without
   * this, moving a volume's bound leaves the wireframe where it was, i.e. a diagnostic that quietly
   * describes the previous edit. `setShowFlag(id, true)` cannot serve: it early-returns on a flag
   * that is already up, deliberately, so that raising a flag twice does not rebuild its buffers.
   *
   * It exists so the editor does NOT get its own copy of those boxes. One implementation of "draw
   * the authored volumes", on the runtime side of the `src/editor` ↛ `render/debugView` fence, with
   * the editor holding a handle to it by name. No-op in a production build.
   */
  syncDebugView(): void;
  /**
   * S5. Hand the `collision` show flag its data: what the PHYSICS WORLD holds, in world space.
   * Physics lives in mount and presentation never imports it, so mount sets this once at boot
   * (`() => physics.entityCollisionShapes()`); the overlay re-reads it on every `syncDebugView()`,
   * which the collision binder calls after each bind so an editor move shows the hull move.
   */
  setCollisionOverlaySource(source: () => readonly CollisionOverlayPlacement[]): void;
  /**
   * ── ⭐ WHAT IS UNDER `editorRoot`, AND HOW MUCH OF IT THE LEDGERS CAN SEE (E3) ─────────────────
   *
   * Editor gizmos are scene-graph objects: they are drawn, so they are in `renderer.info.render`,
   * and they own textures and geometry, so `censusScene` would price them. Neither is acceptable
   * silently — "the gizmos were up" and "the frame got slower" must never be the same observation,
   * which is the same rule `debugView.flags` exists to keep for the show flags.
   *
   * `unstamped` is the one that matters and it is the fence: every gizmo root calls `markOverlay`,
   * so a non-zero value here means something under `editorRoot` is being CHARGED to the VRAM
   * budget. `tests/e2e/editorGizmos.e2e.ts` asserts it is 0 with the editor open and the VRAM
   * census byte-identical either way.
   */
  editorGizmos(): {
    objects: number;
    drawable: number;
    unstamped: number;
    /**
     * What the VRAM census would have been charged for this subtree if it were not stamped —
     * i.e. the size of the omission `excludes: 'debug-overlays'` declares. Named as a NUMBER
     * rather than left as a claim, because "the gizmos are excluded" is only meaningful if you
     * can also say how much was excluded; zero here would mean the exclusion is doing nothing
     * and the test asserting it is passing for free.
     */
    chargeableBytes: number;
  };
  /**
   * What the debug view is ACTUALLY doing — the active mode, the flags up, which modes have been
   * built (i.e. compiled) this session, and the pipeline-cache size at entry and now. Null in a
   * production build and until the dev chunk lands.
   *
   * ⚠️ The two program counts are published as two numbers rather than a delta, because a delta
   * cannot separate "this mode compiled four pipelines" from "the scene compiled four while it was
   * up". The assertion that matters — that switching modes does not rebuild the LIT pipelines — is
   * that the count RETURNS on the way back to `lit`, and that is what `tests/e2e/debugView.e2e.ts`
   * checks.
   */
  debugView(): DebugViewInfo | null;
  /**
   * ── ⭐ DEV-ONLY: WHAT WOULD A BAKE OF THIS WORLD COST, AT THIS PRESET? (E5) ────────────────────
   *
   * Runs `compileProbeVolumes` — **the compiler the bake itself runs**, on the working world, with
   * the named preset's `spacingMultiplier` applied — and flattens its report into `GiBakePlan`.
   * That is the whole design decision: the editor's bake panel does NOT re-derive "how many probes
   * does `arcade` need" from bounds ÷ spacing. A second derivation would agree with the authored
   * data by construction and disagree with the bake, which is the one failure a cost preview cannot
   * afford. The compile is PURE and takes microseconds — no GPU, no bake, no browser process — so
   * it can be re-run on every edit that could move the answer.
   *
   * A compile that REFUSES (either hard budget, an inverted bound, an out-of-range level) returns a
   * plan with `structure: null` and the compiler's own message in `refusal`. It never throws: a
   * half-typed volume bound must render as a red block in the panel, not as an exception through a
   * tweakpane change handler.
   *
   * DEV-ONLY, behind a compile-time literal, for the same reason the bake is: `probeCompile.ts` and
   * `bakeQuality.ts` are asserted ABSENT from every emitted chunk (`checkBundle` (c5)). Resolves
   * `null` in a production build and on any unknown preset name.
   *
   * `request` is the panel's explicit override — today only `bounces` (B1-bounces) — resolved
   * through the SAME `resolveBakeQuality` the bake bracket and the CLI use, so a plan priced with
   * `{ bounces: 2 }` on `preview` is the plan of a `--bounces 2 --quality preview` bake and its
   * `quality.overrides` is what that bake's header will record. Absent ⇒ the preset decides.
   */
  giBakePlan(qualityName: string, request?: { bounces?: number }): Promise<GiBakePlan | null>;
  /**
   * Hand the scene an in-flight entity respawn. `giBakePlan` and `setProbeGrid` await it, so no
   * occupancy walk and no bake ever photographs the emptied room.
   */
  settleEntities(pending: Promise<void>): void;
  /**
   * DEV probe-grid toggle (plan §3, vendored r186 files behind a flag): on=true lazy-imports
   * src/render/gi/, builds a coarse grid over the world bounds, runs the in-browser bake() once
   * and adds the grid Light; on=false removes and disposes it. Visual evaluation only — the
   * offline baker is R2.
   */
  setProbeGrid(on: boolean): Promise<void>;
  /**
   * ── ⭐ SHIPS: LOAD THIS WORLD'S BAKED LIGHTING, IF ITS DATA SAYS IT HAS ANY (BUG 1) ────────────
   *
   * The eager, world-driven entry point to the load path — the thing that did not exist, and whose
   * absence meant `?world=sponza-debug-01` opened UNLIT on localhost over a 912 KB atlas on disk,
   * and that a production build had no path to an atlas at all.
   *
   * It resolves `resolveGiActivation(location.search, world.lighting.indirect, DEV)` and acts only
   * on `'file'`: fetch → `checkProvenance` → bind, or fall back to the constant fill with a NAMED
   * state. It NEVER bakes; the bake is `setProbeGrid`'s dev half and folds out of production.
   *
   * FIRE AND FORGET (D25): the fetch may be slow, may fail, or may never arrive, and the world
   * renders either way — with the constant fill until the volume lands, then with the volume. The
   * consequence, stated rather than discovered: there is a ONE-FRAME lighting change when it binds.
   * That is the same pop `?gi=file` always had; hiding it is a loading-screen decision, not a GI one.
   *
   * ⚠️ CALL IT AFTER CONTENT HAS ARRIVED — `host.onAllLoaded`, not `host.onAllSpawned`. See
   * `giDrawnBox`: a bounds read at spawn time refuses a good atlas for `geometryDrift` every boot.
   */
  applyBakedLighting(): Promise<void>;
  /**
   * ── ⭐ "THE LOAD-TIME GI VERDICT HAS SETTLED" — ONE NOTIFICATION, NOT A WATCHER ──────────────
   *
   * `applyBakedLighting` is FIRE AND FORGET (D25) and on the owner's 46,149-capture Sponza its
   * fetch+decode is a multi-second window. The F2 listener is installed synchronously right after
   * `warmup()`, so an artist can — and does — open the editor INSIDE that window, when `gi()` is
   * still null. `wantsStaticPreview('baked', undefined, false)` is TRUE there (correctly: no atlas
   * is bound, so a `static` light is in nothing), preview parents every static light, and until
   * this existed NOTHING re-asked: `bindProbeVolume(…, 'load')` — unlike the `'rebind'` path —
   * does not end preview, and `onGiRebound` is fired only by the bake panel after a completed
   * bake. The clean world that finished loading its atlas kept every static light lit dynamically
   * ON TOP of the atlas that already holds their energy — `src/render/indirect.ts`'s double count
   * — with a `PREVIEW` chip asserting a staleness that was no longer true.
   *
   * ⚠️ THIS IS NOT A WATCHER AND `bakeState.ts` LIMIT 2 STANDS — the same sentence
   * `reloadBakedLighting` already makes. It fires ONCE per `applyBakedLighting`, because a caller
   * asked for the file; nothing polls, nothing subscribes to disk. The editor is the only
   * subscriber and it re-ASKS its own predicate, which is what keeps the verdict in one place: a
   * bind that arrives over a still-dirty world puts preview straight back on, and one that arrives
   * over a clean one takes it off. OFF is not hard-coded here; the predicate is re-run.
   *
   * Returns its own unsubscribe. Fires exactly once per call, in a `finally`, on every arm
   * including the refusals — a refused atlas is a settled verdict too (`bakeState` becomes
   * `stale`/`unbaked`), and a listener that only heard about successes would be a second answer.
   */
  onGiSettled(cb: () => void): () => void;
  /**
   * ── ⭐ DEV: RE-READ THIS WORLD'S ATLAS AND BIND IT, IN SESSION, WITH NO RELOAD (L2) ────────
   *
   * The owner's acceptance sentence is *"bake lighting and see it in the editor. And then when we
   * reload the level, it's there"* — two halves, and until now only the second existed. A finished
   * bake writes bytes in ANOTHER PROCESS; nothing in this tab reads them, no HMR event fires for a
   * file under `public/` that is in no module graph (measured: zero `vite-hmr` messages for a write
   * to `worlds/<id>/gi/probes.json`), and the panel's only answer was a second button.
   *
   * ⚠️ THIS IS NOT A WATCHER, AND `bakeState.ts` LIMIT 2 STANDS. Staleness is still checked at
   * LOAD and at nothing else; this is a caller-driven, one-shot re-read for the ONE moment a tab
   * knows the file moved — a bake it started has just reported `done`. Nothing polls the disk,
   * nothing subscribes to it, and a world edited in the editor still does not flip state until a
   * reload. A watcher would be a second authority on freshness beside `checkProvenance`; this
   * calls `checkProvenance` itself, on the same live inputs the boot load uses.
   *
   * The refusal path is the interesting half: a file that fails provenance (the world moved while
   * the baker was photographing it) LEAVES THE BOUND VOLUME ALONE and reports why. A rebind must
   * never be able to take working lighting away — that would be the "no bake may overwrite a
   * loadable atlas" rule (L1) reintroduced at the other end of the pipe.
   */
  reloadBakedLighting(): Promise<GiRebindResult>;
  /**
   * Moves the baked volume's contribution gain IN SESSION — no recompile, no re-bake, no toggle.
   * Returns false when there is no volume. The gain is a UNIFORM (`SparseProbeVolume.setIntensity`
   * writes both the uniform and the field), which is what makes a one-camera, one-session A/B of
   * the GI term possible at all; see `tests/e2e/gi.e2e.ts` (intensity monotonicity).
   */
  setGiIntensity(gain: number): boolean;
  /**
   * ── `?giSparseSpike=1` — THE HAND-BUILT SPARSE FIXTURE, ON SCREEN (spec §9 · S1) ──────────────
   *
   * Repoints ONE indirection texel at the reserved all-zero `EMPTY_BRICK` and republishes, in
   * session. `null` restores the intact fixture. Returns false when no spike volume is published.
   *
   * WHY THIS IS A DOOR AND NOT A URL PARAM. The claim under test is that a fragment shader can
   * read an indirection texel with an exact unfiltered `textureLoad` and USE THE RESULT to address
   * a brick atlas — and the only observation that can distinguish "it read the texel" from "it
   * sampled a brick it happened to be pointed at anyway" is changing the texel and watching the
   * pixel follow, IN ONE PAGE SESSION, with no recompile. Across two boots the camera, the
   * pipeline cache and the compile order are all different, and R1.6-D passed exactly that kind of
   * comparison. It also exercises `publishAtlas`'s rebind (spec §4.5, the eleventh instance): the
   * two `Texture3DNode`s are permanent, so a republish must reach an ALREADY-COMPILED graph.
   */
  giSpikeRepoint(emptyTexelX: number | null): boolean;
  /**
   * What the probe grid ACTUALLY baked — resolution, probe count, realised spacing and the bake
   * cost — or null while it is off. Published through the dev hook so a run can tell "the grid is
   * on" from "the grid collapsed to a 4 m cube and baked in 40 ms", which is what a picture alone
   * cannot say (see `render/gi/probeVolume.ts`).
   */
  gi(): GiInfo | null;
  /**
   * THE INDIRECT TERM's self-report (`src/render/indirect.ts`) — the constant fill that is actually
   * on the light right now, where it came from, and whether it is double-counting with a probe
   * volume. Read from the LIGHT, not from the URL: `?hemi=0` reaching the parser and `?hemi=0`
   * reaching the shader are different claims, and only the second one is evidence.
   */
  indirect(): IndirectTerm;
  /**
   * THE SKY's self-report (`src/render/sky.ts`) — read off the MESH's uniforms, never off the world
   * data that asked for them. `on: false` when the world has no dome. A skylight only exists if the
   * sky is really in the scene for the bake to photograph, and "we asked for a sky" is not evidence
   * that one is there (the failure mode is a dome clipped by the far plane, which looks identical
   * to no dome at all in every state assertion).
   */
  sky(): SkyInfo;
  /**
   * ── ⭐ CAPTURE THE SKY AS SIX HDR CUBE FACES (`render/bake/skyCube.ts`) ────────────────────────
   *
   * The owner's ruling: *"whatever the colour information that is conveyed by our sky … is what we
   * also want to contribute lighting … the lighting information is already there in whatever form
   * it takes."* This is the door onto it, and it is deliberately the ONLY one: the capture renders
   * the objects `isSkyMesh` identifies and nothing else, so nothing downstream ever learns what
   * kind of sky the world has.
   *
   * `sky()` is the dome's SETTINGS; this is the dome's RADIANCE. The distinction is the whole
   * point — a consumer that took the settings would have to re-implement the shader, which is the
   * second implementation `tools/bake/traceLighting.ts` is being deleted for.
   *
   * ⚠️ **DEV-ONLY, AT THE ABSENT BAR.** The body is one `await import()` inside a compile-time
   * `import.meta.env.DEV` branch, so a production build resolves the refusal below forever and
   * `checkBundle` (c11) asserts the module, its chunk and its marker are all gone.
   *
   * ⚠️ **IT MUTATES THE LIVE SCENE FOR THE LENGTH OF THE CALL** (the sky's render layer, the
   * background, the fog and the solar disc) and restores every one in a `finally`. Do not call it
   * inside the bake bracket: `bakeHoldsFrame()` is true there and the bracket owns the same four
   * mutations. A refusal is a SENTENCE — never a smaller or emptier cube.
   */
  captureSkyCube(opts?: {
    faceSize?: number;
  }): Promise<import('../render/bake/skyCube').SkyCubeCaptureResult>;
  /**
   * ⭐ F1 · THE FOG TERM's self-report. `plan` is what `resolveFogPlan` folded; `sceneFog` is what
   * the SCENE actually carries, read off `scene.fog`/`scene.fogNode` rather than off the plan that
   * asked for them — the R1.6-D rule (a self-report proves nothing). The two disagreeing is
   * exactly the failure this field exists to make visible: "the mode picker did nothing" and "the
   * mode picker did something I do not like" are the same picture until somebody can read this.
   */
  fog(): FogState;
  /*
   * ⚠️ `readProbeSh` IS GONE (F3), AND THE G4 SKYLIGHT ACCEPTANCE TEST HAS NO INSTRUMENT.
   *
   * It read the nearest probe of a `(x, y, z)` LATTICE, which is the one thing
   * the sparse format does not have — the field is a sparse brick set at up to five densities
   * behind an indirection texture. It had already been returning `null` on every path except
   * `?gi=dense`, because a sparse bake publishes a `SparseProbeVolume` and the reader took its
   * grid handle. Deleting it is the honest move: a method that compiles, is called by an e2e case
   * and answers `null` is worse than an absent one, and this repo has twelve of those logged.
   *
   * The replacement is a `sparseAddress` lookup, not a lattice index. Named in
   * `render/gi/probeRead.ts`; not built here, because nothing in this stage needs it.
   */
  /**
   * READ THE WHOLE ATLAS BACK, plus the provenance header that says what it was baked from (G5).
   *
   * This is what `npm run bake:world` writes to disk: the header is built HERE, in the one module
   * that knows the resolved lighting rig, and the payload is the atlas in file order. `null` when
   * no grid is baked. Dev-only in practice — it is reached through the dev hook.
   *
   * The base64 is the shape that survives `page.evaluate`; a 45,000-probe atlas is 2.5 MB and a
   * JSON number array of it is 12 MB of text.
   */
  readProbeAtlas(): Promise<{
    header: import('../render/gi/probeFile').ProbeVolumeHeader;
    /** Little-endian half-float bit patterns, sub-volume major. */
    payloadBase64: string;
    /** `'target'` (read off the GPU) or `'cpu'` (read out of a loaded texture's backing array). */
    readFrom: string;
    /**
     * ⭐ **WOULD A BOOT WITH NO URL PARAMETERS LOAD THIS?** — `checkProvenance`'s own verdict,
     * run here, against this session's world with every dev override neutralised.
     *
     * It exists because a bake that writes an atlas nothing can load is worse than a bake that
     * does not run: it OVERWRITES the last atlas that could. That is not hypothetical — it is
     * what left the owner with no loadable Sponza atlas and the sentence *"I actually haven't
     * seen a single bake work or load in the editor at all."*
     *
     * The page is the only process that can answer it, for the same reason the header is built
     * here: the node baker can see the REQUEST (a URL and a world file), not the RESULT. And it is
     * the SAME function the loader will run, not a second opinion about it — the two ends ask one
     * question, exactly as the `volumes` cross-check does.
     */
    loadable: import('../render/gi/probeFile').ProvenanceVerdict;
  } | null>;
  /**
   * THE DIRECT TERM's intensity, read off the light (`?sun=<n>`, dev-only, changes no default).
   * Reported because "how much of this picture is the sun?" turned out to be the question under
   * every GI measurement we have taken, and it was never asked.
   */
  sunIntensity(): number;
  /**
   * THE DIRECT TERM's DIRECTION, read off the light — unit, pointing from the scene toward the sun.
   * `?sunAz=`/`?sunEl=` are dev-only and change no default. Reported because a pose is exactly what
   * G3.1 could not see: the sun was 0.2 % of the arcade frame because it aimed at another level,
   * and an intensity readout can never distinguish "too dim" from "pointing elsewhere".
   */
  sunDirection(): [number, number, number];
  /**
   * Set the constant ambient fill IN-SESSION (`n`), or hand it back to the derived value (`null`).
   * Dev-only lever, and it exists for the same reason `setGiIntensity` does: the four-state
   * comparison (fill+grid · fill only · grid only · neither) is only evidence if all four frames
   * come from ONE session and ONE camera — a re-boot per state costs a re-bake and 0.6 m of camera
   * drift, which swings frame luminance by 19 %. Returns the resolved term.
   */
  setAmbientFill(n: number | null): IndirectTerm;
  /**
   * The resolution seam (docs/ai/plans/dynamic-resolution.md, stages B and C). Two independent
   * levers behind one handle — see `ResolutionHandle`.
   */
  resolution: ResolutionHandle;
  /**
   * ⭐ O2 · TRUE WHILE A BAKE OWNS THE FRAME — the whole rAF turn, not just the draw.
   *
   * ⚠️ IT IS NOT A CONVENIENCE. Before O2 a bounce pass was ONE synchronous JS turn, so nothing
   * else ran during it by construction: no rAF, no sim tick, no platform pose, no entity update.
   * Stage A drains the submission queue at every chunk boundary (the bake is queue-depth-bound —
   * drain scales as passes^2), which means ~361 yields on a Sponza bake where there used to be
   * one per bounce, and every one of them is a door the frame loop can walk through.
   *
   * `bakeCapturePass` photographs the SCENE GRAPH. A sim tick between two chunks moves platforms,
   * poses the character and steps every entity — so half the atlas would be a picture of one
   * arrangement of the level and half of another, with every downstream check green. That is the
   * failure `CONTRIBUTING.md` warns about for an editor Save mid-bake, arriving through a door
   * that Save fence does not cover.
   *
   * So the whole callback returns early, which restores the pre-O2 semantics exactly: while a bake
   * is running, nothing else happens. The caller is `src/app/mount.ts`'s animation loop; the same
   * predicate gates `render()` from the inside, so a second caller cannot forget.
   */
  bakeHoldsFrame(): boolean;
  render(): void;
  dispose(): void;
}

/**
 * The VRAM number, with its own limits attached. Flat + JSON-serializable: the dev hook publishes
 * it, and a browser test asserting "Sponza is not 0 MB" is the regression net for the blindness
 * this replaced.
 */
export interface VramBreakdown extends VramCensus {
  /** `(textureBytes + geometryBytes)` in MiB — the number `getVramMB()` returns. */
  totalMB: number;
  /** The accrual ledger's grand total in MiB. A SUBSET of the census; never add the two. */
  ledgerMB: number;
  /** Age of the cached census in ms — it is recomputed on demand, not per frame. */
  ageMs: number;
}

/**
 * What the drawing buffer currently IS, plus what the controller currently thinks.
 *
 * Flat and JSON-serializable on purpose: this is what `window.__fwg.state().resolution` returns,
 * and the browser tier asserts 3D through STATE, never through pixels. `drawingBufferWidth ==
 * floor(cssWidth × effectivePixelRatio)` is the whole of stage B in one checkable identity.
 */
export interface ResolutionState extends ResolutionStatus {
  /** OUTPUT resolution: the settings/tier lever. Never touched by the controller. */
  outputPixelRatio: number;
  /** `outputPixelRatio × scale`, after the per-axis ≥1px clamp — what the renderer was given. */
  effectivePixelRatio: number;
  drawingBufferWidth: number;
  drawingBufferHeight: number;
  cssWidth: number;
  cssHeight: number;
  /** `renderer.hasFeature('timestamp-query')` — capability-detected, never assumed. */
  gpuTimingAvailable: boolean;
  /**
   * The most recently RESOLVED GPU time for the render group, ms (0 until one arrives, and 0
   * forever where the backend has no timer query).
   *
   * Deliberately the RAW last sample rather than the controller's median: the controller stops
   * filling its window the moment a rung is pinned or frozen, and stage D's cost table has to be
   * measurable with the rung held still. It exists because rAF intervals CANNOT price a post
   * pass — vsync quantises them, so on a 120 Hz panel a chain that adds 0.4 ms and one that adds
   * 7 ms both read as "one refresh longer" the moment they cross 8.3. This is the number that
   * separates them.
   */
  gpuTimeMs: number;
  /**
   * WHICH UPSCALER IS ACTUALLY RUNNING, and what it costs (plan stages D/E). Reported rather
   * than inferred: `mode` alone does not tell you whether the render scale reached the scene
   * pass or the pixel ratio, and `inputWidth × inputHeight` against `drawingBufferWidth ×
   * drawingBufferHeight` is the whole of stage D in one checkable identity.
   */
  pipeline: PipelineState;
}

export interface ResolutionHandle {
  state(): ResolutionState;
  /** The rungs, for a settings UI / the dev pane. */
  ladder: readonly number[];
  /**
   * Reference-counted by reason. The controller must be FROZEN during anything where a
   * resolution pop is worse than a slow frame: world load/rebuild, editor mode, and any
   * stage/cutscene layer (`uiHasFocus()` answers that one). Boot warm-up is the controller's own.
   */
  freeze(reason: string): void;
  unfreeze(reason: string): void;
  setEnabled(on: boolean): void;
  /** Pin the render scale to the nearest rung, or null for automatic. */
  pin(scale: number | null): void;
  /** OUTPUT lever: override the tier cap, or null to go back to it. Re-clamps and re-applies. */
  setOutputPixelRatio(ratio: number | null): void;
}

const STATION_COLORS: Record<string, number> = {
  /*
   * The FLOOR (`station: 'ground'`) — the hex the deleted 240 m quad carried, kept to the digit.
   * Four worlds author a floor box under their spawn since the forced plane went away, and two
   * owner-gated e2es (`giSparse`, `debugView`) derive predicted luma from this exact colour's
   * linear value (0.0436, 0.0974, 0.0665). The gym material is roughness 0.85 where the quad was
   * 0.95, so the specular lobe moves slightly and those two derived numbers must be RE-MEASURED
   * on a headed run rather than assumed — the ALBEDO term is what this line protects.
   */
  ground: 0x3b5849,
  gapLadder: 0xc98f3b,
  stepLadder: 0x7d9c5b,
  slopeFan: 0x9c6f5b,
  heightLadder: 0x5b7d9c,
  hoverCanyon: 0x8a5b9c,
  ledgeRow: 0x9c5b6f,
  perch: 0x5b9c94,
  rhythmRun: 0xb0a44c,
  cornerAlley: 0x7d8494,
  surfaceGrid: 0x777788,
};

/** Surface-grid patches color by TAG (overrides the station color) so each reads at a glance. */
const SURFACE_COLORS: Record<string, number> = {
  metal: 0x9aa4b0,
  grass: 0x5f9c4a,
  wood: 0xa07a4a,
  sand: 0xd6c08a,
  ice: 0xa8d8e8,
  water: 0x4a7ac9,
};

const BASE_FOV = 55;
const ARC_MAX_POINTS = 160;

/**
 * The resolution policy's constant table (`content/tuning/render.json`), parsed ONCE at module
 * load. Parsed rather than trusted: a hand-edited ladder that is not strictly descending would
 * make the controller quietly unable to reach full resolution, and nobody would notice for weeks.
 */
const RENDER_TUNING = parseResolutionTuning(rawRenderTuning);

// ── Matrix policy boot flip (plan §4.1) — module scope so it runs BEFORE any Object3D in this
// runtime is constructed (main → mount → this module). See the opt-in list in the file header.
THREE.Object3D.DEFAULT_MATRIX_AUTO_UPDATE = false;

/**
 * ⭐ `OffsetReport` → the seven optional `bake.placement` fields, in ONE place.
 *
 * The header is written from two arms (adaptive compiled, and the uniform fallback), and BOTH must
 * carry the offset counts or a bake that fell back would report "no probe moved" while having moved
 * some — a self-report that is wrong in the direction nobody checks. One spread, two call sites.
 *
 * `null` ⇒ no pass ran, and the fields are omitted entirely rather than written as zeros:
 * `canonicalJson` drops `undefined`, so a world that does not use virtual offset writes exactly the
 * header it wrote before this existed. That is what keeps this off the format-bump ledger.
 *
 * The type is written as an `import(...)` annotation so the module stays type-only here — it is
 * BAKE-ONLY code and `tests/renderGiFence.test.ts` holds it inside the DEV branch.
 */
function offsetFields(
  report: import('../render/gi/probeOffset').OffsetReport | null,
): Partial<import('../render/gi/probeFile').ProbeFilePlacement> {
  if (report === null) return {};
  return {
    offsetMoved: report.moved,
    offsetUnmovable: report.unmovable,
    offsetMaxM: report.maxOffsetM,
    offsetMeanM: report.meanOffsetM,
    offsetSearchRadiusM: report.searchRadiusM,
    offsetClearanceM: report.clearanceM,
    offsetHash: report.hash,
  };
}

/** `OffsetReport` → the plan's offset block. The panel reads this; the header reads the one above. */
function planOffset(
  report: import('../render/gi/probeOffset').OffsetReport | null,
): NonNullable<import('../render/gi/bakePlan').GiBakePlan['adaptive']>['offset'] {
  return report === null
    ? null
    : {
        moved: report.moved,
        unmovable: report.unmovable,
        blocked: report.blocked,
        meanOffsetM: report.meanOffsetM,
        maxOffsetM: report.maxOffsetM,
        searchRadiusM: report.searchRadiusM,
        clearanceM: report.clearanceM,
      };
}

/**
 * Opt a subtree back into automatic matrix updates AND keep opting in anything added under it
 * later (`childadded` propagates the hook). updateMatrix() runs once immediately so poses set
 * before the opt-in are never lost.
 */
function optInSubtree(root: THREE.Object3D): void {
  // ⚠️ A STATIC SUBTREE WINS. The two policies MEET on every static placement, in this order: the
  // host adds the group to `entityRoot` (whose hook opts it in), and only then does `applyMobility`
  // declare it static. Without this guard the group's INHERITED opt-in hook would fire again when
  // its GLB lands seconds later and quietly opt the actual meshes back in — which is the whole
  // cost, arriving late and looking like the fix never worked.
  if (root.userData['apStatic'] === true) return;
  root.traverse((o) => {
    o.matrixAutoUpdate = true;
    o.updateMatrix();
    if (o.userData['apMatrixHook'] !== true) {
      o.userData['apMatrixHook'] = true;
      o.addEventListener('childadded', (e) => {
        if (o.userData['apStatic'] === true) return;
        optInSubtree(e.child);
      });
    }
  });
}

/**
 * ⭐ THE STATIC HALF OF THE MATRIX POLICY — COMPOSE ONCE, AND AGAIN ONLY WHEN SOMETHING CHANGES.
 *
 * `optInSubtree`'s counterpart, and the reason `EntityMeta.mobility` exists. A static entity DOES
 * need a composed matrix — `DEFAULT_MATRIX_AUTO_UPDATE` is false repo-wide, so without one it sits
 * at the origin — and its geometry arrives seconds after `create()`, which is why the blanket
 * opt-in was reached for. But it needs that matrix ONCE, not sixty times a second forever.
 *
 * So the `childadded` hook is kept — a GLB landing under a placement must still be composed — and
 * what is dropped is `matrixAutoUpdate = true`. `updateMatrix()` runs immediately for whatever is
 * there now, and again for anything that arrives later, and never on a frame in between.
 *
 * ⚠️ AN EDITOR EDIT MUST CALL {@link refreshStaticTransform}. Writing `object3d.position` on a
 * subtree with `matrixAutoUpdate === false` changes nothing on screen — the object keeps drawing at
 * its old pose with no error anywhere. That is the one hazard this policy introduces, and it is why
 * the refresh is exported rather than left as a thing each caller remembers.
 */
function composeStaticSubtree(root: THREE.Object3D): void {
  root.traverse((o) => {
    o.userData['apStatic'] = true;
    o.matrixAutoUpdate = false;
    o.updateMatrix();
    // ⚠️ ITS OWN MARKER, not `apMatrixHook`. A static placement usually arrives here having ALREADY
    // been through `optInSubtree` (see the guard there), so `apMatrixHook` is set and reusing it
    // would mean this hook is never attached — and the GLB that lands seconds later would be
    // composed by nobody, leaving every mesh of the level at the origin.
    if (o.userData['apStaticHook'] !== true) {
      o.userData['apStaticHook'] = true;
      o.addEventListener('childadded', (e) => composeStaticSubtree(e.child));
    }
  });
  // The subtree's world matrices, once, forced — `matrixAutoUpdate` false means three will not
  // recompute the locals, and `matrixWorldNeedsUpdate` is already false on a freshly composed node.
  root.updateMatrixWorld(true);
}

/**
 * Re-compose a static subtree after something moved it. The editor's move/rotate/scale arms and
 * anything else that writes a static entity's transform must call this, or the change is invisible.
 */
export function refreshStaticTransform(root: THREE.Object3D): void {
  root.traverse((o) => {
    if (o.matrixAutoUpdate === false) o.updateMatrix();
  });
  root.updateMatrixWorld(true);
}

export async function createScene(
  host: HTMLElement,
  capsule: { radius: number; height: number },
  feel: Pick<MovementTuning, 'maxRunSpeed' | 'maxWalkSpeed' | 'rollDurationMs' | 'jumpTimeToApex'>,
  /** The loaded world (E0.5) — gym boxes + platform defs are built from ITS data, not gym.ts. */
  world: WorldData,
  /** Occlusion probe (target → desired camera): allowed distance, or null if clear. */
  cameraProbe?: (from: V3, to: V3) => number | null,
  /** Surface physics profiles — drives per-surface foot-cadence slip on the character. */
  surfaces?: SurfaceMap,
  /**
   * ⭐ P1 · THE ASSET LIBRARY (ADR-0003), INJECTED — never imported and never constructed here.
   *
   * The character stopped being twelve literal paths under `public/assets/character/` and became
   * a catalog id with a content hash, a byte budget and a dispose path. Presentation is handed the
   * ONE service mount built, plus the accountant that service charges, because a second
   * `VramAccountant` in here would be a second answer to "what is resident" — which is the exact
   * split `vramAccountant.ts`'s own header records as the reason the census had to be invented.
   *
   * ABSENT ⇒ no character, and the capsule stays. That is not a degraded path so much as the
   * headless one: `tests/**` build scenes without a library and must not fetch.
   */
  library?: SceneLibraryDeps,
  /**
   * `uiHasFocus()` (hard rule 9's predicate), injected rather than imported: presentation asking
   * the input layer a question every frame is a seam we do not need, and mount already holds the
   * eager-safe door. Used for ONE thing — freezing the resolution controller while a stage or
   * cutscene layer is up, because a resolution pop mid-cutscene is the worst possible place for
   * one. Omitted ⇒ the controller simply never freezes for UI.
   */
  uiFocused?: () => boolean,
  /** TEMPORARY skin recolor (`?skin=` — mount.ts SKIN_PALETTE). Undefined ⇒ the shipped color. */
  characterColor?: number,
): Promise<GameScene> {
  // Tier (src/render/tier.ts): `?tier=` override, else backend + UA. `outputBufferType` is
  // CONSTRUCTOR-ONLY in r185 (`_outputBufferType` has just a getter), so the tier-0 decision to
  // halve post-pass bandwidth (plan §4.7: RGBA8 vs the HalfFloat default) rides a pre-init guess
  // (navigator.gpu presence / explicit ?tier=0). If WebGPU adapter acquisition later fails the
  // guess, we keep the HalfFloat default for that session — acceptable until renderer
  // construction moves fully behind the tier system (R2).
  const params = new URLSearchParams(location.search);
  const tierOverride = params.get('tier');
  const preTier = detectTier(
    {
      backend: {
        isWebGPUBackend: typeof navigator !== 'undefined' && 'gpu' in navigator,
      },
    },
    tierOverride,
  );
  let antialias = true;
  let outputBufferType: THREE.TextureDataType | undefined =
    preTier === 0 ? THREE.UnsignedByteType : undefined;
  /*
   * `?antialias=0` — MSAA off (renderer.samples 4 → 0). NOT dev-only, and that is the whole point.
   *
   * `antialias` is a `WebGPURenderer` CONSTRUCTOR option (`Renderer.samples` is getter-only in
   * r185), so this read, right here before `new WebGPURenderer`, is the ONLY place the lever
   * exists. It used to sit inside the `import.meta.env.DEV` block below — which Vite folds out of
   * a production build, so in `dist/` MSAA was unconditionally ON and no anti-aliasing setting a
   * creator or a player could ever turn had any effect at all. A control that compiles, passes and
   * does nothing IN PRODUCTION ONLY is this repo's named failure at its most expensive, so the
   * read is outside the fold and the tell is `describeBoot`'s `MSAA 4` / `no MSAA` across a
   * reload. Same precedent as `?drs=`, `?scale=`, `?upscale=` and `?tier=`, which are all
   * non-dev for the reason stated at the upscale resolve below: "hold this still" is what a
   * screenshot, a like-for-like A/B and a device report all need.
   *
   * The Settings tab's Anti-aliasing control (`editor/renderPresets.ts`) stages this param and
   * `settings.reload` navigates; nothing else writes it.
   */
  if (params.get('antialias') === '0') antialias = false;
  /*
   * ⛔ `?revdepth=1` — reversed-Z, OFF BY DEFAULT, AND IT WAS ON FOR ONE COMMIT. See the constructor
   * option below: enabling it blows 44.68 % of the frame to pure white on `mars-hangar-01`. It stays
   * as an opt-in lever because the precision argument for it is sound and the fault is ours to find,
   * not because it is ready.
   *
   * NOT dev-only, for `antialias`'s reason exactly: it is a constructor flag, so a fold would make
   * it a control that does nothing in production only, and "hold this still" is what an A/B and a
   * bug report both need.
   */
  const reversedDepth = params.get('revdepth') === '1';
  /*
   * DEV-ONLY CONSTRUCTOR OVERRIDE (Stage A of docs/ai/plans/dynamic-resolution.md).
   *
   * `outputBufferType` is CONSTRUCTOR-ONLY too, and unlike `antialias` it stays folded out: it
   * exists to PRICE one hypothesis on the owner's real GPU — RGBA8 output on tier 2 — and the
   * answer becomes a tier default or it is discarded. Worse, it rides a PRE-INIT GUESS of the
   * tier, so it is not even a clean function of the device. `import.meta.env.DEV` is a
   * compile-time literal, so Vite folds this block out rather than shipping the param.
   *
   *   ?outbuf=u8|f16      force the output buffer type regardless of tier
   */
  if (import.meta.env.DEV) {
    const outbuf = params.get('outbuf');
    if (outbuf === 'u8') outputBufferType = THREE.UnsignedByteType;
    else if (outbuf === 'f16') outputBufferType = undefined;
  }
  /*
   * ── ⭐ WHICH GPU. NOT DEV-ONLY, AND THAT IS THE WHOLE POINT ────────────────────────────────────
   *
   * `src/render/gpuAdapter.ts` carries the argument; the short version is that we passed NO
   * `powerPreference` at all, so every hybrid laptop quietly ran the game on its integrated GPU.
   * The owner found his RTX 3060 Mobile idle behind an Intel iGPU by reading a vendor string.
   *
   * ⚠️ THE READ IS OUTSIDE `import.meta.env.DEV` FOR THE `?antialias=` REASON, AND THIS ONE MATTERS
   * MORE. Vite folds a DEV branch out of `dist/`, which is exactly how MSAA came to be
   * unconditionally on in production with no control able to change it. A GPU-selection lever that
   * only worked in a dev build would be that bug with a bigger number attached: the players on
   * hybrid machines are precisely the ones who never run a dev build.
   */
  const gpuRaw = params.get('gpu');
  const gpuParam = parsePowerPreference(gpuRaw);
  if (!gpuParam.known) {
    console.warn(
      `[render] unknown ?gpu=${gpuRaw} — using '${DEFAULT_POWER_PREFERENCE}'. ` +
        'Valid: high-performance | low-power | default (pass nothing through to the browser).',
    );
  }
  /*
   * PROBED BEFORE THE RENDERER IS BUILT, so the report survives a WebGL2 fallback — the session
   * that reads `GI · CAN'T TELL` had no diagnosis at all before this, and `webgpu: false` is it.
   * Two adapter requests, no device created; see `probeGpus`.
   */
  const gpuProbe: GpuProbe = await probeGpus(navigator, gpuParam.preference);
  /*
   * ONE info line, never a warning. A page cannot tell whether this machine even has a second GPU
   * (`gpuAdapter.ts`'s NAMED LOSS), so anything louder would cry wolf on every single-GPU laptop.
   * The remedy rides the perf bar's tooltip, where the person who already suspects it is looking.
   */
  console.info(`[render] gpu: ${describeGpu(gpuProbe)} — ${GPU_REMEDY}`);
  /*
   * ⭐ Installed BEFORE the renderer exists, and therefore before any material can be built. It
   * gives every unnamed WGSL buffer a name derived from its position in its OWN build instead of
   * from a global node id — which is the only thing separating 1,952 otherwise byte-identical
   * vertex shaders on `mars-hangar-01`. `src/render/sharedBufferNames.ts` carries the whole
   * argument, including what it does NOT buy and why the neighbouring change is unsafe.
   *
   * Idempotent, and inert on the WebGL2 fallback, which uses a different builder entirely.
   */
  installSharedBufferNames();
  const renderer = new THREE.WebGPURenderer({
    antialias,
    // Forwarded verbatim by three to `requestAdapter` (`WebGPUBackend.js:212`). Omitted entirely
    // for the browser's-choice arm, which is spelled `?gpu=default`.
    ...(gpuParam.preference !== undefined ? { powerPreference: gpuParam.preference } : {}),
    /*
     * REAL GPU TIME, on BOTH backends — the dynamic-resolution controller's primary signal
     * (plan §3). WebGPU uses the `timestamp-query` device feature; the WebGL2 fallback maps the
     * same name onto `EXT_disjoint_timer_query_webgl2`. It is a request, not an assumption: if
     * the backend has no such feature the flag is cleared inside three and the controller falls
     * back to its wall-clock mode. `hasFeature('timestamp-query')` below is what we actually
     * believe.
     */
    trackTimestamp: true,
    /*
     * ── ⭐ REVERSED-Z, BECAUSE A 0.1 m NEAR PLANE OVER A KILOMETRE LEVEL HAS NO PRECISION LEFT ────
     *
     * The camera is `PerspectiveCamera(fov, aspect, 0.1, 400)` — a 4,000:1 range on a conventional
     * depth buffer, whose precision is concentrated almost entirely in the first few metres. Depth
     * resolution falls off as roughly `d² / near`, so at 300 m a 24-bit buffer resolves about FIVE
     * CENTIMETRES. `mars-hangar-01` is full of coplanar modular kit pieces at exactly those
     * distances, and the owner reports black faces that flicker as the camera turns and that "look
     * like z-fighting but inconsistent" — which is what z-fighting looks like when whether two
     * surfaces resolve depends on where you are standing.
     *
     * Reversed-Z inverts the mapping so the floating-point exponent's dense region lines up with
     * the far plane instead of fighting it, giving near-uniform RELATIVE precision across the whole
     * range. It is not a trade: it costs nothing and loses nothing.
     *
     * three r185 implements it properly on this backend, which is the reason this is one flag and
     * not a project: `WebGPUPipelineUtils.js:892` swaps every material's depth comparison through
     * `ReversedDepthFuncs`, and `WebGPUTextureUtils.js:546-551` switches the depth attachment to
     * `FloatType` when it is on — reversed-Z with an integer buffer would be no better than what we
     * have, so the float half is the half that matters.
     *
     * ⛔ ── AND IT IS OFF, BECAUSE TURNING IT ON BLOWS HALF THE SCREEN TO WHITE ────────────────────
     *
     * Shipped ON for exactly one commit. The owner reported "a lot of white" in sweep tests, and a
     * four-arm A/B — {gi off|file} × {revdepth 0|1}, eight orientations each — put it beyond doubt:
     *
     *     revdepth=1   blown 44.68 % / 43.40 %   (worst orientation 54.1 %)   mean luma 191 / 167
     *     revdepth=0   blown  0.22 % /  0.25 %                                mean luma  68 /  40
     *
     * "Blown" is near-white on ALL THREE channels, so it is a lighting term running away rather
     * than a bright colour. GI makes no difference to it (44.68 vs 43.40), which is what isolates it
     * to this flag.
     *
     * ⚠️ AND IT INVALIDATES THE MEASUREMENT THAT JUSTIFIED IT. The commit that enabled this claimed
     * "2.6× less black" — very-dark pixels 16.32 % → 6.33 % across a camera sweep. That fall was the
     * blowout: pixels stopped being dark because they had become WHITE. The z-fighting it was meant
     * to fix was never shown to improve. A one-sided metric measured a two-sided failure, which is
     * this repo's signature mistake and was mine this time.
     *
     * The precision argument above is still sound and this is still worth having — a 0.1 m near
     * plane over a kilometre level really does resolve ~5 cm at 300 m. What is missing is the CAUSE:
     * reversed-Z needs the depth CLEAR and the comparison to agree everywhere, and something in our
     * frame (the sky dome is the leading suspect — the white has a dome-shaped silhouette and
     * geometry pokes through it) is not participating. Until that is found this is an opt-in lever,
     * not a default.
     *
     * ⚠️ A CONSTRUCTOR OPTION, so it is a RELOAD lever and can never be toggled live: `?revdepth=1`
     * opts in, and it exists because a rendering change that cannot be A/B'd cannot be shown to have
     * fixed anything — which is exactly how this got shipped on.
     */
    reversedDepthBuffer: reversedDepth,
    ...(outputBufferType !== undefined ? { outputBufferType } : {}),
  });
  /*
   * ── LIGHTING SYSTEM (R1.5, dev-only) ───────────────────────────────────────────────────────
   *
   * ⚠️ THIS BLOCK MUST STAY BETWEEN `new WebGPURenderer` AND `renderer.init()`. `RenderLists`
   * captures the `Lighting` instance at construction while `Renderer.render` reads it live, so a
   * post-init swap silently SPLITS: half the paths use the new system, the render list keeps the
   * old one, and nothing anywhere reports it. Full reasoning, and the tier-detection collision it
   * runs into, are in `src/render/lighting.ts`.
   *
   * `import.meta.env.DEV` is a compile-time literal, so Rollup folds the branch, the module and
   * the three addon it pulls out of a production build (checkBundle asserts all three). No
   * default changes: with no `?lighting=` the module is never even imported.
   */
  let lighting: LightingSystemId = 'default';
  if (import.meta.env.DEV) {
    const raw = params.get('lighting');
    if (raw !== null) {
      const { parseLightingSystem, installLightingSystem } = await import('../render/lighting');
      const want = parseLightingSystem(raw);
      if (want === null)
        console.warn(`[render] unknown ?lighting=${raw} — keeping the stock system`);
      else lighting = await installLightingSystem(renderer, want);
    }
  }
  await renderer.init();
  const tier: Tier = detectTier(
    renderer as { backend?: { isWebGPUBackend?: boolean } },
    tierOverride,
  );
  /*
   * ── TWO LEVERS, NOT ONE (plan stage B) ────────────────────────────────────────────────────
   * This used to be a single `setPixelRatio(min(devicePixelRatio, 2))`, run once at boot and
   * never re-read. It conflated two independent decisions:
   *
   *   OUTPUT resolution  — what the compositor presents, and what every future post pass will
   *                        cost. A settings/tier decision, re-evaluated on resize and on a DPI
   *                        change. NEVER driven by the frame-time controller.
   *   RENDER resolution  — output × renderScale. What the SCENE costs. The controller's lever.
   *
   * With no post chain the two multiply into the same `setPixelRatio`, and that is stage B0: a
   * smaller drawing buffer with the canvas still CSS-sized, upscaled by the browser compositor
   * with hardware bilinear at ZERO extra GPU passes, identical on both backends. Soft, but honest
   * — and it already satisfies "rendering at lower resolutions than the monitor for perf reasons".
   *
   * ⚠️ `setPixelRatio` (or `setSize(w, h, false)`) is the mechanism. A plain smaller `setSize`
   * would shrink the canvas IN THE PAGE. `CanvasTarget.setPixelRatio` re-runs `setSize(w, h,
   * false)` internally, so the CSS box set by the `setSize` below survives every scale change.
   *
   * `?dpr=` is a device-testing override in the same spirit as `?tier=`; `?drs=off` kills the
   * controller (the growth e2e uses it — a leak net must hold the resolution still).
   */
  const dprOverride = params.get('dpr');
  let outputPixelRatio = resolveOutputPixelRatio(
    typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    tier,
    RENDER_TUNING,
    dprOverride,
  );
  let renderScale = 1;
  /*
   * WHICH UPSCALER (plan stages D/E). A tier DEFAULT from `content/tuning/render.json`, with
   * `?upscale=<bilinear|sharpen|sharpen-out|fsr1|taau>` as the device-testing override — the
   * same shape as `?tier=` and `?dpr=`, and NOT dev-only: "hold this mode still" is what a
   * screenshot, a like-for-like A/B and a device report all need.
   *
   * The mode is resolved HERE and the pipeline is built further down, once the camera exists.
   */
  const upscaleMode = resolveUpscaleMode(
    tier,
    RENDER_TUNING,
    parseUpscaleMode(params.get('upscale')),
  );
  renderer.setPixelRatio(outputPixelRatio);
  renderer.setSize(host.clientWidth, host.clientHeight);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  /*
   * The canvas is not content to an assistive technology (X2-S11 a11y sweep). It has no accessible
   * name, nothing inside it is reachable, and everything a screen reader needs about the world
   * arrives through the ONE aria-live channel (`src/ui/a11y/announcer.ts`) and the DOM surfaces
   * above it. Left unmarked it appears in the a11y tree as an unlabelled graphic the user can
   * neither identify nor act on — noise sitting directly before every real surface.
   */
  renderer.domElement.setAttribute('aria-hidden', 'true');
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x1a2836);
  /*
   * ── ⭐ FOG IS WORLD DATA NOW, AND IT WAS THE ANSWER TO A BUG HUNT ──────────────────────────────
   *
   * This was `new THREE.Fog(0x1a2836, 60, 150)`, unconditional, for every world, with no way to
   * see it or switch it off. The owner reported a "fade to completely black" past his cascades and
   * we spent a session inside the sun-shadow term looking for it — while THIS was saturating a
   * near-black blue-grey at 150 m on a level whose cascades stop at 90. He found it, not us.
   *
   * `applyFog` is the ONE writer (`applyWorldLights` calls it too, so an editor edit lands without
   * a reload). Absent world data reproduces the old literals exactly, so no `worldHash` moves and
   * no baked atlas is orphaned — see `WorldFogSchema`.
   *
   * ⚠️ THIS IS NOT THE VOLUMETRIC FOG COMPONENT. That one is tiers, probe lighting and a height-fog
   * fallback, and it is a subsystem rather than four fields. This is the switch that should always
   * have existed under it.
   */
  /*
   * ── ⭐ F1 · THE SAME ONE WRITER, NOW OF BOTH `scene.fog` AND `scene.fogNode` ──────────────────
   *
   * The height fog is a SECOND IMPLEMENTATION of the term above, not a second fog. `resolveFogPlan`
   * (`render/fog/fogModel.ts`, pure) is the ONE fold over (config × tier); this function is the ONE
   * place either three-side handle is assigned; and the panel, the outliner chip and
   * `__fwg.state().fog` all read the SAME plan, so the panel cannot describe a fog the frame is not
   * running. A second writer here would be exactly the "two competing values/systems" the owner
   * objected to in the shadow settings.
   *
   * ⚠️ `scene.fogNode` WINS OVER `scene.fog` (`NodeManager.getFogNode`, r185), so the two must
   * never both be live — every arm below sets both, and `linear` deliberately keeps using the
   * `THREE.Fog` object so its picture is byte-for-byte the one that shipped.
   *
   * ⚠️ AND A NODE SWAP IS A FULL PIPELINE REBUILD. `NodeManager.getCacheKey` pushes
   * `fogNode.getCacheKey()` into the per-call scene key, so null ⇄ node regenerates every visible
   * material's WGSL — the fog analogue of `LightsNode.customCacheKey`. Counted, never hidden:
   * `fogKeyCycles` is published as `__fwg.state().fog.keyCycles` and a colour/density/top drag must
   * report 0 (uniform writes on a stable node, pattern 5's `renderGroup`).
   */
  let heightFog: HeightFogHandle | null = null;
  let fogKeyCycles = 0;
  let fogPlan: FogPlan = resolveFogPlan(resolveFogConfig(undefined), tier);
  const setFogNode = (node: HeightFogHandle['node'] | null): void => {
    if ((scene.fogNode ?? null) === node) return;
    fogKeyCycles++;
    scene.fogNode = node;
  };
  const applyFog = (cfg: WorldFogConfig | undefined): void => {
    // ⭐ ABSENT ⇒ OFF, and `resolveFogConfig` is the ONE place that says so (its header carries
    // the ruling). This used to read `cfg ?? WorldFogSchema.parse({})`, which drew the shipped
    // literal into every world that had never mentioned fog — the forced ground plane again.
    const f = resolveFogConfig(cfg);
    const plan = resolveFogPlan(f, tier);
    fogPlan = plan;
    if (plan.impl === 'off') {
      setFogNode(null);
      scene.fog = null;
      return;
    }
    if (plan.impl === 'height') {
      /*
       * The node is built ONCE and kept for the life of the scene: after the first switch to
       * height, every subsequent drag of density/top/colour is a uniform write and `keyMoves`-free.
       * `scene.fog` is nulled so there is exactly one live authority even though `fogNode` would
       * have won anyway — a reader of `scene.fog` (the bake freeze, a future debug view) must see
       * the same answer the shader does.
       */
      heightFog ??= createHeightFog({ color: plan.color, density: plan.density, topM: plan.topM });
      heightFog.set({ color: plan.color, density: plan.density, topM: plan.topM });
      scene.fog = null;
      setFogNode(heightFog.node);
      return;
    }
    setFogNode(null);
    const col = new THREE.Color(plan.color);
    if (scene.fog instanceof THREE.Fog) {
      // Colour/near/far are plain uniforms on the fog node — no material rebuild, no key move.
      scene.fog.color.copy(col);
      scene.fog.near = plan.nearM;
      scene.fog.far = plan.farM;
    } else {
      scene.fog = new THREE.Fog(col, plan.nearM, plan.farM);
    }
  };
  applyFog(world.lighting?.fog);

  const camera = new THREE.PerspectiveCamera(
    BASE_FOV,
    host.clientWidth / host.clientHeight,
    0.1,
    400,
  );
  camera.matrixAutoUpdate = true; // matrix-policy opt-in: posed every frame (rig / editor fly)

  // Lights: posed once → updateMatrix() once (matrix policy). The hemi's DIRECTION lives in its
  // matrixWorld (constructor position (0,1,0)) — skipping its updateMatrix zeroes the direction.
  /*
   * ── THE INDIRECT TERM ─────────────────────────────────────────────────────────────────────────
   *
   * This light and the probe volume in `setProbeGrid` are TWO IMPLEMENTATIONS OF ONE THING, and
   * running both adds indirect light to the frame twice. `render/indirect.ts` holds the rule, the
   * evidence, and the reason it is a derived value rather than a comment nobody acted on; the
   * intensity below is now the ONLY place `hemi.intensity` is ever assigned.
   *
   * `?hemi=<n>` — DEV-ONLY, changes no default, and it exists because of a measurement. It is the
   * experiment's lever, so it MAY still produce the double-counted state (the four-state shot set
   * needs that state to exist in order to photograph it being wrong) — but no longer quietly:
   * `indirect.doubleCounted` is in the snapshot and the warning below fires once.
   */
  /**
   * ── THE WORLD'S LIGHTING RIG (G4) ─────────────────────────────────────────────────────────────
   *
   * Sun pose, sun colour/intensity, sky and the base constant fill are ART DECISIONS PER WORLD and
   * now live in `world.lighting` (`src/world/schema.ts`), fully defaulted so a world that says
   * nothing is lit exactly as before. Read ONCE, here, so nothing downstream can read a second
   * copy: the sun light, the shadow camera and the sky dome's `sunPosition` uniform are all driven
   * from the single `sunPos` below.
   */
  const lightRig = world.lighting;
  /*
   * `?sunAz=` / `?sunEl=` — DEV-ONLY, change no default, and they exist for the same reason
   * `?hemi=` and `?sun=` do. G3.1's finding was not "the sun is too dim", it was **"the sun does
   * not geometrically reach anything"** — 42× the intensity bought 3.7× the light — and that is a
   * question about POSE that no intensity knob can ask. Posing a sun is also an art loop, and an
   * art loop that needs a rebuild per iteration does not happen.
   *
   * ⚠️ `raw !== null` FIRST, everywhere. `Number(null)` is **0**, not NaN — and here 0 is a
   * perfectly legal azimuth AND a legal elevation, so the landmine that blackened every Sponza
   * screenshot once (`?hemi=`) and was survived twice by accident (`?giIntensity=`) would this time
   * silently drop the sun onto the horizon at due north.
   */
  const sunAngles = {
    az: lightRig.sun.azimuthDeg,
    el: lightRig.sun.elevationDeg,
  };
  if (import.meta.env.DEV) {
    const rawAz = params.get('sunAz');
    if (rawAz !== null) {
      const n = Number(rawAz);
      if (Number.isFinite(n)) sunAngles.az = n;
    }
    const rawEl = params.get('sunEl');
    if (rawEl !== null) {
      const n = Number(rawEl);
      if (Number.isFinite(n) && n >= -90 && n <= 90) sunAngles.el = n;
    }
  }
  const sunPos = sunPositionOf({
    ...lightRig.sun,
    azimuthDeg: sunAngles.az,
    elevationDeg: sunAngles.el,
  });
  const hemi = new THREE.HemisphereLight(
    new THREE.Color(lightRig.ambient.skyColor),
    new THREE.Color(lightRig.ambient.groundColor),
    0,
  );
  /*
   * ⚠️ `raw !== null` FIRST. `Number(null)` is **0**, not NaN — so the obvious
   * `const n = Number(params.get('hemi')); if (Number.isFinite(n) && n >= 0)` sets the intensity
   * to 0 on EVERY boot, param or no param. It shipped for about an hour and turned every Sponza
   * screenshot black; nothing threw, nothing warned, and the only tell was that the atrium's
   * mean luminance fell from 25 to 0.6 between two runs of the same command. The other members
   * of this family (`?giSpacing=`, `?giMaxProbes=`, `?giIntensity=`) survive the same mistake
   * only by accident, because their guards happen to reject 0.
   */
  let hemiOverride: number | null = null;
  if (import.meta.env.DEV) {
    const raw = params.get('hemi');
    if (raw !== null) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) hemiOverride = Math.min(10, n);
    }
  }
  /** True once a baked, visible probe volume is carrying the indirect term. Owned by setProbeGrid. */
  let giCarriesIndirect = false;
  /**
   * ⚠️ THE VOLUME'S PROPERTY, NOT THE SCENE'S, AND IT IS LATCHED AT BAKE TIME.
   *
   * "Is there a sky in the scene right now?" is the wrong question and would be wrong in both
   * directions: a sky added after the bake is not in the atlas (so the volume is NOT a skylight and
   * dropping the fill to the skylit residual would black the room out), and a sky removed after the
   * bake still is (so the volume IS a skylight and keeping the larger residual would double-count).
   * `setProbeGrid` sets this from what was actually in the scene when `bake()` ran.
   */
  let giSkyBaked = false;
  /** The world's base fill — data, not a literal. `AMBIENT_FILL_BASE` is still its default. */
  const ambientBase = lightRig.ambient.intensity;
  let indirect: IndirectTerm = resolveIndirect(false, hemiOverride, {
    base: ambientBase,
  });
  let doubleCountWarned = false;
  /**
   * THE ONE WRITER of `hemi.intensity`. Re-derives the constant fill from the state of the
   * indirect term and applies it. Called at construction, whenever the volume appears or goes
   * away, and by the dev hook's `setAmbientFill`.
   */
  const applyIndirect = (): IndirectTerm => {
    indirect = resolveIndirect(giCarriesIndirect, hemiOverride, {
      skyBaked: giSkyBaked,
      base: ambientBase,
    });
    hemi.intensity = indirect.fill;
    if (indirect.doubleCounted && !doubleCountWarned) {
      doubleCountWarned = true;
      console.warn(
        `[gi] DOUBLE-COUNTED INDIRECT: a probe volume is carrying the indirect term and the ` +
          `constant hemisphere fill is still ${indirect.fill.toFixed(2)} (residual is ` +
          `${resolveIndirect(true, null, { skyBaked: giSkyBaked }).fill.toFixed(2)}). The frame is being lit indirectly ` +
          `twice; the volume's spatial variation cannot be seen on top of it. See ` +
          `src/render/indirect.ts.`,
      );
    }
    return indirect;
  };
  applyIndirect();
  hemi.updateMatrix();
  scene.add(hemi);
  /**
   * THE DIRECT TERM. The scene's whole light budget is this plus the indirect term above, and
   * `?sun=<n>` is here for the same reason `?hemi=` is: the two have to be movable independently
   * or the question "which of them is lighting the picture?" cannot be asked.
   *
   * It was asked on 2026-08-31 (G3) and the answer was not what anyone expected. With the
   * hemisphere at 0, mean frame luminance on Sponza reads **0.05 / 255** at the arcade camera and
   * **1.0** at the atrium camera, against **28.1** and **27.7** with it at 1.25. The direct term
   * contributes ~0.2 % of the arcade image and under 5 % of the others: the ground floor of our
   * stress scene has **no direct light on it at all**, and the "ambient fill" is not a fill, it is
   * the entire picture.
   *
   * That is why probe GI kept measuring as a flat ambient lift. The bake photographs the scene as
   * lit, so an atlas baked in a room whose only light is a position-independent constant contains
   * the bounce of a position-independent constant. There was never any directional light for the
   * volume to carry into the arcade.
   *
   * (C) three's own `webgpu_lightprobes_sponza.html` runs `DirectionalLight( 0xfff2dc, 100.0 )` and
   * a `SkyMesh`, with no ambient term whatsoever, at the same `ACESFilmicToneMapping` and
   * `toneMappingExposure = 1.0` we use. Our sun is **2.4**.
   *
   * ⚠️ NO DEFAULT IS CHANGED HERE. 2.4 is what it was; the intensity of our shipped sun is an
   * owner decision, not a fix to be slipped in behind a GI investigation.
   */
  const sun = new THREE.DirectionalLight();
  /*
   * ── ⭐ B1 · THE SUN IS POSED BY `applySun`, THE SAME WRITER `applyWorldLights` CALLS ─────────
   *
   * Until B1 this block posed the sun BY HAND — position, colour, intensity, castShadow, map size,
   * the ±extent box, the far plane, the policy — and `applyWorldLights` below never touched it. L1
   * had made the sun a ROW of the resolved rig with a `writeTarget`, and L2 had routed its shadow
   * policy through the one function; what neither did was make the row reachable: an editor that
   * listed the sun would have moved `world.lighting.sun` and not one pixel. `applySun`
   * (`worldLights.ts`) is now the ONE writer, and this is its first of two calls — with the BOOT
   * vector, which folds the `?sunAz=`/`?sunEl=` dev overrides in, and which `createSky` below is
   * aimed from so the light and the dome cannot disagree.
   *
   * The L2 argument for the policy line is unchanged and now lives inside `applySun`: the sun
   * spent its whole life outside every mechanism the light rig grew, and a second hand-rolled
   * answer to "how is this light posed/scheduled" beside the first is how that happens again.
   * The sun still has no authored policy field and cannot be given one (`WorldSunSchema` is
   * `prefault({})`; a field there moves all six committed `worldHash`es).
   */
  applySun(sun, lightRig.sun, sunPos);
  if (import.meta.env.DEV) {
    // `raw !== null` FIRST — the `Number(null) === 0` landmine, same as `?hemi=` above.
    // Applied AFTER `applySun` so the override wins at boot; the FIRST editor apply supersedes it
    // with the authored intensity (`applyWorldLights` says so in its header).
    const raw = params.get('sun');
    if (raw !== null) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) sun.intensity = Math.min(1000, n);
    }
  }
  scene.add(sun);
  renderer.shadowMap.enabled = true;
  /*
   * ── ⭐ B3 · THE SUN'S SHADOW IS CASCADED, AND THE RIG IS ATTACHED HERE, ONCE ──────────────────
   *
   * `applySun` above wrote the stock single map — `±shadowExtentM` about the world origin, which
   * does not follow the camera and shadows nothing outside its box. `csmRig` puts r185's
   * `CSMShadowNode` on `sun.shadow.shadowNode`, which `AnalyticLightNode.setupShadow` reads in
   * place of `setupShadowNode()` at the first material build; from then on the sun's shadow is N
   * camera-fitted cascades (`lightModel.SUN_SHADOW_TIERS`, 3 at tier 2, 1 below) and the stock box
   * is only what the BAKE photographs (the bracket detaches for it — see `detachForBake`).
   *
   * `?csm=` is the dev control arm: `0` keeps the single map (the arm every L1/L2 number was
   * measured on, so the owner can A/B), `1..4` forces a count. `?csm=` is NOT in `worldHash` and
   * never reaches a file — `csmOverride` is folded into every policy resolve so the editor's
   * apply cannot silently drop it.
   */
  let csmOverride: number | null = null;
  if (import.meta.env.DEV) {
    const raw = params.get('csm');
    if (raw !== null) {
      const n = Number(raw);
      if (Number.isInteger(n) && n >= 0 && n <= 4) csmOverride = n;
    }
  }
  /**
   * ── ⭐ `?shadowRes=` — THE SHADOW-MAP MULTIPLIER, AND IT IS THE SAME SHAPE AS `?csm=` ──────────
   *
   * The owner's item 4: *"the shadow map resolution should be a part of the rendering settings,
   * not a local setting? And that it'd scale down based on rendering settings? We could perhaps
   * have a resolution cap or multiplier ... it should be tied to general perf settings, no?"*
   *
   * Three quantities, kept apart: the world file's `shadowMapSize` is the artist's REQUEST (level
   * data, in `worldHash`, untouched by any of this); `SUN_SHADOW_TIERS[tier].mapSizeCap` /
   * `.localMapSizeCap` is a DEVICE FACT; this is a SESSION PREFERENCE.
   * `lightModel.shadowMapSizeFor` folds the three, and it is folded into every policy resolve
   * below exactly the way `csmOverride` is — one variable, every call site, so an apply cannot
   * silently drop it.
   *
   * ⚠️ NOT inside `import.meta.env.DEV`, unlike `?csm=`. This is a SHIPPING lever: it is the row
   * the creator's Shadow quality control drives, and the `?antialias=` lesson (S4) is that a
   * folded-out read makes a setting that compiles, passes and does nothing IN PRODUCTION ONLY.
   */
  let shadowResScale = 1;
  {
    const raw = params.get('shadowRes');
    if (raw !== null) {
      const n = Number(raw);
      if (n === 1 || n === 0.5 || n === 0.25) shadowResScale = n;
    }
  }
  /**
   * ── ⭐ R5 · `?shadowSplit=off|near|all` — THE TWO-MAP SPLIT, SELECTABLE ─────────────────────────
   *
   * `off` (the default, and what ships) is today's rig byte for byte. `near` renders the static
   * world's shadow into ONE frozen camera-anchored map and leaves cascade 0 seeing both channels,
   * so everything inside the first split keeps exactly today's shadow quality; `all` sends every
   * cascade dynamic-only for the maximum saving and the coarsest static contact shadows.
   *
   * ⛔ DEFAULT OFF, AND DEV-GATED LIKE `?csm=`, BECAUSE THE TRADE IS A PICTURE. The saving is
   * structural and large (this level is 5,563 static meshes and one moving player) and the cost is
   * static contact-shadow resolution — `lightModel.SunShadowSplit`'s header carries the metres.
   * Choosing that for an owner from a build agent's chair is what rule 0 exists to stop; making it
   * one flag he can A/B is what it asks for.
   */
  let shadowSplitMode: SunShadowSplitMode | null = null;
  if (import.meta.env.DEV) shadowSplitMode = parseSunShadowSplitMode(params.get('shadowSplit'));
  /**
   * ⭐ `?shadowDist=<metres>` — a SESSION override of the cascade reach, DEV-only like `?csm=`.
   *
   * The reach is the strongest single lever on shadow cost: the far cascade holds 1,073 of 1,717
   * shadow draws on `mars-hangar-01` because it covers 28–90 m. The owner has called the shipped
   * 90 m "too much by a long way", and a number that is going to be authored into every world
   * should be chosen from a measured sweep rather than from taste.
   *
   * ⚠️ A BOOT OVERRIDE RATHER THAN AN EDIT, because editing `sun.shadowDistanceM` moves `worldHash`
   * and stales the atlas — and Mars's is the first bake that world has ever had. It also flows into
   * the frozen static map's radius for free (`resolveSunShadowSplit` takes the resolved distance),
   * so a sweep measures the split and the cascades together rather than one at a time.
   */
  let shadowDistOverride: number | null = null;
  if (import.meta.env.DEV) {
    const n = Number(params.get('shadowDist'));
    if (Number.isFinite(n) && n > 0) shadowDistOverride = n;
  }
  /**
   * ⭐ R2 · `?sunShadowUpdate=everyFrame|everyNFrames|staticOnly` + `?sunShadowUpdateN=` — the sun's
   * SCHEDULE as a session override, dev-only; `null` is the authored arm, and no committed world
   * moves off `everyFrame`.
   *
   * The mechanism is B6's and has shipped since. What it has never had is a way to be looked at on
   * a real frame without editing a world file — and editing `world.json` to try it moves
   * `worldHash` and stales that world's atlas, which is a high price for an experiment.
   */
  let sunUpdateOverride: { update: AppliedShadowUpdate; n?: number } | null = null;
  if (import.meta.env.DEV) {
    const raw = params.get('sunShadowUpdate');
    if (raw === 'everyFrame' || raw === 'everyNFrames' || raw === 'staticOnly') {
      const n = Number(params.get('sunShadowUpdateN'));
      sunUpdateOverride = { update: raw, n: Number.isFinite(n) && n > 0 ? n : undefined };
    }
  }
  const csmRig: CsmRig = createCsmRig({
    sun,
    camera,
    renderer,
    policy: sunShadowPolicy(tier, lightRig.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride),
    castShadow: lightRig.sun.castShadow,
    /*
     * B6 — the root the sun's `staticOnly` arm opts into `FWG_STATIC_SHADOW_LAYER`. The SCENE,
     * i.e. the same root `worldLightRig` walks for a frozen local light: a frozen sun's map must
     * be a picture of the level, and anything marked `markDynamicShadowCaster` (the player, the
     * platforms, the companion's rig) is skipped whole so it is never burned into it.
     */
    staticRoot: scene,
  });

  /**
   * ── ⭐ THE WORLD'S OTHER LIGHTS (E2) — DATA, READ ONCE, RIGHT HERE ─────────────────────────────
   *
   * `world.lighting.lights[]` becomes three objects through the ONE constructor
   * (`src/render/lights/worldLights.ts`); mobility decides PRESENCE, never `intensity = 0`
   * (`src/render/indirect.ts`). A `static` light is built, marked `BAKE_ONLY` and deliberately NOT
   * parented — the bake bracket below parents it for the bake and its `finally` removes it again.
   *
   * It is read HERE, synchronously, between the sun and the sky, for the two reasons argued at
   * `lightModel.ts`: `liveLighting()` (a few lines down) builds the provenance fingerprint by
   * reading the light objects, and every material compiled before a light arrives has to be
   * rebuilt when it does. A world that authors no lights builds nothing and is byte-identical to
   * before this existed — `lights` is `.optional()` precisely so its `worldHash` is too.
   */
  const worldLightRig = createWorldLights(scene, lightRig.lights, tier, {
    /*
     * ⭐ AND THE SESSION'S SHADOW MULTIPLIER GOES IN AT CONSTRUCTION — MEASURED DEFECT, NOT TIDINESS.
     *
     * This argument was MISSING and the omission was invisible: `?shadowRes=0.25` threaded into
     * `sunShadowPolicy` (the cascades scaled) and into every `describeLightRig` (the panel PRICED
     * the scale), but the rig itself initialised its own `shadowResScale` to 1, so `applyShadow`
     * ALLOCATED the unscaled map. One authored `castShadow` point light at 1024 on tier 2 booted
     * at 1024² x 8 B x 6 = 50,331,648 B while the frame-cost line reported 256² x 8 x 6 =
     * 3,145,728 B — a 16x under-report, with the Shadow-quality widget reading `Low` over it
     * because `scene.shadowRes()` reads this file's own variable and not the graph.
     *
     * Nothing else could have covered it: `applyWorldLights` passes only `{ previewStatic }` (and
     * an omitted key deliberately leaves the scale UNCHANGED, so an apply cannot repair it), and
     * `worldLightRig.setShadowResScale` has exactly one caller — `GameScene.setShadowResScale`,
     * which only a human dial reaches. `?shadowRes=` is read OUTSIDE the DEV fold precisely
     * because it is the SHIPPING route (the creator's Shadow-quality row writes it into the URL),
     * so this was a production-only wrong number. `tests/renderWorldLights.test.ts` drives the
     * boot wiring now rather than only the post-boot setter.
     */
    shadowResScale,
  });
  /**
   * THE LIGHTING BLOCK THE ROWS ARE RESOLVED FROM, re-pointed by `applyWorldLights` (L1).
   *
   * `lightRig` is the BOOT world's block and the sun/sky objects above were built from it, so it
   * is the right starting value. It is a `let` because the editor replaces the whole world object
   * on undo/redo/Open/New and then calls `applyWorldLights` with the new block — see that method.
   */
  let activeLighting: WorldLightingConfig = lightRig;
  /**
   * ── ⭐ PREVIEW LIGHTING FOR UNBAKED `static` LIGHTS — THE SESSION FLAG, AND IT IS `false` HERE
   *
   * A boot is PLAY, and Play never previews: the owner's rule is *"never shipped if the bake is
   * stale"*. Only `setLightPreview` writes it, only the editor calls that (and the bake bracket,
   * which forces it false around every capture loop), and `setLightPreview` is folded out of a
   * production build entirely — so in `dist/` this variable is `false` for the life of the process
   * and no code that could change it exists. See that method for the four fences.
   */
  let lightPreview = false;
  {
    const li = describeLightRig(worldLightRig.info(), activeLighting, tier, csmRig.policy(), lightPreview, shadowResScale);
    if (li.runtime + li.bakeOnly + li.refusals.length > 0) {
      console.info(
        `[lights] ${li.runtime} runtime (${li.shadowCasters} shadow-casting) + ${li.bakeOnly} ` +
          `static/bake-only, budget ${li.budget.runtime}/${li.budget.shadow} at tier ${tier}.`,
      );
    }
    /*
     * ⭐ THE WHOLE RIG'S PRICE, INCLUDING THE SUN — printed on every world, even one authoring no
     * lights at all, because the interesting case is exactly that one: a default world is already
     * 2 of tier 0's 2 cost units and 1 of its 1 shadow traversal, spent entirely on the sun. A
     * budget line that only counted `lights[]` reported 0/8 over the same frame.
     */
    console.info(
      `[lights] rig: ${li.rows.length} rows (${li.cost.costUnits} cost units of ` +
        `${li.costBudget.costUnits}, ${li.cost.shadowTraversals} shadow traversals of ` +
        `${li.costBudget.shadowTraversals}, ` +
        // L2: the number `censusScene` used to declare unmeasurable. On every shipped world it is
        // the sun's map and nothing else, and on Sponza that one map is 33.55 MB — larger than the
        // whole geometry bill the same instrument reports to the byte.
        `${(li.cost.shadowMapBytes / (1024 * 1024)).toFixed(2)} MB of shadow maps) at tier ${tier}` +
        (li.costBudget.provisional ? ' — ⚠ PROVISIONAL, no tablet has ever been measured' : '') +
        (li.overCost ? ' — ⚠ OVER BUDGET (reported, never enforced)' : '') +
        '.',
    );
    for (const row of li.rows) {
      for (const note of row.notes) console.info(`[lights] '${row.id}': ${note}`);
    }
    for (const r of li.refusals) {
      // Constitution rule 6: an unsupported combination WARNS and is reported as data (the editor
      // renders `refusals`); it is never silently downgraded to a neighbouring mobility.
      console.warn(`[lights] '${r.id}' (${r.type} · ${r.mobility}) REFUSED — ${r.reason}`);
    }
  }

  /*
   * ── THE SKY, AS IN-SCENE GEOMETRY (G4) ────────────────────────────────────────────────────────
   *
   * A skylight is a hemisphere light that knows about occlusion, and on this stack you get one by
   * making the sky a REAL EMITTER the probe bake photographs. `src/render/sky.ts` holds the whole
   * argument, the four traps, and the hard-rule-7 judgement; this is only the wiring.
   *
   * It is NOT a third implementation of the indirect term. The hemisphere and the volume each ADD
   * a shading term; the dome adds none — it is a surface that emits, and it reaches materials
   * through exactly one path, the volume that bakes it. `applyIndirect()` is still the only writer
   * of `hemi.intensity`.
   *
   * `?sky=0|1` — DEV-ONLY, changes no default, and it exists for the same reason `?hemi=` and
   * `?sun=` do: the acceptance measurement is "how much sky does this point receive?", and that
   * question cannot be asked unless the sky can be taken away with everything else held still.
   */
  let skyWanted = lightRig.sky.enabled;
  if (import.meta.env.DEV) {
    const raw = params.get('sky');
    if (raw === '1') skyWanted = true;
    else if (raw === '0') skyWanted = false;
  }
  /**
   * `?sunDisc=1` — DEV-ONLY, changes no default. It keeps the solar disc IN the probe bake, i.e.
   * it reaches the double-counted state on purpose, for the same reason `?hemi=` can still reach
   * the hemisphere-plus-volume state: **the error has to be photographable or the fence is a
   * claim.** See `src/render/sky.ts` trap 4.
   */
  const bakeSunDisc = import.meta.env.DEV && params.get('sunDisc') === '1';
  // `let`, since B1: `applyWorldLights` constructs or removes the dome when the editor flips
  // `sky.enabled`. Every reader below (`liveLighting`, the bake bracket, `sky()`, `dispose`) reads
  // the variable at call time, so a dome that arrives after boot is seen by all of them.
  let sky: SkyHandle | null = createSky({ ...lightRig.sky, enabled: skyWanted }, sunPos);
  if (sky) {
    scene.add(sky.mesh);
    console.info(
      `[sky] dome in scene at scale ${sky.info().scaleM.toFixed(0)} m — the probe bake will ` +
        `photograph it (turbidity ${lightRig.sky.turbidity}, rayleigh ${lightRig.sky.rayleigh}, ` +
        `clouds ${lightRig.sky.cloudCoverage}).`,
    );
  }

  /**
   * ── THE RIG THIS SESSION RESOLVED, READ OFF THE LIGHTS (G5) ────────────────────────────────
   *
   * The one place a lighting fingerprint is formed, used for BOTH halves of the provenance
   * contract: the offline baker stamps it into the header it writes, and the loader compares the
   * header against it. One function, so the two can never disagree about what "the same rig" means.
   *
   * ⚠️ EVERY FIELD IS READ OFF THE OBJECT, NEVER OFF `world.lighting` OR A URL PARAM. That is the
   * same discipline as `indirect()` reading `hemi.intensity` and `sky()` reading the dome's
   * uniforms, and it is what makes the check catch a dev override: a session running `?sun=26`
   * resolves a different rig from the one a default bake photographed, and the atlas must be
   * refused rather than quietly applied to a scene it does not describe.
   *
   * `ambientFillAtBake` is the fill a bake WOULD run under — `resolveIndirect`'s volume arm, picked
   * by whether a dome is really in the scene — not the fill the light is holding right now (which,
   * before a volume exists, is the base). Comparing the current value would refuse every file on
   * the grounds that no volume is loaded yet, which is the state the check is trying to leave.
   */
  const liveLighting = (): import('../render/gi/probeFile').ProbeFileLighting => {
    const p = sun.position;
    const len = Math.hypot(p.x, p.y, p.z) || 1;
    const skyInfo = sky ? sky.info() : SKY_OFF;
    const skyBaked = sky !== null && sky.mesh.visible;
    return {
      sunDir: [p.x / len, p.y / len, p.z / len],
      sunIntensity: sun.intensity,
      sunColor: sun.color.getHexString(),
      skyBaked,
      sky: {
        on: skyInfo.on,
        scaleM: skyInfo.scaleM,
        turbidity: skyInfo.turbidity,
        rayleigh: skyInfo.rayleigh,
        mieCoefficient: skyInfo.mieCoefficient,
        mieDirectionalG: skyInfo.mieDirectionalG,
        cloudCoverage: skyInfo.cloudCoverage,
      },
      ambientFillAtBake: resolveIndirect(true, hemiOverride, {
        skyBaked,
        base: ambientBase,
      }).fill,
      hemiSkyColor: hemi.color.getHexString(),
      hemiGroundColor: hemi.groundColor.getHexString(),
    };
  };

  /**
   * ── ⭐ THE HOLE FALLBACK'S CONTENTS — WHAT A CELL WITH NO BRICK IS LIT BY (S2) ──────────
   *
   * Surface-adaptive placement drops bricks that have no geometry near them, and a dropped cell's
   * indirection texel points at atlas slot 0. Slot 0 has always held zeros, and zero reads as BLACK
   * — the same hard step `fillEmptySpaces: false` produces by design. `scatterCaptures` now writes
   * this world's own constant hemisphere there instead, so a hole degrades per fragment to *"the
   * indirect term this world had before GI existed"* for zero shader instructions and zero bytes.
   * The whole argument is at `src/render/indirect.ts` → `hemisphereFillSh`.
   *
   * ⚠️ **READ OFF THE LIGHT, NEVER OFF `world.lighting`** — the same discipline `liveLighting()`
   * keeps two lines above, and for the same reason: `THREE.Color` holds LINEAR components while
   * `world.lighting.ambient.skyColor` is an sRGB hex string, and `HemisphereLightNode` hands the
   * shader the linear value. A fill built from the hex would be a different light from the one the
   * bake photographed, and nothing downstream could tell.
   *
   * ⚠️ The intensity is `base − ambientFillAtBake`, not `base`: a hole is ALSO lit by the
   * `HemisphereLight` still in the scene at the residual, so writing the full base would make a
   * hole BRIGHTER than the same world with no GI at all. The subtraction is what makes the sum at
   * the fragment exactly the pre-GI term. `holeFillIntensity` owns the clamp.
   *
   * It is computed at the SCATTER rather than latched at boot because `?hemi=` and the sky's
   * visibility can both move between construction and a bake, and the atlas must describe the rig
   * that was photographed — which is the same rule `liveLighting()` exists to enforce.
   */
  const giHoleFillSh = (): Array<[number, number, number]> => {
    const skyBakedNow = sky !== null && sky.mesh.visible;
    const fillAtBake = resolveIndirect(true, hemiOverride, {
      skyBaked: skyBakedNow,
      base: ambientBase,
    }).fill;
    return hemisphereFillSh(
      [hemi.color.r, hemi.color.g, hemi.color.b],
      [hemi.groundColor.r, hemi.groundColor.g, hemi.groundColor.b],
      holeFillIntensity(ambientBase, fillAtBake),
    );
  };

  /*
   * ── ⭐ THE GROUND PLANE IS GONE, AND THE GRID IS EDITOR FURNITURE ────────────────────────────
   *
   * There used to be a 240 x 240 m `PlaneGeometry` here, unconditionally, in every world: a green
   * Lambertian quad no world authored, no creator could select, and nothing could turn off. It was
   * doing active damage. Under Sponza's `scale: 2` the marble floor sits at y = -0.991 m
   * (GLB min y -1.0115 x 2 + pos 1.032), so the quad was drawn a METRE ABOVE the atrium and the
   * player walked on engine furniture while the building hung below him. It was also 580 of the
   * pinned occupancy fixture's 3,346 surface voxels (17.3 %) and the sole occupant of 300 of them,
   * so every bake photographed its bounce light.
   *
   * The floor is level CONTENT now — a `world.boxes` entry with its top face at exactly y = 0,
   * which makes it simultaneously an instance of the gym's `InstancedMesh`, a Rapier cuboid, a
   * shadow caster/receiver, an occupancy solid and a member of the editor's pick set. Four worlds
   * author one (`src/level/gym.ts` for sandbox-01; the JSON directly for the other three);
   * route-test-01 and sponza-debug-01 deliberately do not, and `tests/worldFloor.test.ts` is
   * where that decision is written down and enforced.
   *
   * ⚠️ The GRID stays, because the owner objected to a floor he could not select and asked for
   * grid SNAP settings in the same breath — a snap grid with no visible grid is worse. It shipped
   * to PLAYERS before this change (no dev guard, no editor gate); now it is hidden at construction
   * and the editor turns it on, and `markOverlay` makes it what it always was: furniture, off the
   * VRAM census and un-repaintable by a false-colour view mode. That one call is also what lets
   * `render/debugView/view.ts` drop its `o.type === 'GridHelper'` special case — one definition
   * of "this object is furniture", not two.
   */
  const grid = new THREE.GridHelper(240, 240, 0x557a68, 0x466555);
  grid.visible = false;
  markOverlay(grid);
  scene.add(grid);

  // The world whose data drives the meshes below — swapped by rebuildWorld (editor edits).
  let activeWorld = world;

  // Gym: ONE InstancedMesh for every box (unit cube scaled per instance) — the R1 draw-call rule.
  // Boxes come from world DATA; the emitter wrote them from src/level/gym.ts (drift-guarded).
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const color = new THREE.Color();
  // R1: world-geometry materials come from the archetype factory (never direct construction) —
  // same colors/roughness as before; per-instance colors keep riding instanceColor.
  const gymMat = makeSurface('surf.opaque', { roughness: 0.85 });
  // WEBGPU WORKAROUND — an EMPTY InstancedMesh must never reach the render list.
  //
  // A world may legitimately carry zero platforms (route-test-01) or, after an editor delete-all,
  // zero boxes. `new InstancedMesh(geo, mat, 0)` then allocates `instanceMatrix` over a
  // `Float32Array(0)`. Under WebGPU that is a hard GPUValidationError, four per boot, on the
  // owner's daily-driver backend:
  //   THREE.WebGPURenderer: Uncaptured WebGPU GPUValidationError: Binding size for
  //   [Buffer "bindingBufferundefined_UniformBuffer_2_(vertex)"] is zero.
  // The three.js path, r185.1 (verified in node_modules, cited so a reader can check it):
  //   • nodes/accessors/Instance.js `createInstanceMatrixNode()` takes
  //     `matrixCount = Math.max(instanceMatrix.count, 1)` and, since 1 mat4 fits the uniform-buffer
  //     limit, emits `buffer(instanceMatrix.array, 'mat4', matrixCount)` — so the WGSL declares
  //     `array<mat4x4<f32>, 1>` (64 bytes) while the bound array is 0 bytes.
  //   • renderers/webgpu/WebGPUBackend.js `createUniformBuffer()` sets
  //     `_bufferDescriptor.size = uniformBuffer.byteLength` (0) and labels it with a `.id` that
  //     `Binding`/`Buffer` do not define — which is the literal `undefined` in the message.
  //   • renderers/common/Renderer.js `_projectObject()` has NO zero-instance skip, so the mesh is
  //     pushed, a bind group is built (once per pass: one for the shadow pass, one for the colour
  //     pass) and validation fires.
  // WebGL2 binds the same data as an instanced ATTRIBUTE and draws 0 instances silently, which is
  // why our default (WebGL2) e2e run never saw it.
  //
  // No three.js issue exists for this as of 2026-08-27 (searched: "Binding size" zero,
  // "bindingBuffer", open InstancedMesh+WebGPU — nearest neighbours #29500/#32099/#33890 are all
  // different defects). The fix is ours regardless and is not a papering-over: an instanced mesh
  // with nothing in it is a draw call with no output and has no business in the render list.
  // `visible = false` is the earliest possible exit — `_projectObject` returns on it before any
  // render object, bind group or draw is created, so it also costs nothing (setting `count = 0` on
  // an over-allocated buffer would instead trade the error for a "Draw with an instance count of 0
  // is unusual" warning and still build the bind group).
  // REMOVE WHEN: three.js skips zero-instance objects in `_projectObject`, or `Instance.js` pads
  // the bound array to `matrixCount`. Re-check at the r186 upgrade (rendering plan) by deleting
  // these two calls and running `FWG_E2E_WEBGPU=1 npm run test:e2e -- webgpu`.
  const hideIfEmpty = (mesh: THREE.InstancedMesh): void => {
    mesh.visible = mesh.count > 0;
  };
  const writeBoxInstances = (mesh: THREE.InstancedMesh, boxes: WorldData['boxes']): void => {
    boxes.forEach((b, i) => {
      q.setFromEuler(new THREE.Euler(0, 0, ((b.rotZDeg ?? 0) * Math.PI) / 180));
      m.compose(new THREE.Vector3(...b.pos), q, new THREE.Vector3(...b.size));
      mesh.setMatrixAt(i, m);
      const hex = (b.surface && SURFACE_COLORS[b.surface]) || STATION_COLORS[b.station] || 0x888888;
      mesh.setColorAt(i, color.setHex(hex));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
  };
  const buildGymMesh = (boxes: WorldData['boxes']): THREE.InstancedMesh => {
    const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), gymMat, boxes.length);
    writeBoxInstances(mesh, boxes);
    applyShadowFlags(mesh, SHADOW_FLAGS.gymBoxes);
    mesh.matrixAutoUpdate = true; // matrix-policy opt-in (the mesh OBJECT, if ever moved)
    hideIfEmpty(mesh);
    scene.add(mesh);
    return mesh;
  };
  let gym = buildGymMesh(world.boxes);

  // Moving platforms (station 10): one InstancedMesh, matrices re-posed per frame from the same
  // pure platformPosAt the physics service uses — render and collision cannot drift apart.
  const platMat = makeSurface('surf.opaque', {
    color: 0x8f6fc9,
    roughness: 0.7,
  });
  const buildPlatformMesh = (count: number): THREE.InstancedMesh => {
    const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), platMat, count);
    // Moving platforms are re-posed every frame — `movable` keeps them out of frozen maps (L2),
    // and a NEW mesh from `rebuildWorld` gets the same declaration through the same call.
    applyShadowFlags(mesh, SHADOW_FLAGS.platforms);
    mesh.frustumCulled = false; // matrices are re-posed per frame; skip stale-bounds culling
    mesh.matrixAutoUpdate = true; // matrix-policy opt-in
    hideIfEmpty(mesh); // route-test-01 has no platforms — see the WEBGPU WORKAROUND note above
    scene.add(mesh);
    return mesh;
  };
  let platforms = buildPlatformMesh(world.platforms.length);
  const platQ = new THREE.Quaternion();
  const platM = new THREE.Matrix4();

  // Player: squash/stretch + teeter wobble live on a wrapper so any later mesh swap survives.
  const player = new THREE.Group();
  const squash = new THREE.Group();
  player.add(squash);
  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x4fc1cc,
    roughness: 0.5,
  });
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(capsule.radius, capsule.height - 2 * capsule.radius, 8, 16),
    bodyMat,
  );
  body.position.y = capsule.height / 2;
  squash.add(body);
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.1, 0.3, 12),
    new THREE.MeshStandardMaterial({ color: 0xe8e26a, roughness: 0.4 }),
  );
  nose.rotation.x = Math.PI / 2;
  nose.position.set(0, capsule.height * 0.65, -(capsule.radius + 0.18));
  squash.add(nose);
  /*
   * ── ⭐ B3 · THE PLAYER'S SHADOW DECLARATION — AND THE OWNER'S BUG, IN ONE LINE ───────────────
   *
   * `SHADOW_FLAGS.player` is cast + receive + movable. The body used to be `castShadow = true` and
   * NOTHING ELSE, and under the node path `AnalyticLightNode.setup()` compiles a shadow term only
   * for `builder.object.receiveShadow` — so the character cast shadows and could not receive one,
   * which is the report. The applier walks the group now (capsule, nose) and hooks `childadded`
   * down the chain, which is what reaches the real character glTF when `CharacterView.load`
   * parents it under `rollSpin` seconds from now (`render/lights/shadowFlags.ts` says why a walk
   * alone would have re-created the bug for every late arrival).
   *
   * `movable` is L2's "this subtree moves": a light with `shadowUpdate: 'staticOnly'` renders its
   * map ONCE and freezes it, and freezing a map with the player in it would burn a permanent
   * shadow of him standing at spawn into the level. The mark is on the GROUP, and
   * `enableStaticShadowLayer` skips a marked subtree WHOLE. It costs nothing on a world with no
   * `staticOnly` light, which is every world today.
   */
  applyShadowFlags(player, SHADOW_FLAGS.player);
  scene.add(player);

  // Real character: UE glTF export, loaded async — the capsule stays if assets are missing.
  // MEASURED from the rig (foot→toe = +Z, right shoulder at -X): the exporter normalized UE's
  // X-forward to glTF's spec +Z-forward. Our game forward is -Z ⇒ 180° yaw fix. Panel-tunable.
  // scale: the mesh is 1.31 m tall inside a 1.6 m capsule (forensics) — undersized bodies
  // read as frantic legs at speed. Default fills the capsule; strideMul is the taste knob
  // (raising it slows cadence at the cost of proportional foot slide).
  const charFix = {
    yawDeg: 180,
    scale: 1.22,
    strideMul: 1.0,
    cadenceCapHz: 2.3,
  };
  const charHolder = new THREE.Group();
  squash.add(charHolder);
  // Roll spin wrapper: procedural dive-tuck-spin. Pivot at (scaled) hip height so the body
  // rotates about its center, not its feet.
  const HIP_Y = 0.52;
  const rollSpin = new THREE.Group();
  charHolder.add(rollSpin);
  // Matrix-policy opt-in: the whole player wrapper chain moves per frame, and the hook makes the
  // async-loaded character subtree (incl. bones) opt in when it attaches under rollSpin.
  optInSubtree(player);
  /*
   * ⭐ P1 · ONE LEDGER, AND IT IS NOW THE SERVICE'S (ADR-0003 §4.4).
   *
   * `vramAccountant.ts:97-131` records that the character loader was the ONE caller that ever
   * charged this ledger, which is why the scene CENSUS had to be invented. That caller is gone:
   * the library service charges every source it parses, from measured bytes at `ready` rather than
   * from a walk of whatever the loader happened to attach — so `ledgerMB` finally describes
   * something, and it describes it once. Mount hands the same object down; the fallback exists for
   * the headless scenes in `tests/**`, which have no library and charge nothing.
   */
  const vram = library?.vram ?? new VramAccountant();
  let charView: CharacterView | null = null;
  /*
   * ⭐ THE CHARACTER COMES OFF THE CATALOG (plan §9's P1 gate), IN THE BOOT LANE.
   *
   * `CharacterView.load('/assets/character')` was twelve literal paths, no version, no hash, no
   * budget and no dispose. It is now one body id and eleven clip ids resolved through the library,
   * and the three properties that buys are the gate's: the boot lane holds `playing` until they
   * are resident (`world/readiness.worldPlayable`), the bytes are charged to the accountant, and
   * `charView.dispose()` exists — a body swap used to leak the whole view (research 02 §2).
   *
   * The failure policy is UNCHANGED and is the reason this is `.then/.catch` and not an await: no
   * character, keep the capsule. Nothing about the animation graph, `clipMeta`, `charFix`, the
   * 1.22 scale or the hip pivot moved — the rig sidecar rides along in the handle and P2 is what
   * makes the runtime read it.
   *
   * `characterColor` (TEMPORARY skin recolor, `?skin=` in mount.ts) rides along as the library
   * load's third argument — see the header comment on CharacterView.load for why this is not the
   * eventual cosmetics system. The vram-charging loop this replaced is gone deliberately: the
   * library now charges bytes to the accountant at `ready` time, from measured bytes, not from a
   * traversal of whatever the loader happened to attach (see the P1 comment just above `vram`).
   */
  if (library !== undefined) {
    CharacterView.load(library.assets, {}, characterColor)
      .then((view) => {
        charView = view;
        body.visible = false;
        nose.visible = false;
        rollSpin.add(view.root);
      })
      .catch((err: unknown) => {
        console.warn('Character assets unavailable, keeping capsule:', err);
      });
  }

  /*
   * THE VRAM NUMBER (R1.5). `mount.ts` reads this EVERY FRAME for the tuner readout, so the walk
   * is cached and recomputed on demand at most once per interval — a scene-graph traversal per
   * frame to feed a number a human reads once a second would be an instrument that changes what
   * it measures. `ageMs` is published so nobody mistakes a stale total for a live one.
   *
   * Why the census and not the ledger: see the block comment in `render/assets/vramAccountant.ts`.
   * The short version is that the ledger reported 0.0 MB for a 68-texture scene, and a budget
   * enforced with a blind instrument is not enforced.
   */
  /** Frames this scene has DRAWN — the phase source for the shadow-refresh LOD (L2). */
  let shadowFrame = 0;
  // The per-frame draw/triangle latch — see `GameScene.frameStats` for why a poll cannot read
  // `renderer.info.render` directly. Written once per frame right after `pipeline.render()`.
  let lastFrameDraws = 0;
  let lastFrameTris = 0;
  /**
   * Entity subtrees that pose themselves every frame — see `entityRoot.matrixWorldAutoUpdate`.
   * A Set because `respawnAllEntities` re-adds the same objects, and a detached one (its entity
   * disposed) is pruned on the next tick rather than tracked with a second lifetime.
   */
  const dynamicRoots = new Set<THREE.Object3D>();
  /**
   * B4: what `tickShadows` selects the live local-shadow slots from. `cameraPos` is the camera's
   * own `Vector3` (it satisfies `Vec3Like` structurally — the pure selector never sees `three`),
   * and `budgetTraversals` is rewritten every frame from the sun's live policy.
   */
  const shadowSlotInput = { cameraPos: camera.position, budgetTraversals: 0 };
  const VRAM_CENSUS_INTERVAL_MS = 1000;
  let censusAt = -Infinity;
  let census: VramCensus = censusScene(null);
  const vramBreakdown = (): VramBreakdown => {
    const now = performance.now();
    if (now - censusAt >= VRAM_CENSUS_INTERVAL_MS) {
      census = censusScene(scene as unknown as Parameters<typeof censusScene>[0]);
      censusAt = now;
    }
    return {
      ...census,
      /*
       * ⚠️ L2 STEPPED THIS NUMBER UP, ON PURPOSE, AND ON SPONZA IT IS +33.55 MB IN ONE JUMP.
       * `shadowMapBytes` was `excludes: 'shadow-maps'` — a declared hole larger than the geometry
       * bill beside it. Leaving it out of the total would have been a census that MEASURES a thing
       * and then reports a number that omits it, which is worse than the honest blank it replaced.
       * The breakdown carries the term separately so the step is attributable rather than
       * mysterious.
       */
      totalMB:
        (census.textureBytes + census.geometryBytes + census.shadowMapBytes) / (1024 * 1024),
      ledgerMB: vram.grandTotal() / (1024 * 1024),
      ageMs: Math.max(0, now - censusAt),
    };
  };

  const vfx = new DustVfx();
  vfx.points.matrixAutoUpdate = true; // matrix-policy opt-in
  scene.add(vfx.points);

  // Registry entities (E0) parent here; the host adds/removes meshes, the scene owns the group.
  /*
   * ⚠️ THE ROOT IS NO LONGER BLANKET-OPTED IN, AND THAT WAS 20 % OF A FRAME.
   *
   * This was `optInSubtree(entityRoot)`, whose `childadded` hook opted EVERY host-spawned object —
   * and everything any entity added later — into per-frame matrix auto-update, permanently. Right
   * for a pot and a companion; on the Mars port it is ~11,000 objects recomposing a matrix sixty
   * times a second, none of which ever move. Measured by CDP profile: `updateMatrixWorld` at
   * 20.4 % of a 45 ms CPU frame with the GPU idle at 2.2 ms.
   *
   * The root itself is composed once (it never moves — it is a bare group at the origin), and each
   * class's `EntityMeta.mobility` decides what happens to what it spawns: `dynamic` takes
   * `optInSubtree` exactly as before, `static` takes `composeStaticSubtree`. Anything parented here
   * from a LATER callback that the host cannot attribute to a class (pot shards, the anchor's
   * crate) is not covered by either — it inherits three's default, which is
   * `DEFAULT_MATRIX_AUTO_UPDATE = false`, i.e. STATIC. That is the wrong default for a shard, so
   * the classes that do this opt their own late children in; `destructiblePot` is the one that
   * does, and its `mobility: 'dynamic'` is what makes that legible.
   */
  const entityRoot = new THREE.Group();
  /*
   * The root KEEPS its opt-in hook, and that is deliberate: it is the conservative default for
   * anything a class parents here from a LATER callback, which the host's create()-time diff cannot
   * attribute — `destructiblePot`'s shards and `anchor`'s crate are the two that do it today, and
   * shards that do not move are a gameplay bug, not a perf win. It is the same reasoning as
   * `ENTITY_SHADOW_DEFAULT` one block down: unattributable ⇒ assume it moves.
   *
   * What changed is that a class's declared `mobility` now OVERRIDES this for everything the host
   * CAN attribute, and `optInSubtree` refuses to undo that (its `apStatic` guard).
   */
  optInSubtree(entityRoot);
  /*
   * ⚠️ …AND THEN THE ROOT ITSELF OPTS BACK OUT, WHICH IS THE HALF THAT ACTUALLY SAVES THE FRAME.
   *
   * MEASURED, because the first version of this fix did not work and looked like it had.
   * `optInSubtree` above is wanted for its `childadded` HOOK (the conservative default for late,
   * unattributable children — pot shards, the anchor's crate). What is NOT wanted is the
   * `matrixAutoUpdate = true` it also sets on the ROOT.
   *
   * three's `updateMatrixWorld` sets `matrixWorldNeedsUpdate = true` inside `updateMatrix()`, and
   * recomputing a node's `matrixWorld` sets `force = true` for its whole subtree. So an
   * auto-updating `entityRoot` — a bare group at the origin that never moves — forced all ~11,000
   * descendants to `multiplyMatrices` every frame REGARDLESS of their own flags. Turning the
   * children static without this line moved the cost rather than removing it: `updateMatrixWorld`
   * self-time fell 20.4 % → 13.9 % while `multiplyMatrices` rose 2.6 % → 12.8 %.
   *
   * The root is composed ONCE. Its children then keep `matrixWorldNeedsUpdate === false` and the
   * per-frame walk over the static subtree does no arithmetic at all.
   */
  entityRoot.matrixAutoUpdate = false;
  entityRoot.updateMatrix();
  /*
   * ⭐ …AND THE SCENE SKIPS THE SUBTREE ENTIRELY. This is the third and largest step, and each of
   * the three was measured because the first two were not enough on their own.
   *
   * With the arithmetic gone, what remained was the WALK: `updateMatrixWorld` recursing ~11,000
   * objects three times a frame (once per `frameCalls`) to do two boolean checks and loop. three
   * skips a child only when `child.matrixWorldAutoUpdate === false` AND the parent did not force,
   * so turning it off HERE — one flag on one group — removes the entire entity subtree from the
   * per-frame walk.
   *
   * ⚠️ WHICH MEANS DYNAMIC ENTITIES MUST NOW POSE THEMSELVES, or the companion freezes at spawn.
   * `dynamicRoots` is that list, and it is short by construction: `EntityMeta.mobility` puts
   * `static_mesh` — 5,564 of the level's ~5,570 placements — on the other side of it.
   */
  entityRoot.matrixWorldAutoUpdate = false;
  entityRoot.updateMatrixWorld(true);
  /*
   * B3: the entity root's DEFAULT shadow declaration. The host applies each class's own column to
   * what its `create()` parents here; anything a class parents here LATER, from a callback (pot
   * shards, the anchor's crate at world start), is not attributable to a class and takes this —
   * cast + receive + movable, the conservative answer. `ENTITY_SHADOW_DEFAULT` says why.
   *
   * ⚠️ `markRoot: false`, AND WITHOUT IT THE WHOLE LEVEL IS EXCLUDED FROM EVERY FROZEN SHADOW MAP.
   * `movable` means "skip this SUBTREE whole" — `enableStaticShadowLayer` returns on the mark — and
   * `entityRoot` is not a caster, it is the container every static wall hangs from. Marked, it
   * pruned all ~1,200 batches and 1,319 entity meshes, so a `staticOnly` light would freeze an
   * EMPTY map. The subtree stamp and the `childadded` hook are unaffected: a later arrival still
   * takes the conservative answer, which is the whole reason that default is `movable`.
   */
  applyShadowFlags(entityRoot, ENTITY_SHADOW_DEFAULT, { markRoot: false });
  scene.add(entityRoot);
  // Editor helpers (selection box, gizmo proxy, TransformControls helper, drag preview) parent
  // here — same hook-propagated opt-in; the gizmo repositions its handles every frame.
  const editorRoot = new THREE.Group();
  optInSubtree(editorRoot);
  scene.add(editorRoot);
  /*
   * ── SYNTHETIC STRESS AXES (R1.5, dev-only) ─────────────────────────────────────────────────
   *
   * `?lights=N&shadowLights=K` / `?overdraw=N` / `?props=N` / `?lightType=` / `?lightReach=` —
   * the axes that ride on ANY world, so a measurement is a difference against the same bed with
   * exactly one thing changed. `src/render/stress.ts` has the design and the deliberate hard-rule-2 exception it
   * takes; the cheap presence test here is what keeps a normal boot from importing it at all.
   *
   * Folded out of production by the compile-time `import.meta.env.DEV` literal, exactly like the
   * lighting override, the editor and the mock gateway (checkBundle asserts marker + chunk + map).
   */
  let stress: { update(): void; info(): StressInfo; dispose(): void } | null = null;
  if (import.meta.env.DEV) {
    const wanted =
      params.has('lights') ||
      params.has('shadowLights') ||
      params.has('overdraw') ||
      params.has('props') ||
      params.has('lightType') ||
      params.has('lightReach');
    if (wanted) {
      const { createStressRig, parseStressSpec } = await import('../render/stress');
      const spec = parseStressSpec(location.search);
      if (spec) stress = createStressRig(scene, camera, spec);
    }
  }

  // Editor camera override: while true, syncPlayer's camera block is bypassed entirely.
  let externalCam = false;
  const raycaster = new THREE.Raycaster();
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const rayHit = new THREE.Vector3();

  // Debug jump arc.
  const arcPositions = new Float32Array(ARC_MAX_POINTS * 3);
  const arcGeo = new THREE.BufferGeometry();
  arcGeo.setAttribute('position', new THREE.BufferAttribute(arcPositions, 3));
  arcGeo.setDrawRange(0, 0);
  const arcLine = new THREE.Line(
    arcGeo,
    new THREE.LineBasicMaterial({
      color: 0x6fe3ec,
      transparent: true,
      opacity: 0.7,
    }),
  );
  arcLine.frustumCulled = false;
  arcLine.matrixAutoUpdate = true; // matrix-policy opt-in
  // Same shape as the empty-InstancedMesh note above: an EMPTY draw must not reach the render
  // list. The arc is hidden by drawRange(0, 0) whenever there is nothing to show, and WebGL2 is
  // happy to issue a 0-vertex draw — but Dawn logs "Calling [RenderPassEncoder].Draw with a vertex
  // count of 0 is unusual" on every boot of every world for it, which is exactly the kind of
  // standing noise that hides the next real message on the owner's daily backend. `visible`
  // carries the hidden state instead; setArc keeps the two in sync.
  arcLine.visible = false;
  scene.add(arcLine);

  // Auto-yaw defaults OFF: camera-relative input + camera chasing velocity is a feedback loop
  // (holding strafe makes the camera carousel). Astro's own camera is manual + stabilized.
  const rig: CameraRig = {
    yaw: 0,
    pitch: 0.28,
    distance: 6.5,
    msSinceCamInput: 1e9,
    autoYaw: false,
  };
  const camTune: CameraTuning = {
    horizSmooth: 0.16,
    lookaheadTime: 0.08,
    lookaheadMax: 0.6,
    lookaheadLateral: 0.35,
    lookaheadSmooth: 0.5,
    fovKickDeg: 4,
    autoYawRateDeg: 30,
  };

  // Camera springs (critically damped — C1).
  const springX = damped(0);
  const springY = damped(0);
  const springZ = damped(0);
  const lookX = damped(0);
  const lookZ = damped(0);
  const fov = damped(BASE_FOV);
  const camDist = damped(rig.distance);
  let groundY = 0;
  let airborneS = 0;
  let clearFrames = 0;
  let trauma = 0;
  const bodyOpacity = damped(1);

  let scaleY = 1;
  let scaleVel = 0;
  let facingYaw = 0;
  let teeterPhase = 0;
  let lastFrame = performance.now();
  const camTarget = new THREE.Vector3();

  /*
   * THE POST CHAIN (plan stage D). Built here rather than beside the renderer because `pass()`
   * needs the camera, and torn down in dispose(). `bilinear` returns a handle that simply calls
   * `renderer.render` — the mode is a row in the table, never an absence of one.
   */
  const pipeline: RenderPipelineHandle = createRenderPipeline(renderer, scene, camera, upscaleMode);

  /**
   * The ONE place the drawing buffer is sized. Everything else — boot, resize, DPI change,
   * controller rung, settings override — routes through here.
   *
   * ⚠️ `Math.max(1, …)` on BOTH axes, expressed as a floor on the RATIO. A scale that rounds a
   * drawing-buffer dimension to zero is exactly the zero-instance-`InstancedMesh` class of bug
   * this repo has already shipped once, and a 1-pixel-tall host (a collapsed flex parent during a
   * layout pass) is not hypothetical.
   *
   * ⚠️ When a post chain lands (plan stage D) the RENDER half must move off `setPixelRatio` and
   * onto `PassNode.setResolutionScale` — driving DRS through the pixel ratio would reallocate the
   * swap chain and change what the DOM composites against. This function is the one place that
   * changes; `renderScale` is already separated from `outputPixelRatio` for exactly that day.
   */
  const applyResolution = (): void => {
    const cssW = Math.max(1, host.clientWidth);
    const cssH = Math.max(1, host.clientHeight);
    const minRatio = Math.max(1 / cssW, 1 / cssH, RENDER_TUNING.output.minPixelRatio);
    /*
     * THE ONE BRANCH, and it asks the HANDLE rather than the mode name. A pipeline that owns the
     * render scale wants the drawing buffer at OUTPUT resolution (its post passes run there and
     * the scene pass is what shrinks); one that does not — `bilinear`, and `sharpen`, which
     * deliberately sharpens 1:1 at the cheap resolution — wants the whole buffer smaller, which
     * is stage B0 unchanged. Driving DRS through `setPixelRatio` in the first case would
     * reallocate the swap chain and change what the DOM composites against.
     */
    const bufferScale = pipeline.ownsRenderScale ? 1 : renderScale;
    const ratio = Math.max(minRatio, outputPixelRatio * bufferScale);
    renderer.setPixelRatio(ratio);
    renderer.setSize(cssW, cssH); // updateStyle: true — the CSS box stays the WINDOW size
    // AFTER the buffer is sized: the ≥1px clamp inside needs the buffer's real dimensions.
    pipeline.setRenderScale(renderScale);
    camera.aspect = cssW / cssH;
    camera.updateProjectionMatrix();
  };

  // `?drs=off|0|false` kills dynamic resolution for a session. It is a real knob, not a dev one:
  // "hold the resolution still" is what a leak test, a screenshot and a like-for-like A/B all need.
  const drsParam = params.get('drs');
  const drsEnabled =
    drsParam === null
      ? RENDER_TUNING.controller.enabled
      : !(drsParam === '0' || drsParam === 'off' || drsParam === 'false');

  const controller = createResolutionController(
    { ...RENDER_TUNING.controller, enabled: drsEnabled },
    (scale) => {
      renderScale = scale;
      applyResolution();
    },
  );

  /*
   * `?scale=<0..1>` PINS the render scale to the nearest rung for the session — the third member
   * of the `?dpr=` / `?drs=` / `?tier=` family, and the one stages D and E could not be measured
   * without: "how much does this chain cost at 0.67" is not a question a self-adapting controller
   * can be asked. It is also what lets the resolution e2e assert the stage-D identity
   * (`pass size == floor(buffer × scale)`) without a stopwatch. Not dev-only, for the same
   * reason `?drs=off` is not: hold-it-still is what an A/B and a screenshot both need.
   */
  const scaleParam = Number(params.get('scale'));
  if (Number.isFinite(scaleParam) && scaleParam > 0) controller.pin(scaleParam);

  // Establish the two levers ONCE at boot, through the same door every later change uses. At
  // renderScale 1 this is a no-op in pixels, but it is what puts the pass scale and the pixel
  // ratio into a state that agrees with `pipeline.ownsRenderScale` before the first frame.
  applyResolution();

  /**
   * Capability detection, never assumption (plan §3): Firefox flag-gates the WebGL2 timer query
   * for Spectre and Safari only grew it in 17. Without it the controller runs its wall-clock
   * mode, which can lower but only PROBES upward — honest, and visible as `mode` in `__fwg`.
   */
  let gpuTimingAvailable = false;
  try {
    gpuTimingAvailable = renderer.hasFeature('timestamp-query') === true;
  } catch {
    gpuTimingAvailable = false;
  }

  // Timestamp resolution is ASYNCHRONOUS and self-guarded against overlap, so a sample arrives
  // every few frames rather than every frame. `gpuSeq` is what lets the controller tell a FRESH
  // measurement from the same one again — feeding it the latest-known value every frame would
  // fill the median window with copies of one number and stop it reacting.
  let gpuTimeMs = 0;
  let gpuSeq = 0;
  let reportedGpuSeq = 0;
  let resolvePending = false;
  const pumpTimestamps = (): void => {
    if (!gpuTimingAvailable || resolvePending) return;
    resolvePending = true;
    void renderer
      .resolveTimestampsAsync('render')
      .then((ms) => {
        if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0) {
          gpuTimeMs = ms;
          gpuSeq++;
        }
      })
      .catch((err: unknown) => {
        // A disjoint event invalidates a whole batch of WebGL queries. Degrade to wall clock
        // once and say so — never spam, never steer on a frozen number.
        gpuTimingAvailable = false;
        console.warn(
          '[render] GPU timestamps unavailable — dynamic resolution falls back to wall clock:',
          err,
        );
      })
      .finally(() => {
        resolvePending = false;
      });
  };

  let lastFrameAt = 0;
  let uiFrozen = false;

  const onResize = (): void => {
    // THE BUG THAT WAS ALREADY INSIDE THE OWNER'S ASK: this never re-read `devicePixelRatio`, so
    // dragging the window to a different-DPI monitor did not re-scale. "A range of screen
    // resolutions and scaling settings, all working" requires it.
    const next = resolveOutputPixelRatio(window.devicePixelRatio, tier, RENDER_TUNING, dprOverride);
    if (next !== outputPixelRatio) {
      outputPixelRatio = next;
      // A different panel is a different machine as far as the budget is concerned; every sample
      // in the window was measured against the old pixel count.
      controller.reset();
    }
    applyResolution();
  };
  window.addEventListener('resize', onResize);

  /**
   * A `resize` listener alone does not cover the HOST changing size with no window resize at all
   * — a CSS layout change, a sidebar opening, the game embedded in a resizable pane. Feature
   * detected: happy-dom has no ResizeObserver and this module must still import cleanly there.
   */
  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => onResize()) : null;
  resizeObserver?.observe(host);

  /**
   * ── DPI CHANGES ARE WATCHED PER FRAME, and that is a considered choice. ──────────────────────
   *
   * The textbook idiom is `matchMedia('(resolution: Xdppx)')` re-armed on each change, and it was
   * written here first. It FAILED the browser test, for a reason worth recording: this browser
   * reports `devicePixelRatio` as **0.9999999734530078**, not 1. A `(resolution: 0.99999997dppx)`
   * query does not match the device it was built from, so it never transitions and never fires —
   * the watch is silently dead from the moment it is armed, on every machine whose ratio is not
   * an exact float. Bracketing it with min-/max-resolution would work, but it trades one browser
   * quirk for an epsilon nobody can justify.
   *
   * Comparing a cached number against `window.devicePixelRatio` once per frame is a property read
   * with no layout cost, it cannot be defeated by float precision, and it covers every cause at
   * once: a window dragged to another monitor, an OS scaling change, browser zoom. The frames it
   * needs are the frames where the answer matters.
   */
  let lastDpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;

  const resolutionHandle: ResolutionHandle = {
    ladder: RENDER_TUNING.controller.ladder,
    state: () => {
      const buf = renderer.getDrawingBufferSize(new THREE.Vector2());
      return {
        ...controller.status(),
        outputPixelRatio,
        effectivePixelRatio: renderer.getPixelRatio(),
        drawingBufferWidth: buf.x,
        drawingBufferHeight: buf.y,
        cssWidth: host.clientWidth,
        cssHeight: host.clientHeight,
        gpuTimingAvailable,
        gpuTimeMs,
        pipeline: pipeline.state(),
      };
    },
    freeze: (reason) => controller.freeze(reason),
    unfreeze: (reason) => controller.unfreeze(reason),
    setEnabled: (on) => controller.setEnabled(on),
    pin: (scale) => controller.pin(scale),
    setOutputPixelRatio: (ratio) => {
      outputPixelRatio = resolveOutputPixelRatio(
        window.devicePixelRatio,
        tier,
        RENDER_TUNING,
        ratio === null ? dprOverride : String(ratio),
      );
      controller.reset();
      applyResolution();
    },
  };

  /**
   * ── ⭐ THE VOLUME. ONE HANDLE, ONE LIGHT CLASS, ONE ATLAS FORMAT (F3) ─────────────────────────
   *
   * There used to be TWO — this and a `probeGrid` holding the dense v1 `LightProbeGrid` — because
   * the load path published a `SparseProbeVolume` while the in-browser bake still built a lattice.
   * They were two light CLASSES with two node classes, so one variable could not hold both without
   * a union that lied about `texture`, `resolution` and `getProbePosition`, none of which a sparse
   * volume has: its atlas is addressed through an indirection texture rather than a lattice. The
   * spec named that as "a second path that can rot" (§2.4) and said the pair would collapse once
   * the bake compiled a structure and scattered into a brick atlas (§3.2/§3.6). It has, and
   * `?gi=dense` — the only route to the other half — is retired with it.
   *
   * ⚠️ SO `giInfo.seamBreaks` IS NOW STRUCTURALLY 0 rather than watched per frame. The invariant
   * "parented AND visible ⇒ the atlas exists" was about `LightProbeGridNode.setup()`'s branch on
   * `light.texture`, a field three's light cache key cannot see. A `SparseProbeVolume` binds both
   * its textures in its CONSTRUCTOR, to zero dummies, so `setup()` has nothing to branch on and
   * there is exactly one structural variant for the life of the page (`render/gi/sparseVolume.ts`
   * states it; `tests/renderSparseAddress.test.ts`'s cold/warm graph census asserts it).
   */
  let sparseVolume:
    | (THREE.Object3D & {
        dispose(): void;
        intensity: number;
        setIntensity(gain: number): void;
        setNormalBias(metres: number): void;
        /*
         * ⭐ THE POST-BAKE REBIND'S ONE DOOR (L2), AND IT IS A METHOD RATHER THAN A CALL TO
         * `publishAtlas` FROM HERE FOR THE REASON THE TYPE ABOVE IS STRUCTURAL AT ALL: this file
         * is EAGER and may not name the light class. `SparseProbeVolume.republish` delegates to
         * `publishAtlas` — still the ONE writer of the two permanent `Texture3DNode`s (§4.5) —
         * so the rebind adds a door, not a second mechanism.
         */
        republish(
          atlasTexels: Uint16Array,
          indirTexels: Uint16Array,
          geom: import('../render/gi/sparseAddress').SparseGeometry,
        ): void;
      })
    | null = null;
  /**
   * ⭐ THE INTERNAL SHAPE IS `GiInfo` MINUS `bakeState` AND `rebinds`, and that omission is the
   * fence.
   *
   * `bakeState` is DERIVED in `gi()` from three fields this record already carries, so there is
   * exactly one derivation. Making it structurally impossible to write here is the difference
   * between "one answer" and "eight sites that each remember to agree" — and eight sites that
   * must agree is the shape of every failure this subsystem has collected. `rebinds` (L2) joins it
   * for the identical reason: it is a fact about the SESSION, not about any one of these records,
   * and every site that builds one would have to remember to carry the previous value forward.
   */
  let giInfo: Omit<GiInfo, 'bakeState' | 'rebinds'> | null = null;
  /**
   * How many times `reloadBakedLighting` has put a NEW file in front of the shader in this
   * session. Spread into `gi()` on the way out; see `GiInfo.rebinds` for what it witnesses.
   */
  let giRebinds = 0;
  /**
   * True when three fell back to the WebGL2 backend. Read by the tier-0 gate in `setProbeGrid`,
   * which is the one place that must NOT trust `renderer.isWebGPURenderer` (true on both).
   */
  const giFallbackBackend =
    (renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend !== true;
  /*
   * ── `?gi=probe` — THE PROBE GRID AS A URL PARAM (R2c, dev-only) ────────────────────────────
   *
   * Same family and same shape as `?tier=` / `?upscale=` / `?lighting=`: an explicit, reportable
   * override, never an inference. Until now the grid was reachable ONLY through a dev-panel
   * checkbox, which means it could not be put in a screenshot pair, a perf row or an e2e case —
   * and a GI feature nobody can photograph OFF-then-ON has not been evaluated, it has been looked
   * at. `?giSpacing=<m>` is the one knob that matters for the bake budget (probe COUNT is what
   * six-renders-each multiplies), so it rides along.
   *
   * ⚠️ IT CANNOT FIRE AT BOOT. The bake photographs the scene from 6·N cameras, and world content
   * arrives ASYNCHRONOUSLY — Sponza is a 50 MB glTF. A bake run at boot would capture an empty
   * room, produce a plausible atlas of the sky, and read as "the probe grid does nothing". So the
   * trigger waits for the drawn triangle count to stop moving, exactly as the stress rig waits
   * for its bounds to stop moving, and for the same reason.
   */
  let giAutoBake = false;
  /**
   * ── `?gi=` HAS THREE VALUES NOW (G5) ────────────────────────────────────────────────────────
   *
   * | value | meaning |
   * |---|---|
   * | `probe` | **prefer the world's baked file; bake in-browser if there isn't a usable one.** The default, and what the dev-panel checkbox does. |
   * | `file` | the file or nothing. Never bakes. This is the SHIPPING-shaped path: a build step produced the atlas, the runtime only loads it. |
   * | `bake` | never load; always bake in the tab. The dev/authoring path, and what every G0–G4 measurement in the roadmap was taken through. |
   *
   * `probe` is deliberately the one that changed behaviour rather than a new fourth value: the
   * in-browser bake was never the product, it was the only implementation. Making the FILE the
   * preferred answer under the existing name means the dev toggle, the URL and the panel all move
   * together, and `bake` exists so the old behaviour is still reachable by name rather than by
   * accident.
   */
  let giMode: 'auto' | 'file' | 'bake' = 'auto';
  /**
   * ── ⭐ WHAT THIS SESSION DOES ABOUT GI, AND WHERE THAT DECISION CAME FROM (BUG 1) ─────────────
   *
   * Resolved EAGERLY and OUTSIDE `import.meta.env.DEV`, which is the whole fix. Until this line,
   * `giMode` was assigned only inside the dev block, so a world's own `lighting` could not turn its
   * baked atlas on and **a production build had no path to an atlas at all** — Sponza opened unlit
   * on localhost over a 912 KB file sitting next to its world.json.
   *
   * `resolveGiActivation` is pure and holds the precedence (`?gi=` beats world data, in DEV only;
   * production always resolves from world data). The two `let`s below are DERIVED from it so that
   * the rest of this file keeps exactly one vocabulary.
   */
  const giActivation: GiActivation = resolveGiActivation(
    location.search,
    activeWorld.lighting.indirect,
    import.meta.env.DEV,
  );
  if (giActivation.ignoredParam !== null) {
    // Warn once, then fall through to world data — NEVER silently resolve to a different real
    // mode. Two runs that differ while both self-report correctly is the failure this prevents.
    console.warn(
      `[gi] ?gi=${giActivation.ignoredParam} is not a mode — off | file | probe | bake | dense. ` +
        `Ignored; this session resolves '${giActivation.mode}' from the world's ` +
        `lighting.indirect='${activeWorld.lighting.indirect}'.`,
    );
  }
  /**
   * True when the SESSION asked for a specific bake — `?giSpacing=`, `?giBounces=`, `?giValidity=`,
   * `?giMaxProbes=`, `?giIntensity=`. Under `?gi=probe` that request WINS over the file, because
   * a file baked at 1 m is not an answer to "bake this at 4 m and tell me what changes".
   *
   * ⚠️ It is a PRESENCE test, not a value comparison, and that is what keeps every G0–G4
   * measurement in `tests/e2e/gi.e2e.ts` reproducible: those cases all name their spacing and
   * bounces, so they keep baking in the tab exactly as they did, whether or not a baked file
   * happens to be sitting on this machine's disk. A run whose numbers silently changed because
   * someone had run `npm run bake:world` locally would be the worst kind of irreproducible.
   *
   * Lighting overrides (`?sun=`, `?hemi=`, `?sky=`, …) are deliberately NOT in this list: they are
   * caught by `checkProvenance`, which compares the rig the atlas was baked under against the one
   * this session resolved — a stricter test than presence, and one that also catches a world file
   * that changed since the bake.
   */
  let giBakeRequested = false;
  let giSpacingM = GI_DEFAULT_SPACING_M;
  // The WORLD's own ceiling when it authors one (`GiSchema.maxProbes`), else the engine fallback.
  // Authoring it in data is what makes the refusal answerable from the editor rather than only
  // from a CLI flag the artist hitting it never sees.
  let giMaxProbes = activeWorld.lighting.gi.maxProbes ?? GI_DEFAULT_MAX_PROBES;
  /**
   * The runtime gain on the volume's contribution.
   *
   * ⭐ IT IS SEEDED FROM THE WORLD (`lighting.gi.intensity`), which until BUG 1 was AUTHORED AND
   * READ BY NOTHING — a fourth dead field of the same family as the activation itself. `?giIntensity=`
   * (dev) still overrides it, in the knob block below.
   */
  let giIntensity = activeWorld.lighting.gi.intensity;
  let giBounces = 1;
  let giValidity = false;
  let giBakeLights = false;
  /**
   * `?giDilate=` — flood-fill passes for the buried-probe repair (spec 5/M4), or `null` to take
   * `probeDilate.DEFAULT_DILATION_PASSES`.
   *
   * ⚠️ NULL RATHER THAN 4, DELIBERATELY. The default lives in ONE place — the module that does the
   * work — and this is an override, not a copy of it. A second literal here is how a "default 4"
   * becomes a 4 in the URL parser and a 2 in the module with nothing to say which one an atlas was
   * made with.
   */
  let giDilatePasses: number | null = null;
  /**
   * ⭐ `?giDrainChunks=` — HOW MANY CHUNKS MAY BE OUTSTANDING IN ONE SUBMISSION WINDOW (O2).
   *
   * 1 = drain after every chunk (the default and the fix); n = every n chunks; **0 = never**,
   * which is the pre-O2 behaviour exactly and is the A/B's off arm.
   *
   * ⚠️ IT IS NOT A BAKE PARAMETER AND IT IS DELIBERATELY NOT IN `bakeQuality`. A preset may change
   * what a bake COSTS; it may not change what the file IS (L1) — and this changes only the cost.
   * Keeping it out of the quality table keeps it out of the header's `bake.qualityOverrides` and
   * therefore out of `checkProvenance`, which is the point: a scheduling knob in the provenance
   * fingerprint would refuse a perfectly good atlas because it was baked on a different schedule.
   * The fence that it changes nothing is byte-exactness, not a header field — see the drain block
   * in the bake bracket.
   *
   * It is also NOT in `giBakeRequested`, for the same reason `?giIntensity=` is not: it changes
   * no byte of the atlas, so a request for it must never beat a file on disk.
   */
  let giDrainChunks = 1;
  /**
   * ⭐ `?giFacingCache=` — CACHE THE FACING/VALIDITY CAPTURE ACROSS BOUNCE PASSES (lever 1).
   *
   * `1` (the default and the fix) measures the facing cube and the 1×1 validity quad on pass 0,
   * stores the two scalars, and skips both on every bounce pass. **`0` re-measures and re-stores on
   * EVERY pass**, which is the pre-lever behaviour plus one 1-row blit per chunk (+0.056 %), and is
   * the A/B's off arm.
   *
   * ⚠️ THE OFF ARM STILL STORES, AND THAT IS DELIBERATE. Making it read batch column 9 directly
   * would need a SECOND repack program chosen from a per-bake flag — `probeChunk.BATCH_COLUMNS`'s
   * header records what a two-program repack cost the retired `LightProbeGridNode` (a compile-time
   * branch three's cache key could not see; whichever compiled first won for the life of the page,
   * 48,880 draws on the wrong one). Both arms therefore run ONE program over ONE cache, and the A/B
   * differs in exactly the variable it tests: whether a bounce pass RE-MEASURES a value that cannot
   * have changed.
   *
   * ⚠️ NOT IN `bakeQuality`, NOT IN `checkProvenance`, AND IT MOVES NO FORMAT VERSION — the
   * `--drain-chunks` ruling verbatim: it changes what a bake COSTS, never what the file IS, and a
   * scheduling knob in the provenance fingerprint would refuse good atlases across a scheduling
   * change. ⚠️ It DOES change `sceneRenderEstimate`, which `--drain-chunks` did not, so the compile
   * event below prices the arm this bake will actually run rather than the shipping one.
   *
   * It is also NOT in `giBakeRequested`: it changes no byte of the atlas, so a request for it must
   * never beat a file on disk.
   */
  let giFacingCache = true;
  /** `?giSamples=` — the SH projection's sample count. See {@link GI_BAKE_SAMPLE_COUNT}. */
  let giSampleCount = GI_BAKE_SAMPLE_COUNT;
  /** `?giCubemap=` — the capture cube's face resolution. See `bakeQuality.cubemapSize`. */
  let giCubemapSize = GI_BAKE_CUBEMAP_SIZE;
  /**
   * ── ⭐ THE QUALITY PRESET (`?giQuality=`, owner direction #5) ─────────────────────────────────
   *
   * `giQualityName` + `giQualityRequest` are what the URL SAID; `giQuality` is what the preset
   * RESOLVED to, and the six `let`s above are assigned FROM it at the top of the bake bracket so
   * that there is exactly one path from a request to a setting.
   *
   * ⚠️ THE REQUEST IS SPARSE ON PURPOSE — a field is present only when its parameter was present.
   * `validity: false` and `dilationPasses: 0` are meaningful REQUESTS (the measurement's off arms),
   * so a resolver that tested truthiness would run the ON arm of the very comparison the flag
   * exists to make. That is the `?hemi=` landmine, and this family has already been bitten by it
   * twice (`?giIntensity=0` was written up as an impossible anomaly for a whole round).
   */
  let giQualityName: BakeQualityName = 'medium';
  const giQualityRequest: import('../render/gi/bakeQuality').BakeQualityRequest = {};
  let giQuality: ResolvedBakeQuality | null = null;
  /**
   * The resolved `spacingMultiplier`, applied in `giCompileSpacing()` — the ONE place a session
   * says what level-0 spacing it compiles at, and therefore the one place a preset may move it.
   */
  let giSpacingMultiplier = 1;
  /**
   * ── `?giSparseSpike=1` — THE HAND-BUILT SPARSE FIXTURE, ON SCREEN (spec §9 · S1) ─────────────
   *
   * DEV-ONLY, and it is the ONE thing that can kill this design being asked of a real GPU. Until
   * this lever existed, `sparseNode.ts` had emitted no WGSL in the life of the branch: the load
   * path is wired (`scene.ts` 2193 → `probeLoad.ts` → `publishAtlas`) but STARVED, because no
   * producer for `axon.probeVolume/2` exists yet, so the fetch always resolves `'absent'` and the
   * graph is never built. Feeding it hand-written bytes is what makes the fetch chain reachable
   * before the compiler, the bake and the file format are involved.
   *
   * It is not a fourth path into `hemi.intensity`: it publishes through the SAME `publishAtlas`
   * door, onto the SAME `SparseProbeVolume` class the load path uses, and sets `giCarriesIndirect`
   * exactly as the load path does — so `applyIndirect()` stays the only writer of the fill
   * (`src/render/indirect.ts` holds that law). The fixture is the pure module `sparseSpike.ts`,
   * shared byte for byte with the headless address test, so the browser and the arithmetic can
   * never be asserting against two different atlases.
   */
  let giSparseSpike = false;
  /**
   * Republish the spike fixture with one indirection texel repointed at `EMPTY_BRICK`, or `null`
   * to restore it. Non-null only while a spike volume is published. See `giSpikeRepoint`.
   */
  let giSpikeRepublish: ((emptyTexelX: number | null) => void) | null = null;
  /*
   * `?giCaps=` IS GONE, with `PROBE_CAPS` and `OFFLINE_PROBE_CAPS` (spec §7). Per-axis probe caps
   * were an artefact of the dense atlas's `7·(nz+2) ≤ MAX_3D_TEXTURE_SIZE` depth, and their real
   * effect was to make "a 1 m bake" 3.1 m tall on the y axis for four measurement rounds while
   * every self-report said 1 m. Nothing caps now; a request that does not fit is refused with the
   * numbers in the message. An old URL carrying `?giCaps=` is simply ignored.
   */
  /*
   * ⚠️ `giDisposeLoaded` IS GONE, AND IT WAS ALREADY DEAD (found while retiring the dense path).
   *
   * It held a LOADED volume's disposer, because `LightProbeGrid.dispose()` freed the bake path's
   * `RenderTarget3D` and nothing else — a loaded `Data3DTexture` needed its own owner. But it was
   * CALLED from exactly one place: inside `giTeardown`'s `if (probeGrid)` arm, and every load has
   * published a `SparseProbeVolume` — never a `probeGrid` — since G5. So it was assigned on four
   * paths and invoked on none. Nothing leaked, which is why nobody noticed: `probeLoad`'s handle
   * disposes by calling `light.dispose()`, which is exactly what the sparse teardown arm already
   * does, and `SparseProbeVolume.dispose()` frees both of its `Data3DTexture`s.
   */
  /** The last loaded file's header, so `readProbeAtlas` can re-emit it verbatim. */
  let giLoadedHeader: import('../render/gi/probeFile').ProbeVolumeHeader | null = null;
  /**
   * ── ⭐ `/5` · THE SUN'S DISTANT SHADOW TERM, AND THE LIGHT IT WAS BUILT OVER ───────────────────
   *
   * The opaque node `csmRig.setDistantShadowTerm` holds, remembered here WITH its light so a
   * republish onto the SAME `SparseProbeVolume` does not spend a full-scene rebuild reinstalling
   * the node it already has. That is the whole reason the pair is stored rather than just the node:
   * `bindProbeVolume`'s `sameShape` arm reuses the light and its two permanent `Texture3DNode`s, so
   * the already-built graph follows the new bytes through `.value` — there is nothing to rebuild,
   * and rebuilding anyway would put a two-frame unshadowed-sun flash on every re-bake pickup.
   *
   * ⚠️ ONE SETTER (`bindProbeVolume`) AND ONE CLEARER (`giTeardown`), which is what makes the
   * lifetime statable in a sentence: the term exists exactly while a FILE-loaded volume whose
   * header says `sunVisibility.baked` is bound. The DEV in-browser bake path publishes its own
   * `SparseProbeVolume` without going through `bindProbeVolume` and therefore installs no term —
   * deliberately: that path's atlas is never written to disk until the bake completes, and the
   * pickup (`scene.reloadBakedLighting` → `bindProbeVolume(…, 'rebind')`) is what installs it. A
   * bake in flight must not be the thing shadowing the world it is photographing.
   */
  let giSunTerm: { light: unknown; node: import('three/webgpu').Node<'float'> } | null = null;
  /**
   * …and its two payload sections, for the same reason. Under v1 the re-emit read the atlas back
   * off the GPU; under v2 the runtime textures are `Data3DTexture`s built from these arrays and a
   * readback would be a second, independent description of bytes we already hold. Keeping them is
   * ~0.4 MB at a true 1.2 m Sponza volume and it makes the round trip the IDENTITY rather than a
   * re-derivation — which is the property `giFile.e2e.ts` asserts bit for bit.
   */
  let giLoadedVolume: import('../render/gi/probeFile').DecodedProbeVolume | null = null;
  /**
   * ── WHAT THE LAST IN-BROWSER SPARSE BAKE WAS MADE FROM, latched at the end of the bake ────────
   *
   * `readProbeAtlas` emits the v2 header from THESE, never from `giInfo` plus arithmetic. The
   * distinction is the whole reason the header is built in the browser at all: `giInfo` is a
   * REPORT, and a header assembled from a report describes an atlas that was never made the moment
   * any field diverges. These three are the compiler's actual output and the settings actually in
   * force, so the header is a statement about the bytes rather than about the request.
   *
   * All null unless a sparse bake completed in this session; cleared by `setProbeGrid(false)`.
   */
  let giBakedStructure: import('../render/gi/probeCompile').CompiledStructure | null = null;
  /** The scattered brick atlas the last pass published — the payload's second section. */
  let giBakedAtlas: Uint16Array | null = null;
  /** Bake settings that are resolved rather than requested, and therefore only knowable here. */
  let giBakeSettings: {
    far: number;
    baseSpacingM: number;
    normalBiasM: number;
    /**
     * ⭐ THE BOX THE BAKE WAS TAKEN OVER — `giProvenanceBounds`, latched at the bake so the header
     * records what was PHOTOGRAPHED rather than what the graph happens to hold whenever
     * `readProbeAtlas` is eventually called. `checkProvenance`'s `geometryDrift` arm compares the
     * next boot's answer against exactly this.
     */
    drawnBounds: { center: [number, number, number]; size: [number, number, number]; walk: string };
  } | null = null;
  /**
   * What the last bounce pass's DILATION actually did — the only record that it ran at all.
   *
   * A dilated capture is indistinguishable from one that legitimately saw that light, so neither
   * the atlas nor the header can be re-derived into this. It is latched here, from the module that
   * did the work, and copied verbatim into `bake.dilationPasses`/`dilationFilled`.
   */
  let giDilation: import('../render/gi/probeDilate').DilationReport | null = null;
  /**
   * What the last bake's SUN-VISIBILITY fit and tap actually were (`axon.probeVolume/5`) — the
   * geometry half of `bake.sunVisibility`, latched here for the same reason `giDilation` is: it is
   * a fact about a bracket that has already closed by the time the header is written, and it is not
   * re-derivable from the atlas. The DATA half (`mean`/`variance`) IS re-derivable and is read off
   * the bytes at header time by `atlasSunVisibility`, so the two halves come from the two places
   * that can actually answer for them.
   *
   * `null` ⇒ no bake ran this session; the header then records `baked: false`, which is the honest
   * statement and the one the loader degrades to "the sun is unshadowed beyond the cascades" on.
   */
  let giSunVisFit: {
    baked: boolean;
    extentM: [number, number, number, number];
    fitted: boolean;
    authoredExtentM: number;
    mapSize: number;
    texelM: [number, number];
    radiusM: number;
    outsideCaptures: number;
  } | null = null;

  /*
   * ── THE THREE AUTHORING QUESTIONS, EACH WITH ONE ANSWER ────────────────────────────────────────
   *
   * The bake and the LOAD both have to agree on what this session would compile, or a file baked
   * five seconds ago is refused as stale by the provenance check on the next boot. So each of them
   * is a function, called from both sites, instead of an expression written out twice.
   */

  /**
   * The AUTHORED volume list, or empty. `enabled` is the gate (it defaults false, the same fence
   * `WorldSkySchema.enabled` carries), so a world that authors volumes but has not switched them
   * on gets today's coverage — and the provenance check compares the same empty list the header
   * records, rather than the volumes nothing compiled.
   */
  const giAuthoredVolumes = (): import('../world/schema').GiVolumeConfig[] =>
    authoredGiVolumes(activeWorld.lighting.gi);

  /**
   * ⚠️ `?giSpacing=` wins ONLY where there is nothing authored to override. A world with authored
   * volumes has a base spacing that its LEVELS are relative to — `spacing = baseSpacingM · 2^level`
   * — so changing it under the artist's feet would silently re-scale the whole ladder. Where
   * nothing is authored the single synthesised volume IS the authoring, and the session's request
   * is the only spacing there is.
   *
   * The AUTHORED level-0 base, or `null` where nothing is authored and the session supplies one.
   * Written once so the two functions below cannot drift apart on the arm that distinguishes them.
   */
  const giAuthoredBase = (): number | null =>
    giAuthoredVolumes().length > 0 ? activeWorld.lighting.gi.baseSpacingM : null;

  /**
   * ⭐ **THE SPACING A BOOT WITH NO URL PARAMETERS COMPILES AT — i.e. THE ATLAS'S IDENTITY.**
   *
   * Every dev override is neutralised here on purpose: no `?giSpacing=`, no quality multiplier.
   * `checkProvenance` compares `structure.baseSpacingM` against what the LOADING session compiles,
   * and the loading session that matters is the ordinary one — the owner double-clicking a URL
   * with nothing on it. A bake whose header disagrees with THIS number produces a file that the
   * world it belongs to can never load, which is exactly what happened: a `preview` bake wrote
   * 2.4 m over a world that declares 1.2 m, three surfaces advised a `?giQuality=preview` boot to
   * read it back, and no such boot exists (the multiplier is assigned in the bake bracket below,
   * which the load path never enters). `readProbeAtlas` answers the question with this function so
   * the baker can refuse to write before it overwrites a good atlas with an unloadable one.
   */
  const giPlainBootSpacing = (): number => giAuthoredBase() ?? GI_DEFAULT_SPACING_M;

  /**
   * The level-0 spacing THIS session compiles at — identity plus every dev override in force.
   *
   * ⭐ THE QUALITY PRESET'S `spacingMultiplier` IS APPLIED HERE AND ONLY HERE. No row in
   * `bakeQuality.ts` sets it to anything but 1 any more (see that file's header for what happened
   * when one did), so today this differs from `giPlainBootSpacing()` only under an explicit
   * `?giSpacing=` on a world that authors no volumes. That case still produces an atlas a plain
   * boot cannot load, and it is caught at the WRITE rather than argued about here: `readProbeAtlas`
   * reports `checkProvenance`'s verdict against `giPlainBootSpacing()` and the baker refuses.
   */
  const giCompileSpacing = (): number => (giAuthoredBase() ?? giSpacingM) * giSpacingMultiplier;

  /**
   * The resolved preset, flattened for `GiInfo`. A COPY of `overrides`, not the array itself: the
   * dev hook JSON-serializes this and a shared array is a handle into the scene's own state.
   *
   * Null until the bake bracket resolves one — which is a real distinction, not a placeholder: a
   * volume LOADED from a file reports the FILE's preset here, and a session that never ran a GI
   * path has no preset to report.
   */
  const giQualityInfo = (): { name: string; overrides: string[] } | null =>
    giQuality === null ? null : { name: giQuality.name, overrides: [...giQuality.overrides] };

  /**
   * The sample-time normal offset, metres. ABSOLUTE, not spacing-scaled: under a hierarchy the
   * local spacing at the vista level is 19.2 m and v1's `spacing · 0.5` would push the sample point
   * 9.6 m through the wall (`world/schema.ts`, `normalBiasM`).
   */
  const giNormalBiasM = (): number => {
    const authored = activeWorld.lighting.gi.normalBiasM;
    return authored ?? Math.min(0.5 * giCompileSpacing(), 0.25);
  };
  /**
   * ── ⭐ THE OCCUPANCY GRID, BUILT ONCE PER (WORLD × GEOMETRY × VOXEL) — S1 ──────────────────────
   *
   * Handed to `compileProbeVolumes` so it can drop bricks with no geometry near them. Two callers —
   * `giBakePlan()` (the panel's cost preview) and the bake bracket — and they MUST get the same
   * grid or the panel would price a bake the baker does not run.
   *
   * ⚠️ IT IS CACHED, AND THE CACHE KEY IS THE FENCE. `bakePanel.refresh()` runs N+1 compiles per
   * pointermove of a volume drag; a 262k-triangle walk per compile would make the drag unusable.
   * The key carries the world id, the voxel size, the drawn mesh COUNT and the drawn BOUNDS, so
   * adding, deleting or moving anything drawn rebuilds it. ⚠️ What it CANNOT see is a mesh edited
   * IN PLACE at an unchanged count and unchanged bounds — the same limit `worldHash` already
   * carries in the bake badge's own tooltip, and the reason `occupancyHash` is reported out of the
   * compiler rather than trusted here.
   *
   * `null` means "this world declares no adaptivity, or nothing is drawn" — never "the walk found
   * nothing", which is refused below, because an empty grid would delete every brick in the world.
   */
  let giOccupancyCache: {
    key: string;
    value: import('../render/gi/giOccupancy').SceneOccupancy | null;
  } | null = null;
  /**
   * An in-flight entity respawn the scene walks must wait for. See `settleEntities` — the plan and
   * the bake photograph the scene graph, and for a few frames of a respawn there is no scene.
   */
  let entitiesSettling: Promise<void> | null = null;
  /**
   * ⭐ ONE RESOLVER, FOUR SITES, THREE PROCESSES (B2-sparse). `resolveGiAdaptive` in
   * `world/schema.ts` is the answer to "is this world placed adaptively, and with what" — the
   * page's compile, the panel's plan, the header write and the baker's cross-check all call it.
   * DEFAULT ON: a world that says nothing is placed near geometry only. `null` here means the
   * world wrote `adaptive.enabled: false` — the explicit uniform arm — and nothing else.
   */
  const giAdaptive = (): {
    voxelM: number;
    surfaceGuardM: number;
    virtualOffset: boolean;
  } | null => {
    const r = resolveGiAdaptive(activeWorld.lighting.gi, giCompileSpacing());
    return r.enabled
      ? { voxelM: r.voxelM, surfaceGuardM: r.surfaceGuardM, virtualOffset: r.virtualOffset }
      : null;
  };
  /**
   * WHY the last `giOccupancyGrid()` call handed back no grid while the world asked for one, or
   * null. It is the sentence the panel prints in amber and the header records as
   * `placement.fallback` — the console line alone was the "adaptive but inert" arm the report's
   * docstring refuses, made reachable.
   */
  let giPlacementNote: string | null = null;
  const giOccupancyGrid = async (): Promise<
    import('../render/gi/occupancyGrid').OccupancyGrid | undefined
  > => {
    giPlacementNote = null;
    const cfg = giAdaptive();
    if (!cfg) return undefined;
    /*
     * ⚠️ A COMPILE-TIME BLOCK, not `if (!DEV) return`. Rollup folds the literal and deletes this
     * whole branch with both imports; an early return leaves the `await import` in the graph and a
     * production build downloads the voxeliser. `tests/renderGiFence.test.ts` scans for exactly
     * this shape and caught the early-return version of this function.
     */
    if (import.meta.env.DEV) {
      const { buildSceneOccupancy } = await import('../render/gi/giOccupancy');
      const { drawnBounds, forEachDrawnMesh } = await import('../render/gi/probeVolume');
      const { occupancyExtentForVolumes } = await import('../render/gi/probeCompile');
      let meshes = 0;
      forEachDrawnMesh(scene, () => {
        meshes += 1;
      });
      const db = drawnBounds(scene);
      /*
       * ⭐ THE GRID IS SIZED TO THE AUTHORED VOLUMES, NOT TO EVERY DRAWN TRIANGLE — and this is the
       * line that makes `mars-hangar-01` bakeable at all (317 M voxels of vista terrain holding no
       * probe, against a 64 M ceiling). `occupancyExtentForVolumes` owns the derivation and its
       * header owns the argument; `buildSceneOccupancy` intersects it with the drawn bounds.
       * `null` where nothing is authored — that world compiles a box synthesised from the drawn
       * bounds, so the drawn bounds are the honest extent and nothing is clipped.
       */
      const extentM = occupancyExtentForVolumes(giAuthoredVolumes(), giCompileSpacing(), {
        voxelM: cfg.voxelM,
        surfaceGuardM: cfg.surfaceGuardM,
        virtualOffset: cfg.virtualOffset,
      });
      /*
       * ⚠️ THE EXTENT IS IN THE CACHE KEY, or a volume drag would price a bake against the grid the
       * PREVIOUS volume produced. It is snapped to the voxel lattice by construction, so a drag of
       * less than one voxel is still a cache HIT — which is what keeps the panel's N+1 compiles per
       * pointermove off the 262k-triangle walk this key exists to avoid.
       */
      const key = [
        activeWorld.worldId,
        cfg.voxelM,
        meshes,
        db
          ? [db.min.toArray(), db.max.toArray()]
              .flat()
              .map((n) => n.toFixed(3))
              .join(',')
          : 'none',
        extentM ? [...extentM.minM, ...extentM.maxM].map((n) => n.toFixed(3)).join(',') : 'world',
      ].join('|');
      if (giOccupancyCache?.key !== key) {
        const built = buildSceneOccupancy(scene, cfg.voxelM, undefined, extentM);
        if (built) {
          console.info(
            `[gi] occupancy: ${built.meshes} meshes / ${built.triangles.toLocaleString()} triangles ` +
              `→ ${built.grid.dims.join('×')} voxels at ${cfg.voxelM} m — ` +
              `${built.grid.surfaceVoxels} surface, ${built.grid.sealedVoxels} sealed of ` +
              `${built.grid.totalVoxels} (hash ${built.grid.hash}) in ${built.ms} ms` +
              (built.clippedToVolumes
                ? ' — sized to the AUTHORED VOLUMES, so there is drawn geometry outside it.'
                : '.'),
          );
          if (built.unreadable > 0) {
            console.warn(
              `[gi] occupancy: ${built.unreadable} drawn object(s) could not be read as triangles. ` +
                'They are MISSING from the grid, so adaptive placement may delete bricks over them.',
            );
          }
        }
        giOccupancyCache = { key, value: built };
      }
      const built = giOccupancyCache.value;
      /*
       * ⚠️ A GRID WITH NO SURFACE IN IT IS REFUSED, NOT USED. Under the keep test it would drop every
       * brick in the world and write a completely empty atlas — which would load, pass provenance,
       * report success, and render the room black. That is this subsystem's whole failure genre and
       * it is one `if`. Falling back to the uniform grid is the right degrade: the artist gets the
       * bake they had yesterday plus a console line, never a silently emptied one.
       */
      if (!built || built.grid.surfaceVoxels === 0) {
        giPlacementNote =
          'nothing drawn was voxelised, so adaptive placement is SKIPPED for this compile and the ' +
          'uniform grid is used. Nothing is wrong with the atlas; it is the one you would have got ' +
          'anyway.';
        console.warn(`[gi] occupancy: ${giPlacementNote}`);
        return undefined;
      }
      if (built.unreadable > 0) {
        // Not a fallback — the grid IS used — but the panel must say the grid under-reports the
        // world, or a deleted brick over a BatchedMesh reads as a placement success.
        giPlacementNote =
          `${built.unreadable} drawn object(s) could not be read as triangles and are MISSING ` +
          'from the occupancy grid, so adaptive placement may delete bricks over them.';
      }
      return built.grid;
    }
    return undefined;
  };

  /**
   * Why the last attempt to LOAD a baked volume produced nothing, or null. Reset at the top of
   * every `setProbeGrid(true)` so a stale reason from a previous toggle can never be reported as
   * this one's.
   */
  let giStale: { reason: string; detail: string } | null = null;
  /**
   * Read `giStale` through a CALL, never as a bare reference at a site that has just cleared it.
   * TypeScript's control-flow analysis narrows a captured `let` to `null` after `giStale = null`
   * and cannot see that `loadBakedVolume` writes it — so a bare read there is typed `never` and,
   * worse, would tempt someone to "fix" it by deleting the reset. The reset is the fence (a stale
   * reason from a previous toggle must never be reported as this one's); this is the read.
   */
  const giStaleNow = (): { reason: string; detail: string } | null => giStale;
  /** The cube cameras' far plane used by the last in-browser bake — recorded in the file header. */
  let giLastBakeFarM = 100;
  /**
   * Lights marked bake-only — the per-frame leak guard's watch list.
   *
   * ⚠️ SEEDED FROM THE WORLD'S `static` LIGHTS, not empty. Before E2 this list was only ever
   * populated INSIDE the bake bracket, so the leak fence could not fire on a session that LOADED
   * an atlas rather than baking one — which is every shipped session. A world-authored static
   * light exists from boot, so it is watched from boot: if anything ever parents one while a
   * volume is bound (an editor mobility edit that forgot to rebuild, say), `bakedLightLeaks` counts
   * the frame. `?bakeLights=1` still appends to this list in the bracket below.
   */
  let bakeOnlyLights: THREE.Light[] = worldLightRig.bakeOnlyLights();
  let bakedLightLeakWarned = false;
  /**
   * O2: rAF frames the bake gate refused (see `render()`). It is the POSITIVE evidence that the
   * gate is reached — "no leak" and "the guard never ran" look identical from `bakedLightLeaks`
   * alone, and this subsystem's whole history is of fences that were silently inert. Printed once
   * per bake beside the cost report; a bake that reports 0 suppressed frames on a machine whose
   * rAF is running is a gate that is not wired up.
   */
  let giBakeSuppressedFrames = 0;
  /*
   * ── ⭐ THE ACTIVATION, MAPPED ONTO THIS FILE'S EXISTING VOCABULARY — OUTSIDE THE DEV BLOCK ────
   *
   * These two assignments used to live inside `if (import.meta.env.DEV)`, reading `?gi=` directly.
   * That is BUG 1 in one line: with no URL parameter nothing was ever asked for, and in a
   * production build the whole block folded away, so a shipped world had no path to its own atlas.
   *
   *   off   ⇒ nothing runs (`applyBakedLighting` returns immediately, `gi()` stays null)
   *   file  ⇒ `'file'` — the file or nothing, never bakes. The shipping shape.
   *   probe ⇒ `'auto'` — file first, in-browser bake if there is no usable one (DEV values only)
   *   bake  ⇒ `'bake'` — the authoring path, and what every G0–G4 measurement was taken
   *           through.
   *
   * `dense ⇒ 'dense'` was the fifth arm and is GONE (F3, spec S5): `?gi=dense` now takes
   * `resolveGiActivation`'s unknown-value arm, which warns once and falls through to world data.
   */
  giMode = giActivation.mode === 'file' ? 'file' : giActivation.mode === 'bake' ? 'bake' : 'auto';
  /*
   * ⚠️ THE DEFERRED TRIGGER IS NOW FOR THE BAKE MODES ONLY, and `'file'` is deliberately not one.
   * A bake photographs the world from 6·N cameras and MUST wait for content; a LOAD only needs
   * `drawnBounds()` to be truthful for the drift check, and mount fires it from
   * `host.onAllLoaded` — a real readiness signal instead of a triangle-count heuristic, so the
   * shipping path no longer pays ~3 s of constant-fill boot before the lighting arrives.
   */
  giAutoBake = giActivation.mode === 'probe' || giActivation.mode === 'bake';
  if (import.meta.env.DEV) {
    giBakeRequested = [
      'giSpacing',
      'giBounces',
      'giValidity',
      'giMaxProbes',
      'giIntensity',
      // A dilation request is a request for a DIFFERENT ATLAS, so it beats a file on disk for the
      // same reason a spacing request does: a file baked without the fill is not an answer to
      // "dilate this and tell me what changes". Same for the sample count.
      'giDilate',
      'giSamples',
      // A quality preset is a request for a DIFFERENT ATLAS for the same reason: a file baked at
      // `medium` is not an answer to "bake this at production and tell me what changes".
      'giQuality',
      'giCubemap',
    ].some((k) => params.get(k) !== null);
    /*
     * ── `?giQuality=preview|medium|production` — THE PRESET (owner direction #5) ────────────────
     *
     * REFUSED rather than defaulted on an unknown value, and the reason is the whole point of the
     * feature: `?giQuality=prod` silently baking `medium` would write a file whose header says
     * `medium`, which is TRUE and is not what anyone asked for — an artist would then compare two
     * "different quality" bakes that are the same bake. Warn once, never throw (hard rule 6).
     */
    const qualityRaw = params.get('giQuality');
    if (qualityRaw !== null && qualityRaw !== '') {
      if (qualityRaw === 'preview' || qualityRaw === 'medium' || qualityRaw === 'production') {
        giQualityName = qualityRaw;
      } else {
        console.warn(
          `[gi] ?giQuality=${qualityRaw} is not a preset — preview | medium | production. ` +
            `Ignored; this bake resolves '${giQualityName}'.`,
        );
      }
    }
    const raw = Number(params.get('giSpacing'));
    if (Number.isFinite(raw) && raw > 0) {
      giSpacingM = Math.max(0.25, Math.min(64, raw));
      /*
       * ⭐ AN EXPLICIT SPACING IS A SPACING, NOT A BASE TO SCALE. `--spacing 1.2 --quality preview`
       * must mean 1.2 m, not 2.4 — "an explicit flag overrides the preset" (§6.4), and the
       * multiplier is the preset field that this flag overrides. Recorded as an override in the
       * header, so a file never claims to be a plain `preview`.
       */
      giQualityRequest.spacingMultiplier = 1;
    }
    /*
     * `?giCubemap=` — the capture cube's face resolution, powers of two in [8, 256].
     *
     * ⚠️ IT HAS A DOCUMENTED CAVEAT AND THE PAGE SAYS SO OUT LOUD AT BAKE TIME (see
     * `bakeQuality.cubemapSize` and `sky.ts` trap 4): at 32 the 1.07° solar disc is a fifth of a
     * texel, which is why hiding it for the capture measured as no change. The bake always hides
     * it, so a larger cube is not a correctness risk — but no sun-related measurement taken at 32
     * transfers to 64, and this knob exists so the header can record which one an atlas was made
     * with rather than so the number can be changed casually.
     */
    const cubemapRaw = Number(params.get('giCubemap'));
    if (Number.isFinite(cubemapRaw) && cubemapRaw >= 8) {
      giQualityRequest.cubemapSize = Math.min(256, 2 ** Math.round(Math.log2(cubemapRaw)));
    }
    /*
     * The ceiling on an EXPLICIT `?giMaxProbes=` was 8,704, which silently coarsened any run that
     * asked for the density the DDGI authors call sufficient: 1 m spacing in Sponza's 22 × 34 m
     * atrium needs 11,730 probes. The DEFAULT budget is unchanged and is still the rail against a
     * pathological `?giSpacing=`; this is the ceiling on deliberately overriding it, and the cost
     * it guards is BAKE TIME, not memory — the atlas for 11,730 probes is 0.7 MB.
     */
    const cap = Number(params.get('giMaxProbes'));
    /*
     * ⚠️ THE EXPLICIT CEILING WAS 32,768 AND G5 RAISES IT TO 262,144 — the DEFAULT budget is
     * untouched. What that ceiling guards is BAKE TIME, and bake time stopped being a tab freeze
     * when it became a build step: `npm run bake:world` can afford minutes, a player's browser
     * cannot. Memory is not the constraint and never was — 262,144 probes is a 14 MB atlas.
     */
    if (Number.isFinite(cap) && cap >= 8) giMaxProbes = Math.min(262_144, Math.floor(cap));
    /*
     * `?giIntensity=` is the SANITY CONTROL, not a look knob. "The grid changes the picture by
     * 0.1 %" has two readings — the bounce really is that small, or the atlas is empty — and they
     * are indistinguishable at intensity 1. Cranking it separates them in one shot: a grid that
     * is alive gets obviously, wrongly bright; a grid that baked black stays black.
     *
     * ⚠️ **ZERO IS A LEGAL VALUE HERE AND THE GUARD USED TO REJECT IT** — `gain > 0`, the same
     * shape as every other member of this family, where it is correct (a 0 m spacing and an
     * 8-probe budget are nonsense; a 0× gain is the *control arm*). So `?giIntensity=0` silently
     * ran the grid at 1, and the frame it produced — 28.02 mean luminance against 28.01 for gain
     * 1 and 25.42 with the grid removed — was written up as G1's open anomaly: *"~90 % of the
     * grid's contribution is present at zero intensity, which should be impossible"*. It was
     * impossible. The reading was of gain 1, mislabelled.
     *
     * This is the `?hemi=` landmine (R1.6-F) with the sign flipped: there, `Number(null)` being
     * `0` made an ABSENT param look like a supplied 0; here, a guard written to survive that made
     * a SUPPLIED 0 look absent. So the presence test and the range test are separated — the
     * string decides whether the param was given, the number decides whether it is in range —
     * which is the shape `?hemi=` already uses and the one every future member of this family
     * should copy.
     */
    const gainRaw = params.get('giIntensity');
    if (gainRaw !== null && gainRaw !== '') {
      const gain = Number(gainRaw);
      if (Number.isFinite(gain) && gain >= 0) giIntensity = Math.max(0, Math.min(64, gain));
    }
    /*
     * ⛔ `?giVisibility=` WAS HERE (F2 → O3) and is REMOVED rather than left as an accepted no-op.
     * An unknown `?gi*` parameter is silently ignored by this block, which is the right degrade for
     * a stale bookmark; what would NOT be right is accepting the name and doing nothing, because
     * this is the one family of parameters whose whole job is to make a measurement reproducible.
     */
    /*
     * `?giBounces=` — extra bake passes after the direct one. Zero is legal and meaningful (direct
     * light only, no bounce at all), so this uses the presence/range split above, not `> 0`.
     * BUILD-time cost only: each pass is another `probes × 6` scene renders.
     */
    const bouncesRaw = params.get('giBounces');
    if (bouncesRaw !== null && bouncesRaw !== '') {
      const b = Number(bouncesRaw);
      if (Number.isFinite(b) && b >= 0) {
        giQualityRequest.bounces = Math.max(0, Math.min(8, Math.floor(b)));
      }
    }
    /*
     * `?bakeLights=1` — THE STATIC-LIGHT LEVER (G3.2). Marks every PUNCTUAL light in the scene
     * (point/spot — never the sun, never the hemisphere) bake-only: baked into the volume, then
     * removed from the scene graph before it can render. It stands in for the per-light `bakeOnly`
     * checkbox an artist would tick; the enforcement it drives is the product, the marker is not.
     */
    giBakeLights = params.get('bakeLights') === '1';
    /*
     * `?giValidity=` — the fork's per-probe validity term. PRESENCE decides whether this is a
     * request at all, the value decides which arm, and absence hands the answer to the preset
     * (`medium` says off, `production` says on). `?giValidity=0` is therefore a real off-arm
     * request that beats a `production` preset, which is what an A/B needs.
     */
    const validityRaw = params.get('giValidity');
    if (validityRaw !== null && validityRaw !== '') giQualityRequest.validity = validityRaw === '1';
    /*
     * `?giDilate=` — DILATION PASSES. Presence/range split, because **0 is the measurement's off
     * arm** and is the single most important value this parameter takes (the `?hemi=` landmine,
     * hard-won: a guard written `> 0` turns a supplied 0 into "absent" and runs the ON arm).
     *
     * ⭐ DILATION IS ON BY DEFAULT, and that is a quality ruling rather than a convenience.
     * It repairs a DEFECT IN THE CAPTURE — a probe that baked inside stone and therefore
     * photographed black — and every leak and quality number this design is judged against was
     * measured with it running. Left opt-in, it stayed off for the whole of `render/gi-sparse-volumes`
     * and the arcade column came out DARKER with the volume than without it.
     *
     * It is also **provably inert when there is nothing to repair**: `repackNode` writes
     * `mix(1, measured, validityMix)` and `validityMix` is 0 without `?giValidity=1`, so every
     * probe of a bake without validity reports validity 1, `invalid` is 0, and `dilateCaptures`
     * returns having changed no bytes. So this default cannot alter a non-validity bake at all —
     * it can only repair a bake that measured which of its probes are buried.
     */
    const dilateRaw = params.get('giDilate');
    if (dilateRaw !== null && dilateRaw !== '') {
      const d = Number(dilateRaw);
      if (Number.isFinite(d) && d >= 0) {
        giQualityRequest.dilationPasses = Math.max(0, Math.min(32, Math.floor(d)));
      }
    }
    /*
     * `?giSamples=` — the SH projection's sample count (see `GI_BAKE_SAMPLE_COUNT`). Clamped to
     * [16, 16384]: below 16 the nine coefficients are not determined at all, and the ceiling is a
     * rail against a typo that would loop a shader 10^9 times, not a considered budget.
     */
    /*
     * `?giDrainChunks=` — the submission window, in chunks (O2 · stage A). PRESENCE/RANGE split,
     * because **0 is the off arm** and is the single most important value this parameter takes:
     * it reproduces the pre-O2 single-window bake exactly, which is what the byte-exactness fence
     * and any re-measurement on a real GPU need. A `> 0` guard here would silently run the ON arm
     * of the comparison the flag exists to make — the `?hemi=` landmine, in its fourth costume.
     */
    const drainRaw = params.get('giDrainChunks');
    if (drainRaw !== null && drainRaw !== '') {
      const n = Number(drainRaw);
      if (Number.isFinite(n) && n >= 0) giDrainChunks = Math.max(0, Math.min(4096, Math.floor(n)));
    }
    /*
     * `?giFacingCache=` — lever 1's off arm. PRESENCE/VALUE split for the same `?hemi=` reason the
     * drain parameter is: **0 is the arm this flag exists for**, so a truthiness guard would
     * silently run the ON arm of the comparison. Anything other than `0`/`false` that is PRESENT is
     * the on arm, which is also what absence means.
     */
    const facingCacheRaw = params.get('giFacingCache');
    if (facingCacheRaw !== null && facingCacheRaw !== '') {
      giFacingCache = !(facingCacheRaw === '0' || facingCacheRaw === 'false');
    }
    const samplesRaw = Number(params.get('giSamples'));
    if (Number.isFinite(samplesRaw) && samplesRaw >= 16) {
      giQualityRequest.sampleCount = Math.min(16_384, Math.floor(samplesRaw));
    }
    /*
     * `?giSparseSpike=1` — publish the hand-built two-brick fixture instead of loading or baking.
     * It rides `giAutoBake`'s existing trigger rather than firing at boot, for a reason that is
     * NOT the bake's reason: nothing is photographed here, but a light added to the scene AFTER
     * the world's materials have compiled forces three to rebuild them WITH the GI term, and
     * doing that once, after content has settled, is what keeps `setup()`'s invocation count at
     * one for the page (spec §4.5). Firing at boot would compile the lit materials twice.
     */
    giSparseSpike = params.get('giSparseSpike') === '1';
    if (giSparseSpike) giAutoBake = true;
  }
  /**
   * ── ⭐ DEV-ONLY: VIEW MODES AND SHOW FLAGS (E1, `src/render/debugView/`) ───────────────────────
   *
   * The owner asked for "view modes and debug modes similar to how Unreal handled it". The system
   * answer is a TABLE — one named row per visualiser, each declaring what it costs and what it
   * needs to be meaningful — and `registry.ts` holds it, `view.ts` holds the mechanism, and this
   * is the only place either is reached from. `scene.ts` resolves and hands in; the EDITOR only
   * picks a row by name, through `setViewMode`/`setShowFlag`, and never imports the directory
   * (the `editor-*.js` chunk is still EMITTED in a production build, so an editor-side import
   * would carry the whole table into `dist/`).
   *
   * The gate is a COMPILE-TIME `import.meta.env.DEV` literal, so Rollup deletes the branch, the
   * chunk and the `fwg-debug-view` marker — the dev-hook bar (ABSENT), not the editor's
   * unreachable-not-absent one. `scripts/checkBundle.mjs` asserts all three legs.
   *
   * ⚠️ IMPORTED AT SCENE CONSTRUCTION, BUILT LAZILY PER MODE. The module is a stub until a mode
   * is entered — nothing compiles a debug pipeline until someone asks for one — but the IMPORT
   * happens up front so `debugView()` is a synchronous self-report the dev hook can publish every
   * frame without an await. The two setters await the import; the reporter does not.
   */
  let debugView: DebugViewHandle | null = null;
  let debugViewReady: Promise<unknown> = Promise.resolve(null);
  /** S5: mount's window onto the physics world's static-mesh colliders (`setCollisionOverlaySource`). */
  let collisionShapeSource: (() => readonly CollisionOverlayPlacement[]) | null = null;
  if (import.meta.env.DEV) {
    debugViewReady = import('../render/debugView/view')
      .then((m) => {
        debugView = m.createDebugView({
          renderer,
          scene,
          // `editorRoot` and not a new root: it already carries the matrix-policy opt-in, it is
          // already where editor-only objects live, and a second root would be a second answer.
          overlayParent: editorRoot,
          // Structural on purpose — see `DebugViewCtx.giVolume`. Widening this file's
          // `sparseVolume` to the real class would static-import the light into the main chunk.
          giVolume: () => sparseVolume,
          authoredVolumes: () => activeWorld.lighting.gi.volumes,
          // S5: read through the `let` so a source set AFTER the chunk lands still reaches it.
          collisionShapes: () => collisionShapeSource?.() ?? [],
        });
        return m;
      })
      .catch((err: unknown) => {
        // Hard rule 6: an unavailable diagnostic warns once and never breaks the frame.
        console.warn('[debugView] unavailable:', err);
        return null;
      });
  }

  /**
   * ── ⭐ SHIPS: TEARDOWN — hand the indirect term back to the constant fill ──────────────────────
   *
   * Lifted out of `setProbeGrid(false)` so that the shipping half of this subsystem and the DEV
   * A/B toggle share ONE implementation of "there is no volume any more". It is the only arm of
   * `setProbeGrid` that a production build can still reach, and it must stay reachable: it is what
   * `resolveIndirect` needs in order to restore `hemi.intensity` (`src/render/indirect.ts` is the
   * only writer, and this is what makes it write).
   */
  const giTeardown = (): void => {
    if (sparseVolume) {
      /*
       * `/5`: the sun's distant term names this volume's textures, so it goes FIRST — symmetrically
       * to `bindProbeVolume`'s install, and for the harder reason: a node left in
       * `sun.shadow.shadowNode` after `dispose()` would name a freed `GPUTexture` on the next
       * build, which is the measured `Destroyed texture … used in a submit` failure in a new place.
       */
      csmRig.setDistantShadowTerm(null);
      giSunTerm = null;
      scene.remove(sparseVolume);
      // Owns two `Data3DTexture`s (indirection + brick atlas); `dispose()` frees both.
      sparseVolume.dispose();
      sparseVolume = null;
      giSpikeRepublish = null;
    }
    giAutoBake = false;
    giInfo = null;
    // Nothing is baked any more, so nothing is a leak; the lights themselves stay removed
    // (re-baking is a fresh `setProbeGrid(true)`, and a light we deleted is not coming back).
    bakeOnlyLights = [];
    giLoadedHeader = null;
    giLoadedVolume = null;
    giBakedStructure = null;
    giBakedAtlas = null;
    giBakeSettings = null;
    giDilation = null;
    giStale = null;
    // The volume no longer carries the indirect term, so the constant fill comes back.
    giCarriesIndirect = false;
    giSkyBaked = false;
    applyIndirect();
    // …and the overlays drawn FROM the volume empty with it, rather than going stale on screen.
    debugView?.sync();
  };

  /**
   * ── ⭐ SHIPS: THE TIER-0 GATE (G0). THE VOLUME DOES NOT EXIST ON THE WebGL2 FALLBACK. ──────────
   *
   * The vendored bake guarded itself with `renderer.isWebGPURenderer !== true` — and that flag is
   * TRUE ON THE WebGL2 FALLBACK BACKEND TOO (it is a property of the renderer class, not of the
   * backend). So the guard we inherited read as a tier check and was not one, which is why this
   * one exists here rather than inside the baker.
   *
   * The owner's ruling (roadmap, R1.6 preamble) is that **we target WebGPU everywhere and the
   * fallback renders sun-and-shadow only**. That is a decision, so it is written down as one rather
   * than left to `isWebGPURenderer` to imply. Both readings of "tier 0" are refused: the real WebGL2
   * backend, and a deliberately forced `?tier=0`.
   *
   * ⚠️ A REFUSAL HERE LEAVES `gi()` NULL ON PURPOSE, and must not invent a sixth `bakeState`. It is
   * a fact about the SESSION, not about the world — reporting it as `stale` would tell an artist to
   * re-bake something that baked perfectly well.
   */
  const giTierRefused = (): boolean => {
    if (!(giFallbackBackend || tier === 0)) return false;
    console.warn(
      `[gi] probe volume refused: ${giFallbackBackend ? 'WebGL2 (fallback)' : 'WebGPU'}, tier ${tier}. ` +
        `We target WebGPU everywhere; the fallback renders sun-and-shadow only ` +
        `(roadmap R1.6). Nothing loaded, nothing baked. ` +
        // `/5`: the baked sun-visibility term rides the SAME bind, so it goes with the volume.
        // Without this sentence the one observable is a line about GI, and the symptom the artist
        // reports is about the SUN — flat, unshadowed geometry past the last cascade, which on
        // this tier starts at 40 m rather than 90.
        `That also means NO baked distant sun shadow (state().shadows.csm.distantTerm stays ` +
        `'none'): past the last cascade the sun is unshadowed here, as it was before /5.`,
    );
    giAutoBake = false;
    return true;
  };

  /**
   * ── ⭐ SHIPS: THE LOAD PATH — fetch, check provenance, bind. NEVER BAKES. ──────────────────────
   *
   * Extracted from `setProbeGrid`'s bake bracket so that BUG 1's fix (a world's own
   * `lighting.indirect` loading its atlas, in dev AND in a production build) does not drag the
   * vendored baker into a shipping chunk. `probeGrid.ts` — the cube capture, the projections and
   * the capture-atlas repack — is bake-only; imported at the top of a bracket shared with the
   * loader it would land in whatever chunk the loader lands in on EVERY production build. That is
   * not tidiness; it is the difference between shipping a loader and shipping a baker, and
   * `scripts/checkBundle.mjs` asserts both halves.
   *
   * Returns TRUE when a volume was bound. Every other outcome — absent, malformed, truncated,
   * refused by any provenance field, or a device 3D-texture ceiling — returns false and leaves the
   * constant fill carrying the indirect term. **Nothing here throws** (D25 + hard rule 6); the one
   * thing that can (`assertFits3D`) is caught by the caller's bracket and reported as `failed`.
   */
  /**
   * ── WHAT THIS SCENE GRAPH ACTUALLY DRAWS, UNIONED WITH THE WORLD'S COLLISION BOXES ────────────
   *
   * The volume comes from the SCENE GRAPH, not from `activeWorld.boxes` (R2c): the boxes are empty
   * for any world whose geometry is a glTF mesh — `sponza-debug-01` carries `"boxes": []` — and the
   * grid once collapsed to a 4 m cube at the origin, lighting nothing, with no error anywhere. The
   * boxes are still UNIONED in so a world whose collision volume is bigger than its drawn geometry
   * is not clipped away.
   *
   * ⚠️ IT IS ONLY TRUTHFUL AFTER ASYNC CONTENT HAS ARRIVED, which is why the shipping trigger is
   * `host.onAllLoaded` (a real readiness signal) and not `host.onAllSpawned`. `staticMesh.create()`
   * returns synchronously and adds its GLB inside a `.then()`, so on Sponza a bounds read at spawn
   * time is a near-empty box — and `checkProvenance`'s `geometryDrift` arm would then refuse a
   * perfectly good atlas on EVERY boot, with a plausible, self-consistent, completely wrong verdict
   * reported to the artist as `stale`.
   */
  /**
   * A drawn span larger than this is a bug report, not a level. Chosen well above anything we
   * author — Mars, the largest world here, spans ~1.1 km on its longest axis through this walk —
   * and well below the two wrong answers this has actually produced (1.9 km and 3.8 km, both on an
   * 18 m building). It gates a console line and nothing else.
   */
  const GI_IMPLAUSIBLE_SPAN_M = 2000;
  const giDrawnBox = async (): Promise<{
    sizeM: [number, number, number];
    centerM: [number, number, number];
  }> => {
    const { drawnBounds } = await import('../render/gi/probeVolume');
    /*
     * ⚠️ COMPOSE THE WORLD MATRICES FIRST, AND THIS IS NOT BELT-AND-BRACES — IT IS THE MEASUREMENT'S
     * PRECONDITION, AND OMITTING IT REFUSED SPONZA'S ATLAS ON EVERY BOOT.
     *
     * `drawnBounds` transforms each geometry's LOCAL box by `o.matrixWorld`. The load fires the
     * instant `staticMesh`'s GLB lands, which is inside a `.then()` and therefore BEFORE the next
     * render — and `matrixWorld` is only composed during a render (`entityRoot`'s matrix policy
     * turns `matrixAutoUpdate` off, so nothing composes it on `add`). Every one of Sponza's 103
     * primitives was therefore measured with an unwritten parent chain, which read as a
     * **1882 x 1279 x 861 m** drawn box against a 76.8 m grid and refused a perfectly good atlas as
     * `geometryDrift` — a plausible, self-consistent, completely wrong verdict, reported to the
     * artist as "re-bake". MEASURED, 2026-09-01, and it is exactly the class of failure the trigger
     * work in `host.onAllLoaded` exists to avoid; readiness was necessary and not sufficient.
     *
     * `force = true` because the policy's objects have `matrixAutoUpdate === false`: their local
     * `matrix` is already correct (the `childadded` hook writes it) and only the composition is
     * missing, which is precisely what the forced walk does.
     */
    scene.updateMatrixWorld(true);
    const db = drawnBounds(scene) ?? new THREE.Box3();
    const dbv = new THREE.Vector3();
    for (const b of activeWorld.boxes) {
      db.expandByPoint(
        dbv.set(b.pos[0] - b.size[0] / 2, b.pos[1] - b.size[1] / 2, b.pos[2] - b.size[2] / 2),
      );
      db.expandByPoint(
        dbv.set(b.pos[0] + b.size[0] / 2, b.pos[1] + b.size[1] / 2, b.pos[2] + b.size[2] / 2),
      );
    }
    if (db.isEmpty()) db.expandByPoint(dbv.set(0, 0, 0));
    const c = db.getCenter(new THREE.Vector3());
    const z = db.getSize(new THREE.Vector3());
    /*
     * ── ⭐ A UNION NAMES NO CULPRIT, AND THAT IS HOW THIS WENT WRONG TWICE ───────────────────────
     *
     * This box has been catastrophically wrong before and said nothing about it. Measured on
     * `sponza-debug-01` — an EIGHTEEN METRE building — it has read **1882 x 1279 x 861 m** (the
     * uncomposed-`matrixWorld` bug the `updateMatrixWorld(true)` above exists to fix) and, more
     * recently, **938.5 x 2551.1 x 3764.7 m**, which refused that world's atlas for `geometryDrift`
     * on every boot. The owner was on the constant fill in Sponza for an unknown length of time and
     * only found out because a DIFFERENT world's refusal was investigated.
     *
     * Both times the box was the only observable, and a box cannot say which object made it. So the
     * largest contributors are printed beside it, in DEV, whenever the result is implausibly large.
     * The threshold is a smoke alarm and nothing hangs on its exact value: it is not a refusal, it
     * changes no verdict, and `checkProvenance` remains the one authority on staleness.
     */
    if (import.meta.env.DEV) {
      const span = Math.max(z.x, z.y, z.z);
      if (span > GI_IMPLAUSIBLE_SPAN_M) {
        const { drawnBoundsContributors } = await import('../render/gi/probeVolume');
        const top = drawnBoundsContributors(scene, 5)
          .map(
            (o) =>
              `${o.name} [${o.type}] world r=${o.radiusM.toFixed(1)} m at ` +
              `[${o.centre.map((n) => n.toFixed(1)).join(', ')}] · local span ` +
              // NOT metres — the geometry's own units. See `localSpan`'s note; the `m` this used
              // to carry is what made Sponza's honest 68.23 asset-units read as corrupt data.
              `${Number.isFinite(o.localSpan) ? `${o.localSpan.toFixed(2)} (asset units)` : 'n/a'}` +
              `${o.path === '' ? '' : ` · under ${o.path}`}`,
          )
          .join('\n           ');
        console.warn(
          `[gi] ⚠️ drawn bounds span ${span.toFixed(1)} m — implausible for a level. ` +
            `This is what refuses a good atlas as 'geometryDrift'.\n` +
            `           box ${[z.x, z.y, z.z].map((n) => n.toFixed(1)).join(' x ')} m at ` +
            `[${[c.x, c.y, c.z].map((n) => n.toFixed(1)).join(', ')}]\n` +
            `           largest contributors:\n           ${top}`,
        );
      }
    }
    return { sizeM: [z.x, z.y, z.z], centerM: [c.x, c.y, c.z] };
  };

  /**
   * ── ⭐ WOULD A BOOT WITH NO URL PARAMETERS ACCEPT THIS HEADER? ─────────────────────────
   *
   * The bake side of the question `loadBakedVolume` asks on the way in — same function, same
   * fields, one difference: `baseSpacingM` comes from `giPlainBootSpacing()` rather than
   * `giCompileSpacing()`, so the answer is about the ORDINARY boot rather than about the
   * override-laden one that happens to be running the bake.
   *
   * ⚠️ WHY THIS EXISTS AT ALL. `checkProvenance` is a fence at LOAD, hours after the bake, in a
   * different process, and by then the damage is already done: the write has replaced the last
   * atlas that DID load. The owner's Sponza is the proof — a `preview` bake wrote 2.4 m over a
   * world declaring 1.2 m, every boot refused it as `structure`, and the atlas it clobbered is
   * gone. So the same question is asked one step earlier, where the answer can still prevent the
   * write. It is deliberately NOT a second implementation of the check: a re-derived "is this
   * loadable" predicate here would be a second answer to `checkProvenance`'s one question, which
   * is this subsystem's documented failure shape.
   *
   * `bounds` is the live drawn box, exactly as the load path passes it, so a bake whose grid does
   * not contain the geometry it photographed is refused HERE instead of at the artist's next boot.
   */
  const giPlainBootVerdict = async (
    header: import('../render/gi/probeFile').ProbeVolumeHeader,
  ): Promise<import('../render/gi/probeFile').ProvenanceVerdict> => {
    const { checkProvenance } = await import('../render/gi/probeFile');
    const drawn = await giDrawnBox();
    return checkProvenance(header, {
      ...(await giLiveProvenance(drawn)),
      // ⭐ THE ONE FIELD THAT DIFFERS FROM THE LOAD PATH, AND THE WHOLE POINT OF THE FUNCTION.
      // Everything else — including the floor-clamped bounds — must be the LOAD path's, or this
      // fence would be answering a slightly different question from the one it claims to answer.
      baseSpacingM: giPlainBootSpacing(),
    });
  };

  /**
   * ── ⭐ THE ONE DEFINITION OF "THE BOX THIS LEVEL DRAWS", FOR BOTH SIDES OF `geometryDrift` ─────
   *
   * The drawn box as `checkProvenance` must judge it: everything below `GI_FLOOR_Y` belongs to the
   * region a probe volume is deliberately not built over, so it is not evidence of drift. Pure
   * arithmetic on a centre/size pair — the box itself is measured once, by `giDrawnBox`.
   *
   * ⚠️ **IT IS ALSO WHAT THE BAKE RECORDS INTO THE HEADER** (`bake.drawnBounds`, latched into
   * `giBakeSettings`). That is the whole point of routing both through here: the arm compares a
   * BAKED box against a LIVE box, and the instant those two are produced by two pieces of
   * arithmetic they will disagree about the floor clamp, or the boxes union, or the 120 m skip —
   * and the disagreement renders as a staleness verdict about the artist's mesh. One function, one
   * clamp, one `walk` word.
   */
  const giProvenanceBounds = async (drawn: {
    sizeM: [number, number, number];
    centerM: [number, number, number];
  }): Promise<{
    center: [number, number, number];
    size: [number, number, number];
    walk: string;
  }> => {
    const { DRAWN_BOUNDS_WALK_SCENE_GRAPH } = await import('../render/gi/probeFile');
    const minY = drawn.centerM[1] - drawn.sizeM[1] / 2;
    const maxY = drawn.centerM[1] + drawn.sizeM[1] / 2;
    // A box entirely under the floor would invert; keep it degenerate-but-valid at the floor.
    const lo = Math.min(Math.max(minY, GI_FLOOR_Y), maxY);
    return {
      center: [drawn.centerM[0], (lo + maxY) / 2, drawn.centerM[2]],
      size: [drawn.sizeM[0], maxY - lo, drawn.sizeM[2]],
      /*
       * `giDrawnBox` is `probeVolume.drawnBounds` — `forEachDrawnMesh` over the LIVE graph, with the
       * `BOUNDS_SKIP_RADIUS_M` skip — unioned with `world.boxes`, and clamped here. That IS the
       * scene-graph walk, and naming it is what stops a triangle-soup box from ever being held
       * against it.
       */
      walk: DRAWN_BOUNDS_WALK_SCENE_GRAPH,
    };
  };

  /**
   * ── ⭐ WHAT THIS SESSION WOULD COMPILE, AS ONE OBJECT, FOR THE TWO PLACES THAT ASK ─────────────
   *
   * `checkProvenance`'s live half. It is a function rather than an inline literal because there
   * are now two callers — the boot load and the post-bake rebind — and a file is stale if and only
   * if THIS SESSION would compile something different. Two literals would be two definitions of
   * "this session", which is the second-answer-to-one-question shape this subsystem is made of.
   */
  const giLiveProvenance = async (drawn: {
    sizeM: [number, number, number];
    centerM: [number, number, number];
  }): Promise<Parameters<typeof import('../render/gi/probeFile').checkProvenance>[1]> => {
    const { worldHash } = await import('../render/gi/probeFile');
    return {
      worldId: activeWorld.worldId,
      worldHash: worldHash(activeWorld),
      lighting: liveLighting(),
      // ⭐ THE SAME THREE FUNCTIONS THE BAKE COMPILES FROM. A file is stale if and only
      // if this session would compile something different, and "what this session would
      // compile" must therefore have exactly one definition.
      volumes: giAuthoredVolumes(),
      originM: activeWorld.lighting.gi.origin,
      baseSpacingM: giCompileSpacing(),
      /*
       * ⭐ THE RAW DRAWN BOUNDS, NOT THE PADDED SYNTHESISED VOLUME. `live.bounds` is
       * documented as *"the world bounds the CURRENT scene graph draws"*, and this used to
       * pass `probeVolume()`'s output — the drawn box plus a 2 m pad, floor-clamped — which
       * made the one-sided drift tolerance disagree with its own specification. Passing the
       * drawn box is both what the check says it compares and one bake-only module fewer on
       * the shipping load path.
       */
      /*
       * ⭐ CLAMPED AT `GI_FLOOR_Y`, WHICH IS THE SAME NUMBER THE VOLUME WAS BUILT WITH. See that
       * constant for the measurement: without this the `geometryDrift` arm refuses an atlas this
       * build wrote minutes earlier, because the volume was told to stop at the floor and the check
       * was not told that. The clamp is applied HERE, once, rather than inside `giDrawnBox` —
       * that box is also what the BAKE measures, and `probeVolume` does its own clamping from the
       * raw box. One reader, one clamp.
       */
      bounds: await giProvenanceBounds(drawn),
    };
  };

  /**
   * ── ⭐ ONE BIND, TWO CALLERS: THE BOOT LOAD, AND THE POST-BAKE REBIND (L2) ───────────────
   *
   * Everything that happens AFTER `checkProvenance` has said yes — building or re-pointing the
   * light, taking ownership of the indirect term, and publishing the report. It was inline in
   * `loadBakedVolume` until `reloadBakedLighting` needed the same eleven steps a second time. Two
   * copies would be two answers to "what is bound", and the one that drifted would be the one the
   * artist is looking at.
   *
   * ⭐ REPUBLISH BEATS RECONSTRUCT, AND `probeLoad.ts`'s HEADER NAMED THIS CASE BEFORE IT EXISTED:
   * *"a structure that can be REPUBLISHED — a rebake in the same page session, a load after a
   * bake"*. §4.5's two PERMANENT `Texture3DNode`s exist precisely so new bytes can reach an
   * ALREADY-COMPILED shader. Constructing a second light instead would also work, and would cost
   * the whole frame: `LightsNode.customCacheKey()` hashes every light's `id` into every render
   * object's dynamic key, so swapping the light REGENERATES EVERY LIT MATERIAL'S WGSL (the same
   * mechanism the view-mode rules measure at 22 → 32 pipelines on Sponza).
   *
   * ⚠️ A SHAPE CHANGE TAKES THE CONSTRUCT ARM, and that is a fence rather than a path.
   * `publishAtlas` reuses its `Data3DTexture` only when the dims match; its realloc arm is
   * documented there as safe for a light that has never been rendered, and is the exact shape of
   * the measured `Destroyed texture … used in a submit` bug on one that has. A re-bake of the
   * SAME world cannot change the shape — `checkProvenance` has already refused any file whose
   * volumes, spacing or origin moved, and `compileProbeVolumes` is deterministic — so reaching
   * this arm means something upstream is wrong, and it degrades to the boot path rather than to a
   * GPU validation error every frame for the life of the page.
   */
  const bindProbeVolume = async (
    volume: import('../render/gi/probeFile').DecodedProbeVolume,
    origin: 'load' | 'rebind',
  ): Promise<'republish' | 'construct'> => {
    const { payloadSections } = await import('../render/gi/probeFile');
    const h = volume.header;
    /*
     * ── ⭐ `/5` · DOES THIS FILE CARRY A SUN-VISIBILITY TERM, AND IS IT WORTH INSTALLING? ────────
     *
     * `bake.sunVisibility.baked` is the ONE predicate, read off the HEADER and never off the
     * session — the same discipline `quality` and `placement` are read under. `false` means the
     * bake had no shadow map to tap (no `castShadow`, no receiver, VSM, shadows disabled), and
     * band 7 is then a constant 1.0 in every probe: installing the term would be correct and
     * would cost 1 `textureLoad` + 1 `textureSample` per sun-shadowed fragment to multiply by one.
     * So the arm is refused at bind time, `state().shadows.csm.distantTerm` says `'none'`, and the
     * emitted WGSL is byte-identical to the build before this feature existed.
     *
     * The factory is imported HERE, before either arm, so the install itself is synchronous and no
     * `await` can land between `sparseVolume = loaded.light` and `scene.add(loaded.light)`. Same
     * reason `SparseProbeVolumeNode` is imported here rather than inside `probeLoad`: the module
     * that decides WHEN the TSL graph is parsed should be the one that owns the volume's life.
     */
    const sunTermFactory =
      h.bake.sunVisibility.baked === true
        ? (await import('../render/gi/sunVisibilityNode')).sunVisibilityTerm
        : null;
    /**
     * Install (or remove) the distant term for whatever `sparseVolume` currently is. Called from
     * BOTH arms below, so "which node is in the sun's slot" has one derivation rather than two.
     *
     * ⚠️ ORDER IS LOAD-BEARING IN THE CONSTRUCT ARM: this runs BEFORE `scene.add`, because adding
     * the volume puts a new `light.id` into `LightsNode.customCacheKey()`'s hash and already forces
     * a full-scene rebuild — landing csmRig's own two-frame `castShadow` swap inside that same
     * rebuild wave instead of behind it. `csmRig.setDistantShadowTerm` explains the mechanism.
     */
    const applySunTerm = (): void => {
      if (sunTermFactory === null || sparseVolume === null) {
        giSunTerm = null;
        csmRig.setDistantShadowTerm(null);
        return;
      }
      if (giSunTerm === null || giSunTerm.light !== sparseVolume) {
        giSunTerm = {
          light: sparseVolume,
          /*
           * This file is EAGER and may not NAME the light class at runtime, so `sparseVolume` is
           * held STRUCTURALLY (see its declaration); the lazy module's function wants the class.
           * The inline `import(...)` sits in a TYPE position and is erased at build — the same
           * door `giLoadedHeader` uses for `ProbeVolumeHeader` — so this narrows at the seam and
           * adds no import edge. It is a DOWNCAST of the one real object, not a reinterpretation.
           */
          node: sunTermFactory(
            sparseVolume as import('../render/gi/sparseVolume').SparseProbeVolume,
          ),
        };
      }
      // Identical node ⇒ `setDistantShadowTerm` is a no-op and no rebuild is spent. That is what
      // makes a re-bake pickup onto the same light free of an unshadowed-sun flash.
      csmRig.setDistantShadowTerm(giSunTerm.node);
    };
    const prev = giLoadedHeader;
    const sameShape =
      prev !== null &&
      prev.structure.brickGrid.every((n, i) => n === h.structure.brickGrid[i]) &&
      prev.structure.indirDims.every((n, i) => n === h.structure.indirDims[i]);
    let how: 'republish' | 'construct';
    if (sparseVolume !== null && sameShape) {
      sparseVolume.republish(volume.atlas, volume.indirection, {
        indirOriginM: h.structure.indirOriginM,
        indirDims: h.structure.indirDims,
        brickGrid: h.structure.brickGrid,
        baseSpacingM: h.structure.baseSpacingM,
      });
      /*
       * OUT OF THE HEADER, NOT OUT OF A LIVE DEFAULT — the offset in force when the captures
       * were placed is the offset they are correct for. Identical argument to
       * `makeLoadedProbeVolume`'s, restated here because this arm does not go through it.
       * `intensity` and `visibility` are deliberately NOT re-applied: they are SESSION levers
       * the artist may have moved, and the atlas has nothing to say about them.
       */
      sparseVolume.setNormalBias(h.structure.normalBiasM);
      applySunTerm();
      how = 'republish';
    } else {
      /*
       * The node class is imported HERE and handed in, rather than imported by
       * `probeLoad.ts`: `sparseNode.ts` is the whole TSL graph, and the module that
       * REGISTERS a light class with the renderer should not be the module that decides
       * when the graph is parsed.
       */
      const { SparseProbeVolumeNode } = await import('../render/gi/sparseNode');
      const { makeLoadedProbeVolume } = await import('../render/gi/probeLoad');
      if (sparseVolume !== null) {
        // The old light leaves the graph BEFORE the new one enters it, so no frame can name a
        // `GPUTexture` that is about to be freed. Same ordering as `giTeardown`'s.
        scene.remove(sparseVolume);
        sparseVolume.dispose();
        sparseVolume = null;
      }
      const loaded = makeLoadedProbeVolume(volume, renderer, SparseProbeVolumeNode);
      loaded.light.setIntensity(giIntensity);
      sparseVolume = loaded.light;
      // BEFORE `scene.add` — see `applySunTerm`. The old light (if any) has already left the graph.
      applySunTerm();
      /*
       * NO `visible = false` DANCE HERE, and under v2 it is not merely satisfied by
       * construction — it is UNREACHABLE. `SparseProbeVolumeNode.setup()` has no
       * `texture === null` branch to cache, because both `Data3DTexture`s are bound from
       * the light's constructor over zero dummies and `publishAtlas` only ever reassigns
       * their `.value`. The dense bake path still needs its invisible interval, and
       * `render()`'s `seamBreaks` counter still watches it.
       */
      scene.add(loaded.light);
      how = 'construct';
    }
    giLoadedHeader = h;
    giLoadedVolume = volume;
    /*
     * OWNERSHIP OF THE INDIRECT TERM, AND `skyBaked` READ OUT OF THE HEADER RATHER THAN
     * OFF THE SCENE. It is a property of the ATLAS, latched when the bake ran — the same
     * reason the bake path latches it one turn before `bake()`. A sky added after the
     * bake is not in the atlas; one removed after it still is. `checkProvenance` has
     * already refused any file whose sky disagrees with this session's, so these two
     * always agree by the time we get here — reading the header keeps the RULE stated in
     * one direction instead of relying on that.
     */
    giCarriesIndirect = true;
    giSkyBaked = h.lighting.skyBaked;
    applyIndirect();
    // The `brickBounds` overlay is drawn from the BOUND indirection texture, so it is only
    // meaningful once one is bound. Dev-only and null in production (see `debugView` above).
    debugView?.sync();
    const sections = payloadSections(h.structure);
    /*
     * The volume's box is DERIVED from the indirection grid, not stored: one texel is
     * one FINEST brick, `BRICK_N · baseSpacingM` metres. Two stored definitions of this
     * box would be the "locally sensible, wrong overall" shape rule 0 names, and
     * `checkProvenance` derives it exactly this way for the drift check.
     */
    const gridCell = 4 * h.structure.baseSpacingM; // BRICK_N, folded — see probeBrick.ts
    const gridSize = h.structure.indirDims.map((n) => n * gridCell) as [number, number, number];
    const gridCentre = h.structure.indirOriginM.map((v, i) => v + gridSize[i]! / 2) as [
      number,
      number,
      number,
    ];
    giInfo = {
      state: 'baked',
      on: true,
      /*
       * ⚠️ `resolution` AND `probes` NO LONGER DESCRIBE A LATTICE, because there is not
       * one — the field is a sparse brick set at up to five densities. They carry the
       * INDIRECTION grid and the CAPTURE count instead, which are the two numbers that
       * scale the same way (memory, bake cost) and the two an artist can act on. The
       * per-level truth is in `structure.bricksByLevel`, and that is where any question
       * about density has to be asked from here on.
       */
      resolution: h.structure.indirDims,
      probes: h.structure.captureCount,
      spacingM: [h.structure.baseSpacingM, h.structure.baseSpacingM, h.structure.baseSpacingM],
      spacingRequestedM: h.structure.baseSpacingM,
      structure: {
        brickCount: h.structure.brickCount,
        captureCount: h.structure.captureCount,
        bricksByLevel: h.structure.bricksByLevel,
        transitionBricks: h.structure.transitionBricks,
        indirDims: h.structure.indirDims,
        brickGrid: h.structure.brickGrid,
        indirectionBytes: sections.indirectionBytes,
        atlasBytes: sections.atlasBytes,
        paddingRatio: h.structure.paddingRatio,
        normalBiasM: h.structure.normalBiasM,
      },
      intensity: giIntensity,
      sizeM: gridSize,
      centerM: gridCentre,
      bakeMs: 0,
      bakeMsPerProbe: 0,
      bounces: h.bake.bounces,
      validity: h.bake.validity,
      /*
       * ⭐ READ OFF THE HEADER, NEVER OFF THIS SESSION'S `?giQuality=`. The file
       * records what it was baked FROM; the URL records what this boot asked
       * for, and a quality mismatch is REPORTED rather than refused (a preview
       * atlas is a valid atlas of the same world). Reporting the request here
       * would make the two indistinguishable, which is the whole failure this
       * header exists to prevent. Null for a file baked before presets existed —
       * a different fact from `medium`, and kept different.
       */
      quality:
        h.bake.quality === undefined
          ? null
          : {
              name: h.bake.quality,
              overrides: h.bake.qualityOverrides ?? [],
            },
      cubemapSize: h.bake.cubemapSize,
      sampleCount: h.bake.sampleCount,
      // Off the HEADER, like `quality`: what the file's bricks were placed with, never what this
      // session would place. Null for a file written before placement was recorded.
      placement:
        h.bake.placement === undefined
          ? null
          : { adaptive: h.bake.placement.adaptive, capturesUniform: h.bake.placement.capturesUniform },
      seamBreaks: 0,
      bakedLights: h.bake.bakedLights,
      bakedLightLeaks: 0,
      source: 'file',
      stale: null,
      // Off the HEADER, like `quality` and `placement`. Absent in the file stays `null` here.
      producer: h.producer ?? null,
      fileBytes: h.payload.bytes,
      dilation: {
        passes: h.bake.dilationPasses ?? 0,
        filled: h.bake.dilationFilled ?? 0,
      },
    };
    console.info(
      `[gi] sparse volume ${origin === 'rebind' ? 'REBOUND' : 'LOADED'} from ` +
        `worlds/${activeWorld.worldId}/gi — ` +
        `${h.structure.brickCount} bricks (by level ${h.structure.bricksByLevel.join('/')}, ` +
        `${h.structure.transitionBricks} transition) over ` +
        `${h.structure.indirDims.join('x')} indirection cells, ` +
        `${h.structure.captureCount} captures at ${h.structure.baseSpacingM} m base, ` +
        `${(h.payload.bytes / 1024).toFixed(0)} KB, bounces ${h.bake.bounces}` +
        `${h.bake.validity ? ', validity on' : ''} — no in-browser bake` +
        `${origin === 'rebind' ? ` (${how}, no reload)` : ''}.`,
    );
    /*
     * ── ⭐ `/5` · ONE LINE THAT SAYS WHETHER THE SUN'S DISTANT SHADOW IS LIVE, AND WHAT IT IS ────
     *
     * A constant 1.0 in band 7 renders EXACTLY the frame this feature replaces, so "installed but
     * inert" and "not installed" are indistinguishable by eye — which is why the two numbers that
     * separate them are printed at bind rather than left in the header for someone to open. The
     * mean is the bake's own reading OFF THE BYTES IT WROTE (`atlasSunVisibility`); a variance of
     * zero over tens of thousands of probes is a producer that did not run, and the baker already
     * refuses it. The band is where the cascades hand over, in metres, from the LIVE policy.
     */
    const sv = h.bake.sunVisibility;
    const band = csmRig.state().handoverBandM;
    /*
     * ⚠️ THE INTENT, NOT `state().distantTerm`, AND THE DIFFERENCE IS TWO FRAMES. `applySunTerm`
     * has armed the `castShadow` cycle; the composed node does not reach the slot until `tick`
     * completes it, so reading the rig's state HERE would print `'none'` on every successful
     * install — a log line that contradicts the frame it describes. The published state stays the
     * fact (`__fwg.state().shadows.csm.distantTerm`); this line says what was asked for.
     */
    console.info(
      `[gi] sun visibility: ${
        sv.baked
          ? `baked (${sv.taps} taps, r ${sv.radiusM.toFixed(2)} m, mean ${sv.meanVisibility.toFixed(
              3,
            )}, variance ${sv.varianceVisibility.toFixed(4)}, ${sv.fullyShadowed}/${sv.fullyLit} fully shadowed/lit)`
          : 'NOT baked — band 7 is a constant 1.0, the sun stays unshadowed beyond the cascades'
      } — distant sun shadow ${
        giSunTerm === null
          ? 'NOT installed'
          : 'handed to the cascade rig (it lands after the two-frame castShadow swap)'
      }` +
        `${band[1] > 0 ? `, handover ${band[0]}–${band[1]} m` : ' (no cascades this session)'}.`,
    );
    return how;
  };

  const loadBakedVolume = async (drawn: {
    sizeM: [number, number, number];
    centerM: [number, number, number];
  }): Promise<boolean> => {
    const { checkProvenance } = await import('../render/gi/probeFile');
    const { fetchProbeVolume } = await import('../render/gi/probeLoad');
    const fetched = await fetchProbeVolume(activeWorld.worldId);
    if (!fetched.ok) {
      /*
       * `'absent'` is silent BY DESIGN: most worlds have no baked volume and never will, so
       * a console line here would fire on every boot of every shipping world. A file that
       * EXISTS and is unusable is the case worth naming.
       */
      if (fetched.failure !== 'absent') {
        console.warn(`[gi] baked volume refused (${fetched.failure}): ${fetched.detail}`);
      }
      giStale = { reason: fetched.failure, detail: fetched.detail };
    } else {
      /*
       * THE AUTHORED GI BLOCK IS PART OF THE PROVENANCE NOW (§6.2). `worldHash` already
       * covers it — it hashes the parsed world, `lighting.gi` included — but it cannot tell
       * a re-AUTHORING from a re-ORDERING, and reordering `volumes[]` changes not one byte
       * of the compiled output. So the narrower comparison is made explicitly, sorted by id,
       * and the coarse hash keeps catching everything else.
       */
      const verdict = checkProvenance(fetched.volume.header, await giLiveProvenance(drawn));
      if (!verdict.ok) {
        /*
         * ⭐ STALE MEANS REFUSE, AND THE WORLD STILL RENDERS. The ruling and its reasoning
         * live at `checkProvenance` in `render/gi/probeFile.ts`; the short version is that a
         * volume belonging to another version of the level does not render nothing, it
         * renders confidently wrong light in the one part of the frame nobody can check by
         * eye — and a `console.warn` is a self-report, not a fence. So the file is dropped,
         * `gi.stale` names the field that caught it, and the constant fill (or, under
         * `?gi=probe`, an in-browser bake) carries the indirect term instead.
         */
        console.warn(
          `[gi] STALE baked volume for '${activeWorld.worldId}' — refused, not used: ` +
            `${verdict.reason}: ${verdict.detail}. Re-run \`npm run bake:world -- ` +
            `${activeWorld.worldId}\`.`,
        );
        giStale = {
          reason: verdict.reason ?? 'unknown',
          detail: verdict.detail,
        };
      } else {
        // ONE bind, and this is its first caller; `reloadBakedLighting` is the second.
        await bindProbeVolume(fetched.volume, 'load');
        return true;
      }
    }
    return false;
  };

  /**
   * ── ⭐ SHIPS: "the volume was asked for and no usable one arrived" — AS A STATE, NOT A NULL ────
   *
   * `gi() === null` means the GI system was never asked for this session; this means it WAS asked
   * for, nothing usable arrived, and here is the field that refused. Those are different facts and
   * a test has to be able to tell them apart — a staleness fence nobody can assert on is a fence
   * nobody can prove works. `deriveBakeState` turns `stale: null` into `unbaked` and a named
   * refusal into `stale`, which is what the dev badge shows and what the artist acts on.
   *
   * `resolution` is `[0,0,0]` and the box is the DRAWN bounds: there is no lattice to describe
   * (the one this used to report belonged to a synthesised volume that was never compiled), and
   * `probeVolume()`'s budget arithmetic is bake-only and does not ship.
   */
  const giReportNoFile = (
    sizeM: [number, number, number],
    centerM: [number, number, number],
  ): void => {
    giInfo = {
      state: 'failed',
      on: false,
      resolution: [0, 0, 0],
      probes: 0,
      spacingM: [0, 0, 0],
      spacingRequestedM: giCompileSpacing(),
      structure: null,
      intensity: giIntensity,
      sizeM,
      centerM,
      bakeMs: 0,
      bakeMsPerProbe: 0,
      bounces: giBounces,
      validity: giValidity,
      quality: giQualityInfo(),
      cubemapSize: giCubemapSize,
      sampleCount: giSampleCount,
      seamBreaks: 0,
      bakedLights: 0,
      bakedLightLeaks: 0,
      source: 'none',
      stale: giStale,
      fileBytes: 0,
      dilation: null,
    };
  };

  /** Consecutive stable triangle-count checks before the auto-bake fires, and the interval. */
  let giStableChecks = 0;
  let giLastTris = -1;
  let giLastCheckMs = -Infinity;

  /**
   * ── ⭐ THE LOAD-TIME GI VERDICT, NAMED — so that "it has settled" has somewhere to be said ────
   *
   * Lifted out of `applyBakedLighting` verbatim (every arm, every refusal, every console line is
   * the same code) for exactly one reason: the method now wraps it in a `try/finally` that fires
   * `onGiSettled`, and a `finally` around a body with three `return`s is the only shape that
   * cannot miss an arm. The two "nothing was asked for" guards stay in the method, OUTSIDE the
   * bracket, because they write no state and settle no verdict.
   */
  const giLoadVerdict = async (): Promise<void> => {
    giStale = null;
    giLoadedHeader = null;
    giLoadedVolume = null;
    // ROW 2 — a session fact, not a world fact: `gi()` stays null and no bakeState is invented.
    if (giTierRefused()) return;
    const drawn = await giDrawnBox();
    try {
      if (await loadBakedVolume(drawn)) return;
      /*
       * ROWS 3–13 ARE ONE CODE PATH: refuse and fall back. There is no warn-and-use arm and none
       * may be added — a volume belonging to another version of the level renders confidently
       * wrong light in the one part of the frame nobody can check by eye, which no console line
       * fixes. `giStale` names the field that refused; `deriveBakeState` turns that into
       * `unbaked` (nothing on disk) or `stale` (something unusable on disk) for the badge.
       */
      giAutoBake = false;
      giReportNoFile(drawn.sizeM, drawn.centerM);
      /*
       * ⚠️ `'absent'` IS SILENT UNDER A DEV `?gi=` URL and NOT silent under world data, and the
       * difference is real rather than pedantic. Most worlds have no atlas and never will, so a
       * console line on the URL path would fire on every boot of every shipping world. But a
       * world that DECLARED `lighting.indirect: 'baked'` and has no atlas is a build that is
       * missing a step — worth exactly one line, naming the command that fixes it. `info`, not
       * `warn`: it is the normal state of a level nobody has baked yet, and the badge's UNBAKED
       * is the real channel.
       */
      if (giActivation.from === 'world') {
        console.info(
          `[gi] '${activeWorld.worldId}' declares lighting.indirect: 'baked' and has no usable ` +
            `volume (${giStaleNow()?.reason ?? 'absent'}${giStaleNow() ? `: ${giStaleNow()?.detail}` : ''}) — the ` +
            `constant fill carries the indirect term. Run \`npm run bake:world -- ` +
            `${activeWorld.worldId}\`.`,
        );
      } else {
        console.info(
          `[gi] ?gi=file and no usable baked volume for '${activeWorld.worldId}' ` +
            `(${giStaleNow()?.reason ?? 'absent'}) — the constant fill carries the indirect term.`,
        );
      }
    } catch (err) {
      /*
       * ROW 13 — `assertFits3D` is the one thing on this path that throws (a device 3D-texture
       * ceiling smaller than the file's dimensions). A failure is a STATE, not an absence.
       */
      giCarriesIndirect = false;
      giSkyBaked = false;
      applyIndirect();
      giReportNoFile(drawn.sizeM, drawn.centerM);
      console.warn(`[gi] baked lighting for '${activeWorld.worldId}' failed to bind:`, err);
    }
  };

  /**
   * Subscribers to "the load-time GI verdict has settled" — see `GameScene.onGiSettled` for the
   * measured defect this exists to close (preview lighting latching ON across a boot-time bind).
   * A `Set`, so a double `open()` cannot register the editor twice.
   */
  const giSettledSubs = new Set<() => void>();
  const fireGiSettled = (): void => {
    // A throwing listener must not swallow the others, nor reject the caller's `void` promise —
    // `applyBakedLighting` is FIRE AND FORGET (D25) and an unhandled rejection there is invisible.
    for (const cb of [...giSettledSubs]) {
      try {
        cb();
      } catch (err) {
        console.warn('[gi] an onGiSettled listener threw (ignored):', err);
      }
    }
  };

  // Named rather than returned anonymously so `render()` can reach `setProbeGrid` for the
  // deferred `?gi=probe` bake (see `giAutoBake` above) without a second copy of the bake code.
  const gameScene: GameScene = {
    renderer,
    backend: giFallbackBackend ? 'WebGL2 (fallback)' : 'WebGPU',
    gpu: gpuProbe,
    tier,
    rig,
    camTune,
    camera,
    charFix,
    entityRoot,
    editorRoot,
    setGridVisible(on) {
      grid.visible = on;
    },
    setExternalCamera(pose) {
      if (pose) {
        externalCam = true;
        camera.position.set(pose.pos.x, pose.pos.y, pose.pos.z);
        camera.quaternion.set(pose.quat[0], pose.quat[1], pose.quat[2], pose.quat[3]);
      } else {
        externalCam = false;
      }
    },
    rebuildWorld(next) {
      /*
       * ⚠️ LIGHTING IS NOT REBUILT, and that is deliberate rather than forgotten. `world.lighting`
       * is read ONCE in `createScene`; this path exists for the EDITOR, which edits geometry and
       * entity placements and never the lighting block. Re-posing the sun here would also have to
       * re-pose the sky dome and re-decide the indirect term, and — the real reason — any probe
       * volume already baked would still hold the OLD lighting, so the frame and the atlas would
       * disagree with nothing able to say so. A lighting change is a reload.
       */
      // A rebuild disposes and re-uploads instanced geometry; the frames either side of it are
      // not evidence about how expensive the WORLD is. Freeze + unfreeze in the same call is
      // deliberate: unfreezing is what arms the controller's resume warm-up.
      controller.freeze('world');
      activeWorld = next;
      if (next.boxes.length === gym.count) {
        writeBoxInstances(gym, next.boxes);
      } else {
        scene.remove(gym);
        gym.geometry.dispose();
        gym.dispose();
        gym = buildGymMesh(next.boxes);
      }
      if (next.platforms.length !== platforms.count) {
        scene.remove(platforms);
        platforms.geometry.dispose();
        platforms.dispose();
        platforms = buildPlatformMesh(next.platforms.length); // declares its own shadow flags
      }
      /*
       * L2: the level just changed shape, so every FROZEN shadow map is now a picture of geometry
       * that is gone. Re-scheduling them is one `needsUpdate` write per cached caster (0 on every
       * world today) and it also re-runs the static-layer walk over the new meshes — a rebuild
       * that left them off the channel would drop them out of every cached shadow silently, which
       * reads as "that wall stopped casting" and gets blamed on the light.
       */
      worldLightRig.refreshCachedShadows();
      // B6: and the sun's, which is NOT one of that rig's entries (it is this module's own object
      // — `applySun` says why). A frozen SUN map over a rebuilt level is the same defect one rung
      // louder: it is every shadow in the world, not one lamp's.
      csmRig.refreshSunShadow();
      controller.unfreeze('world');
    },
    worldLights() {
      return describeLightRig(worldLightRig.info(), activeLighting, tier, csmRig.policy(), lightPreview, shadowResScale);
    },
    shadows() {
      let character: ShadowCensus | null = null;
      let companion: ShadowCensus | null = null;
      if (charView) character = shadowCensus(charView.root);
      const axon = entityRoot.getObjectByName('axon-companion');
      if (axon) companion = shadowCensus(axon);
      return {
        receivers: {
          player: shadowCensus(player),
          character,
          companion,
          entities: shadowCensus(entityRoot),
        },
        csm: csmRig.state(),
      };
    },
    applyWorldLights(lighting) {
      // Fog rides the same apply as the rig: an editor edit lands without a reload, and there is
      // exactly ONE writer of `scene.fog` (see `applyFog`).
      applyFog(lighting.fog);
      /*
       * ⚠️ A SEPARATE DOOR FROM `rebuildWorld`, ON PURPOSE. That method's header says lighting is
       * NOT rebuilt and gives the reason — a lighting change invalidates any bound atlas and
       * nothing would be able to say so. That reason still stands and this does not weaken it:
       * `bakeState`'s standing limit is that **staleness is checked at LOAD, not continuously**,
       * so an editor light edit leaves the frame and the atlas disagreeing until a reload. What
       * changes is only that the disagreement is now REACHABLE deliberately (the editor's Lighting
       * section calls this) instead of being unreachable by accident. The editor is where the
       * "re-bake this world" prompt belongs; the renderer's job is to apply what it was handed.
       *
       * The reconcile keeps existing lights alive wherever it can — `info().keyMoves` is how many
       * lights were actually constructed or destroyed, i.e. how many times every visible
       * material's WGSL was regenerated. A slider drag must report 0.
       */
      activeLighting = lighting;
      // The session's preview verdict rides EVERY apply: an artist editing a static light's colour
      // must not have it vanish because this call forgot the flag that parented it.
      const next = worldLightRig.apply(lighting.lights, { previewStatic: lightPreview });
      // The leak fence watches whatever the world now declares static, not whatever it declared at
      // boot — otherwise a light switched to `static` in the editor would be watched by nobody.
      bakeOnlyLights = worldLightRig.bakeOnlyLights();
      /*
       * ── ⭐ B1 · THE SUN AND THE SKY ROWS ARE APPLIED, NOT ONLY PRICED ─────────────────────────
       *
       * Before this, the rows `describeLightRig` returns for `lighting.sun` and `lighting.sky`
       * were priced on every call and applied on none — the sun object was posed once at
       * construction and `SkyHandle.setSunPosition` had no caller. So this is the seam the
       * resolved-rig editor crosses: the runtime applies every row it prices.
       *
       * CONSEQUENCE, STATED: the boot-time dev overrides (`?sun=` intensity, `?sunAz=`/`?sunEl=`,
       * `?sky=0|1`) are SUPERSEDED by the authored block the first time this runs — i.e. the first
       * editor apply. That is correct: the editor edits the world, and a session that kept an
       * override under an authored edit would show a frame the file does not describe.
       * `liveLighting()` reads off the light and the dome, so provenance follows the frame: an
       * edited sun or sky makes the atlas honestly OUT OF DATE at the next load (the `worldHash`
       * arm fires first, because the edit is in `world.json`; the `sun`/`sky` arms are for the
       * overrides). The e2e that boots `?sun=26` for an OUT OF DATE arm never opens the editor
       * and is unaffected.
       *
       * The sun is ALWAYS a uniform/pose write except `castShadow` (`applySun` says why). The sky
       * is a uniform write while `enabled` holds; a flip constructs or removes the dome — a real
       * emitter appearing or leaving the scene graph, which is exactly what the row's note says a
       * re-bake is for.
       */
      const pos = sunPositionOf(lighting.sun);
      applySun(sun, lighting.sun, pos);
      // B3: the cascade policy follows the authored sun block (`cascades`/`shadowDistanceM`), the
      // boot override folded in. Reach and map sizes are free writes; a count change is a scheduled
      // two-frame rebuild, and the rig — not `applySun` — has the last word on `sun.castShadow`.
      csmRig.apply(sunShadowPolicy(tier, lighting.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride), lighting.sun.castShadow);
      if (lighting.sky.enabled) {
        if (sky) sky.apply(lighting.sky);
        else {
          sky = createSky(lighting.sky, pos);
          if (sky) scene.add(sky.mesh);
        }
        sky?.setSunPosition(pos);
      } else if (sky) {
        scene.remove(sky.mesh);
        sky.dispose();
        sky = null;
      }
      return describeLightRig(next, activeLighting, tier, csmRig.policy(), lightPreview, shadowResScale);
    },
    setLightPreview(on) {
      /*
       * ⭐ FENCE 2. `import.meta.env.DEV` is a compile-time literal, so Rollup folds this whole
       * body — the marker string included — out of a production build. In `dist/` this method is
       * `return describeLightRig(...)` over an unchanged rig: a caller cannot turn preview on
       * because the code that would has been deleted, not merely made unreachable. That is the
       * dev-hook bar, not the editor's unreachable-not-secret bar, and it is the right one here
       * because the owner's sentence is *"never shipped"*.
       */
      if (import.meta.env.DEV) {
        /*
         * ⚠️ THE MARKER IS ASSIGNED, NOT EXPORTED. `checkBundle` (c9)'s own experiment recorded
         * why: an exported-but-unused constant is tree-shaken and the absence leg then passes
         * vacuously — it would go green over a preview path that shipped. A live assignment inside
         * the fold cannot be dropped while the fold is retained, so the leg fires when it should.
         * `userData` on the scene is the cheapest live use that survives minification.
         */
        scene.userData['fwgLightPreview'] = on ? 'fwg-light-preview' : null;
        if (on !== lightPreview) {
          lightPreview = on;
          /*
           * ONE construct/destroy per static light — `LightsNode.customCacheKey()` moves and every
           * visible material's WGSL is regenerated once. `info().keyMoves` is that cost, made
           * visible on the editor's own budget line. It is paid on a PREDICATE TRANSITION (editor
           * open, editor close, the first edit after a clean load, a fresh atlas binding) and
           * never per selection or per frame — see the editor's `previewWanted()`.
           */
          const next = worldLightRig.apply(activeLighting.lights, { previewStatic: lightPreview });
          /*
           * ⚠️ AND THE LEAK FENCE HAS TO BE RE-READ IN THE SAME BREATH. `bakeOnlyLights` is the
           * subject set `GiInfo.bakedLightLeaks` watches; a previewing light is deliberately NOT
           * marked `BAKE_ONLY` (its energy is not in the bound atlas, so lighting it is not a
           * double count), which means it leaves that set while preview is on and rejoins it the
           * moment preview ends. Reading the rig here is what makes the set follow the graph
           * instead of describing a rig from before the flip.
           */
          bakeOnlyLights = worldLightRig.bakeOnlyLights();
          return describeLightRig(next, activeLighting, tier, csmRig.policy(), lightPreview, shadowResScale);
        }
      }
      return describeLightRig(worldLightRig.info(), activeLighting, tier, csmRig.policy(), lightPreview, shadowResScale);
    },
    getPickMeshes() {
      return { gym, platforms };
    },
    burstAt(pos, count) {
      vfx.burst(pos, count);
    },
    raycastGround(ndc) {
      raycaster.setFromCamera(new THREE.Vector2(ndc.x, ndc.y), camera);
      const hit = raycaster.ray.intersectPlane(groundPlane, rayHit);
      return hit ? { x: hit.x, y: hit.y, z: hit.z } : null;
    },
    syncPlayer(feetPos, state, events) {
      const now = performance.now();
      const dt = Math.min((now - lastFrame) / 1000, 0.1);
      lastFrame = now;

      player.position.set(feetPos.x, feetPos.y, feetPos.z);

      // Orient-to-movement (UE parity kept): mesh turns toward velocity, camera stays manual.
      const sp = Math.hypot(state.vel.x, state.vel.z);
      if (sp > 0.5) {
        const target = Math.atan2(-state.vel.x, -state.vel.z);
        let dy = target - facingYaw;
        while (dy > Math.PI) dy -= 2 * Math.PI;
        while (dy < -Math.PI) dy += 2 * Math.PI;
        facingYaw += dy * Math.min(1, dt * 14);
      }
      player.rotation.y = facingYaw;

      // Feedback-bus subscribers: squash impulses + dust.
      for (const e of events) {
        if (e.type === 'land') {
          scaleY = 1 - 0.28 * e.intensity;
          scaleVel = 0;
        } else if (e.type === 'jump') {
          scaleY = 1.3;
          scaleVel = 0;
        }
      }
      vfx.handle(events, feetPos);
      vfx.update(state, feetPos, dt);

      const omega = 22;
      const accel = -omega * omega * (scaleY - 1) - 2 * 0.55 * omega * scaleVel;
      scaleVel += accel * dt;
      scaleY += scaleVel * dt;
      const xz = 1 / Math.sqrt(Math.max(scaleY, 0.4));
      squash.scale.set(xz, Math.max(scaleY, 0.4), xz);

      // Teeter wobble: lean over the edge with a nervous oscillation.
      if (state.teetering) {
        teeterPhase += dt * 14;
        squash.rotation.x = -(0.14 + Math.sin(teeterPhase) * 0.05);
      } else {
        squash.rotation.x *= Math.max(0, 1 - dt * 10);
      }

      bodyMat.emissive.setHex(state.mode === 'hover' ? 0x1a6f78 : 0x000000);

      charHolder.rotation.y = (charFix.yawDeg * Math.PI) / 180;

      // Keep mesh scale + roll pivot in sync with the panel.
      const pivotY = HIP_Y * charFix.scale;
      rollSpin.position.y = pivotY;
      if (charView) {
        charView.root.scale.setScalar(charFix.scale);
        charView.root.position.y = -pivotY;
        charView.strideScale = charFix.scale * charFix.strideMul;
        charView.maxCycleHz = charFix.cadenceCapHz;
        charView.surfaceCadenceMul = surfaceFor(surfaces, state.groundSurface).cadenceMul;
      }

      // Procedural roll: dive-delayed spin, exactly 2π by ~72% progress (sign = head-over-
      // forward, flipped after playtest); hard zero the frame it ends. During the dive window
      // the hand IK reaches toward a point ahead along the roll direction — the physical
      // "hands lead into the roll" read.
      const rollProgress =
        state.rollMsLeft > 0 && feel.rollDurationMs > 0
          ? 1 - state.rollMsLeft / feel.rollDurationMs
          : 0;
      rollSpin.rotation.x = rollProgress > 0 ? rollSpinAngle(rollProgress) : 0;
      // Dive-over-the-ground: center dips low and leads forward of the capsule through the
      // tumble (mesh local -Z is the roll direction while facing tracks velocity).
      if (rollProgress > 0) {
        rollSpin.position.y = pivotY - rollDip(rollProgress) * charFix.scale;
        rollSpin.position.z = -rollLead(rollProgress);
      } else {
        rollSpin.position.z = 0;
      }
      if (charView) {
        const reach = rollProgress > 0 ? rollReachBlend(rollProgress) : 0;
        if (reach > 0.01) {
          // Character LEFT of the roll direction d is up×d = (dz, -dx) — getting this backwards
          // crosses the arms (shipped once; keep the derivation here).
          const dx = state.rollDirX;
          const dz = state.rollDirZ;
          const leftX = dz;
          const leftZ = -dx;
          const ahead = 0.65 + 0.5 * rollProgress;
          // Hands plant TOWARD THE GROUND ahead — from chest height down to near-floor.
          const height = feetPos.y + Math.max(0.95 - 2.2 * rollProgress, 0.25);
          const w = 0.2; // ~shoulder width
          charView.pose.setHandTarget(
            'left',
            {
              x: feetPos.x + dx * ahead + leftX * w,
              y: height,
              z: feetPos.z + dz * ahead + leftZ * w,
            },
            reach,
          );
          charView.pose.setHandTarget(
            'right',
            {
              x: feetPos.x + dx * ahead - leftX * w,
              y: height,
              z: feetPos.z + dz * ahead - leftZ * w,
            },
            reach,
          );
        } else {
          charView.pose.setHandTarget('left', null);
          charView.pose.setHandTarget('right', null);
        }
      }

      if (charView) {
        charView.update(
          {
            speed: sp,
            grounded: state.grounded,
            vy: state.vel.y,
            hovering: state.mode === 'hover',
            // Anim-level roll exits at ROLL_ANIM_EXIT (spin done) and crossfades into
            // locomotion — the mechanic's tail then reads as rolling into stride.
            rolling: state.rollMsLeft > 0 && rollProgress < ROLL_ANIM_EXIT,
            rollDuration: feel.rollDurationMs / 1000,
            rollProgress01: rollProgress,
            jumpTimeToApex: feel.jumpTimeToApex,
            maxWalkSpeed: feel.maxWalkSpeed,
            maxRunSpeed: feel.maxRunSpeed,
          },
          { x: state.vel.x, z: state.vel.z },
          facingYaw,
          dt,
          events,
        );
      }

      // ── Camera ──
      // Editor override (A3): the fly camera owns the pose — skip the whole gameplay rig.
      if (externalCam) return;
      // Ground-projected Y tracking: no bob on every jump.
      airborneS = state.grounded ? 0 : airborneS + dt;
      if (state.grounded || Math.abs(feetPos.y - groundY) > 1.5 || airborneS > 0.9) {
        groundY = feetPos.y;
      }
      smoothDamp(springX, feetPos.x, camTune.horizSmooth, dt);
      smoothDamp(springZ, feetPos.z, camTune.horizSmooth, dt);
      smoothDamp(springY, groundY, state.grounded ? 0.22 : 0.4, dt);

      // Lookahead, feathered: decomposed into camera space so lateral swings (the sickening
      // component on left↔right flips) are damped much harder than forward lead.
      const fwdX = -Math.sin(rig.yaw);
      const fwdZ = -Math.cos(rig.yaw);
      const rightX = Math.cos(rig.yaw);
      const rightZ = -Math.sin(rig.yaw);
      const velFwd = state.vel.x * fwdX + state.vel.z * fwdZ;
      const velRight = state.vel.x * rightX + state.vel.z * rightZ;
      const aheadF = velFwd * camTune.lookaheadTime;
      const aheadR = velRight * camTune.lookaheadTime * camTune.lookaheadLateral;
      let lookTX = fwdX * aheadF + rightX * aheadR;
      let lookTZ = fwdZ * aheadF + rightZ * aheadR;
      const lookMag = Math.hypot(lookTX, lookTZ);
      if (lookMag > camTune.lookaheadMax) {
        lookTX *= camTune.lookaheadMax / lookMag;
        lookTZ *= camTune.lookaheadMax / lookMag;
      }
      smoothDamp(lookX, lookTX, camTune.lookaheadSmooth, dt);
      smoothDamp(lookZ, lookTZ, camTune.lookaheadSmooth, dt);

      camTarget.set(springX.value + lookX.value, springY.value + 1.2, springZ.value + lookZ.value);

      // Auto-yaw assist (C2), default OFF. When on: only while moving roughly AWAY from the
      // camera (within 50° of camera-forward) — this breaks the strafe→camera→strafe feedback
      // carousel — plus the usual gates: idle stick 0.6 s, grounded, fast, never airborne.
      rig.msSinceCamInput += dt * 1000;
      if (
        rig.autoYaw &&
        rig.msSinceCamInput > 600 &&
        state.grounded &&
        sp > 0.6 * feel.maxRunSpeed &&
        velFwd > sp * Math.cos((50 * Math.PI) / 180)
      ) {
        const behind = Math.atan2(-state.vel.x, -state.vel.z);
        let diff = behind - rig.yaw;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        const deadCone = (25 * Math.PI) / 180;
        if (Math.abs(diff) > deadCone) {
          const maxStep = ((camTune.autoYawRateDeg * Math.PI) / 180) * dt;
          rig.yaw += Math.sign(diff) * Math.min(Math.abs(diff) - deadCone, maxStep);
        }
      }

      // Speed FOV kick (feathered) and a slight tighten while hovering (−2°).
      let fovTarget = BASE_FOV;
      if (sp > 0.9 * feel.maxRunSpeed) fovTarget += camTune.fovKickDeg;
      if (state.mode === 'hover') fovTarget -= 2;
      smoothDamp(fov, fovTarget, 0.25, dt);
      if (Math.abs(camera.fov - fov.value) > 0.01) {
        camera.fov = fov.value;
        camera.updateProjectionMatrix();
      }

      // Framing by pitch (C4): pull in when looking up, back out when looking down.
      const pitchNorm = (rig.pitch - 0.28) / (rig.pitch >= 0.28 ? 1.1 - 0.28 : 0.28 + 0.15);
      const desiredDist = rig.distance * (1 + 0.15 * pitchNorm);

      // Collision (C3, asymmetric): pull-in is instantaneous; push-out is damped and waits for
      // 3 consecutive clear frames (no pillar-edge oscillation).
      const cp = Math.cos(rig.pitch);
      const dirX = Math.sin(rig.yaw) * cp;
      const dirY = Math.sin(rig.pitch);
      const dirZ = Math.cos(rig.yaw) * cp;
      const desiredPos = {
        x: camTarget.x + dirX * desiredDist,
        y: camTarget.y + dirY * desiredDist,
        z: camTarget.z + dirZ * desiredDist,
      };
      const allowed = cameraProbe
        ? (cameraProbe({ x: camTarget.x, y: camTarget.y, z: camTarget.z }, desiredPos) ??
          desiredDist)
        : desiredDist;
      if (allowed < camDist.value) {
        camDist.value = allowed; // snap in
        camDist.velocity = 0;
        clearFrames = 0;
      } else {
        clearFrames++;
        if (clearFrames >= 3) smoothDamp(camDist, Math.min(allowed, desiredDist), 0.4, dt);
      }

      // Too close: dither the character instead of jamming the camera closer (C3).
      smoothDamp(bodyOpacity, camDist.value < 1.2 ? 0.35 : 1, 0.15, dt);
      bodyMat.transparent = bodyOpacity.value < 0.995;
      bodyMat.opacity = bodyOpacity.value;

      // Trauma shake (C5): trauma², decays 1.6/s, smooth pseudo-noise — never sum raw shakes.
      for (const e of events) {
        if (e.type === 'land' && e.intensity > 0.5) {
          trauma = Math.min(trauma + 0.35 * (e.intensity - 0.5) * 2, 1);
        }
      }
      trauma = Math.max(0, trauma - 1.6 * dt);
      const shakeAmp = trauma * trauma * 0.14;
      const tSec = now / 1000;
      const shakeX = shakeAmp * (Math.sin(tSec * 37.1) * 0.6 + Math.sin(tSec * 23.7) * 0.4);
      const shakeY = shakeAmp * (Math.sin(tSec * 41.3 + 1.7) * 0.6 + Math.sin(tSec * 29.9) * 0.4);

      camera.position.set(
        camTarget.x + dirX * camDist.value + shakeX,
        camTarget.y + dirY * camDist.value + shakeY,
        camTarget.z + dirZ * camDist.value,
      );
      camera.lookAt(camTarget);
      camera.rotation.z += trauma * trauma * 0.028 * Math.sin(tSec * 31.7);
    },
    setArc(origin, points) {
      if (!origin || points.length < 2) {
        arcGeo.setDrawRange(0, 0);
        arcLine.visible = false; // no 0-vertex draw (see the arcLine note above)
        return;
      }
      const n = Math.min(points.length, ARC_MAX_POINTS);
      for (let i = 0; i < n; i++) {
        const p = points[i]!;
        arcPositions[i * 3] = origin.x + p.x;
        arcPositions[i * 3 + 1] = origin.y + p.y + 0.05;
        arcPositions[i * 3 + 2] = origin.z + p.z;
      }
      arcGeo.setDrawRange(0, n);
      arcLine.visible = true;
      (arcGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    },
    updatePlatforms(timeS) {
      activeWorld.platforms.forEach((def, i) => {
        const [x, y, z] = platformPosAt(def, Math.max(0, timeS));
        platM.compose(
          new THREE.Vector3(x, y, z),
          platQ,
          new THREE.Vector3(def.size[0], def.size[1], def.size[2]),
        );
        platforms.setMatrixAt(i, platM);
      });
      platforms.instanceMatrix.needsUpdate = true;
    },
    async warmup() {
      /*
       * ── ⭐ WHAT THIS CAN AND CANNOT WARM, MEASURED — BECAUSE IT WARMED NOTHING FOR ITS WHOLE LIFE ─
       *
       * `mount` awaited this ONCE, before the world's meshes existed: `loadAllIndexed` returns on
       * CONSTRUCTION and every GLB arrives in a later `.then()`, so the "fully built scene" the old
       * comment described was an empty one. Every pipeline was therefore created on the first frame
       * that actually drew a batch — through `device.createRenderPipeline`, the SYNCHRONOUS one
       * (`WebGPUPipelineUtils.js:261-263` takes that arm whenever `promises === null`, which is
       * every ordinary render). Measured on `mars-hangar-01`: reaching a warm frame takes ~29 s
       * after the last collider binds and contains a single **14.7 s** frame, for 1,953 programs.
       *
       * Called with the world built, the same work goes through `device.createRenderPipelineAsync`
       * (`:292`, the `promises !== null` arm) and is AWAITED BEHIND THE LOADING PLATE instead of
       * landing in a live frame. That is the whole of the win, and it is a scheduling win: the
       * compiles still happen, they just stop happening while someone is looking.
       *
       * ⚠️ IT DOES NOT WARM THE SHADOW PASSES, AND ON THIS LEVEL THAT IS THREE OF FIVE. Read out of
       * `Renderer.compileAsync` (r185 `:884-983`): it builds ONE render list, from ONE camera, and
       * calls `_projectObject` once. There is no `_renderShadows` anywhere in it, so every CSM
       * cascade's pipelines are still created synchronously in the first shadowed frame.
       * ⚠️ AND IT ONLY WARMS WHAT IS IN FRUSTUM — `:957-966` culls before building the list — so a
       * camera that turns around meets uncompiled pipelines. Both are why this reduces the storm
       * rather than removing it, and neither is a reason to skip it.
       */
      const t0 = performance.now();
      let drawable = 0;
      scene.traverse((o) => {
        if ((o as THREE.Mesh).isMesh && o.visible) drawable += 1;
      });
      /*
       * ── ⭐ THE FRUSTUM IS DEFEATED FOR THE WARM-UP, AND THAT IS THE POINT OF THE WARM-UP ─────────
       *
       * `compileAsync` culls before it builds its list (`Renderer.js:957-966`), so warming from the
       * spawn camera compiled only what happened to be in front of it — about 600 of ~1,950 render
       * objects. Everything else waited to be LOOKED AT, and then compiled synchronously, mid-frame.
       *
       * MEASURED, identical scripted 360° yaw sweeps on `mars-hangar-01`, camera pinned so both arms
       * see the same orientations:
       *
       *     cold   44.1 s   66 hitch frames   worst 856 ms   +600 pipelines
       *     warm   18.1 s    0 hitch frames   —              +0 pipelines
       *
       * and in the profiled cold arm `(idle)` is 42.86 % — 22.8 s blocked in Chrome's synchronous
       * `createRenderPipeline` IPC — plus 7.6 s inside `NodeBuilder.build()`, together 99.7 % of the
       * cold-minus-warm delta. The warm control arm idles 3.01 % over the same draws, which is what
       * rules out "waiting for the GPU" and pins it to compilation.
       *
       * So the fix is to make the warm-up see the whole level: `frustumCulled = false` while it
       * compiles, restored immediately after. The flag is read per object by `_projectObject` and is
       * in no cache key, so nothing about the resulting pipelines differs — only how many of them
       * exist by the time a player can turn around.
       *
       * ⚠️ RESTORED IN A `finally`, PER OBJECT, FROM ITS OWN PREVIOUS VALUE. Leaving it false would
       * disable frustum culling for the session — every batch submitted every frame from every
       * angle, which is the regression the batcher's CELL term exists to prevent, arriving silently
       * and looking like a warm-up win.
       */
      const culling: { o: THREE.Object3D; was: boolean }[] = [];
      scene.traverse((o) => {
        if ((o as THREE.Mesh).isMesh && o.frustumCulled) {
          culling.push({ o, was: true });
          o.frustumCulled = false;
        }
      });
      try {
        // r185 signature: compileAsync(scene, camera, targetScene = null) — works on both the
        // WebGPU backend and the WebGL2 fallback (base Renderer implementation).
        await renderer.compileAsync(scene, camera);
      } catch (err) {
        console.warn('[render] compileAsync warm-up failed — continuing without it:', err);
        return;
      } finally {
        for (const c of culling) c.o.frustumCulled = c.was;
      }
      /*
       * Printed, and printed with the COUNT, because "the warm-up ran" and "the warm-up warmed
       * something" are different claims and only the second one is worth anything. A line reading
       * `0 drawable` is the defect this block documents, arriving as a sentence instead of as a
       * 14.7 s frame nobody attributes to it.
       */
      /*
       * The PROGRAM COUNT is printed beside the mesh count because they answer different questions
       * and only together do they say whether this worked: `drawable` is what we offered the
       * compiler, `programs` is what it built. A warm-up that offers 1,950 meshes and leaves the
       * program count where it started has warmed nothing, and would otherwise look identical to
       * one that worked.
       */
      const programs = (renderer.info as { memory?: { programs?: number } }).memory?.programs ?? -1;
      console.info(
        `[render] warm-up: ${drawable} drawable mesh(es), frustum defeated, ` +
          `${programs < 0 ? 'programs n/a' : `${programs} program(s)`} in ` +
          `${Math.round(performance.now() - t0)} ms` +
          (drawable === 0 ? ' — ⚠️ NOTHING WAS IN THE SCENE YET, so no pipeline was warmed.' : ''),
      );
    },
    getVramMB() {
      return vramBreakdown().totalMB;
    },
    frameStats() {
      return { drawCalls: lastFrameDraws, triangles: lastFrameTris };
    },
    applyMobility(root, mobility) {
      if (mobility === 'static') {
        composeStaticSubtree(root);
        dynamicRoots.delete(root);
      } else {
        optInSubtree(root);
        dynamicRoots.add(root);
      }
    },
    refreshStaticTransform(root) {
      refreshStaticTransform(root);
    },
    getVramBreakdown() {
      return vramBreakdown();
    },
    lighting,
    stress() {
      return stress?.info() ?? null;
    },
    async setViewMode(id) {
      await debugViewReady;
      return debugView?.setMode(id) ?? false;
    },
    async setShowFlag(id, on) {
      await debugViewReady;
      return debugView?.setFlag(id, on) ?? false;
    },
    async setProbeView(patch) {
      await debugViewReady;
      return debugView?.setProbeView(patch) ?? null;
    },
    syncDebugView() {
      debugView?.sync();
    },
    setCollisionOverlaySource(source) {
      collisionShapeSource = source;
      debugView?.sync();
    },
    editorGizmos() {
      let objects = 0;
      let drawable = 0;
      let unstamped = 0;
      const renders = (o: THREE.Object3D): boolean =>
        (o as THREE.Mesh).isMesh === true ||
        (o as THREE.Sprite).isSprite === true ||
        (o as THREE.Line).isLine === true ||
        (o as THREE.Points).isPoints === true;
      /*
       * ⚠️ TWO DIFFERENT WALKS, BECAUSE THE TWO LEDGERS ASK DIFFERENT QUESTIONS.
       *
       *  · `drawable` is the DRAW-CALL question, so it stops at an invisible subtree: a hidden
       *    gizmo costs nothing and must not read as if it did. That is also what makes it return to
       *    0 when the editor closes — the gizmo roots are hidden, not rebuilt.
       *  · `unstamped` is the VRAM question, and `censusScene` does NOT consult `visible` (a hidden
       *    texture is still resident), so it counts the whole subtree. A gizmo that is merely
       *    hidden is still on the bill unless it is stamped.
       */
      // Distinct by IDENTITY, exactly as `censusScene` counts: a geometry shared by ten gizmos is
      // one upload, and counting it ten times would overstate the omission.
      const geos = new Set<THREE.BufferGeometry>();
      const visit = (o: THREE.Object3D, shown: boolean): void => {
        objects++;
        const vis = shown && o.visible;
        if (renders(o)) {
          if (vis) drawable++;
          // The same predicate `censusScene` uses — asking the GRAPH, not the code that built it.
          if (o.userData[DEBUG_OVERLAY_USERDATA] !== true) unstamped++;
          const geo = (o as THREE.Mesh).geometry;
          if (geo) geos.add(geo);
        }
        for (const child of o.children) visit(child, vis);
      };
      for (const child of editorRoot.children) visit(child, editorRoot.visible);
      let chargeableBytes = 0;
      for (const geo of geos) {
        for (const attr of Object.values(geo.attributes)) {
          chargeableBytes += (attr as THREE.BufferAttribute).array?.byteLength ?? 0;
        }
        chargeableBytes += geo.index?.array.byteLength ?? 0;
      }
      return { objects, drawable, unstamped, chargeableBytes };
    },
    debugView() {
      return debugView?.info() ?? null;
    },
    settleEntities(pending) {
      /*
       * ⚠️ THE PLAN AND THE BAKE PHOTOGRAPH THE SCENE GRAPH, AND `respawnAllEntities()` EMPTIES IT
       * FIRST. `host.dispose()` removes every entity, then each is re-spawned behind an `await` —
       * so for a few frames the occupancy walk sees Sponza with NO mesh. The verifier caught the
       * panel showing "2,057 probes · 92 % air" against that empty room, with a Fit button that
       * would have shrunk `arcade` to a 19 m box. The editor hands the in-flight respawn here; the
       * two scene walks await it; the empty room is never photographed. Cleared when it settles
       * either way — `then(clear, clear)`, not `finally`, so a rejected respawn does not surface a
       * second, unhandled rejection from the derived promise.
       */
      entitiesSettling = pending;
      const clear = (): void => {
        if (entitiesSettling === pending) entitiesSettling = null;
      };
      void pending.then(clear, clear);
    },
    async giBakePlan(qualityName, request) {
      if (entitiesSettling) await entitiesSettling;
      /*
       * ⭐ THE COMPILE-TIME GATE IS THE WHOLE PRODUCTION STORY. `probeCompile.ts` (667 lines of
       * octree refinement) and `bakeQuality.ts` are both on `checkBundle` (c5)'s "in NO emitted
       * chunk" list, and `tests/renderGiFence.test.ts` asserts at the SOURCE that every import of
       * them in this file sits inside an `import.meta.env.DEV` block. Rollup deletes this branch,
       * both imports and both modules; a production build resolves `null` here forever.
       */
      if (import.meta.env.DEV) {
        const { compileProbeVolumes } = await import('../render/gi/probeCompile');
        const {
          BAKE_QUALITY,
          BAKE_QUALITY_NAMES,
          describeBakeQuality,
          isBakeQualityName,
          resolveBakeQuality,
        } = await import('../render/gi/bakeQuality');
        // REFUSED, never defaulted — the same rule the CLI applies to `--quality`. A typo that
        // quietly previewed `medium` would show costs for a bake nobody asked for.
        if (!isBakeQualityName(qualityName)) {
          console.warn(
            `[gi] giBakePlan('${qualityName}') is not a preset — one of ` +
              `${BAKE_QUALITY_NAMES.join(' | ')}. No plan computed.`,
          );
          return null;
        }
        /*
         * ⚠️ THE PRESET'S OWN MULTIPLIER, NOT THE SESSION'S. `giCompileSpacing()` folds in
         * `giSpacingMultiplier`, which is whatever `?giQuality=` resolved at boot — so using it
         * would price the preset the artist is LOOKING AT with the spacing of the preset this
         * session happens to be running. The panel's job is to answer "what if I baked this
         * instead", so the base is the world's own, times the picked preset's multiplier.
         */
        /*
         * ── ⭐ THE PANEL'S REQUEST RESOLVES THROUGH THE CLI'S RESOLVER (B1-bounces) ─────────────
         *
         * `request.bounces` is what the Bake folder's slider asked for; `resolveBakeQuality` is
         * the ONE definition of "preset plus explicit request" and it is the function the bake
         * bracket and `tools/bundler/bake-world.ts` both call. So the plan's `quality.line`, its
         * `bounces` and the scene-render count all price the override the same way the bake will
         * pay it, and the `overrides` array the plan carries is byte-for-byte what the header will
         * record as `bake.qualityOverrides`. The clamp is the `?giBounces=` parser's (0–8, floor),
         * so a slider value and a URL value cannot resolve differently.
         *
         * ⚠️ SPARSE: a field is present only when it was asked for. `bounces: 0` is a real
         * request ("direct light only") and must reach the resolver as 0, not vanish as falsy —
         * the `?hemi=` landmine, again.
         */
        const qualityRequest: import('../render/gi/bakeQuality').BakeQualityRequest = {};
        if (request?.bounces !== undefined && Number.isFinite(request.bounces)) {
          qualityRequest.bounces = Math.max(0, Math.min(8, Math.floor(request.bounces)));
        }
        const quality = resolveBakeQuality(qualityName, qualityRequest);
        /** The plan's `quality` block — built ONCE so the refusal arm and the priced arm agree. */
        const qualityFacts: GiBakePlan['quality'] = {
          name: quality.name,
          line: describeBakeQuality(quality),
          bounces: quality.bounces,
          // The row the request started from, so the head can say `bounces 2* (preset says 1)`.
          presetBounces: BAKE_QUALITY[quality.name].bounces,
          overrides: [...quality.overrides],
          spacingMultiplier: quality.spacingMultiplier,
          // The scene-render count is `x12 x(1+bounces)` -- the bake bracket's own arithmetic.
          // The panel omitted a factor once and printed half the truth on every bake (U1).
          validity: quality.validity,
        };
        const authored = giAuthoredVolumes();
        const baseSpacingM =
          (authored.length > 0 ? activeWorld.lighting.gi.baseSpacingM : giSpacingM) *
          quality.spacingMultiplier;
        /*
         * §2.4 — THE SYNTHESISED CASE, BUILT THE WAY THE BAKE BUILDS IT. A world that authors
         * nothing (or has `gi.enabled: false`) gets ONE volume over `probeVolume()`'s padded,
         * floor-clamped drawn box. Re-deriving that box here from raw `drawnBounds` would price a
         * DIFFERENT volume than the one the bake would compile, which is the same class of error
         * as re-deriving the probe count.
         */
        let volumes = authored;
        let synthesised = false;
        if (volumes.length === 0) {
          const { drawnBounds, probeVolume } = await import('../render/gi/probeVolume');
          scene.updateMatrixWorld(true); // see `giDrawnBox` — matrixWorld is composed during a render
          const bounds = drawnBounds(scene) ?? new THREE.Box3();
          const bv = new THREE.Vector3();
          for (const b of activeWorld.boxes) {
            bounds.expandByPoint(
              bv.set(b.pos[0] - b.size[0] / 2, b.pos[1] - b.size[1] / 2, b.pos[2] - b.size[2] / 2),
            );
            bounds.expandByPoint(
              bv.set(b.pos[0] + b.size[0] / 2, b.pos[1] + b.size[1] / 2, b.pos[2] + b.size[2] / 2),
            );
          }
          if (bounds.isEmpty()) bounds.expandByPoint(bv.set(0, 0, 0));
          try {
            const vol = probeVolume(bounds, {
              spacingM: baseSpacingM,
              padM: 2,
              floorY: GI_FLOOR_Y,
              maxProbes: giMaxProbes,
            });
            volumes = [
              {
                id: '__drawn',
                min: vol.center.map((c, i) => c - vol.size[i]! / 2) as [number, number, number],
                max: vol.center.map((c, i) => c + vol.size[i]! / 2) as [number, number, number],
                level: 0,
                expandBricks: 0,
              },
            ];
            synthesised = true;
          } catch (err) {
            // `ProbeBudgetError` from the synthesis itself — the same refusal, one step earlier.
            return {
              worldId: activeWorld.worldId,
              qualityNames: [...BAKE_QUALITY_NAMES],
              quality: qualityFacts,
              baseSpacingM,
              synthesised: true,
              // The SYNTHESIS itself refused, so there is no box to author and nothing to price.
              bootstrapCost: null,
              volumes: [],
              structure: null,
              adaptive: null,
              budgets: {
                maxCaptures: giMaxProbes,
                maxAtlasBytes: GI_MAX_ATLAS_BYTES,
              },
              refusal: String(err instanceof Error ? err.message : err),
            };
          }
        }
        // Built ONCE for the whole plan (N+1 compiles), not per compile — see `giOccupancyGrid`.
        const occupancy = await giOccupancyGrid();
        /*
         * ⚠️ `withOffset` IS FALSE BY DEFAULT AND IT IS A DRAG-PERFORMANCE DECISION, NOT A
         * CORRECTNESS ONE. `bakePanel.refresh()` runs N+1 compiles per POINTERMOVE of a volume
         * drag, and the extra N exist only to price "what does this volume add" — they read
         * `.captureCount` and nothing else. Virtual offset cannot change `captureCount` (it moves a
         * camera, it never adds or removes a probe), so running the relocation search N extra times
         * per mouse move would buy a number nobody reads. Measured cost of one pass at Sponza's
         * scale on a 0.3 m grid: ~20 ms steady-state per 30k captures, ~85 ms worst case when every
         * capture is buried. Only the ONE compile whose report the panel and the bake both consume
         * asks for it.
         */
        const compile = (list: typeof volumes, spacing = baseSpacingM, withOffset = false) =>
          compileProbeVolumes({
            originM: activeWorld.lighting.gi.origin,
            baseSpacingM: spacing,
            volumes: list,
            fillEmptySpaces: activeWorld.lighting.gi.fillEmptySpaces,
            maxCaptures: giMaxProbes,
            maxAtlasBytes: GI_MAX_ATLAS_BYTES,
            occupancy,
            surfaceGuardM: giAdaptive()?.surfaceGuardM,
            // ⭐ Virtual offset (`probeOffset.ts`). Default OFF and paired with a fine
            // `gi.adaptive.voxelM` — the compiler REFUSES a voxel coarser than
            // baseSpacingM/4 rather than relocating probes on a lattice too coarse to say
            // where the air is. The PLAN runs it on its ONE headline compile, deliberately: a
            // panel that priced a bake without the pass the bake then runs is the "two
            // processes read one world.json and disagreed" shape this subsystem keeps
            // producing.
            virtualOffset: withOffset ? giAdaptive()?.virtualOffset : undefined,
          });
        /*
         * ── ⭐ WHAT AUTHORING THE SYNTHESISED BOX WOULD COST, PRICED BEFORE THE CLICK (B3) ────────
         *
         * ⚠️ MEASURED, AND IT INVALIDATES THE OBVIOUS ASSUMPTION: **authoring the synthesised box
         * does NOT keep the probe count, and `expandBricks` is only the smaller half of why.** The
         * larger half is `giCompileSpacing()`'s two arms, which are deliberate and documented —
         * with NOTHING authored the level-0 base is the SESSION's (`GI_DEFAULT_SPACING_M` = 4 m, or
         * `?giSpacing=`), because "the single synthesised volume IS the authoring and the session's
         * request is the only spacing there is"; with something authored it is the WORLD's
         * `gi.baseSpacingM` (1.2 m by default), because the authored LEVELS are relative to it.
         *
         * Measured on `playground-01`: the same box, same corners, same `expandBricks: 0`, is
         * **585 captures at 4.0 m and 5,445 at 1.2 m**. So a bootstrap button promising "the probe
         * count does not change" would be making a claim that is false by a factor of nine.
         *
         * THE FIX IS NOT TO FORCE THEM EQUAL. Writing the session's 4 m into `world.lighting.gi.
         * baseSpacingM` would put a `?giSpacing=` dev override into committed world data and
         * silently re-scale every level an artist authors afterwards. The world's own base is the
         * versioned, artist-facing decision and 1.2 m is what the schema calls "the interior default
         * the owner specified" — the count MOVING is the world becoming properly authored.
         *
         * So we price it instead, with the same compiler, before the click. The button's title and
         * the state block both carry the move. That is this repo's rule everywhere else (a preset
         * name is a request, the resolved values are the fact); a refusal here is real too — 37×
         * the probes on a big world can blow a budget — so it is caught and reported, never thrown.
         */
        let bootstrapCost: GiBakePlan['bootstrapCost'] = null;
        if (synthesised) {
          const authoredSpacingM = activeWorld.lighting.gi.baseSpacingM * quality.spacingMultiplier;
          try {
            bootstrapCost = {
              captures: compile(volumes, authoredSpacingM).captureCount,
              spacingM: authoredSpacingM,
              refusal: null,
            };
          } catch (err) {
            bootstrapCost = {
              captures: null,
              spacingM: authoredSpacingM,
              refusal: String(err instanceof Error ? err.message : err),
            };
          }
        }
        const base: Omit<GiBakePlan, 'structure' | 'volumes' | 'refusal' | 'adaptive'> = {
          worldId: activeWorld.worldId,
          qualityNames: [...BAKE_QUALITY_NAMES],
          quality: qualityFacts,
          baseSpacingM,
          synthesised,
          bootstrapCost,
          budgets: {
            maxCaptures: giMaxProbes,
            maxAtlasBytes: GI_MAX_ATLAS_BYTES,
          },
        };
        try {
          const s = compile(volumes, baseSpacingM, true);
          const snapOf = (id: string): number => {
            const snap = s.report.snaps.find((x) => x.id === id);
            if (!snap) return 0;
            return Math.max(...snap.minSnapM, ...snap.maxSnapM);
          };
          /*
           * ── ⭐ THE MARGINAL COST — N MORE COMPILES, AND THEY ARE THE COLUMN THAT MEANS SOMETHING ─
           *
           * `report.capturesByVolume` attributes a shared capture to the FIRST volume in `volumes[]`
           * order that contains it. That is right for the refusal message it exists for (the parts
           * must sum to the whole) and WRONG as a cost, and Sponza is the proof: the coarse `world`
           * volume geometrically contains `arcade`, so first-claim reads world 9,081 / arcade 0 —
           * i.e. "the level-0 arcade refinement is free", when it is 6,480 of the 9,081 probes in
           * the bake. So each volume is also priced by REMOVING it and recompiling.
           *
           * COST: one extra `compileProbeVolumes` per volume, measured at 4.45 ms on Sponza's two.
           * Pure arithmetic, no GPU — but it is why the editor calls this on a trailing throttle
           * rather than synchronously per pointermove of a volume drag (`bakePanel.ts`).
           *
           * A sub-compile can legitimately THROW (removing the volume that bounded the grid can
           * blow a budget the full authoring fitted). That is not the artist's problem and must not
           * become a refusal of the whole plan: the marginal falls back to the full total, which is
           * the honest reading of "everything here depends on it".
           */
          const marginal = (i: number): number => {
            if (volumes.length <= 1) return s.captureCount;
            try {
              return s.captureCount - compile(volumes.filter((_, j) => j !== i)).captureCount;
            } catch {
              return s.captureCount;
            }
          };
          /*
           * ── ⭐ PER-VOLUME AIR AND THE SHRINK-WRAP BOX (B2-sparse) ────────────────────────────
           *
           * The SAME grid the compile above dropped bricks with, asked per volume at the volume's
           * own brick size with the compiler's own per-level guard (`guardByLevelM`), so "N % of
           * this volume's cells have no geometry within X m" is exactly "N % of it became the
           * constant fill". The fit box is the union of the cells that survive that test; the
           * `fit.captures` column is one more compile per volume — pure arithmetic, throttled by
           * the panel like the marginal column — so the button's tooltip can promise a number
           * that the compile after the click will reproduce.
           *
           * `null` everywhere when there is no grid: an absent verdict must not read as 0 % air.
           */
          const adaptiveReport = s.report.adaptive;
          const { volumeOccupancy, surfaceOutsideBoxes } = await import(
            '../render/gi/occupancyGrid'
          );
          const { BRICK_N } = await import('../render/gi/probeBrick');
          const occupancyOf = (i: number) => {
            const v = volumes[i]!;
            if (!occupancy || !adaptiveReport) return null;
            const guardM = adaptiveReport.guardByLevelM[v.level] ?? adaptiveReport.surfaceGuardM;
            return volumeOccupancy(
              occupancy,
              v,
              activeWorld.lighting.gi.origin,
              baseSpacingM,
              guardM,
              BRICK_N,
            );
          };
          const perVolume = volumes.map((_, i) => occupancyOf(i));
          const airOf = (i: number): GiBakePlanVolume['air'] => {
            const o = perVolume[i];
            if (!o) return null;
            return {
              cells: o.cells,
              airCells: o.airCells,
              solidCells: o.solidCells,
              airPct: o.cells === 0 ? 0 : Math.round((1000 * o.airCells) / o.cells) / 10,
              guardM: o.guardM,
              cellM: o.cellM,
            };
          };
          const fitOf = (i: number): GiBakePlanVolume['fit'] => {
            const o = perVolume[i];
            if (!o) return null;
            if (o.fit === null) return { box: null, cells: 0, captures: null };
            let captures: number | null = null;
            try {
              captures = compile(
                volumes.map((v, j) =>
                  j === i ? { ...v, min: [...o.fit!.minM] as [number, number, number], max: [...o.fit!.maxM] as [number, number, number] } : v,
                ),
              ).captureCount;
            } catch {
              captures = null;
            }
            return {
              box: {
                minM: [...o.fit.minM] as [number, number, number],
                maxM: [...o.fit.maxM] as [number, number, number],
              },
              cells: o.fit.cells,
              captures,
            };
          };
          const adaptiveRequested = resolveGiAdaptive(activeWorld.lighting.gi, baseSpacingM).enabled;
          let adaptiveFacts: GiBakePlan['adaptive'] = null;
          if (adaptiveReport && occupancy) {
            // The SNAPPED boxes — the region the compile actually covered — not the authored ones.
            const snappedBoxes = volumes.map((v) => {
              const snap = s.report.snaps.find((x) => x.id === v.id);
              return {
                min: v.min.map((m, a) => m - (snap?.minSnapM[a] ?? 0)) as [number, number, number],
                max: v.max.map((m, a) => m + (snap?.maxSnapM[a] ?? 0)) as [number, number, number],
              };
            });
            const outside = surfaceOutsideBoxes(occupancy, snappedBoxes);
            adaptiveFacts = {
              requested: adaptiveRequested,
              enabled: true,
              voxelM: adaptiveReport.voxelM,
              surfaceGuardM: adaptiveReport.surfaceGuardM,
              guardByLevelM: [...adaptiveReport.guardByLevelM],
              occupancyHash: adaptiveReport.occupancyHash,
              capturesUniform: adaptiveReport.capturesUniform,
              bricksDroppedFar: adaptiveReport.bricksDroppedFar,
              bricksDroppedSealed: adaptiveReport.bricksDroppedSealed,
              surfaceOutsidePct:
                outside.surface === 0 ? 0 : Math.round((1000 * outside.outside) / outside.surface) / 10,
              note: giPlacementNote,
              offset: planOffset(s.report.offset),
            };
          } else if (adaptiveRequested) {
            // Requested and NOT done — the safety rail's arm, as data the panel prints in amber.
            adaptiveFacts = {
              requested: true,
              enabled: false,
              voxelM: 0,
              surfaceGuardM: 0,
              guardByLevelM: [],
              occupancyHash: '',
              capturesUniform: s.captureCount,
              bricksDroppedFar: 0,
              bricksDroppedSealed: 0,
              surfaceOutsidePct: 0,
              note: giPlacementNote,
              // No grid ⇒ no offset pass, by construction (`compileProbeVolumes` throws rather
              // than running one without occupancy). This is not "we did not look".
              offset: null,
            };
          } else {
            adaptiveFacts = {
              requested: false,
              enabled: false,
              voxelM: 0,
              surfaceGuardM: 0,
              guardByLevelM: [],
              occupancyHash: '',
              capturesUniform: s.captureCount,
              bricksDroppedFar: 0,
              bricksDroppedSealed: 0,
              surfaceOutsidePct: 0,
              note: null,
              offset: null,
            };
          }
          return {
            ...base,
            adaptive: adaptiveFacts,
            volumes: volumes.map((v, i) => ({
              id: v.id,
              level: v.level,
              spacingM: baseSpacingM * 2 ** v.level,
              sizeM: [v.max[0] - v.min[0], v.max[1] - v.min[1], v.max[2] - v.min[2]] as [
                number,
                number,
                number,
              ],
              /*
               * ⭐ TWO LITERAL COPIES, AND THEY ARE WHAT LET THE EDITOR BOOTSTRAP GI (B3).
               *
               * `sizeM` above is derived from exactly these two vectors, so carrying them costs
               * nothing and derives nothing new. What it buys: when `synthesised` is true this row
               * IS `probeVolume()`'s own box, so the panel's "Set up baked GI" button can AUTHOR
               * that box into `world.json` instead of re-deriving `drawnBounds` inside
               * `src/editor/` (a second answer to "what is this world's extent", which would agree
               * with the authored data by construction and disagree with the bake) or guessing a
               * box at the camera (worse — the artist would have to find it and fix it).
               */
              minM: [...v.min] as [number, number, number],
              maxM: [...v.max] as [number, number, number],
              // 0 on the synthesised box, the schema default (2) on an authored one. COPIED — see
              // `GiBakePlanVolume.expandBricks` for the 9.6 m the default would silently add.
              expandBricks: v.expandBricks,
              marginalCaptures: marginal(i),
              // `capturesByVolume` is in `volumes[]` order by construction (probeCompile §2.7).
              claimedCaptures: s.report.capturesByVolume[i]?.captures ?? 0,
              snapM: snapOf(v.id),
              air: airOf(i),
              fit: fitOf(i),
            })),
            structure: {
              captureCount: s.captureCount,
              brickCount: s.bricks.length,
              bricksByLevel: [...s.report.bricksByLevel],
              transitionBricks: s.report.transitionBricks,
              unattributedCaptures: s.report.unattributedCaptures,
              atlasBytes: s.report.atlasBytes,
              indirectionBytes: s.report.indirectionBytes,
              paddingRatio: s.report.paddingRatio,
              indirDims: [...s.indirDims] as [number, number, number],
              brickGrid: [...s.brickGrid] as [number, number, number],
            },
            refusal: null,
          };
        } catch (err) {
          /*
           * ⭐ THE REFUSAL IS DATA, NOT A CONSOLE LINE, AND IT DOES NOT THROW THROUGH THE CALLER.
           * The compiler refuses rather than coarsening (T29) and its message already names both
           * counts, every volume's contribution and the three ways out. The panel renders it in
           * red with the Bake button disabled; that is the whole point of computing a plan.
           */
          return {
            ...base,
            volumes: volumes.map((v) => ({
              id: v.id,
              level: v.level,
              spacingM: baseSpacingM * 2 ** v.level,
              sizeM: [v.max[0] - v.min[0], v.max[1] - v.min[1], v.max[2] - v.min[2]] as [
                number,
                number,
                number,
              ],
              // Carried even on a refusal: a REFUSED plan is exactly when an artist wants to see
              // and move the box, and the bootstrap button reads these two off `volumes[0]`.
              minM: [...v.min] as [number, number, number],
              maxM: [...v.max] as [number, number, number],
              expandBricks: v.expandBricks,
              marginalCaptures: 0,
              claimedCaptures: 0,
              snapM: 0,
              air: null,
              fit: null,
            })),
            structure: null,
            adaptive: null,
            refusal: String(err instanceof Error ? err.message : err),
          };
        }
      }
      return null;
    },
    onGiSettled(cb) {
      giSettledSubs.add(cb);
      return () => {
        giSettledSubs.delete(cb);
      };
    },
    async applyBakedLighting() {
      /*
       * ── ⭐ ROW 1 OF THE DECISION TABLE — nothing was asked for, so nothing is reported ─────────
       *
       * `gi()` stays NULL, which is documented as "the GI system was never asked for this session".
       * It is deliberately NOT `unbaked`: a world running on the constant fill by its own authored
       * choice has no bake to be missing, and a badge saying UNBAKED there would be a confident
       * wrong answer about a world nobody asked a question about.
       */
      if (giActivation.mode !== 'file') return;
      if (sparseVolume) return;
      /*
       * ⭐ AND THE VERDICT SETTLES HERE, ON EVERY ARM. `finally`, not a line at the end: the body
       * has three exits (tier refusal, a successful bind, the refuse-and-fall-back path) plus a
       * catch, and a notification that only fired on some of them would be exactly the "the flag
       * says one thing, the graph does another" shape this whole subsystem keeps failing on.
       */
      try {
        await giLoadVerdict();
      } finally {
        fireGiSettled();
      }
    },
    async reloadBakedLighting() {
      /*
       * ── ⭐ THE FIRST HALF OF THE OWNER'S SENTENCE, AND IT IS A RE-READ, NOT A RE-BAKE ───────
       *
       * Same three steps as the boot load — fetch, `checkProvenance`, bind — through the same two
       * functions, with exactly two differences: the fetch carries a cache bust, and the bind may
       * land on the LIVE light instead of a new one. Everything else, including every refusal arm
       * and every word of the provenance table, is shared with the path that runs at boot. If this
       * accepted a file the boot would refuse, the artist would see lighting in the editor that
       * vanishes on reload, which is the failure mode this whole subsystem was built to end.
       */
      const before = giInfo?.probes ?? 0;
      const no = (reason: string, detail: string): GiRebindResult => ({
        ok: false,
        reason,
        detail,
        probesBefore: before,
        probesAfter: before,
        how: 'none',
      });
      /*
       * ⚠️ ROW 1 AGAIN, AND IT IS THE SECOND-MOST-LIKELY REASON THE OWNER NEVER SAW A BAKE LAND.
       * A world whose `lighting.indirect` is `'fill'` bakes perfectly and then never asks for the
       * result: `applyBakedLighting` returns on this same test, in silence, with no fetch and no
       * badge. Here the answer has somewhere to go — the panel prints it under the run block — so
       * a bake that could never be seen says so at the moment the artist is looking for it.
       */
      if (giActivation.mode !== 'file') {
        return no(
          'not-file',
          `the indirect term in this session is the constant fill (gi mode '${giActivation.mode}'` +
            `, from ${giActivation.from}), so nothing loads a baked atlas. The bytes are on disk; ` +
            `set lighting.indirect to 'baked' (or boot with ?gi=file) to bind them.`,
        );
      }
      // ROW 2: a session fact, exactly as at boot. Baking works on the WebGL2 fallback; SEEING it
      // does not, and saying that here is cheaper than an artist inferring it from a still picture.
      if (giTierRefused()) {
        return no(
          'tier',
          `this session runs ${giFallbackBackend ? 'the WebGL2 fallback' : `tier ${tier}`}, which ` +
            `carries no probe volume. The bake is on disk and will bind on a WebGPU boot.`,
        );
      }
      const drawn = await giDrawnBox();
      /*
       * A REFUSAL MAY NEVER TAKE WORKING LIGHTING AWAY. With a volume bound, the refused file is
       * simply not adopted and the artist keeps looking at what they had — the runtime end of L1's
       * "no bake may overwrite a loadable atlas". With NOTHING bound, the badge is still reporting
       * the BOOT verdict, which is now out of date about a file that has just been rewritten, so
       * the report is rebuilt: the state moves to `stale`/`unbaked` naming THIS file's reason.
       */
      const refuse = (reason: string, detail: string): GiRebindResult => {
        if (sparseVolume === null) {
          giStale = { reason, detail };
          giReportNoFile(drawn.sizeM, drawn.centerM);
        }
        return no(reason, detail);
      };
      try {
        const { checkProvenance } = await import('../render/gi/probeFile');
        const { fetchProbeVolume } = await import('../render/gi/probeLoad');
        /*
         * ⭐ THE CACHE BUST, AND WHY IT IS UNCONDITIONAL. Measured on this dev server, the two
         * files come back `Cache-Control: no-cache` with a weak `size-mtime` ETag, so a plain
         * re-fetch would already revalidate and a re-bake would already invalidate. But this is the
         * one fetch in the codebase whose whole job is *"the bytes at this URL have just changed"*,
         * and it must also be right under `vite preview`, behind a proxy, and against the browser's
         * in-memory cache — none of which honour that header the same way. One query parameter
         * costs nothing and removes the entire class from the answer.
         */
        const fetched = await fetchProbeVolume(activeWorld.worldId, {
          bust: String(Date.now()),
        });
        if (!fetched.ok) return refuse(fetched.failure, fetched.detail);
        // The SAME live inputs the boot load compares against — one definition of "what this
        // session would compile", so a rebind and a reload can never disagree about one file.
        const verdict = checkProvenance(fetched.volume.header, await giLiveProvenance(drawn));
        if (!verdict.ok) {
          console.warn(
            `[gi] rebind REFUSED for '${activeWorld.worldId}': ${verdict.reason ?? 'unknown'}: ` +
              `${verdict.detail}. The volume bound in this tab is unchanged.`,
          );
          return refuse(verdict.reason ?? 'unknown', verdict.detail);
        }
        const how = await bindProbeVolume(fetched.volume, 'rebind');
        /*
         * ⭐ PREVIEW LIGHTING ENDS HERE, AND THIS IS THE ONE MOMENT THE BOUND ATLAS MOVES.
         *
         * The owner's clause is *"that light doesn't emit dynamic light once baked"*. A `static`
         * light's energy is in the bytes that just bound; leaving it parented as well would light
         * the room TWICE — the atlas's one-bounce indirect plus a full dynamic direct term — which
         * is precisely the double count `src/render/indirect.ts` is the law against.
         *
         * OFF is the safe direction and the only one this module may take: preview's verdict is the
         * editor's, and every input to it has just changed underneath. The editor re-asks on its
         * own next turn (`onGiRebound`), so if the world is STILL dirty the light comes straight
         * back — one extra cache-key move, on a path that has just spent minutes baking.
         *
         * No watcher, no poll: `bakeState.ts` limit 2 is untouched. This runs because a caller told
         * us the file moved, which is the same reason the re-read itself runs.
         */
        if (lightPreview) {
          lightPreview = false;
          worldLightRig.apply(activeLighting.lights, { previewStatic: false });
          bakeOnlyLights = worldLightRig.bakeOnlyLights();
          console.info(
            '[lights] static-light PREVIEW ended: a fresh atlas is bound, so these lights are ' +
              'lit by the probe field now. Their DIRECT term is gone — that is what a bake means ' +
              'here (no lightmaps).',
          );
        }
        // The file that was refused (or absent) at boot is not the file on disk any more.
        giStale = null;
        giRebinds += 1;
        return {
          ok: true,
          reason: null,
          detail: '',
          probesBefore: before,
          probesAfter: giInfo?.probes ?? 0,
          how,
        };
      } catch (err) {
        /*
         * `assertFits3D` is the one thing on this path that throws (a device 3D-texture ceiling
         * below the file's dimensions). It throws BEFORE the light is built, so a bound volume
         * survives it; nothing is torn down here for the same reason a refusal tears nothing down.
         */
        const detail = String(err instanceof Error ? err.message : err);
        console.warn(`[gi] rebind for '${activeWorld.worldId}' failed to bind:`, err);
        return refuse('threw', detail);
      }
    },
    async setProbeGrid(on) {
      if (!on) {
        // ONE implementation of "there is no volume any more" — see `giTeardown`.
        giTeardown();
        return;
      }
      // A bake photographs the scene graph; never the emptied room of a respawn in flight.
      if (entitiesSettling) await entitiesSettling;
      if (sparseVolume) return;
      /*
       * ── ⭐ EVERYTHING BELOW THIS LINE IS THE DEV AUTHORING PATH, AND IT FOLDS ────────────────
       *
       * The quality preset table, the hand-built sparse spike, the volume compiler, the capture
       * pass, the scatter and the readback are all reachable only through a `?gi=` value, which `resolveGiActivation` cannot return outside DEV. The gate is
       * a COMPILE-TIME literal rather than a runtime `let` so that Rollup DELETES the branch —
       * the same bar the mock Axon gateway and the dev hook are held to, and the difference
       * between shipping a loader and shipping a baker. `scripts/checkBundle.mjs` asserts it.
       *
       * A production build reaches the line after this block instead: the only thing this toggle
       * can mean there is "load this world’s atlas", which is `applyBakedLighting`.
       */
      if (import.meta.env.DEV) {
        giStale = null;
        giLoadedHeader = null;
        giLoadedVolume = null;
        giBakedStructure = null;
        giBakedAtlas = null;
        giBakeSettings = null;
        giDilation = null;
        /*
         * ══ ⭐ RESOLVE THE QUALITY PRESET FIRST — IT DECIDES WHAT EVERYTHING BELOW MEANS ═════════
         *
         * Before the tier gate, before the file fetch and before the compile, because
         * `spacingMultiplier` moves `giCompileSpacing()` and that is what `checkProvenance` compares
         * a file's `baseSpacingM` against. Resolving it after the fetch would load a file against
         * one spacing and bake against another.
         *
         * `resolveBakeQuality` is the SAME pure function `tools/bundler/bake-world.ts` calls with
         * the same inputs, so the CLI's printed table and the page's behaviour are one answer rather
         * than two (R2 §5b's `enabled: false` bug is what two answers look like). The six settings
         * below are ASSIGNED from it — the preset is not consulted again anywhere.
         */
        {
          const { resolveBakeQuality } = await import('../render/gi/bakeQuality');
          const q = resolveBakeQuality(giQualityName, giQualityRequest);
          giQuality = q;
          giSpacingMultiplier = q.spacingMultiplier;
          giCubemapSize = q.cubemapSize;
          giSampleCount = q.sampleCount;
          giBounces = q.bounces;
          giValidity = q.validity;
          giDilatePasses = q.dilationPasses;
          /*
           * ⚠️ SAID OUT LOUD WHENEVER THE CUBE IS NOT 32 (`sky.ts` trap 4). The bake hides the solar
           * disc, so a bigger cube is not a correctness risk — but the measurement that justified
           * the hide ("no change at all") was taken at 32, where the 1.07° disc is a fifth of a
           * texel. At another size that headroom is different, so a sun comparison does not transfer
           * between cube sizes. An undocumented limit is paid for by whoever meets it next.
           */
          if (q.cubemapSize !== GI_BAKE_CUBEMAP_SIZE) {
            console.info(
              `[gi] capture cube is ${q.cubemapSize}px, not the ${GI_BAKE_CUBEMAP_SIZE}px every ` +
                `sun/sky measurement was taken at (sky.ts trap 4). The solar disc is still hidden ` +
                `for the capture, so this is a resolution change, not a second sun — but no ` +
                `sun-related number from a 32px bake transfers to this atlas.`,
            );
          }
        }
        // TIER-0 GATE (G0) — ONE implementation, shared with the shipping load path. It refuses
        // both readings of tier 0: the real WebGL2 fallback backend, and a forced `?tier=0`.
        // A refusal here must come BEFORE any `await import('../render/gi/probeGrid')` — the
        // capture pass is the heaviest thing in this file and a software rasteriser cannot pay it.
        if (giTierRefused()) return;
        /*
         * ══ `?giSparseSpike=1` — THE HAND-BUILT FIXTURE, PUBLISHED (spec §9 · S1) ═══════════════
         *
         * FIRST, and it returns: this path neither loads nor bakes, so everything below it — the
         * scene bounds, `probeVolume`, the whole capture pass — is irrelevant to it. It is the
         * shortest possible route from a URL to the sparse graph reaching a shader, which is the
         * entire point of the stage: `sparseNode.setup()` had never run in this repo's history.
         *
         * Every byte comes from `render/gi/sparseSpike.ts`, the SAME pure module the headless
         * address test reads, so the GPU and the arithmetic cannot be asserting against two
         * different atlases. Its two bricks carry deliberately different constant SH colours and
         * the level-1 brick's two halves differ again, so an `ind.w` that never reached the address
         * chain reads as the WRONG COLOUR rather than as a plausible one.
         */
        /*
         * `import.meta.env.DEV &&` FIRST, and it is load-bearing rather than belt-and-braces:
         * `giSparseSpike` is a runtime `let`, so Rollup cannot fold this branch on it and would emit
         * `sparseSpike.ts` — the fixture bytes — as a chunk of every production build. The compile-
         * time flag is what makes the whole block DEAD CODE in `dist/`, which is the same mechanism
         * the mock Axon gateway uses and the same bar `checkBundle` holds the dev hook to.
         */
        if (import.meta.env.DEV && giSparseSpike) {
          const { SparseProbeVolume, ensureSparseVolumeLightNode, publishAtlas } =
            await import('../render/gi/sparseVolume');
          const { SparseProbeVolumeNode } = await import('../render/gi/sparseNode');
          const { SPIKE_GEOMETRY, spikeAtlas, spikeIndirection } =
            await import('../render/gi/sparseSpike');
          const { BRICK_PROBES, BRICK_TEXELS, BYTES_PER_TEXEL } =
            await import('../render/gi/probeBrick');
          /*
           * BEFORE the light can be in front of a shader compile — the G5 bug. A light three has no
           * node class for builds every lit shader WITHOUT the GI term, while every self-report
           * (the light is in the scene, the textures are uploaded, the atlas bytes are right) still
           * passes. `LightsNode` prints one line and moves on.
           */
          ensureSparseVolumeLightNode(renderer, SparseProbeVolumeNode);
          const light = new SparseProbeVolume();
          const atlas = spikeAtlas();
          publishAtlas(light, atlas, spikeIndirection(), SPIKE_GEOMETRY);
          light.setIntensity(giIntensity);
          sparseVolume = light;
          /*
           * The republish door. It reuses the SAME light and therefore the same two permanent
           * `Texture3DNode`s, which is exactly what §4.5 says must survive a structure change: the
           * clones `.sample()`/`.load()` left in the already-compiled graph follow `.value` through
           * `referenceNode`, so no material recompiles and `sparseSetupCount()` must not move.
           */
          giSpikeRepublish = (emptyTexelX: number | null): void => {
            publishAtlas(
              light,
              atlas,
              spikeIndirection(emptyTexelX === null ? {} : { emptyTexelX }),
              SPIKE_GEOMETRY,
            );
          };
          giCarriesIndirect = true;
          // The fixture photographed nothing, so it is not a skylight — the residual fill stays the
          // unlit one. `applyIndirect()` remains the only writer of `hemi.intensity`.
          giSkyBaked = false;
          applyIndirect();
          scene.add(light);
          const brickGrid = SPIKE_GEOMETRY.brickGrid;
          const bricks = brickGrid[0] * brickGrid[1] * brickGrid[2];
          // One indirection cell is `BRICK_N · baseSpacingM` metres — derived, never stored twice.
          const cellM = 4 * SPIKE_GEOMETRY.baseSpacingM;
          const sizeM = SPIKE_GEOMETRY.indirDims.map((n) => n * cellM) as [number, number, number];
          giInfo = {
            state: 'baked',
            on: true,
            resolution: [...SPIKE_GEOMETRY.indirDims] as [number, number, number],
            // The two REAL bricks; slot 0 is the reserved all-zero `EMPTY_BRICK` and is not a capture.
            probes: 2 * BRICK_PROBES,
            spacingM: [
              SPIKE_GEOMETRY.baseSpacingM,
              SPIKE_GEOMETRY.baseSpacingM,
              SPIKE_GEOMETRY.baseSpacingM,
            ],
            spacingRequestedM: SPIKE_GEOMETRY.baseSpacingM,
            structure: {
              brickCount: bricks,
              captureCount: 2 * BRICK_PROBES,
              // One level-0 brick and one level-1 brick — the `.w` ramp's whole reason to exist.
              bricksByLevel: [1, 1],
              transitionBricks: 0,
              indirDims: [...SPIKE_GEOMETRY.indirDims] as [number, number, number],
              brickGrid: [...brickGrid] as [number, number, number],
              indirectionBytes:
                SPIKE_GEOMETRY.indirDims[0] *
                SPIKE_GEOMETRY.indirDims[1] *
                SPIKE_GEOMETRY.indirDims[2] *
                BYTES_PER_TEXEL,
              atlasBytes: bricks * BRICK_TEXELS * BYTES_PER_TEXEL,
              paddingRatio: 1,
              normalBiasM: light.uniforms.normalBiasM.value as number,
            },
            intensity: giIntensity,
            sizeM,
            centerM: SPIKE_GEOMETRY.indirOriginM.map((v, i) => v + sizeM[i]! / 2) as [
              number,
              number,
              number,
            ],
            bakeMs: 0,
            bakeMsPerProbe: 0,
            bounces: 0,
            validity: true,
            // The fixture photographed nothing, so there is no capture to describe and no
            // preset it could have been made under. Zeros and null are the honest record.
            quality: null,
            cubemapSize: 0,
            sampleCount: 0,
            seamBreaks: 0,
            bakedLights: 0,
            bakedLightLeaks: 0,
            source: 'spike',
            stale: null,
            fileBytes: 0,
            dilation: null,
          };
          console.info(
            `[gi] SPARSE SPIKE published — ${bricks} bricks over ` +
              `${SPIKE_GEOMETRY.indirDims.join('x')} indirection cells, ` +
              `origin ${SPIKE_GEOMETRY.indirOriginM.join(',')}, cell ${cellM} m. ` +
              `Hand-built fixture (render/gi/sparseSpike.ts) — NOT a bake, NOT a file.`,
          );
          return;
        }
        try {
          const { drawnBounds, probeVolume } = await import('../render/gi/probeVolume');
          /*
           * THE VOLUME COMES FROM THE SCENE GRAPH, NOT FROM `activeWorld.boxes` (R2c).
           *
           * It used to come from the world JSON's COLLISION boxes, which is empty for any world
           * whose geometry is a glTF mesh — `sponza-debug-01` carries `"boxes": []`. The grid
           * collapsed to a 4 m cube at the origin and lit nothing, with no error anywhere. The
           * roadmap's trigger for this stage is "a world with an interior", and a world with an
           * interior is exactly the shape that has no boxes. Reasoning: `render/gi/probeVolume.ts`.
           *
           * World boxes are still UNIONED in, so a world whose collision volume is bigger than its
           * drawn geometry (a trigger region, an out-of-frustum platform) is not clipped away.
           */
          const bounds = drawnBounds(scene) ?? new THREE.Box3();
          const v = new THREE.Vector3();
          for (const b of activeWorld.boxes) {
            bounds.expandByPoint(
              v.set(b.pos[0] - b.size[0] / 2, b.pos[1] - b.size[1] / 2, b.pos[2] - b.size[2] / 2),
            );
            bounds.expandByPoint(
              v.set(b.pos[0] + b.size[0] / 2, b.pos[1] + b.size[1] / 2, b.pos[2] + b.size[2] / 2),
            );
          }
          if (bounds.isEmpty()) bounds.expandByPoint(v.set(0, 0, 0));
          /*
           * ⭐ THIS CAN NOW THROW, AND THAT IS THE FIX (T29). `probeVolume` used to multiply the
           * requested spacing by 1.15 until the count fitted and report `coarsened: true` — a field
           * that only ever saw the budget arm, never the per-axis caps, which is how "a 1 m bake"
           * was 3.1 m tall through four measurement rounds with every self-report saying 1 m. It
           * refuses now, with both counts and the spacing that would fit in the message, and the
           * `catch` at the bottom of this bracket turns that into `gi.state: 'failed'` with the
           * constant fill still carrying the indirect term. The world renders; the lie does not.
           */
          const vol = probeVolume(bounds, {
            // ⚠️ THE PRESET'S MULTIPLIER APPLIES HERE TOO. This box is the provenance bounds and the
            // synthesised volume's extent, and its probe count is what the budget rail is checked
            // against — a `preview` that budgeted the 1x lattice would refuse a bake it can afford.
            spacingM: giSpacingM * giSpacingMultiplier,
            padM: 2,
            floorY: GI_FLOOR_Y,
            maxProbes: giMaxProbes,
          });
          /*
           * The SAME box the shipping load path measures, derived from the box already walked above
           * rather than by a second traversal — one description of "what this scene draws".
           */
          const dc = bounds.getCenter(new THREE.Vector3());
          const dz = bounds.getSize(new THREE.Vector3());
          const drawn = {
            sizeM: [dz.x, dz.y, dz.z] as [number, number, number],
            centerM: [dc.x, dc.y, dc.z] as [number, number, number],
          };
          /*
           * ══ THE LOAD PATH (G5) ═══════════════════════════════════════════════════════════════
           *
           * Tried FIRST, and the in-browser bake below is now the fallback rather than the only
           * implementation. Three ways out of this block, and all three leave a renderable frame:
           *
           *   - a usable file  ⇒ the volume is loaded, `source: 'file'`, no bake, no frozen tab;
           *   - `?gi=file`     ⇒ no bake ever. Absent or stale ⇒ `source: 'none'`, the constant fill
           *                      keeps the indirect term, and the world looks exactly as it did
           *                      before any of this existed. This is the SHIPPING shape.
           *   - `?gi=probe`    ⇒ fall through to the bake, which is the dev/authoring shape.
           *
           * ⚠️ NOTHING IN HERE MAY THROW OR STALL A BOOT (D25). `fetchProbeVolume` resolves to a
           * verdict rather than rejecting, an absent file is not a warning, and the whole block is
           * inside the same `try` that already guards the bake.
           */
          const wantFile = giMode === 'file' || (giMode === 'auto' && !giBakeRequested);
          if (wantFile) {
            /*
             * ⭐ ONE LOAD PATH, CALLED FROM TWO PLACES. `loadBakedVolume` is the SHIPPING
             * implementation (`applyBakedLighting` is its eager, world-driven entry point); the
             * dev bake reaches the same function rather than carrying a second copy of fetch +
             * provenance + bind. Two answers to "is this file usable" is the exact shape of every
             * failure this subsystem has shipped.
             */
            if (await loadBakedVolume(drawn)) return;
            if (giMode === 'file') {
              /*
               * `?gi=file` NEVER BAKES. The volume is a build artefact; a runtime that quietly
               * baked one when the artefact is missing would hide a broken build behind a frozen
               * tab, and on a player's machine it would freeze the tab for minutes.
               */
              giAutoBake = false;
              giReportNoFile(drawn.sizeM, drawn.centerM);
              console.info(
                `[gi] ?gi=file and no usable baked volume for '${activeWorld.worldId}' ` +
                  `(${giStaleNow()?.reason ?? 'absent'}) — the constant fill carries the indirect term. ` +
                  `Nothing baked.`,
              );
              return;
            }
          }

          /*
           * ══ ⭐ THE SPARSE PRODUCER — `axon.probeVolume/2` IS BAKED HERE (spec §3) ═══════════════
           *
           * This is the link that did not exist, and whose absence made every `npm run bake:world`
           * end `readProbeAtlas returned null after a successful bake`. The format, the loader, the
           * provenance fences, the shader graph and the baker CLI all spoke v2; the only thing that
           * could PRODUCE v2 bytes was missing. Four steps, in order, and none of them optional:
           *
           *   compile  →  capture  →  scatter  →  publish
           *
           *   1. `compileProbeVolumes` turns the AUTHORED volumes (or one synthesised from the drawn
           *      bounds, §2.4) into ONE runtime structure: a brick set, an indirection texture and a
           *      DEDUPLICATED capture list.
           *   2. `bakeCapturePass` photographs that capture list into the 2D-tiled capture atlas.
           *   3. `scatterCaptures` copies capture → brick on the CPU. This is where two bricks that
           *      share a probe come to hold identical bytes, at a face and across a density change.
           *   4. `publishAtlas` points the light's two PERMANENT `Texture3DNode`s at the new
           *      textures. It is the SAME door the load path uses — one publish, one graph, and
           *      "load equals bake" is structural rather than tested (§3.6).
           *
           * ⚠️ AND IT IS NOW THE ONLY PRODUCER (F3, spec S5). The dense `LightProbeGrid` used to
           * live below this block behind `if (giMode !== 'dense')`; it was reachable only as
           * `?gi=dense`, which nothing routed to. It was kept so the G0–G4 numbers stayed
           * reproducible during the changeover — and that window closed when `axon.probeVolume/3`
           * bumped the format and refused every atlas baked before it, at which point no v1
           * comparison transferred anyway. Deleted with `probeGridNode`, the dense `repackNode`,
           * `probeRead`'s lattice readback and the v1 `ATLAS_PADDING`.
           */
          const { compileProbeVolumes, captureWorldPositions } =
            await import('../render/gi/probeCompile');
          const { describeOffsetReport } = await import('../render/gi/probeOffset');
          const { describeBakeQuality, resolveBakeQuality } =
            await import('../render/gi/bakeQuality');
          const { beginCapturePass, makeCaptureAtlas, makeFacingCache } =
            await import('../render/gi/probeGrid');
          // O2: the pass count is the bake's real unit of cost, and it is PURE arithmetic — the
          // same module that owns the batch layout owns it, so the cost report and the tests
          // cannot disagree about what a "pass" is.
          const { capturePassCount } = await import('../render/gi/probeChunk');
          const { scatterCaptures } = await import('../render/gi/probeScatter');
          const { dilateCaptures } = await import('../render/gi/probeDilate');
          const { readCaptureAtlas } = await import('../render/gi/probeRead');
          /*
           * `/5` — the bake shadow map's fit (PURE, no `three`), the atlas instrument that says
           * whether the tap actually ran, and the tap's constant tap count. Lazy for the reason
           * everything else in this block is: the GI chunk is a download only a world with a volume
           * pays for.
           */
          const { bakeShadowTexelM, capturesOutsideAuthoredBox, capturesOutsideBox, fitBakeShadowBox } =
            await import('../render/gi/bakeShadowFit');
          const { atlasSunVisibility } = await import('../render/gi/probeFile');
          const { SUN_VIS_TAPS } = await import('../render/gi/probeGrid');
          const { SparseProbeVolume, ensureSparseVolumeLightNode, publishAtlas } =
            await import('../render/gi/sparseVolume');
          const { SparseProbeVolumeNode } = await import('../render/gi/sparseNode');
          const { BRICK_N } = await import('../render/gi/probeBrick');

          const authored = giAuthoredVolumes();
          const baseSpacingM = giCompileSpacing();
          /*
           * §2.4 — THE DEGENERATE CASE. A world that authors nothing gets ONE volume covering the
           * drawn bounds at the requested spacing, i.e. exactly today's coverage, through the same
           * compiler, the same bake and the same shader. `expandBricks: 0` because `vol` has
           * already applied the lateral/vertical pads and the floor clamp, and the compiler snaps
           * outward to the brick lattice on top of that — a default expansion of 2 here would
           * silently grow a world's coverage by 9.6 m per face relative to v1.
           */
          const volumes =
            authored.length > 0
              ? authored
              : [
                  {
                    id: '__drawn',
                    min: vol.center.map((c, i) => c - vol.size[i]! / 2) as [number, number, number],
                    max: vol.center.map((c, i) => c + vol.size[i]! / 2) as [number, number, number],
                    level: 0,
                    expandBricks: 0,
                  },
                ];
          /*
           * ⭐ THIS THROWS RATHER THAN COARSENING, on either budget, and the `catch` at the bottom
           * of the bracket turns it into `gi.state: 'failed'` with the constant fill still carrying
           * the indirect term. The message names both counts and the per-volume attribution.
           */
          const structure = compileProbeVolumes({
            originM: activeWorld.lighting.gi.origin,
            baseSpacingM,
            volumes,
            fillEmptySpaces: activeWorld.lighting.gi.fillEmptySpaces,
            maxCaptures: giMaxProbes,
            maxAtlasBytes: GI_MAX_ATLAS_BYTES,
            /*
             * ⭐ S1. Undefined ⇒ the uniform grid, byte for byte. The same helper the PANEL priced
             * this bake with, so `✓ matches the plan` still means what it says — a bake that used
             * a different occupancy grid from the one the panel counted would fail that check with
             * no way to tell it from "you baked the file on disk, not this tab".
             */
            occupancy: await giOccupancyGrid(),
            surfaceGuardM: giAdaptive()?.surfaceGuardM,
            // ⭐ Virtual offset (`probeOffset.ts`). Default OFF and paired with a fine
            // `gi.adaptive.voxelM` — the compiler REFUSES a voxel coarser than
            // baseSpacingM/4 rather than relocating probes on a lattice too coarse to say
            // where the air is. The PLAN runs it too, deliberately: a panel that priced a
            // bake without the pass the bake then runs is the "two processes read one
            // world.json and disagreed" shape this subsystem keeps producing.
            virtualOffset: giAdaptive()?.virtualOffset,
          });
          for (const s of structure.report.snaps) {
            const moved = [...s.minSnapM, ...s.maxSnapM].some((m) => Math.abs(m) > 1e-6);
            if (moved) {
              console.info(
                `[gi] volume '${s.id}' snapped OUTWARD to the brick lattice by ` +
                  `min [${s.minSnapM.map((n) => n.toFixed(2)).join(',')}] m / ` +
                  `max [${s.maxSnapM.map((n) => n.toFixed(2)).join(',')}] m.`,
              );
            }
          }
          /*
           * The capture list is LATTICE INDICES from `indirOriginM`, not world positions — the
           * compiler is pure and holds no `Vector3`.
           *
           * ⭐ AND THE MULTIPLY IS NO LONGER HERE. It moved INTO the compiler as
           * `captureWorldPositions` when virtual offset landed, because the offset is added in that
           * one expression and a caller that built its own positions would silently photograph
           * every probe from its un-offset lattice point — the offsets computed, the atlas
           * bit-identical, every self-report green. That is this repo's signature failure and the
           * only structural defence against it is having exactly one door.
           */
          const positions = captureWorldPositions(structure);

          // FIRST, before the light can be in front of a shader compile — the G5 bug. A light three
          // has no node class for builds every lit shader WITHOUT the GI term while every
          // self-report still passes.
          ensureSparseVolumeLightNode(renderer, SparseProbeVolumeNode);
          const light = new SparseProbeVolume();
          light.setIntensity(giIntensity);
          light.setNormalBias(giNormalBiasM());
          /*
           * INVISIBLE FOR PASS 0, which is the direct-light pass. Not the G0 fence — a
           * `SparseProbeVolume` has no null-atlas state to cache, because both textures are bound
           * from its constructor over zero dummies (§4.5) — but the same SEMANTICS the dense path
           * had: pass 0 photographs the room lit by real lights only, and each later pass adds one
           * bounce by reading the atlas the previous pass published.
           */
          light.visible = false; // FWG_LIGHT_TOGGLE_OK: bake bracket, not per frame — pass 0 is direct-only
          scene.add(light);
          // Claim the slot NOW, not after the bake: `setProbeGrid(true)` called twice across the
          // two-frame yield below would otherwise start a second bake on a second light.
          sparseVolume = light;
          /*
           * THE VOLUME OWNS THE INDIRECT TERM FROM HERE, and it takes ownership BEFORE the bake for
           * the same reason the dense path does: pass 0 photographs the room as it is lit, so a
           * hemisphere still at its base value would be baked INTO the atlas as bounce and then
           * added a second time at runtime. See `src/render/indirect.ts`.
           */
          giCarriesIndirect = true;
          giSkyBaked = sky !== null && sky.mesh.visible;
          applyIndirect();

          const cellM = BRICK_N * baseSpacingM;
          const gridSize = structure.indirDims.map((n) => n * cellM) as [number, number, number];
          const gridCentre = structure.indirOriginM.map((v, i) => v + gridSize[i]! / 2) as [
            number,
            number,
            number,
          ];
          const structureInfo = {
            brickCount: structure.bricks.length,
            captureCount: structure.captureCount,
            bricksByLevel: structure.report.bricksByLevel,
            transitionBricks: structure.report.transitionBricks,
            indirDims: [...structure.indirDims] as [number, number, number],
            brickGrid: [...structure.brickGrid] as [number, number, number],
            indirectionBytes: structure.report.indirectionBytes,
            atlasBytes: structure.report.atlasBytes,
            paddingRatio: structure.report.paddingRatio,
            normalBiasM: giNormalBiasM(),
          };
          // PUBLISH THE INTENT BEFORE DOING IT — the bake is long synchronous turns, so nothing
          // outside can observe progress; what it CAN observe is that the trigger fired.
          giInfo = {
            state: 'baking',
            on: false,
            resolution: [...structure.indirDims] as [number, number, number],
            probes: structure.captureCount,
            spacingM: [baseSpacingM, baseSpacingM, baseSpacingM],
            spacingRequestedM: baseSpacingM,
            structure: structureInfo,
            intensity: giIntensity,
            sizeM: gridSize,
            centerM: gridCentre,
            bakeMs: 0,
            bakeMsPerProbe: 0,
            bounces: giBounces,
            validity: giValidity,
            quality: giQualityInfo(),
            cubemapSize: giCubemapSize,
            sampleCount: giSampleCount,
            seamBreaks: 0,
            bakedLights: 0,
            bakedLightLeaks: 0,
            source: 'bake',
            stale: giStale,
            fileBytes: 0,
            dilation: null,
          };
          /*
           * ══ ⭐ THE PROGRESS EMITTER (B2) — THE ONLY VOICE A FROZEN PAGE HAS ═══════════════════
           *
           * From the next `await` to `probe grid baked` this page is ONE SYNCHRONOUS JS TURN: no
           * rAF, no `setInterval`, no paint, no pointer event, and — the part that mattered — no
           * answer to `page.evaluate`. That is the owner's report in one sentence: a bake that
           * reported `state: "running"` for four and a half minutes with nothing to look at.
           *
           * ⭐ MEASURED, AND IT DECIDES THIS DESIGN (Playwright 1.62, `channel: 'chromium'`): a
           * `page.evaluate` that busy-spins 8 x 500 ms and `console.log`s between spins returns
           * after 4,008 ms — but its console lines arrive node-side at +516/+1016/…/+4017 ms, IN
           * REAL TIME, while the main thread is frozen solid. **The console channel is live during
           * a freeze; the round trip is not.** So progress is reported by ANNOUNCEMENT, exactly as
           * `probe grid baked` already is (the baker listens for that line, and racing it against a
           * poll was measured worth 370 seconds), and NO YIELD IS ADDED ANYWHERE — the freeze
           * invariant `bakeCapturePass` is built around is untouched.
           *
           * ⚠️ THE PREFIX IS A DUPLICATED LITERAL, DELIBERATELY. `scripts/bakeSupervisor.mjs` owns
           * the vocabulary (`BAKE_EVENT_PREFIX`, the phase table, the parser) and this is browser
           * code that cannot import a node script. So the page emits the MINIMUM — a phase and two
           * counts — and the baker's `normalizeBakeEvent` fills in the label, the index and the
           * opacity on arrival. `tests/devBakeProgress.test.ts` asserts this literal against the
           * supervisor's constant, so the one copied string cannot drift.
           *
           * It rides the DEV-only bake half of `setProbeGrid`, alongside the `fwg-gi-bake` marker
           * this same block prints, so a production build carries neither — asserted by
           * `checkBundle` (c5), not assumed.
           */
          const emitBakeEvent = (event: Record<string, unknown>): void => {
            console.info(
              `@@bake ${JSON.stringify({ v: 1, t: Date.now(), from: 'page', ...event })}`,
            );
          };
          /*
           * ⭐ THE EVENT THAT PUBLISHES EVERY DENOMINATOR. Until this line nothing outside the page
           * knows what a bake is going to cost — the baker holds a REQUEST (a world id and a preset)
           * and only the compiler holds the RESULT. `passes` is `bounces + 1` because a bounce pass
           * is a full second photograph of the whole capture list, not a cheap fix-up.
           */
          emitBakeEvent({
            ev: 'phase',
            phase: 'compile',
            detail:
              `${structure.captureCount} captures · ${structure.bricks.length} bricks · ` +
              `${giBounces + 1} pass(es)`,
            plan: {
              captures: structure.captureCount,
              bricks: structure.bricks.length,
              passes: giBounces + 1,
              /*
               * ⚠️ NOT `12 × (1 + bounces)` ANY MORE, AND NOT `6 × (validity ? 2 : 1)` EITHER.
               *
               * `bakeProbeRange` does TWO `cubeCamera.update()` calls per capture on a MEASURED
               * pass — the radiance photograph and the facing one — and ONE on a cached pass, so a
               * cached bake is `6 · (2 + bounces)` and the control arm is the old
               * `12 · (1 + bounces)`. Both are spelled here rather than one being assumed, because
               * this number is what the panel's `sceneRenderEstimate` is checked against and this
               * subsystem has shipped a 2×-wrong estimate TWICE (U1, and L1/L2 before it). The
               * panel prices the shipping arm; `?giFacingCache=0` is a URL-only measurement
               * control it cannot request, and this line prices the arm actually about to run.
               *
               * ⚠️ `validity` still gates only the MIX (`_validityMixUniform`), never a cube pass —
               * D1 §5's ruling is untouched, so `--no-validity` still costs what a validity bake
               * costs on whichever arm is running.
               */
              cubeFaceRenders: giFacingCache
                ? structure.captureCount * 6 * (giBounces + 2)
                : structure.captureCount * 12 * (giBounces + 1),
            },
          });
          /*
           * ⭐ THE RESOLVED PRESET, SAID OUT LOUD BEFORE THE MAIN THREAD STOPS. `describeBakeQuality`
           * prints the SIX VALUES, not the name — a name is a request and the values are the fact,
           * and this line is the artist's only chance to notice that `--quality production` did
           * not reach the page before spending several minutes of bake on it.
           */
          console.info(
            `[gi] quality ${describeBakeQuality(giQuality ?? resolveBakeQuality(giQualityName))}`,
          );
          console.info(
            `[gi] compiling ${volumes.length} volume(s) at level(s) ` +
              `${[...new Set(volumes.map((v) => v.level))].sort((a, b) => a - b).join('/')} → ` +
              `${structure.bricks.length} bricks (by level ` +
              `${structure.report.bricksByLevel.join('/')}, ` +
              `${structure.report.transitionBricks} transition, ` +
              `${structure.report.filledCells} filled cells) over ` +
              `${structure.indirDims.join('x')} indirection cells; baking ` +
              `${structure.captureCount} unique captures at ${baseSpacingM} m base spacing ` +
              `(requested and REALISED — nothing coarsens), ` +
              // Lever 1: 12 faces on the measured pass (6 radiance + 6 facing), 6 on each cached
              // bounce pass. The ARM is named beside the number so the log says which one ran —
              // nobody should have to infer it from a draw count.
              `${
                giFacingCache
                  ? structure.captureCount * 6 * (giBounces + 2)
                  : structure.captureCount * 12 * (giBounces + 1)
              } cube-face ` +
              `renders (facing ${giFacingCache ? 'CACHED across bounce passes' : 're-measured every pass'}), ` +
              `padding ratio ${structure.report.paddingRatio.toFixed(3)}. ` +
              `The main thread is about to stop.`,
          );
          /*
           * ⭐ ADAPTIVE PLACEMENT SAYS WHAT IT DID, INTO THE BAKER'S LOG (D2 — review item on
           * `CompileReport.adaptive`).
           *
           * `AdaptiveReport` has existed since S1 with a `capturesUniform` CONTROL ARM computed
           * inside the same compile — and **nothing anywhere read a single field of it.** A
           * predicate that DELETES BRICKS and reports its effect to nobody is the exact shape this
           * repo has thirteen logged instances of: it compiles, it passes, and the only observable
           * of a wrong voxelisation is a black corner somebody eventually notices.
           *
           * It goes here, on the console, rather than into the `@@bake` event's `plan`, and that is
           * deliberate: `plan` is a TYPED protocol whose fields the supervisor's reducer and the
           * panel's `✓ matches the plan` check both consume, and adding to it is a protocol change.
           * The human log is teed to `node_modules/.cache/fwg-bake/<jobId>.log`, folded into the
           * panel's transcript and printed by a standalone `npm run bake:world` — i.e. it reaches
           * every door the baker has, and it survives a dev server that was restarted mid-bake.
           *
           * The line leads with the CONTROL, because the saving is the only number that can lie:
           * `captureCount` alone cannot tell "adaptive removed 79 % of this bake" from "somebody
           * authored a smaller volume", and `capturesUniform` is that same authoring priced with
           * the occupancy grid taken away.
           */
          const adaptiveReport = structure.report.adaptive;
          if (adaptiveReport) {
            const uniform = adaptiveReport.capturesUniform;
            const removedPct =
              uniform > 0 ? (100 * (uniform - structure.captureCount)) / uniform : 0;
            console.info(
              `[gi] adaptive placement: ${structure.captureCount} captures against a uniform ` +
                `control of ${uniform} in this same compile — ${removedPct.toFixed(1)}% removed. ` +
                `${adaptiveReport.bricksDroppedFar} brick(s) dropped as FAR (no surface within ` +
                `${adaptiveReport.surfaceGuardM} m) and ${adaptiveReport.bricksDroppedSealed} as ` +
                `SEALED (entirely inside solid). Occupancy ${adaptiveReport.voxelM} m: ` +
                `${adaptiveReport.surfaceVoxels} surface / ${adaptiveReport.sealedVoxels} sealed ` +
                `of ${adaptiveReport.totalVoxels} voxels, hash ${adaptiveReport.occupancyHash} — ` +
                'that hash is the fingerprint of the MESH this atlas was placed against, so a ' +
                'later bake reporting a different one placed its bricks over different geometry.',
            );
            if (
              adaptiveReport.bricksDroppedFar === 0 &&
              adaptiveReport.bricksDroppedSealed === 0
            ) {
              // The "adaptive but inert" arm `AdaptiveReport`'s own docstring refuses to represent
              // as a field. It is not an error — it is a world where every brick is near geometry —
              // but it must not read as a saving that happened.
              console.warn(
                '[gi] adaptive placement dropped NOTHING: this bake is byte-identical to the ' +
                  'uniform one, at the cost of the voxelisation. Either the world is dense ' +
                  'everywhere, or gi.adaptive.voxelM/surfaceGuardM are too coarse to decide.',
              );
            }
          }
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

          /*
           * ⭐ FENCE 4 OF PREVIEW LIGHTING — PREVIEW IS FORCED OFF FOR THE WHOLE CAPTURE LOOP.
           *
           * A previewing `static` light is a RUNTIME light: parented, unmarked, and therefore not
           * in `bakeOnlyLights()` or `excludedFromBake()`. Photographing the scene in that state
           * would still produce a plausible atlas — which is exactly why this has to be a fence
           * and not a convention. Off here, unconditionally, BEFORE either list is read, so the
           * ordinary `BAKE_ONLY` parenting three blocks down works exactly as it does today; the
           * `finally` puts the editor's verdict back. A throw mid-bake restores it too.
           *
           * ⚠️ IT COSTS TWO CACHE-KEY MOVES on a world with a static light being previewed, i.e.
           * two full-scene WGSL regenerations per bake. That is the same price the `movable`
           * removal three blocks down already pays, on a path the owner has ruled is not to be
           * optimised, and it buys "the atlas is never a picture of a preview".
           */
          const previewDuringBake = lightPreview;
          if (previewDuringBake) {
            lightPreview = false;
            worldLightRig.apply(activeLighting.lights, { previewStatic: false });
            console.info(
              '[lights] static-light PREVIEW switched off for the bake — the capture loop parents ' +
                'these lights through the BAKE_ONLY bracket instead, and preview comes back after.',
            );
          }
          // BAKE-ONLY LIGHTS (G3.2) — marked here, one turn before the bake, removed in the
          // `finally`, exactly as the dense path does. The invariant "a baked light is never also
          // a live light" has to survive a throw as well as a success.
          bakeOnlyLights = [];
          /*
           * ⭐ THE OTHER HALF OF MOBILITY (E2) — `movable` LIGHTS COME **OUT** FOR THE BAKE.
           *
           * `bakeCapturePass` photographs the SCENE GRAPH, so a light that is in the scene is in
           * the bake unless something takes it out. Adding the `static` lights without removing
           * the `movable` ones makes `movable` and `stationary` produce identical atlases, and
           * `movable`'s whole meaning is *"the atlas is unaffected because its light was never in
           * it"*. Measured before it was fixed: `stationary − movable` read **−0.02** at a wall
           * where `static − none` read **+4.01**.
           *
           * The removal changes `LightsNode`'s cache key and therefore rebuilds every material's
           * WGSL, twice per bake. That is a bake-time cost on a path the owner has ruled is not to
           * be optimised, and it is the price of `movable` meaning what it says.
           */
          const excludedFromBake = worldLightRig.excludedFromBake();
          for (const l of excludedFromBake) scene.remove(l);
          if (excludedFromBake.length > 0) {
            console.info(
              `[lights] ${excludedFromBake.length} movable light(s) REMOVED for the bake — their ` +
                'light is not in the atlas, which is what `movable` means.',
            );
          }
          /*
           * ⭐ THE WORLD'S `static` LIGHTS (E2) — PARENTED FOR THE BAKE, REMOVED IN THE `finally`.
           *
           * This is the whole static-light model on this stack, in four lines: the light is built
           * at scene construction and left UNPARENTED, so it costs nothing and is not in
           * `LightsNode`'s cache key; here it joins the scene so `bakeCapturePass`'s cube renders
           * photograph the surfaces it lights; the `finally` takes it out again. Its energy is in
           * the SH from that point on and nowhere else. `src/render/indirect.ts` is the law and
           * `GiInfo.bakedLightLeaks` is the fence.
           */
          for (const l of worldLightRig.bakeOnlyLights()) {
            scene.add(l);
            bakeOnlyLights.push(l);
          }
          if (bakeOnlyLights.length > 0) {
            // Said out loud, at full precision, because it is the ONE observable that separates
            // "the static light was in the bake" from "the atlas is dark for some other reason".
            console.info(
              `[lights] ${bakeOnlyLights.length} static light(s) parented for the bake: ` +
                bakeOnlyLights
                  .map(
                    (l) =>
                      `${l.name} at ${l.position.x.toFixed(1)},${l.position.y.toFixed(1)},` +
                      `${l.position.z.toFixed(1)} intensity ${l.intensity}`,
                  )
                  .join(' · '),
            );
          }
          if (giBakeLights) {
            scene.traverse((o) => {
              const l = o as THREE.Light & {
                isPointLight?: boolean;
                isSpotLight?: boolean;
              };
              if (l.isLight === true && (l.isPointLight === true || l.isSpotLight === true)) {
                // ⚠️ DEDUPE against the world's own `static` lights, just parented above: a light
                // pushed twice would be counted twice by `GiInfo.bakedLights` and removed twice by
                // the `finally` — the second remove is harmless, the wrong count is not.
                // `?bakeLights=1` is a DEV OVERRIDE and deliberately outranks the data, so a
                // `movable` world light IS swept into the bake by it (the same way `?sun=` outranks
                // `lighting.sun.intensity`); the atlas header records the rig it was baked under.
                if (bakeOnlyLights.includes(l)) return;
                l.userData[BAKE_ONLY] = true;
                bakeOnlyLights.push(l);
              }
            });
            console.info(
              `[gi] ${bakeOnlyLights.length} punctual light(s) marked BAKE-ONLY — baked into the ` +
                `volume, then removed from the scene (static-light model).`,
            );
          }
          if (sky && !bakeSunDisc) sky.setSunDiscVisible(false);
          const halfDiagonal = Math.hypot(gridSize[0], gridSize[1], gridSize[2]) / 2;
          const bakeFar = sky ? bakeFarForSky(sky.info().scaleM, halfDiagonal) : 100;
          giLastBakeFarM = bakeFar;
          // The frame-time instrument must not measure the bake — see the dense path's note: the
          // timestamp query pool is 2,048 and a bake is hundreds of thousands of passes.
          const bakeBackend = renderer.backend as unknown as {
            trackTimestamp?: boolean;
          };
          const hadTrackTimestamp = bakeBackend.trackTimestamp === true;
          bakeBackend.trackTimestamp = false;
          /*
           * ── ⭐ B3 · THE BAKE DOES NOT RUN UNDER CASCADES ─────────────────────────────────────
           *
           * The cascades are fitted to THIS camera's frustum and the term is LIT outside every
           * cascade, so a cube capture 40 m away would photograph the sun through walls — an
           * atlas of a different room with every check green. And `freezeSceneForBake` cannot
           * freeze the cascade objects (they are not `isLight`), so their depth passes would
           * re-render on every capture. `detachForBake` puts `applySun`'s single ±extent map back
           * for the bracket; the light node re-reads `shadow.shadowNode` only through a
           * `castShadow` cycle with a build inside it, which is what the render callback is. The
           * restore is in the `finally`. Two rebuilds each way, against minutes of bake.
           */
          const restoreCsm = csmRig.detachForBake(() => renderer.render(scene, camera));
          /*
           * ══ ⭐ `/5` · FIT THE BAKE SHADOW MAP TO THE PROBES, THEN PRIME IT ═══════════════════
           *
           * Two steps that must happen in THIS order, and the order is the deliverable.
           *
           * **STEP 1 — FIT.** `detachForBake` restored `applySun`'s ±`shadowExtentM` box about the
           * WORLD ORIGIN, and that box is AUTHORED (Sponza: 30 m). Measured on the owner's real
           * 51,009-capture list through a matrix built exactly as `applySun` +
           * `LightShadow.updateMatrices` build it: **5,297 probes (10.38 %) fall OUTSIDE it** —
           * 5,087 in light-space y, 210 in x, none in depth — and r185's
           * `ShadowNode.setupShadowFilter` returns `float(1)` outside the frustum, so every one of
           * them would BAKE "the sun reaches me" as a fact, whatever marble stands in front of it.
           * A silently wrong visibility term is worse than none: it is baked in and looks
           * deliberate. `fitBakeShadowBox` (pure) fits the four ortho planes ASYMMETRICALLY to the
           * capture list's own light-space AABB, which costs almost nothing — 2.89 x 3.55 cm texels
           * against the authored box's 2.93 — because three pins the camera AT the light but
           * `OrthographicCamera` takes four independent planes and `applySun` already writes all
           * four. All four are `LIGHT_KEY_FREE_FIELDS`-class: no `castShadow` cycle, no pipeline
           * move, `csmRig.state().keyCycles` unmoved. Restored in the `finally`, beside the CSM.
           *
           * ⚠️ It fits the CAPTURES and not the indirection grid's corners: the captures are the
           * points that will be tapped, and fitting the grid box instead needs 48.93 m on Sponza —
           * texel density spent on empty corners nothing photographs. And it is DERIVED rather than
           * authored, because `WorldSunSchema` is hashed: writing 45 into `world.json` would move
           * `worldHash`, refuse the atlas, cost a SECOND re-bake, and move the `sponza-debug-01`
           * pin in `tests/renderLightRig.test.ts` that is held against his real `gi/probes.json`.
           *
           * **STEP 2 — PRIME.** `freezeSceneForBake` only sets `autoUpdate = false; needsUpdate =
           * true`; the render target itself is created lazily inside `ShadowNode.setupShadow` on
           * the first material build that has a receiver, and `detachForBake` returns with
           * `castShadow` true but NO render since. So `sun.shadow.map` is still null here and a tap
           * built now would be built over nothing. One full-scene render fixes it (r185 assigns
           * `this.shadow.map = shadowMap` at `ShadowNode.js:585`), against a bake measured in
           * minutes. It must come AFTER the fit, or the map is rendered for the box we replace.
           */
          const bakeShadowBox = fitBakeShadowBox(
            positions,
            structure.captureCount,
            [sun.position.x, sun.position.y, sun.position.z],
            [0, 0, 0], // `sun.target` is never added to the scene, so it sits at the world origin
            giCompileSpacing(),
          );
          const authoredExtentM = activeLighting.sun.shadowExtentM;
          const bakeShadowCam = sun.shadow.camera;
          const restoreBakeShadowBox = ((): (() => void) => {
            if (bakeShadowBox.captures === 0) return () => {};
            const prev = {
              left: bakeShadowCam.left,
              right: bakeShadowCam.right,
              bottom: bakeShadowCam.bottom,
              top: bakeShadowCam.top,
              near: bakeShadowCam.near,
              far: bakeShadowCam.far,
            };
            bakeShadowCam.left = bakeShadowBox.left;
            bakeShadowCam.right = bakeShadowBox.right;
            bakeShadowCam.bottom = bakeShadowBox.bottom;
            bakeShadowCam.top = bakeShadowBox.top;
            bakeShadowCam.near = bakeShadowBox.near;
            bakeShadowCam.far = bakeShadowBox.far;
            bakeShadowCam.updateProjectionMatrix();
            return () => {
              bakeShadowCam.left = prev.left;
              bakeShadowCam.right = prev.right;
              bakeShadowCam.bottom = prev.bottom;
              bakeShadowCam.top = prev.top;
              bakeShadowCam.near = prev.near;
              bakeShadowCam.far = prev.far;
              bakeShadowCam.updateProjectionMatrix();
            };
          })();
          // STEP 2 — the priming render. Builds `sun.shadow.map` for the box just written.
          renderer.render(scene, camera);
          /*
           * ⭐ THE TAP, OR AN HONEST `null`. Five ways it cannot run, each of which must produce
           * `sunVisibility.baked: false` and a constant 1.0 rather than a plausible wrong number:
           * the sun does not cast, the renderer's shadow map is off, the map was still not built
           * (no receiver in the scene at all), there are no captures, or the shadow type is VSM —
           * under which `ShadowNode.setupShadow` sets `depthTexture.compareFunction = null`
           * (`ShadowNode.js:445`) and `.compare()` stops being a comparison and becomes a raw depth
           * read: a plausible number in [0,1] that is NOT visibility. This project never sets
           * `renderer.shadowMap.type`, so it is `PCFShadowMap` today and the compare sampler IS
           * configured (`ShadowNode.js:398`) — asserted rather than assumed, because taping over it
           * would bake the one failure nobody can see.
           */
          const bakeSunDepth =
            (sun.shadow as { map?: { depthTexture?: THREE.DepthTexture } }).map?.depthTexture ??
            null;
          const bakeSunTapOk =
            sun.castShadow === true &&
            renderer.shadowMap.enabled === true &&
            renderer.shadowMap.type !== THREE.VSMShadowMap &&
            bakeSunDepth !== null &&
            bakeShadowBox.captures > 0;
          /*
           * The filter radius is WORLD-SPACE and DERIVED — half a probe spacing — so the stored
           * value is "the fraction of the sun visible in a 0.6 m ball around this probe": a real
           * quantity, independent of the map's resolution, pre-filtered at the lattice's own
           * Nyquist. NOT a flag: a tap count or a radius changes what the FILE IS, not what the
           * bake COSTS, so it is fenced by `bakerVersion` (L1's ruling) and recorded in the header.
           */
          const bakeSunRadiusM = 0.5 * giCompileSpacing();
          const bakeSunTap = bakeSunTapOk
            ? {
                light: sun,
                depthTexture: bakeSunDepth!,
                boxSizeM: [
                  bakeShadowBox.right - bakeShadowBox.left,
                  bakeShadowBox.top - bakeShadowBox.bottom,
                ] as [number, number],
                radiusM: bakeSunRadiusM,
              }
            : null;
          /*
           * ⭐ COUNTED AGAINST THE FOUR PLANES `sun.shadow.camera` IS HOLDING RIGHT NOW — READ BACK
           * AFTER THE PRIMING RENDER, NOT RESTATED FROM THE FIT.
           *
           * ⚠️ The first version of this read `bakeShadowBox.captures > 0 ? 0 : capturesOutside…`,
           * and that expression is the CONSTANT 0 for every possible input: `fitBakeShadowBox` and
           * `capturesOutsideAuthoredBox` derive the same `n` from the same two arguments, so the
           * else-branch is reached only when it has nothing to count. `bake-world.ts` then refused
           * on a field nothing could make say YES — a predicate with no positive arm, shipped
           * inside the change whose whole subject is predicates with no positive arm.
           *
           * Reading the CAMERA closes a real hole rather than an argued one. `restoreBakeShadowBox`
           * is a closure whose `finally` runs at the bottom of the bracket; move it, or let any
           * later edit render this map for the authored box, and the tap samples a ±30 m frustum
           * while the header still says `fitted`. That is 5,297 of the owner's 51,009 probes
           * fabricating "the sun reaches me" (r185 `setupShadowFilter` returns `float(1)` outside a
           * frustum) with nothing else in the system able to tell. Three's `LightShadow.update‐
           * Matrices` never touches an ortho camera's four planes, so what is on the camera here is
           * what the map was rendered with.
           */
          const bakeSunCamBox = {
            left: bakeShadowCam.left,
            right: bakeShadowCam.right,
            bottom: bakeShadowCam.bottom,
            top: bakeShadowCam.top,
          };
          const bakeSunOutside = capturesOutsideBox(
            positions,
            structure.captureCount,
            [sun.position.x, sun.position.y, sun.position.z],
            [0, 0, 0], // `sun.target` is never added to the scene, so it sits at the world origin
            bakeSunCamBox,
          ).outside;
          /*
           * ⭐ WHAT THE AUTHORED BOX WOULD HAVE MISSED — the RISK-2 number, PRINTED, not merely
           * argued in a comment. It is the only line that tells the owner what the fit BOUGHT on
           * HIS world (5,297 of 51,009 when it was measured), and it is the control arm for the
           * fence above: `outsideCaptures` counts the box we installed, this counts the box we
           * replaced, and a bake where the two are equal is a bake whose fit did not take.
           * Not a header field — it is a statement about a box this file no longer installs.
           */
          const bakeSunAuthoredMiss = capturesOutsideAuthoredBox(
            positions,
            structure.captureCount,
            [sun.position.x, sun.position.y, sun.position.z],
            [0, 0, 0],
            authoredExtentM,
          );
          giSunVisFit = {
            baked: bakeSunTapOk,
            extentM: [
              bakeShadowBox.left,
              bakeShadowBox.right,
              bakeShadowBox.bottom,
              bakeShadowBox.top,
            ],
            fitted: bakeShadowBox.captures > 0,
            authoredExtentM,
            mapSize: sun.shadow.mapSize.width,
            texelM: bakeShadowTexelM(bakeShadowBox, sun.shadow.mapSize.width),
            radiusM: bakeSunRadiusM,
            outsideCaptures: bakeSunOutside,
          };
          console.info(
            `[gi][bake] sunShadow: single map (${csmRig.policy().enabled ? 'cascades detached for the bake' : 'no cascades this session'})` +
              ` · authored extent ${authoredExtentM} m · ` +
              (bakeShadowBox.captures > 0
                ? `FITTED [${bakeShadowBox.left.toFixed(2)}, ${bakeShadowBox.right.toFixed(2)}] x ` +
                  `[${bakeShadowBox.bottom.toFixed(2)}, ${bakeShadowBox.top.toFixed(2)}] m at ` +
                  `${sun.shadow.mapSize.width}² (` +
                  `${(giSunVisFit.texelM[0] * 100).toFixed(2)} x ` +
                  `${(giSunVisFit.texelM[1] * 100).toFixed(2)} cm/texel), covers ` +
                  // MEASURED against the camera's own four planes, never assumed from the fit.
                  `${bakeSunAuthoredMiss.total - bakeSunOutside}/${bakeSunAuthoredMiss.total} captures` +
                  ` (the authored ±${authoredExtentM} m box would have missed ` +
                  `${bakeSunAuthoredMiss.outside})`
                : 'NOT fitted (no captures)') +
              ` · sunVisibility ${bakeSunTapOk ? `tapped (${SUN_VIS_TAPS} taps, r ${bakeSunRadiusM.toFixed(2)} m)` : 'NOT baked — band 7 is a constant 1.0'}` +
              ` · castShadow ${sun.castShadow}`,
          );
          /*
           * ── ⭐ WHICH DEVICE ACTUALLY DID THIS — RECORDED, NOT ASSUMED ────────────────────────
           *
           * The baker launches with `--enable-unsafe-swiftshader` so a box with no usable adapter
           * can still bake, which makes the run line's `backend WebGPU` EQUALLY TRUE OF A SOFTWARE
           * RASTERISER. Every conclusion the `[gi][bake-cost]` split below supports is a statement
           * about a CPU-bound bake only if the device was not itself a CPU: measured on the owner's
           * 52,149-capture Sponza run, SUBMIT was 97.16 % of `bakeMs` and the device's residual
           * 2.80 % — an answer that means opposite things on hardware and on SwiftShader, and the
           * log had no record either way (docs/ai/plans/gi-objectives.md §6.5.8).
           *
           * `GPUDevice.adapterInfo` is the only place it can be read: three requests the adapter in
           * `WebGPUBackend.init()` and does not retain it. Absent or masked ⇒ SAY SO — a guessed
           * device name is worse than none, because the next person would plan against it.
           */
          const bakeAdapterInfo = (
            renderer.backend as unknown as {
              device?: { adapterInfo?: Record<string, unknown> };
            }
          ).device?.adapterInfo;
          const bakeAdapterName = ['vendor', 'architecture', 'device', 'description']
            .map((k) => bakeAdapterInfo?.[k])
            .filter((v): v is string => typeof v === 'string' && v.length > 0)
            .join(' · ');
          console.info(
            `[gi][bake] device: ${
              bakeAdapterName ||
              (bakeAdapterInfo
                ? 'GPUDevice.adapterInfo is present but empty (Chromium masks it on some boxes)'
                : 'unknown — GPUDevice.adapterInfo is unavailable here')
            } — the baker launches with --enable-unsafe-swiftshader, so "backend WebGPU" alone ` +
              `does not mean a hardware GPU, and the bake-cost split below reads differently if it ` +
              `is a software rasteriser.`,
          );
          const captureAtlas = makeCaptureAtlas(renderer, structure.captureCount);
          /*
           * ⭐ LEVER 1 · THE FACING STORE — `[128 x ceil(captures/128)]`, 817 KB on the owner's
           * Sponza. Allocated HERE beside the capture atlas and disposed in the SAME `finally`, for
           * the T27 reason that one is: its size is a pure function of the capture count, so a
           * pooled target sized for one bake is a second bake writing into the wrong shape and
           * reporting success. Allocated on BOTH arms — `?giFacingCache=0` re-measures and
           * re-stores rather than reading column 9, so that the repack stays ONE program.
           */
          const facingCache = makeFacingCache(renderer, structure.captureCount);
          const t0 = performance.now();
          try {
            /*
             * ── JACOBI, WITH THE CAPTURE ATLAS AS THE SECOND BUFFER (spec §3.5) ────────────────
             *
             * Nothing written during pass `p` is read during pass `p`. The captures land in the
             * capture atlas; the runtime atlas the bounce passes SAMPLE is replaced only between
             * passes, after a full readback and scatter. So the result does not depend on chunk
             * order, and no scratch atlas and no ping-pong parity are needed — the dense path
             * needed both.
             */
            /*
             * ── ⭐ THE ONE PHASE WITH A TRUE DENOMINATOR (B2 §4.2) ─────────────────────────────
             *
             * `capture` is ~90 % of the wall clock and it is a real loop over a real list, so its
             * fraction is a FACT rather than a feeling — which is what licenses a bar over it at
             * all. The counter is GLOBAL across bounce passes (`pass * chunks + chunk`), because
             * "chunk 44 of 71" resetting to 1 halfway through a two-bounce bake reads as a restart.
             *
             * ⚠️ THROTTLED ON THE PAGE, NOT ON THE READER. A Sponza `medium` bake is 71 chunks and
             * a Mars-sized one is thousands; unthrottled this would put a line on the pipe every
             * few hundred ms for six minutes, and the dev server's 400-line ring buffer would hold
             * nothing but progress. 1.5 s is below the threshold at which a human calls a UI stuck
             * and far above the cost of one `JSON.stringify`. The FIRST and LAST chunk of every
             * pass are always emitted, so the bar starts moving immediately and always lands on a
             * pass boundary rather than stopping at 68/71.
             */
            const passes = giBounces + 1;
            let lastEmitMs = 0;
            for (let pass = 0; pass <= giBounces; pass++) {
              light.visible = pass > 0; // FWG_LIGHT_TOGGLE_OK: once per bounce PASS in the bake, not per frame
              /*
               * ⭐ LEVER 1 · WHICH ARM THIS PASS RUNS, AND `light.visible` ON THE LINE ABOVE IS
               * EXACTLY WHY IT IS SAFE.
               *
               * That toggle is the ONLY difference in the scene between two passes. It cannot reach
               * the facing capture: `_facingMaterial` is a bare `NodeMaterial`, which leaves
               * `lights = false` and `lightsNode = null`, so `setupLighting` puts no `LightsNode`
               * into the graph at all and the program does not depend on the scene's light set.
               * Everything else the facing cube and the validity quad read is frozen for the whole
               * bake — world matrices (`freezeSceneForBake`), the override material (a module
               * singleton), the cube camera's near/far, the sun's shadow matrix (installed by
               * `restoreBakeShadowBox`'s closure BEFORE this loop) and the tap's radius. So a bounce
               * pass would recompute a value pass 0 already holds.
               *
               * ⚠️ THE PROOF THAT THE LICENCE STILL HOLDS IS A TEST, NOT THIS PARAGRAPH:
               * `tests/renderGiFacingCache.test.ts` drives `makeFacingMaterial()` and asserts
               * `lights === false` / `lightsNode === null` / `outputNode !== null`, because the
               * moment someone lights that material every cached pass is silently wrong and the
               * only observable is the picture.
               */
              const facingArm: 'measure' | 'cached' =
                giFacingCache && pass > 0 ? 'cached' : 'measure';
              /*
               * ══ ⭐ O2 · STAGE A — THE SUBMISSION WINDOW IS THE CHUNK, NOT THE PASS ════════════
               *
               * The chunk loop lives HERE, beside the per-pass yield that already existed, rather
               * than inside `bakeCapturePass` — whose no-yield invariant is load-bearing and
               * documented at its own site. `beginCapturePass` opens the freeze; `captureChunk` is
               * still one synchronous turn; the `await` between chunks is the only new behaviour.
               *
               * ── WHY AN AWAIT IS THE FIX, WHEN EVERY INSTINCT SAYS IT IS THE BUG ──────────────
               *
               * MEASURED, six bakes of `playground-01` at 1,445 captures / 22,539 passes, varying
               * only the window: one window per pass costs 5,148 ms of SUBMIT+DRAIN, and any
               * segmentation costs ~3,265 ms — **1.58x, saturating by a quarter of a pass.** The
               * full table and the mechanism (it is submission that halves, not the drain that
               * shrinks) are in `probeGrid.ts`'s session header, beside the prediction it refutes.
               *
               * ⚠️ THE PLAN PREDICTED 42x FROM A `passes²` DRAIN AND THAT MODEL DID NOT SURVIVE
               * CONTACT. 11 segment drains sum to 1,793 ms against one tail drain of 1,982 ms —
               * the device work is identical, merely paid in instalments. Nor does 1.58x explain
               * the owner's 15,393 s gap, which is ~270x beyond anything reproducible here. So
               * `?giDrainChunks=0` reproduces the OLD behaviour exactly and permanently: a
               * scheduling change that cannot be switched off cannot be measured, and the machine
               * that has the pathology is not this one.
               *
               * ⚠️ IT IS NOT BYTE-NEUTRAL, AND THE REASON IS NOT THIS CHANGE. The plan's
               * acceptance fence was "both arms print the same `payload <hash>`". **That fence
               * does not exist**: two bakes at the SAME setting differ here — 2.29 % of atlas
               * halves, 0.197 % relative on the SH bands. The bake was never byte-reproducible on
               * this machine, and `--repeat`'s DETERMINISM line says so out loud when asked.
               * Measured against that floor, draining is the MORE reproducible arm by a wide
               * margin: two drained bakes agree to 0.005 % on the SH bands, forty times tighter
               * than two undrained ones. Cross-arm is 0.38–0.45 %, about twice the undrained
               * arm's own noise. Every arm reported `lit 100.0 %` and `mean |L0| 0.243`.
               *
               * The knob is still NOT in the header and NOT in `checkProvenance`: it changes what
               * a bake COSTS, and a scheduling knob in the provenance fingerprint would refuse
               * atlases across a scheduling change.
               */
              const tSubmit0 = performance.now();
              /*
               * ⚠️ THE DRAW COUNT IS ACCUMULATED PER CHUNK, AND THE FIRST VERSION OF THIS LINE WAS
               * WRONG IN A WAY THE MEASUREMENT ITSELF CAUSED. `renderer.info` is reset once per
               * ANIMATION FRAME — `Animation.js:75`, `if ( this.info.autoReset === true )
               * this.info.reset()` — not once per render. Before stage A a bounce pass was one
               * synchronous turn with no rAF in it, so a single before/after subtraction over the
               * whole pass was correct by accident. With a yield at every chunk boundary the
               * counter is reset ~250 times mid-pass and the same subtraction reported 3,914 draws
               * where the un-yielded arm reported 153,917 — a 39x under-count, and one that would
               * have read as "merging the scene already happened".
               *
               * A chunk contains no yield, so a delta across ONE chunk is exact; summing them is
               * the only form of this measurement that survives its own fix.
               */
              let drawCount = 0;
              /*
               * The device queue, reached through the backend. `onSubmittedWorkDone` is the only
               * WebGPU call that answers "is the queue empty" — it is the drain, and it is also
               * the instrument that measures the drain. One line, two jobs, and deliberately so:
               * an instrument that is not the mechanism is an instrument that can drift from it.
               * Absent on a backend that does not expose it (WebGL2), where this degrades to the
               * old single-window behaviour rather than throwing.
               */
              const bakeQueue = (
                renderer.backend as unknown as {
                  device?: { queue?: { onSubmittedWorkDone?: () => Promise<void> } };
                }
              ).device?.queue;
              const session = beginCapturePass(
                renderer,
                scene,
                positions,
                structure.captureCount,
                captureAtlas,
                facingCache,
                {
                  cubemapSize: giCubemapSize,
                  sampleCount: giSampleCount,
                  near: 0.1,
                  far: bakeFar,
                  validity: giValidity,
                  // `/5` — the sun-visibility tap, or `null` for "this bake cannot measure it".
                  // Built once above, outside the pass loop, because it describes the frozen scene.
                  sunShadow: bakeSunTap,
                  // Lever 1 — see `facingArm` above.
                  facing: facingArm,
                  onChunk: (done: number, total: number) => {
                    const now = performance.now();
                    const edge = done === 1 || done === total;
                    if (!edge && now - lastEmitMs < 1500) return;
                    lastEmitMs = now;
                    emitBakeEvent({
                      ev: 'progress',
                      phase: 'capture',
                      opaque: false,
                      done: pass * total + done,
                      total: passes * total,
                      unit: 'chunks',
                      detail:
                        `pass ${pass + 1}/${passes} · chunk ${done}/${total} · ` +
                        `${Math.min(structure.captureCount, done * 128).toLocaleString()} / ` +
                        `${structure.captureCount.toLocaleString()} captures`,
                    });
                  },
                },
              );
              // The IN-LOOP drains only. The pass's final drain is `tDrained - tSubmit1`, kept
              // apart so the report can show what the segmentation removed beside what one
              // undrained tail still costs — and so SUBMIT can be the JS loop MINUS these waits.
              let segDrainMs = 0;
              let segDrains = 0;
              try {
                for (let chunk = 0; chunk < session.chunkCount; chunk++) {
                  const d0 = renderer.info.render.drawCalls;
                  session.captureChunk(chunk);
                  drawCount += renderer.info.render.drawCalls - d0;
                  /*
                   * NOT AFTER THE LAST CHUNK: the readback below drains the queue anyway, so a
                   * drain here would be measured twice and attributed to the wrong term. The
                   * modulo is on `chunk + 1` so `drainEvery = 1` means "every chunk" rather than
                   * "every chunk except the first".
                   */
                  const last = chunk + 1 === session.chunkCount;
                  if (
                    !last &&
                    giDrainChunks > 0 &&
                    (chunk + 1) % giDrainChunks === 0 &&
                    bakeQueue?.onSubmittedWorkDone
                  ) {
                    const tD = performance.now();
                    await bakeQueue.onSubmittedWorkDone();
                    segDrainMs += performance.now() - tD;
                    segDrains++;
                  }
                }
              } finally {
                session.end();
              }
              /*
               * OPAQUE, AND SAID SO. The readback blocks on the device finishing everything queued
               * ahead of it — measured at 0.1–0.3 s, but on a big atlas it is the longest thing
               * that happens between two capture fractions, and a bar frozen at 100 % of pass 1
               * with no phase change is how "it hung" gets reported for a bake that is working.
               */
              const tSubmit1 = performance.now();
              emitBakeEvent({
                ev: 'phase',
                phase: 'readback',
                detail: `pass ${pass + 1}/${passes}`,
              });
              // The pass's FINAL drain, measured separately from the in-loop ones so the report can
              // show what one undrained tail still costs beside what the segmentation removed.
              if (bakeQueue?.onSubmittedWorkDone) await bakeQueue.onSubmittedWorkDone();
              const tDrained = performance.now();
              const captured = await readCaptureAtlas(
                renderer,
                captureAtlas,
                structure.captureCount,
              );
              const tRead = performance.now();
              /*
               * ── ⭐ DILATION, BEFORE THE SCATTER AND INSIDE THE BOUNCE LOOP (spec 5/M4) ──────
               *
               * BEFORE THE SCATTER because this is the only point in the pipeline where a probe's
               * spatial neighbours are knowable: after `scatterCaptures` a probe exists once per
               * brick that touches it, and a brick's neighbour in the atlas is whichever brick got
               * the next slot. Filling here also means one filled capture reaches every brick that
               * references it, so the fill cannot tear a level boundary that 5/M2 just made exact.
               *
               * INSIDE THE LOOP, i.e. once per bounce pass, because the atlas a bounce pass
               * PHOTOGRAPHS THROUGH must be the same field the runtime will sample. Dilating only
               * the final pass would light the bounce from a field full of black holes the shipped
               * frame does not have, and the second bounce would be systematically darker than the
               * scene it claims to describe. It is a pure function of this pass's own captures, so
               * Jacobi (spec 3.5) is untouched.
               */
              // Two more opaque phases, per pass. Both are CPU walks over the whole capture list:
              // fast on Sponza, not free on a Mars-sized one, and between the last capture fraction
              // and `probe grid baked` they are the ONLY thing standing between the baker and a
              // silence its watchdog would have to interpret. Naming them costs one line each.
              emitBakeEvent({
                ev: 'phase',
                phase: 'dilate',
                detail: `pass ${pass + 1}/${passes}`,
              });
              const dilation = dilateCaptures(
                captured,
                structure,
                giDilatePasses === null ? {} : { passes: giDilatePasses },
              );
              const tDilated = performance.now(); // TEMP-INSTRUMENT
              giDilation = dilation;
              emitBakeEvent({
                ev: 'phase',
                phase: 'scatter',
                detail: `pass ${pass + 1}/${passes}`,
              });
              // The fill is written UNCONDITIONALLY, not only when adaptivity is on. Slot 0 is
              // unreachable under `fillEmptySpaces: true` (every cell inside the grid gets a
              // brick, and the shader's clamp keeps every sample inside the grid), so on every
              // shipping world this changes not one rendered pixel — which is exactly why it can
              // land ahead of deletion and be measured on its own.
              const atlasTexels = scatterCaptures(captured, structure, {
                fill: { sh: giHoleFillSh() },
              });
              publishAtlas(light, atlasTexels, structure.indirection, structure);
              giBakedAtlas = atlasTexels;
              /*
               * ══ ⭐ O2 · THE COST REPORT — `bakeMs` SPLIT INTO THE PARTS IT IS MADE OF ═════════
               *
               * ⚠️ IT EXISTS BECAUSE THE OLD ACCOUNTING WAS A FALSE LABEL, AND THE LABEL COST TWO
               * SESSIONS. `bakeMs` is a `performance.now()` bracket around this entire loop, and
               * `bake-world.ts` prints it under the word `submit` — but between those two
               * timestamps sit the submission, the drain, the readback copies, the dilation and
               * the scatter. On the owner's Sponza bake that read `submit 15,711.9 s` against a
               * capture counter that reached 46,149/46,149 at +325 s, and the two describe the
               * same work. There was never a contradiction; there was a name on the wrong number,
               * and it sent the last two optimisation attempts at submission, which is 2 % of it.
               *
               * The five parts below sum to `bakeMs` by construction, which is the property that
               * makes this an accounting rather than a set of opinions. `SUBMIT` is the JS loop
               * MINUS the in-loop drains, so it stays comparable across `?giDrainChunks=` arms.
               *
               * The two fitted constants are the calibration stages B–D are ranked on: everything
               * downstream of here was measured through a software rasteriser, and the per-pass
               * and per-draw split is what decides whether merging or amortising is worth more.
               * Printed on every bake so the owner's real GPU answers it in passing.
               */
              const tScattered = performance.now();
              /*
               * ⚠️ THE DENOMINATOR IS PER-PASS SINCE LEVER 1, AND IT IS A DENOMINATOR RATHER THAN
               * A DISPLAY NUMBER. `probeChunk.ts` says it in its own words: *"a denominator that is
               * 25 % wrong turns that agreement into a coincidence"*. A cached pass submits 7 per
               * capture and 9 per chunk against a measured pass's 14 and 10, so dividing a cached
               * pass's SUBMIT by the measured arm's count would make the fitted µs/pass and µs/draw
               * fiction on every bounce pass — and those two constants are what the NEXT
               * optimisation (render bundles) will be ranked on.
               */
              const passCount = capturePassCount(structure.captureCount, facingArm);
              const submitMs = tSubmit1 - tSubmit0 - segDrainMs;
              const tailDrainMs = tDrained - tSubmit1;
              console.info(
                `[gi][bake-cost] pass ${pass + 1}/${passes} · ` +
                  `${structure.captureCount.toLocaleString()} captures · ` +
                  `${passCount.toLocaleString()} passes · ${drawCount.toLocaleString()} draws · ` +
                  `SUBMIT ${submitMs.toFixed(1)} ms · ` +
                  `DRAIN ${(segDrainMs + tailDrainMs).toFixed(1)} ms ` +
                  `(${segDrains} segment drain(s) ${segDrainMs.toFixed(1)} + tail ${tailDrainMs.toFixed(1)}; ` +
                  `drainEvery ${giDrainChunks === 0 ? 'never' : `${giDrainChunks} chunk(s)`}) · ` +
                  /*
                   * ⭐ WHICH ARM THIS PASS RAN, SAID OUT LOUD. The alternative is inferring it from
                   * a draw count, which is exactly the kind of reading the `submit`/`execute`
                   * mislabelling cost this subsystem two sessions.
                   */
                  `facing ${facingArm === 'cached' ? 'CACHED' : 'measured'} · ` +
                  /*
                   * ⭐ LEVER 3's WITNESS — the facing material's own `version` delta over this
                   * pass, which the `alphaTest` pin in `makeFacingMaterial` holds at 0. It is not a
                   * cost: it is the ONLY observable the pin has. Unpinned, this read 14,020 on the
                   * measured pass of the 585-capture Sponza corner and 106,118 of that pass's
                   * 246,025 draws rebuilt a cache key that had not changed. A non-zero number here
                   * on a `measured` pass means the shadow stopped working and SUBMIT silently went
                   * back — an atlas that is still correct and a measured pass 1.9x dearer
                   * (3,892.2 ms against 2,043.1 on that arm, both runs printed this counter).
                   */
                  `override churn ${session.overrideVersionChurn().toLocaleString()} · ` +
                  `COPIES ${(tRead - tDrained).toFixed(1)} ms · ` +
                  `DILATE ${(tDilated - tRead).toFixed(1)} ms · ` +
                  `SCATTER ${(tScattered - tDilated).toFixed(1)} ms · ` +
                  /*
                   * ⭐ THE VALIDITY CENSUS, PER PASS — THE INTEGER THAT LICENSED THE FACING CACHE,
                   * AND WHICH THE CACHE THEN STOPPED BEING ABLE TO FALSIFY.
                   *
                   * `DilationReport.invalid` counts captures whose validity is below
                   * `validityThreshold` (`probeDilate.ts`), read back off the atlas. This line was
                   * landed FIRST, on the unmodified build, where every bounce pass took its own
                   * facing capture: `invalid` held still at 108 of 1,105 across three passes, which
                   * is what licensed caching the pass at all. Until then the number was computed
                   * every pass and OVERWRITTEN (`giDilation = dilation`), so the only one anybody
                   * ever saw was the last.
                   *
                   * ⛔ IT IS NOT A PER-PASS AGREEMENT WITNESS ANY MORE, AND SAYING SO WOULD BE THE
                   * "compiled, passed, did nothing" SHAPE IN A COMMENT. On `--facing-cache 1` —
                   * i.e. every shipping bake — `captureRepackNode` sources validity from the store
                   * pass 0 wrote, so passes 1..N re-encode the SAME texel and CANNOT disagree.
                   * What this still catches is a store that is EMPTY or unwritten (disabling the
                   * blit printed `1,105 invalid / 0 filled` here one pass before the baker's own
                   * "the atlas carries no light" refusal). What it CANNOT catch is a store that is
                   * merely STALE — geometry moving between passes — which is precisely the failure
                   * `bakeHoldsFrame()` was promoted to guard. Its only live falsifying arm is
                   * `--facing-cache 0`.
                   */
                  `VALIDITY ${dilation.invalid.toLocaleString()} invalid / ` +
                  `${dilation.filled.toLocaleString()} filled / ` +
                  `${dilation.remaining.toLocaleString()} sealed of ` +
                  `${dilation.probes.toLocaleString()} · ` +
                  `fit ${((submitMs * 1000) / Math.max(1, passCount)).toFixed(1)} us/pass ` +
                  `(${(drawCount / Math.max(1, passCount)).toFixed(1)} draws/pass, ` +
                  `${((submitMs * 1000) / Math.max(1, drawCount)).toFixed(2)} us/draw)`,
              );
            }
            light.visible = true; // FWG_LIGHT_TOGGLE_OK: the bake bracket handing the volume back
          } catch (err) {
            scene.remove(light); // never leave a half-baked light in the scene
            light.dispose();
            sparseVolume = null;
            giCarriesIndirect = false;
            giSkyBaked = false;
            applyIndirect();
            if (giInfo) giInfo = { ...giInfo, state: 'failed' };
            throw err;
          } finally {
            /*
             * The capture atlas is a per-bake allocation — 917 KB at Sponza, 4.6 MB at a
             * Mars-sized capture list — and it is freed HERE rather than pooled, because the
             * pooled-and-never-reallocated atlas (T27) is a bug this subsystem has already
             * shipped once.
             */
            captureAtlas.dispose();
            // Lever 1's store, freed in the same breath and for the same reason.
            facingCache.dispose();
            bakeBackend.trackTimestamp = hadTrackTimestamp;
            sky?.setSunDiscVisible(true);
            for (const l of bakeOnlyLights) l.parent?.remove(l);
            if (bakeOnlyLights.length > 0) {
              console.info(
                `[gi] ${bakeOnlyLights.length} bake-only light(s) removed from the scene.`,
              );
            }
            // `/5`: the FITTED ortho box comes back to `applySun`'s authored ±extent FIRST, so the
            // render inside `restoreCsm`'s `castShadow` cycle already rebuilds against the box the
            // player's frame will use. All four planes are `LIGHT_KEY_FREE_FIELDS`-class, so this
            // costs no pipeline and moves no cache key — it is a projection matrix, not a program.
            restoreBakeShadowBox();
            // B3: the cascades come back AFTER the bake-only lights have left and the sky disc is
            // restored, so the one render inside the cycle rebuilds for the scene the player sees.
            restoreCsm();
            // …and the mirror image: the `movable` lights go back in. In the SAME `finally` as the
            // removal above so a throw mid-bake cannot leave a world's movable lighting missing.
            for (const l of excludedFromBake) scene.add(l);
            /*
             * FENCE 4's other half. The editor's verdict is restored, not recomputed — this module
             * holds no staleness verdict and must not invent one. The editor re-evaluates on its
             * own next turn (`reloadBakedLighting` is the moment the bound atlas moves), and a
             * successful pickup then takes preview back off because the world was saved and the
             * fresh atlas is `baked`.
             */
            if (previewDuringBake) {
              lightPreview = true;
              worldLightRig.apply(activeLighting.lights, { previewStatic: true });
              bakeOnlyLights = worldLightRig.bakeOnlyLights();
            }
          }
          const bakeMs = performance.now() - t0;
          /*
           * O2: the bake gate's own report. `bakedLightLeaks` can only ever say "nothing went
           * wrong", which is what an inert fence says too; this says the gate was REACHED, and how
           * many full-scene renders it refused. Before stage A the same window rendered 41 frames
           * on a 20 s bake and every one of them was a wasted scene traversal with a bake-only
           * light in it.
           */
          console.info(
            `[gi][bake-cost] bake gate suppressed ${giBakeSuppressedFrames} rAF frame(s) — ` +
              `each one a full scene render that would have contended with the bake's own queue ` +
              `and (with a static light in the world) landed in \`bakedLightLeaks\`.`,
          );
          /*
           * EVERYTHING `readProbeAtlas` NEEDS, LATCHED HERE. The header is emitted from what the
           * bake was actually made FROM — the compiled structure, the resolved rig, the settings
           * in force — never re-derived later from `giInfo` plus plausible arithmetic, which is
           * how a header comes to describe an atlas that was never made.
           */
          giBakedStructure = structure;
          giBakeSettings = {
            far: bakeFar,
            baseSpacingM,
            normalBiasM: giNormalBiasM(),
            /*
             * ⭐ MEASURED HERE, INSIDE THE BAKE, AND NOT AT `readProbeAtlas`. The scene is frozen for
             * the whole bake (`bakeHoldsFrame`), so this IS the arrangement of the level the captures
             * photographed; `readProbeAtlas` can be called by the offline baker seconds later, with
             * the sim running again and the player somewhere else. The two `await`s are module-cache
             * hits — every module named is already loaded by the bake above — and no frame can slip
             * in, because the gate is still held.
             */
            drawnBounds: await giProvenanceBounds(await giDrawnBox()),
          };
          if (giInfo) {
            giInfo = {
              ...giInfo,
              state: 'baked',
              on: true,
              bakeMs,
              bakeMsPerProbe: bakeMs / Math.max(1, structure.captureCount),
              bakedLights: bakeOnlyLights.length,
              dilation: giDilation,
              // The compile's OWN report, for an in-tab bake — the same numbers the header records.
              placement: {
                adaptive: structure.report.adaptive !== null,
                capturesUniform: structure.report.adaptive?.capturesUniform ?? structure.captureCount,
              },
            };
          }
          /*
           * ⭐ VIRTUAL OFFSET SAYS WHAT IT DID, ON EVERY BAKE THAT RAN IT, INCLUDING A NO-OP ONE.
           *
           * Same discipline as the dilation line below and for a sharper reason: the offsets never
           * reach `probes.bin`, so this console line and `bake.placement`'s counts are the ONLY
           * record that any probe moved. `probeField()` rebuilds its spheres from the indirection
           * texture and will keep drawing lattice positions — correctly — so there is no picture to
           * fall back on if the number is not printed.
           *
           * ⚠️ IT IS PRINTED IMMEDIATELY ABOVE THE DILATION LINE ON PURPOSE. The pair is the whole
           * acceptance test: `moved` going up while `dilation.invalid` stays put is a REFUSAL, not a
           * result — the pass relocated probes and none of them came back with light — and the only
           * way to see that is to have both numbers in one place in the log.
           */
          if (structure.report.offset !== null) {
            console.info(`[gi] ${describeOffsetReport(structure.report.offset)}`);
          }
          if (giDilation !== null) {
            /*
             * ⚠️ SAID OUT LOUD, EVERY BAKE, INCLUDING WHEN IT DID NOTHING. An inert dilation is
             * invisible in every other observable — the atlas is still an atlas, the header is
             * still right, `atlasLitFraction` still passes, and the interior is simply dark — so
             * the counts are printed rather than left for someone to go looking for. `invalid: 0`
             * with validity off is the case that reads like success and is not.
             */
            const d = giDilation;
            console.info(
              d.invalid === 0 && !giValidity
                ? `[gi] dilation: NOTHING TO DO — this bake captured no validity ` +
                    `(?giValidity=1), so every one of ${d.probes} captures reports validity 1 and ` +
                    `no probe can be known to be buried. The fill is inert by construction, not ` +
                    `by accident.`
                : `[gi] dilation: ${d.filled} of ${d.invalid} invalid captures filled in ` +
                    `${d.passes} pass(es) (by level ${d.filledByLevel.join('/')}), ` +
                    `${d.remaining} left sealed, of ${d.probes} captures.`,
            );
          }
          /*
           * ⚠️ `bakeMs` IS SUBMISSION PLUS THE READBACKS' WAIT, NOT THE WHOLE BAKE. A render pass
           * is SUBMITTED when `bakeCapturePass` returns, not executed; the per-pass readback then
           * blocks until the device has FINISHED everything queued ahead of it, so most of the
           * device time is inside this bracket — but not necessarily all of it. Both halves are
           * BAKE TIME and bake time is an EDITOR-time cost, never a player cost: it is reported so
           * a bake can be planned, not so it can be optimised.
           *
           * The phrase "probe grid baked" is load-bearing: `tools/bundler/bake-world.ts` listens
           * for it on the console to know when to stop waiting.
           */
          /*
           * ⭐ `fwg-gi-bake` IS THE ABSENCE MARKER, and it lives on a line that only a real bake
           * can print. `scripts/checkBundle.mjs` (c5) fails the BUILD if this string survives
           * into `dist/` — the same three-legged bar the dev hook, the GI badge and the mock Axon
           * gateway are held to (marker · sourcemap `sources` · no chunk of that name). A marker
           * in a comment would be deleted by minification and prove nothing; a marker in the
           * bake's own output line cannot survive without the bake surviving with it.
           */
          console.info(
            `[gi][fwg-gi-bake] probe grid baked: ${structure.captureCount} captures / ` +
              `${structure.bricks.length} bricks over ` +
              `${gridSize.map((n) => n.toFixed(1)).join('×')} m at ${baseSpacingM} m base ` +
              `in ${(bakeMs / 1000).toFixed(1)}s = ` +
              `${(bakeMs / Math.max(1, structure.captureCount)).toFixed(1)} ms/capture ` +
              `(bake time is a build cost, not a frame cost)`,
          );
        } catch (err) {
          giCarriesIndirect = false;
          giSkyBaked = false;
          applyIndirect();
          /*
           * ⭐ A FAILURE IS A STATE, NOT AN ABSENCE (the bake-state model's `failed` arm).
           *
           * `probeVolume()` and `compileProbeVolumes()` both THROW rather than coarsen, and both
           * throw BEFORE the bracket has published a `giInfo` — so before this, a refused budget
           * left `gi()` returning null, which is the same observation as "the grid is off". Three
           * different facts, one reading, and the middle one is the only one an artist can act on.
           * A `console.warn` is a self-report; this is a field a test and a badge can read.
           */
          giInfo =
            giInfo === null
              ? {
                  state: 'failed',
                  on: false,
                  resolution: [0, 0, 0],
                  probes: 0,
                  spacingM: [0, 0, 0],
                  spacingRequestedM: giCompileSpacing(),
                  structure: null,
                  intensity: giIntensity,
                  sizeM: [0, 0, 0],
                  centerM: [0, 0, 0],
                  bakeMs: 0,
                  bakeMsPerProbe: 0,
                  bounces: giBounces,
                  validity: giValidity,
                  quality: giQualityInfo(),
                  cubemapSize: giCubemapSize,
                  sampleCount: giSampleCount,
                  seamBreaks: 0,
                  bakedLights: 0,
                  bakedLightLeaks: 0,
                  source: 'none',
                  /*
                   * ⚠️ NULL, NOT `giStale`, AND DELIBERATELY. `deriveBakeState` reads a non-null
                   * `stale` as "a FILE was refused" — so passing a stale file's reason through a
                   * BAKE's exception would report `stale` for a world whose bake threw, telling the
                   * artist to re-bake the thing that just failed to bake.
                   */
                  stale: null,
                  fileBytes: 0,
                  dilation: null,
                }
              : { ...giInfo, state: 'failed' };
          console.warn('[gi] probe-grid bake failed:', err);
        }
        return;
      }
      await gameScene.applyBakedLighting();
    },
    setGiIntensity(gain) {
      /*
       * THE INTENSITY IS A UNIFORM, NOT A SHADER CONSTANT — which is what makes the monotonicity
       * test possible at all (`tests/e2e/gi.e2e.ts`). `SparseProbeVolume.setIntensity` writes the
       * `renderGroup` uniform (and the field, so the two can never disagree), so moving it changes
       * the picture with NO recompile, NO re-bake and NO toggling of the volume. That matters: every previous
       * GI A/B we ran either re-booted (0.6 m of camera drift swings frame luminance by 19 %) or
       * called `setProbeGrid(false)` — and toggling the grid is exactly the operation that cannot
       * distinguish "the GI term is zero" from "the GI term is absent". One session, one camera,
       * one bake, one uniform: `?giIntensity=1` vs `4` must move the frame or the grid is dead.
       */
      if (!sparseVolume) return false;
      /*
       * ⚠️ IT DOES NOT WRITE BACK TO `giIntensity`, AND THAT IS DELIBERATE. `giIntensity` is the
       * BAKE-time value: `bake({ bounces: 1 })`'s second pass renders the scene with the grid
       * VISIBLE, so whatever intensity the grid carries at bake time is multiplied INTO the atlas
       * as the bounce term. A sweep that left the gain at 16 and then re-baked (an A/B/A costs a
       * re-bake — the atlas is disposed with the grid) produced an atlas with a 16× bounce, and
       * the next viewpoint measured 136.9 mean luminance against 49.7 for the same nominal
       * settings. Runtime gain and bake gain are different quantities; only the first moves here.
       */
      const clamped = Math.max(0, Math.min(64, gain));
      /*
       * The sparse volume takes it through `setIntensity`, never by assigning the field: the field
       * alone reaches no shader (its node ignores `colorNode`), and the uniform alone would make
       * the light lie about itself in every debug surface. One call writes both.
       */
      sparseVolume.setIntensity(clamped);
      if (giInfo) giInfo = { ...giInfo, intensity: clamped };
      return true;
    },
    giSpikeRepoint(emptyTexelX) {
      if (!giSpikeRepublish) return false;
      giSpikeRepublish(emptyTexelX);
      return true;
    },
    gi() {
      /*
       * ⭐ `bakeState` IS DERIVED HERE AND NOWHERE ELSE (owner direction #4) — one call to the one
       * pure function (`render/gi/bakeState.ts`), on the way out, rather than a field the eight
       * `giInfo` assignments in this file each have to remember to set correctly.
       *
       * ⚠️ `gi()` RETURNING NULL IS NOT A BAKE STATE. Null means the GI system was never asked for
       * in this session — which is a fact about the SESSION, not about the world's lighting. A
       * world with a perfectly good baked volume on disk that nobody requested would read
       * `unbaked`, which is exactly the confident-wrong-answer this whole model exists to remove.
       * So the dev badge shows nothing until a GI path has actually run; `?gi=file` (the shipping
       * shape) is what makes it appear.
       */
      if (giInfo === null) return null;
      return {
        ...giInfo,
        bakeState: deriveBakeState(giInfo),
        rebinds: giRebinds,
      };
    },
    fog() {
      // `sceneFog` is measured off the two handles, so "the plan says height" and "a height node
      // is bound" are separate claims and a swap that silently failed is visible as the mismatch.
      const sceneFog: FogState['sceneFog'] =
        (scene.fogNode ?? null) !== null ? 'node' : scene.fog !== null ? 'linear' : 'none';
      return {
        plan: fogPlan,
        summary: describeFogPlan(fogPlan),
        sceneFog,
        uniforms: heightFog === null ? null : heightFog.values(),
        keyCycles: fogKeyCycles,
      };
    },
    sunIntensity() {
      // Read off the LIGHT, never off the URL — the same rule as `indirect()`.
      return sun.intensity;
    },
    sunDirection() {
      /*
       * READ OFF THE LIGHT, and reported at all because G3.1's finding was invisible without it.
       * "The sun contributes 0.2 % of this frame" has two readings — it is too dim, or it is
       * pointing somewhere else — and every instrument we had could only see the first. A pose is
       * the one thing a screenshot cannot tell you and the one thing that was actually wrong.
       *
       * Normalised, pointing FROM the scene TOWARD the sun (the light targets the origin).
       */
      const p = sun.position;
      const len = Math.hypot(p.x, p.y, p.z) || 1;
      return [p.x / len, p.y / len, p.z / len] as [number, number, number];
    },
    sky() {
      // Read off the MESH's uniforms, never off `world.lightRig.sky` — same rule again. A dome
      // that was clamped, or never constructed, must report what it IS, not what was requested.
      return sky ? sky.info() : SKY_OFF;
    },
    async captureSkyCube(opts) {
      /*
       * ── ⭐ THE GATE IS THE WHOLE PRODUCTION STORY, AND IT IS THE (c6) SHAPE ────────────────────
       *
       * `import.meta.env.DEV` is a compile-time literal, so Rollup deletes this branch, the
       * `await import`, `render/bake/skyCube.ts`, `render/bake/skyCubeFile.ts` and the
       * `fwg-sky-cube` marker. `checkBundle` (c11) asserts all three legs on the OUTPUT side and
       * `tests/renderSkyCube.test.ts` asserts this line sits inside a DEV block on the SOURCE
       * side. A production build resolves the refusal below forever — which is correct: a sky
       * cube is a BAKE INPUT and a player has no bake to feed.
       *
       * ⚠️ It is read THROUGH the `let sky` binding rather than captured at construction, for
       * `applyWorldLights`'s reason: the editor can construct or remove the dome mid-session on a
       * `sky.enabled` flip, and a handle captured at boot would photograph a dome that is no
       * longer the one in the scene — or refuse over one that is.
       */
      if (import.meta.env.DEV) {
        const { captureSkyCube } = await import('../render/bake/skyCube');
        return captureSkyCube(
          { renderer, scene, sky, backend: giFallbackBackend ? 'webgl2' : 'webgpu' },
          opts ?? {},
        );
      }
      return {
        ok: false,
        reason:
          'the sky-cube capture is DEV-ONLY and is absent from this build. It produces a bake ' +
          'INPUT; a shipped page has no bake to feed it to.',
      };
    },
    async readProbeAtlas() {
      /*
       * -- DEV-ONLY, AND GATED AT COMPILE TIME (spec 3.4) ------------------------------------
       *
       * This is a BAKE-SIDE instrument: the dev hook reads it, and `tools/bundler/bake-world.ts`
       * reads it through the dev hook to write `probes.bin`. A player has no bake to read back.
       * The gate is a compile-time literal so `render/gi/probeRead.ts` and the header ENCODER are
       * DELETED from `dist/` rather than merely unreachable - which matters now that the LOAD
       * path ships: without it the loader would drag the writer into a production chunk with it.
       */
      if (import.meta.env.DEV) {
        /*
         * ── THE HEADER IS BUILT HERE, AND ONLY HERE ────────────────────────────────────────────
         *
         * `tools/bundler/bake-world.ts` writes what this returns and adds nothing of its own. That
         * is deliberate: the node baker cannot see the resolved lighting rig, the compiled
         * structure, or which lights were removed into the atlas — it can only see a URL and a world
         * file, i.e. exactly the REQUEST rather than the RESULT. A header assembled from the request
         * describes an atlas that was never made the moment any of them diverges.
         *
         * A volume that was itself LOADED re-emits its own header VERBATIM, so a round trip
         * (`load -> readProbeAtlas -> write`) is the identity rather than a re-derivation — which is
         * what makes the load-equals-bake assertion in `tests/e2e/giFile.e2e.ts` a comparison of
         * bytes rather than of two independent guesses at the same header.
         */
        if (giLoadedHeader !== null) {
          const { encodeProbePayload } = await import('../render/gi/probeFile');
          const { bytesToBase64 } = await import('../render/gi/probeRead');
          const v = giLoadedVolume;
          if (v === null) return null;
          return {
            header: giLoadedHeader,
            payloadBase64: bytesToBase64(encodeProbePayload(v.indirection, v.atlas)),
            readFrom: 'cpu' as const,
            // This header came off disk and through `checkProvenance` already; re-asking is the
            // same call with the same inputs, and asking is cheaper than exempting one arm.
            loadable: await giPlainBootVerdict(giLoadedHeader),
          };
        }
        /*
         * ── ⭐ THE IN-BROWSER SPARSE BAKE'S HEADER — WHAT `npm run bake:world` WRITES ─────────────
         *
         * Assembled from `giBakedStructure` / `giBakedAtlas` / `giBakeSettings`, which the bake
         * latched from the compiler's ACTUAL output and the settings ACTUALLY in force — never from
         * `giInfo`, which is a report. The distinction is the whole reason the header is built in
         * the browser rather than in the node baker: the baker can see the REQUEST (a URL and a
         * world file); only the running scene can see the RESULT.
         */
        if (giBakedStructure !== null && giBakedAtlas !== null && giBakeSettings !== null) {
          const {
            BAKER_VERSION,
            PROBE_FILE_FORMAT,
            PROBE_PRODUCER_RASTER_CUBE,
            atlasSunVisibility,
            encodeProbePayload,
            payloadSections,
            sortVolumesById,
            worldHash,
          } = await import('../render/gi/probeFile');
          const { BRICK_N, SUB_VOLUMES } = await import('../render/gi/probeBrick');
          // `/5` — the tap's constant tap count, recorded in the header beside the numbers it made.
          const { SUN_VIS_TAPS } = await import('../render/gi/probeGrid');
          const { bytesToBase64 } = await import('../render/gi/probeRead');
          const st = giBakedStructure;
          const sections = payloadSections({
            indirDims: st.indirDims,
            brickGrid: st.brickGrid,
          });
          /*
           * NAMED, then returned — rather than built inline as it was — because the atlas is now
           * asked ONE more question before it leaves this function: would the world it belongs to
           * load it? See `loadable` on the return type. Nothing else about the header moved.
           */
          const bakedHeader: import('../render/gi/probeFile').ProbeVolumeHeader = {
            format: PROBE_FILE_FORMAT,
            bakerVersion: BAKER_VERSION,
            /*
             * ⭐ WHO WROTE THIS FILE. This branch IS the rasteriser — six real scene renders per
             * probe per pass — whether it was reached from `?gi=bake` in a tab or from
             * `tools/bundler/bake-world.ts`, which drives this page and writes the header it gets
             * back verbatim. So there is ONE writer of the rasterised producer and it is here.
             * Constant by construction, never read off a URL: a bake that photographed the scene
             * cannot have been traced.
             */
            producer: PROBE_PRODUCER_RASTER_CUBE,
            worldId: activeWorld.worldId,
            worldHash: worldHash(activeWorld),
            structure: {
              originM: [...st.originM] as [number, number, number],
              baseSpacingM: st.baseSpacingM,
              // The BUILD's constants, recorded so a file compiled under different ones is
              // REFUSED rather than addressed with the wrong stride — they are folded into the
              // shader graph as literals, so they are properties of the build, not of the file.
              brickN: BRICK_N,
              subVolumes: SUB_VOLUMES,
              indirOriginM: [...st.indirOriginM] as [number, number, number],
              indirDims: [...st.indirDims] as [number, number, number],
              brickGrid: [...st.brickGrid] as [number, number, number],
              brickCount: st.bricks.length,
              captureCount: st.captureCount,
              bricksByLevel: st.report.bricksByLevel,
              transitionBricks: st.report.transitionBricks,
              filledCells: st.report.filledCells,
              emptyCells: st.report.emptyCells,
              paddingRatio: st.report.paddingRatio,
              normalBiasM: giBakeSettings.normalBiasM,
            },
            /*
             * ⚠️ THE AUTHORED LIST, NOT THE COMPILED ONE, AND THE DIFFERENCE MATTERS.
             * `checkProvenance` asks "would this session compile the same volumes?", and where a
             * world authors none the answer is the SYNTHESISED volume — which is a function of the
             * DRAWN BOUNDS, not of `world.json`, and so cannot be compared against anything in it.
             * Recording the synthesised volume here would make every such file stale on the next
             * boot. What guards the synthesis instead is `structure` (its exact result) plus
             * `geometryDrift` (the drawn geometry escaping the grid it was baked for).
             */
            volumes: sortVolumesById(giAuthoredVolumes()),
            bake: {
              bounces: giBounces,
              validity: giValidity,
              cubemapSize: giCubemapSize,
              sampleCount: giSampleCount,
              farM: giBakeSettings.far,
              intensityAtBake: giIntensity,
              bakedLights: giInfo?.bakedLights ?? 0,
              /*
               * ⭐ THE BOX THIS BAKE WAS TAKEN OVER — `checkProvenance`'s `geometryDrift` subject.
               * Latched INSIDE the bake by `giProvenanceBounds`, the same function the loader's live
               * half runs, so the two sides of that comparison are one piece of arithmetic. Written
               * as min/max corners because that is what a BOX is; the live half spells the identical
               * measurement centre/size because that is what `drawnBounds` hands back. Converting
               * here, at the one point of use, keeps a second definition out of the file.
               */
              drawnBounds: {
                minM: [0, 1, 2].map(
                  (i) => giBakeSettings!.drawnBounds.center[i]! - giBakeSettings!.drawnBounds.size[i]! / 2,
                ) as [number, number, number],
                maxM: [0, 1, 2].map(
                  (i) => giBakeSettings!.drawnBounds.center[i]! + giBakeSettings!.drawnBounds.size[i]! / 2,
                ) as [number, number, number],
                walk: giBakeSettings.drawnBounds.walk,
              },
              /*
               * ⭐ THE MODULE THAT DID THE WORK IS THE ONE THAT RECORDS IT — and under v2 that is
               * this page, not the offline baker. A v2 payload is BRICKS, and the capture identity
               * the fill needs does not survive the scatter, so `dilate-volume.ts`'s
               * bake-then-dilate-the-file shape is not expressible any more (see its header). The
               * baker asks for the fill with `--dilate n`, which becomes `?giDilate=`, and reads
               * the result back out of this header.
               *
               * `0/0` means "no dilation ran"; ABSENT would mean "this header predates dilation".
               * `parseProbeHeader` keeps the two apart, so neither is written by accident.
               */
              dilationPasses: giDilation?.passes ?? 0,
              dilationFilled: giDilation?.filled ?? 0,
              /*
               * ── ⭐ THE QUALITY PRESET, AS A NAME PLUS WHERE A FLAG BEAT IT (D9) ─────────────
               *
               * ⚠️ THE VALUES ARE NOT REPEATED HERE, AND THAT IS THE POINT. `bounces`, `validity`,
               * `cubemapSize`, `sampleCount` and `dilationPasses` immediately above — plus
               * `structure.baseSpacingM` — ARE the six resolved values this preset implied; they
               * are written from the same `ResolvedBakeQuality` the page baked with. Writing them
               * a second time under `quality` would be two descriptions of one bake, and this
               * file's whole discipline is that there is one. What the name adds is the row they
               * started from; what `qualityOverrides` adds is where an explicit flag beat it, so
               * `production` with `--bounces 3` never reads as a plain `production`.
               */
              quality: giQuality?.name ?? 'medium',
              qualityOverrides: giQuality === null ? [] : [...giQuality.overrides],
              /*
               * ── ⭐ HOW THE BRICKS WERE PLACED (B2-sparse) ────────────────────────────────────
               *
               * `requested` is the resolver's answer over THIS world (the baker derives the same
               * one and cross-checks it — two processes, one `world.json`); `adaptive` is what
               * the compile DID, off `st.report.adaptive`, which is null exactly when no grid was
               * handed in. The two differ only when the safety rail fired, and then `fallback`
               * carries the page's own sentence so the header, the panel and the baker's log all
               * say the same thing about the same bake.
               */
              placement: ((): import('../render/gi/probeFile').ProbeFilePlacement => {
                const a = st.report.adaptive;
                const requested = resolveGiAdaptive(activeWorld.lighting.gi, giCompileSpacing()).enabled;
                return a === null
                  ? {
                      requested,
                      adaptive: false,
                      voxelM: 0,
                      surfaceGuardM: 0,
                      guardByLevelM: [],
                      occupancyHash: '',
                      bricksDroppedFar: 0,
                      bricksDroppedSealed: 0,
                      capturesUniform: st.captureCount,
                      fallback: requested ? giPlacementNote : null,
                      ...offsetFields(st.report.offset),
                    }
                  : {
                      requested,
                      adaptive: true,
                      voxelM: a.voxelM,
                      surfaceGuardM: a.surfaceGuardM,
                      guardByLevelM: [...a.guardByLevelM],
                      occupancyHash: a.occupancyHash,
                      bricksDroppedFar: a.bricksDroppedFar,
                      bricksDroppedSealed: a.bricksDroppedSealed,
                      capturesUniform: a.capturesUniform,
                      fallback: null,
                      ...offsetFields(st.report.offset),
                    };
              })(),
              /*
               * ── ⭐ THE SUN-VISIBILITY PASS (`/5`) — REQUIRED, AND CROSS-CHECKED BY THE BAKER ──
               *
               * The geometry half (`extentM`, `texelM`, `outsideCaptures`) comes from the bake
               * bracket's fit; the DATA half (`mean`, `variance`, `fullyLit`, `fullyShadowed`) is
               * read back off the atlas that was actually written, by `atlasSunVisibility` — the
               * same walk `atlasLitFraction` does, over the same emitted slots, one band along.
               *
               * ⚠️ THE DATA HALF IS THE ONLY THING THAT CAN SAY THE PASS RAN. A tap that compiled
               * and wrote a constant produces a plausible mean and a variance of exactly zero, and
               * on screen it is indistinguishable from a correct bake, because a constant 1.0 is a
               * shadow term's identity. `bake-world.ts` refuses a zero variance while the sun casts.
               */
              sunVisibility: ((): import('../render/gi/probeFile').ProbeFileSunVisibility => {
                const fit = giSunVisFit;
                const stats = giBakedAtlas
                  ? atlasSunVisibility(giBakedAtlas, {
                      brickGrid: st.brickGrid,
                      brickCount: st.bricks.length,
                    })
                  : { total: 0, mean: 0, variance: 0, fullyLit: 0, fullyShadowed: 0 };
                return {
                  baked: fit?.baked ?? false,
                  source: fit?.baked === true ? 'sunShadowMap' : 'none',
                  taps: SUN_VIS_TAPS,
                  radiusM: fit?.radiusM ?? 0,
                  mapSize: fit?.mapSize ?? 0,
                  texelM: fit ? [fit.texelM[0], fit.texelM[1]] : [0, 0],
                  extentM: fit
                    ? [fit.extentM[0], fit.extentM[1], fit.extentM[2], fit.extentM[3]]
                    : [0, 0, 0, 0],
                  extentSource: fit?.fitted === true ? 'fitted' : 'authored',
                  authoredExtentM: fit?.authoredExtentM ?? 0,
                  outsideCaptures: fit?.outsideCaptures ?? 0,
                  meanVisibility: stats.mean,
                  varianceVisibility: stats.variance,
                  fullyLit: stats.fullyLit,
                  fullyShadowed: stats.fullyShadowed,
                };
              })(),
              /*
               * ⭐ LEVER 1 — WHICH ARM PRODUCED THE FACING/VALIDITY SCALARS IN THIS FILE.
               *
               * Recorded so a suspect atlas can be IDENTIFIED rather than guessed at. It is not in
               * `checkProvenance` and it moved no version: it changes what a bake COSTS, never what
               * the file IS (`--drain-chunks`'s ruling). See `ProbeFileBake.facingCache` for what
               * that licence rests on and what it would cost if a reviewer rejected it.
               */
              facingCache: {
                enabled: giFacingCache,
                measuredOnPass: 0,
                // 0 on a zero-bounce bake: there is no second pass to cache for, so the lever is
                // inert BY CONSTRUCTION — the distinction the dilation line draws with NOTHING TO DO.
                passesCached: giFacingCache ? giBounces : 0,
              },
            },
            lighting: liveLighting(),
            payload: {
              file: 'probes.bin',
              encoding: 'rgba16f-u16le',
              layout: 'indirection-then-bricks',
              ...sections,
            },
          };
          return {
            header: bakedHeader,
            payloadBase64: bytesToBase64(encodeProbePayload(st.indirection, giBakedAtlas)),
            /*
             * ⚠️ `'bake'`, NOT `'target'`, AND THE CHANGE IS SUBSTANTIVE. Under v1 the runtime atlas
             * WAS a GPU render target and the caller asserted `'target'` to prove the page had baked
             * rather than loaded. Under v2 the runtime atlas is a `Data3DTexture` built by
             * `scatterCaptures` on the CPU — whichever way it arrived — so `'target'` is no longer
             * available to mean anything, and the honest discriminator is the one that was actually
             * wanted: did this page BAKE these bytes, or LOAD them? A `'cpu'` reading here would
             * mean the baker was about to re-write the file it had just read, reporting a fresh
             * bake it never ran.
             */
            readFrom: 'bake' as const,
            /*
             * ⭐ THE LAST QUESTION THE ATLAS IS ASKED BEFORE IT LEAVES THE PAGE, and the one the
             * whole subsystem was missing: **would the world this belongs to actually load it?**
             * Answered by the loader's own `checkProvenance` against a plain-boot provenance
             * (`giPlainBootSpacing()` — no `?giSpacing=`, no quality multiplier), so a bake can
             * never again overwrite a good atlas with one that no boot accepts.
             */
            loadable: await giPlainBootVerdict(bakedHeader),
          };
        }
        /*
         * ⚠️ FALLING THROUGH TO `null` IS THE ANSWER, and it is the only one this function has left.
         *
         * There used to be a third arm here: a session that baked the DENSE v1 lattice (`?gi=dense`)
         * was REFUSED with a named warning rather than given a fabricated header, because its atlas
         * was a lattice in sub-volume-major order and the compiled structure a v2/v3 header needs —
         * brick grid, indirection, capture list — did not exist for it. That path is retired (F3),
         * so the two arms above (a LOADED volume re-emitting its own header verbatim, and a SPARSE
         * bake emitting the compiler's actual output) are exhaustive.
         *
         * ⭐ AND THE RULING SURVIVES THEM: this returns `null` rather than assembling a header from
         * `giInfo` plus plausible arithmetic. Such a header would parse, validate, load, and address
         * real numbers at the wrong addresses — this repo's own failure pattern, in the one module
         * whose entire job is to refuse an atlas that does not describe what it claims to.
         */
        return null;
      }
      return null;
    },
    indirect() {
      /*
       * READ FROM THE LIGHT, NOT FROM THE RESOLVER. `indirect` is what `applyIndirect()` decided;
       * `hemi.intensity` is what the light object actually holds and what the node uniform copies
       * every frame. If anything ever assigns that field behind this module's back, these two
       * disagree and the reported `fill` is the one that is true. This is the same discipline as
       * the bound-program census: assert on what was BOUND, never on what was decided.
       */
      return { ...indirect, fill: hemi.intensity };
    },
    setAmbientFill(n) {
      hemiOverride = n === null ? null : Math.max(0, Math.min(10, n));
      applyIndirect();
      return gameScene.indirect();
    },
    resolution: resolutionHandle,
    csm() {
      return { override: csmOverride, cascades: csmRig.state().cascades, enabled: csmRig.policy().enabled };
    },
    shadowRes() {
      return shadowResScale === 1 ? null : shadowResScale;
    },
    setShadowResScale(n) {
      /*
       * ⭐ THE WHOLE APPLY, AND IT ALLOCATES NOTHING AND REBUILDS NOTHING. The same closed set the
       * `?shadowRes=` read takes; anything else falls back to 1x rather than resolving to a rung
       * an artist did not choose. Two writers hear it — `csmRig.apply` (the sun's cascades,
       * through the SAME policy function the light edits go through) and the world rig's
       * `setShadowResScale` (every authored light's map) — and both write only `shadow.mapSize`,
       * which is in `LIGHT_KEY_FREE_FIELDS`. `keyMoves` stays 0 and no WGSL is regenerated.
       *
       * ⛔ `applySun` is deliberately NOT re-run: its `shadowMapSize` write is the map the bake
       * photographs and must stay the authored value whatever this session's tier or preference.
       */
      shadowResScale = n === 1 || n === 0.5 || n === 0.25 ? n : 1;
      csmRig.apply(sunShadowPolicy(tier, activeLighting.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride), activeLighting.sun.castShadow);
      worldLightRig.setShadowResScale(shadowResScale);
      return gameScene.shadowRes();
    },
    setCsmOverride(n) {
      // The same clamp the `?csm=` read applies: an integer 0..4 or nothing.
      csmOverride = n === null || !Number.isFinite(n) ? null : Math.max(0, Math.min(4, Math.floor(n)));
      csmRig.apply(sunShadowPolicy(tier, activeLighting.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride), activeLighting.sun.castShadow);
      return gameScene.csm();
    },
    /**
     * ── ⭐ R5/R2 · THE TWO NEW SHADOW ARMS' RUNTIME DOORS — THE SHAPE OF `setCsmOverride` ABOVE ───
     *
     * One session variable each, folded into the SAME `csmRig.apply` + `sunShadowPolicy` pair every
     * other door uses. They exist for two reasons and the second is the stronger:
     *
     *  1. Both arms are owner TRADES with a picture attached, so they have to be A/B-able on a real
     *     frame — and `?sunShadowUpdate=` in particular exists so nobody has to edit a `world.json`
     *     to try it, which would move `worldHash` and stale that world's atlas for an experiment.
     *  2. ⚠️ WITHOUT A WITHIN-SESSION TOGGLE AN A/B COSTS TWO PAGE LOADS, AND TWO PAGE LOADS ARE NOT
     *     COMPARABLE ON THIS MACHINE. Measured while building R5: repeated samples of the IDENTICAL
     *     shipped configuration — same pinned camera, same pinned resolution, the same
     *     227/403/1,038 shadow draw counts — read **35.9 / 45.4 / 58.0 / 73.3 ms** of shadow cost
     *     within one session, a 64 % spread, because three other agents hold dev servers and headed
     *     browsers on the box. Sharing one page, one device and one minute between an arm and its
     *     control is the only way the difference measured is the difference made.
     */
    setShadowSplit(mode) {
      // `parseSunShadowSplitMode` is the SAME lenient vocabulary `?shadowSplit=` takes — never a
      // second spelling of the same three arms.
      shadowSplitMode = parseSunShadowSplitMode(mode);
      csmRig.apply(sunShadowPolicy(tier, activeLighting.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride), activeLighting.sun.castShadow);
      return gameScene.csm();
    },
    /**
     * ⭐ THE CASCADE REACH'S RUNTIME DOOR — `?shadowDist=`'s twin, and it exists for the SAME reason
     * `setShadowSplit` does, stated one comment above: two page loads of an identical configuration
     * on this machine have measured **60 % apart** while other agents hold servers and browsers, so
     * an arm and its control must share one page, one device and one minute or the difference
     * measured is not the difference made.
     *
     * The reach is the strongest single lever on shadow cost — the far cascade holds 1,073 of 1,717
     * shadow draws on `mars-hangar-01` purely because it spans 28–90 m — and the owner has called
     * the shipped 90 m "too much by a long way". A number destined to be authored into every world
     * should come from a sweep that can be run in one sitting.
     *
     * `null` restores the authored/tier value. It re-applies through the SAME `sunShadowPolicy` +
     * `csmRig.apply` path every other door uses, so the frozen static map's radius follows it down
     * automatically (`resolveSunShadowSplit` takes the RESOLVED distance) — which is the owner's
     * "precomputed shadows must obey shadow distance" requirement holding by construction.
     */
    setShadowDistance(m) {
      shadowDistOverride = m === null || !Number.isFinite(m) || m <= 0 ? null : m;
      csmRig.apply(sunShadowPolicy(tier, activeLighting.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride), activeLighting.sun.castShadow);
      return gameScene.csm();
    },
    /**
     * ⭐ R4 — the warm's door. LAZY behind a compile-time DEV fold, the `mockGateway` shape: the
     * module reaches six private renderer fields and exists to be driven from the load window, so
     * until something in the shipping load path calls it there is no reason for its bytes to be in
     * a player's entry chunk. Whoever owns `scene.ts`'s warm-up wires it there with one `await`.
     */
    async warmShadowPipelines() {
      if (!import.meta.env.DEV) {
        return { ok: false, reason: 'dev-only until the load window wires it', handled: 0, asyncPipelines: 0, ms: 0 };
      }
      const { warmShadowPipelines } = await import('../render/shadowWarm');
      // A REAL frame, so the shadow passes run nested inside it at their real `_callDepth` — that
      // is the whole reason this warms the correct pipelines where `compileAsync` cannot.
      return warmShadowPipelines(renderer, () => pipeline.render());
    },
    setSunShadowUpdate(mode, n) {
      sunUpdateOverride =
        mode === 'everyFrame' || mode === 'everyNFrames' || mode === 'staticOnly'
          ? { update: mode, n: Number.isFinite(n) && (n as number) > 0 ? (n as number) : undefined }
          : null;
      csmRig.apply(sunShadowPolicy(tier, activeLighting.sun, csmOverride, shadowResScale, shadowSplitMode, sunUpdateOverride, shadowDistOverride), activeLighting.sun.castShadow);
      return gameScene.csm();
    },
    /*
     * ONE predicate, read off `giInfo.state` — never a second boolean. The bake bracket already
     * publishes `'baking'` before it starts and leaves it through both its success and its failure
     * arm, so "is a bake running" has exactly one answer and it cannot be left latched by a throw.
     */
    bakeHoldsFrame() {
      const baking = giInfo?.state === 'baking';
      /*
       * ⚠️ THE COUNT LIVES WITH THE PREDICATE, NOT WITH EITHER GATE, AND THAT IS DELIBERATE — the
       * first version counted inside `render()` and reported **0 suppressed frames on the very
       * bake that suppressed the most**, because the outer gate in `mount.ts` returns before
       * `render()` is ever called. A counter downstream of the thing it is counting measures the
       * door nobody uses. Both doors go through this predicate exactly once per frame, so this is
       * the one place the number is true.
       */
      if (baking) giBakeSuppressedFrames++;
      return baking;
    },
    render() {
      /*
       * ══ ⭐ O2 · THE BAKE GATE — NOTHING ELSE DRAWS WHILE A BAKE IS DRAWING ═══════════════════
       *
       * ⚠️ THIS IS THE OTHER HALF OF THE CHUNK-BOUNDARY DRAIN, AND NEITHER HALF IS SAFE ALONE.
       * Before O2 the bake yielded to rAF twice (once per bounce pass) and those frames rendered:
       * measured, 41 of them on a 20 s sandbox bake, each a full scene render against a frozen
       * matrix world with the sun disc hidden, the movable lights removed and the bake-only light
       * deliberately parented. `GiInfo.bakedLightLeaks` had to EXCLUDE the baking window to stop
       * that reading as a violation. Stage A turns two yields into one per chunk — 361 on the
       * owner's Sponza bake — so the same door goes from 41 wasted frames to ~400, and each one
       * would also stamp on the renderer state (`autoClear`, the bound target) that the bake holds
       * between chunks.
       *
       * So the bake now owns the renderer outright for its duration. This is a fence getting
       * STRONGER in the commit that would otherwise have weakened it: with no frame rendering
       * during the bake, `bakedLightLeaks` no longer needs its `state !== 'baking'` exemption, and
       * the leak guard below is armed through the bake instead of blind to it.
       *
       * The predicate is `giInfo.state`, not a second boolean — the bake bracket already publishes
       * `'baking'` before it starts and leaves it on the way out, through both the success and the
       * failure arm. A parallel flag would be a second answer to "is a bake running", and this
       * subsystem has paid for that shape before.
       *
       * `lastFrameAt` is reset rather than left, so the first real frame after a six-minute bake
       * reports `frameMs = 0` and the resolution controller is not handed a 360,000 ms frame it
       * would answer by collapsing to the lowest rung.
       */
      if (gameScene.bakeHoldsFrame()) {
        lastFrameAt = 0;
        return;
      }
      /*
       * The controller ticks BEFORE the draw, so a rung decided this frame applies to this
       * frame's pixels rather than the next one's. `frameMs` is the interval between successive
       * render() calls, which — since mount calls this exactly once per `setAnimationLoop`
       * callback — IS the rAF interval the player feels.
       */
      const now = performance.now();
      const frameMs = lastFrameAt > 0 ? now - lastFrameAt : 0;
      lastFrameAt = now;
      // The DPI watch (see the note where `lastDpr` is declared) — one number compare per frame.
      if (window.devicePixelRatio !== lastDpr) {
        lastDpr = window.devicePixelRatio;
        onResize();
      }
      if (uiFocused !== undefined) {
        // Hard rule 9's predicate, used here for ONE thing: an input-owning layer (z30 stage /
        // z40 cutscene) is up, so nothing may change resolution. Edge-triggered — freeze() is
        // idempotent but this keeps the per-frame cost to one boolean compare.
        const focused = uiFocused();
        if (focused !== uiFrozen) {
          uiFrozen = focused;
          if (focused) controller.freeze('ui');
          else controller.unfreeze('ui');
        }
      }
      const fresh = gpuSeq !== reportedGpuSeq ? ((reportedGpuSeq = gpuSeq), gpuTimeMs) : null;
      if (frameMs > 0) controller.tick(frameMs, fresh);
      // Dev stress rig (R1.5): re-poses the overdraw stack on the camera and re-places lights
      // while async world content is still arriving. Null on every normal boot, absent in prod.
      stress?.update();
      /*
       * ── THE SEAM GUARD (G1.4) IS GONE, AND ITS INVARIANT IS NOW STRUCTURAL (F3) ─────────────
       *
       * It compared, every frame, before the draw: **"a grid that is parented AND visible has an
       * atlas."** That is the invariant R1.6-D violated for the dense probe grid's entire life —
       * `LightProbeGridNode.setup()` branched on `light.texture`, `LightsNode.customCacheKey()`
       * cannot see `light.texture`, so ONE frame rendered with a null atlas cached the do-nothing
       * program for every lit material and nothing ever invalidated it. A per-frame counter rather
       * than a test, because the damage is done by the FIRST offending frame and is invisible after
       * it.
       *
       * ⭐ THE LIGHT CLASS WITH THE BRANCH IS DELETED. `SparseProbeVolume` binds both its textures
       * in its CONSTRUCTOR, to zero dummies, so `SparseProbeVolumeNode.setup()` has nothing to
       * branch on: an unpublished volume contributes nothing by ARITHMETIC (`EMPTY_BRICK`'s texels
       * are zero, so the validity divide yields 0), never by an early return. There is no state a
       * frame could catch it in. `giInfo.seamBreaks` therefore stays 0 and stays REPORTED — every
       * measurement prints it, and a silently-dropped invariant reads as one nobody checks.
       *
       * The fence moved UP, not away: `tests/renderSparseAddress.test.ts` asserts the cold (dummy)
       * and warm (published) graphs are node-for-node identical with equal branch censuses, which
       * catches the whole failure class at the shader graph instead of counting damage already done.
       */

      /*
       * THE SECOND SEAM GUARD (G3.2) — **a baked light is never also a live light.**
       *
       * The invariant is stated in `src/render/indirect.ts`, the bake bracket enforces it, and this
       * counts the frames where it was violated anyway. A bake-only light still parented once the
       * atlas exists is a punctual light counted TWICE — once in the SH, once live — which is the
       * hemisphere-plus-volume error with a different light type, plus the per-frame cost the bake
       * was supposed to buy back.
       *
       * ⚠️ IT WAS GATED ON `probeGrid !== null`, WHICH HAS BEEN NULL ON EVERY BAKE SINCE G5 — so
       * this guard had been inert for as long as the sparse bake has existed, while `?bakeLights=1`
       * went on marking lights through `bakeOnlyLights` and `gi.bakedLightLeaks` went on reporting
       * 0 for a reason that had nothing to do with the invariant holding. Found while retiring the
       * dense path; retargeted at `sparseVolume`, which is what carries the atlas now.
       *
       * Cost: one loop over a short array (≤ the number of marked lights, empty in every shipped
       * world), not a scene traverse.
       */
      /*
       * ⭐ O2 · THE `state !== 'baking'` EXEMPTION IS GONE, AND THE GUARD IS STRONGER FOR IT.
       *
       * The exemption existed because the bake yielded to rAF between passes and those frames
       * rendered: `sparseVolume` is claimed BEFORE the bake, the bake-only light is deliberately
       * parented in front of the cube cameras, and the guard counted every one of them — measured
       * 41 frames on a 20 s sandbox-01 bake, a false positive on the one path the feature exists
       * for, and a fence that cries wolf is a fence nobody reads.
       *
       * Stage A removed the CAUSE rather than the symptom: `render()`'s bake gate returns before
       * this line for every frame of a bake, so those frames no longer render and no longer reach
       * here. Dropping the exemption therefore costs nothing today and buys a real fence — if the
       * gate ever regresses and a frame renders mid-bake with a bake-only light parented, that is
       * now COUNTED and warned rather than excused by a clause written for the old behaviour.
       *
       * The invariant is unchanged and is `src/render/indirect.ts`'s: a light whose energy is in
       * an atlas must not also be lighting the frame. F3 retargeted this guard at `sparseVolume`
       * and recorded it had never been observed firing; E2 was the first run that fired it.
       */
      if (bakeOnlyLights.length > 0 && sparseVolume !== null) {
        let live = 0;
        for (const l of bakeOnlyLights) if (l.parent !== null && isBakeOnlyLight(l)) live++;
        if (live > 0) {
          if (giInfo) giInfo = { ...giInfo, bakedLightLeaks: giInfo.bakedLightLeaks + 1 };
          if (!bakedLightLeakWarned) {
            bakedLightLeakWarned = true;
            console.warn(
              `[gi] BAKED-LIGHT LEAK: ${live} light(s) baked into the probe volume are STILL in ` +
                'the rendered scene. Their energy is in the atlas AND in the frame — the same ' +
                'double-count as a hemisphere running under the volume — and they are still ' +
                'costing their per-frame price. See src/render/indirect.ts.',
            );
          }
        }
      }
      /*
       * ── ⭐ L2 · THE SHADOW-REFRESH LOD, DRIVEN HERE AND NOWHERE ELSE ──────────────────
       *
       * Schedules `shadow.needsUpdate` for `everyNFrames` casters. It returns 0 on every world
       * shipped today (nothing authors a light, and the default policy is `everyFrame`), and the
       * loop it runs is over `entries`, not over the graph — the same "short array, not a scene
       * traverse" shape as the bake-only leak guard above.
       *
       * ⚠️ IT WRITES `shadow.needsUpdate` AND NOTHING ELSE, AND THAT IS THE WHOLE DESIGN. The
       * obvious implementation of "drop this light's shadow at distance" is a per-frame
       * `castShadow` toggle, and `castShadow` is in `LightsNode.customCacheKey()` — so that
       * version would dispose and rebuild every visible render object EVERY FRAME. `needsUpdate`
       * and `autoUpdate` are in no cache key at all. `tests/renderLightShadowPolicy.test.ts`
       * scans `src/render/**` and `src/presentation/**` to keep the difference enforced rather
       * than remembered.
       *
       * `shadowFrame` is this scene's own counter rather than `renderer.info.render.frame`,
       * because the bake renders thousands of frames through the same renderer and a schedule
       * phased off that counter would jump on the far side of a bake.
       */
      // B3: cascade re-fit on a projection change and the two-frame swap after a count edit —
      // pose/uniform writes only; the `castShadow` cycle it owns is the ONE key move, by design.
      csmRig.tick(shadowFrame);
      /*
       * B4: the local shadow budget is what the tier has LEFT after the sun's live policy — read
       * per frame because `csmRig.policy()` moves under `?csm=`, an authored `cascades` and the
       * two-frame swap, and a budget taken at boot would be right only on unedited frames. The
       * camera is the one scene camera by reference (the editor fly-cam poses the same object),
       * so this is two field writes and no allocation.
       */
      shadowSlotInput.budgetTraversals = localShadowBudget(tier, csmRig.policy().traversals);
      worldLightRig.tickShadows(shadowFrame++, shadowSlotInput);
      /*
       * The dynamic entities' world matrices, because `entityRoot.matrixWorldAutoUpdate` is false
       * and the scene no longer walks that subtree. BEFORE the draw and before the shadow passes,
       * which read the same matrices. A detached root (its entity disposed) is pruned here rather
       * than needing a second lifetime hook — `parent === null` is the only signal that matters.
       */
      for (const root of dynamicRoots) {
        if (root.parent === null) dynamicRoots.delete(root);
        else root.updateMatrixWorld(true);
      }
      /*
       * ⭐ THE SKY FOLLOWS THE CAMERA, WHICH IS WHAT TAKES IT OUT OF THE LEVEL-SIZE ARGUMENT.
       *
       * `SkyMesh` is a `BackSide` box seen from INSIDE, so the only thing that has ever mattered is
       * that the camera is within it. Left at the origin it is a 400 m ball sitting in the middle
       * of a 688 m level: world geometry pokes out through it, and the dome reads as a blue surface
       * clipping through the vista — which is exactly what it was.
       *
       * Locked to the camera it cannot intersect anything, at any scale, in any world. The dome's
       * size then answers one question only — how far can you SEE — and that is the far plane's
       * business, not the level's.
       */
      if (sky !== null) {
        sky.mesh.position.copy(camera.position);
        sky.mesh.updateMatrix();
        sky.mesh.updateMatrixWorld(true);
      }
      pipeline.render();
      /*
       * THE LATCH (see `GameScene.frameStats`). Immediately after the draw and before anything can
       * yield, because this is the only moment `renderer.info.render` holds this frame's totals —
       * the next animation frame resets it. Two field reads, no allocation.
       */
      lastFrameDraws = renderer.info.render.drawCalls;
      lastFrameTris = renderer.info.render.triangles;
      pumpTimestamps();
      /*
       * `?gi=probe` — fire ONCE, and only after the scene has stopped growing. The bake takes 6·N
       * photographs of the world, so firing it at boot photographs an empty room and produces an
       * atlas of the sky that reads, entirely plausibly, as "probe GI does nothing here". The
       * triangle count is the observable: `render.triangles` is what a 50 MB glTF actually moves,
       * and three consecutive unchanged reads a second apart is the same "content has settled"
       * test the stress rig uses on its bounds. Dev-only — `giAutoBake` can only be true under
       * `import.meta.env.DEV`, so a production build folds this to `if (false)`.
       *
       * ⚠️ AFTER `pipeline.render()`, NOT BEFORE, and this is not tidiness. Read before the draw,
       * `renderer.info.render.triangles` is **0 every frame** — measured, not assumed: the counter
       * is reset for the frame that has not been submitted yet, and the previous frame's totals
       * are gone by then. A check placed above the draw therefore never sees a stable non-zero
       * count and the bake NEVER FIRES, which is a silent no-op that looks exactly like a feature
       * that does nothing.
       */
      if (giAutoBake && now - giLastCheckMs > 1000) {
        giLastCheckMs = now;
        const tris = renderer.info.render.triangles;
        if (tris > 0 && tris === giLastTris) giStableChecks++;
        else giStableChecks = 0;
        giLastTris = tris;
        if (giStableChecks >= 3) {
          giAutoBake = false; // once, whatever happens next — a failed bake warns and stays off
          void gameScene.setProbeGrid(true);
        }
      }
    },
    dispose() {
      window.removeEventListener('resize', onResize);
      resizeObserver?.disconnect();
      // P1: the character holds twelve library handles. Releasing them is what lets the service
      // dispose the sources after their grace period; without this the whole boot set stays
      // resident for the life of the page and `stats().resident` never returns to zero, which is
      // precisely the leak the P1 gate measures.
      charView?.dispose();
      charView = null;
      csmRig.dispose();
      debugView?.dispose();
      stress?.dispose();
      sky?.dispose();
      pipeline.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
  return gameScene;
}
